import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/boost";
import axios from "axios";
import { toast } from "sonner";
import Swal from "sweetalert2";
import Header from "./Header.tsx";
import Sidebar from "./Sidebar.tsx";
import ManageTemplatesModal from "./ManageTemplatesModal.tsx";
import ExportDataModal from "./ExportDataModal.tsx";
import PumpDetailsModal from "./PumpDetailsModal.tsx";
import styles from "./Fleet.module.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useTheme from "../hooks/useTheme.ts";
import {
  FiDownload,
  FiInfo,
  FiAlertTriangle,
  FiAlertCircle,
  FiCheckCircle,
  FiExternalLink,
  FiRotateCcw,
} from "react-icons/fi";
import { FiSettings } from "react-icons/fi";
import { CustomMultiSelect, CustomSingleSelect } from "./CustomSelect.tsx";
import { useLocation } from "react-router-dom";
import {
  GROUPED_PUMP_CHANNELS,
  ALL_PUMP_CHANNELS,
  PumpChannel,
  PumpChannelGroup,
  findChannelByValue,
  toWholeNumberReading,
} from "../utils/pumpChannels.ts";
import {
  minMaxDownsampleTuples,
  insertGapBreaks,
  parseColumnarToBuffers,
  appendSamplesToBuffers,
  buildDownsampledSeries,
  minMaxDownsample,
  minMaxDownsampleWithGaps,
  sliceBuffer,
  DEFAULT_MAX_POINTS,
  type FleetBuffers,
  type ChannelBuffer,
} from "../utils/pumpSeries.ts";
import {
  channelAxisGridOptions,
  emptyChartGridAxis,
} from "../utils/chartGrid.ts";
import {
  buildBlenderChartOptions,
  buildBlenderHandoffKey,
  describeBlenderDataError,
  formatBlenderTimestamp,
  formatBlenderValue,
  inferBlenderType,
  mergeBlenderLiveRows,
  parseBlenderDataResponse,
  type BlenderChannel,
  type BlenderChannelGroup,
  type BlenderChannelsResponse,
  type BlenderChartHandoff,
  type BlenderChartView,
  type BlenderData,
  type BlenderDataResponse,
  type BlenderLiveResponse,
  type BlenderType,
} from "../utils/blenderChart.ts";

Highcharts.setOptions({
  time: {
    useUTC: false,
  },
} as any);

const STREAMING_WINDOW_MS = 3 * 60 * 60 * 1000;
// Streaming validation hits /latest-treatment-pump-data-multi, which builds the
// live fleet snapshot. The first (cold) call for a fleet has to run the per-pump
// CROSS APPLY query before the 0.9s server-side cache is populated, and for a
// large electric fleet that can exceed the old 20s ceiling — surfacing as an
// ECONNABORTED "timeout of 20000ms exceeded" and streaming never starting.
// Give the cold call more room, and retry once: the aborted first request still
// primes the backend cache, so the retry almost always returns immediately.
const STREAMING_VALIDATE_TIMEOUT_MS = 60000;
const STREAMING_VALIDATE_RETRIES = 1;

// Real-time updates poll the snapshot API on this cadence. A guard skips a tick
// while the previous request is still in flight, so a slow snapshot response
// can't stack up overlapping XHRs.
const STREAMING_POLL_INTERVAL_MS = 1000;

// Yield to the browser so pending paint/scroll input can be processed between
// expensive historical payload parsing and Highcharts series construction.
const yieldToBrowser = () =>
  new Promise<void>((resolve) =>
    window.requestAnimationFrame(() => window.setTimeout(resolve, 0)),
  );
// The incremental endpoint caps a single response at ~60s of rows. If one batch
// spans that whole window, the tab was likely backgrounded / a poll stalled and
// data older than the cap was dropped — treat it as a possible gap.
const STREAMING_INCREMENTAL_MAX_MS = 60000;
// Alarm markers are drawn in the band above the plot.
const ALARM_BAND_HEIGHT = 36;
const FLEET_VIEW_STATE_KEY = "padtrack:fleet-view-state:v1";
const ALARM_REVIEW_CONTEXT_KEY = "padtrack:alarm-review-context:v1";
const RECENT_BLENDERS_KEY = "padtrack:recent-blenders:v1";
const BLENDER_CHANNEL_FAVORITES_KEY = "padtrack:blender-channel-favorites:v1";

interface BlenderChannelFavorite {
  id: string;
  name: string;
  channels: string[];
  createdAt: number;
}

interface PersistedFleetViewState {
  activeTab?: "fleet" | "asset" | "blender" | "comments";
  selectedFleetId?: number | null;
  expandedFleetIds?: number[];

  fromDateByFleet?: Record<number, number | null>;
  toDateByFleet?: Record<number, number | null>;

  selectedWellIdsByPad?: Record<number, number[]>;
  showAllWellsByPad?: Record<number, boolean>;

  selectedPumpsByFleet?: Record<number, string[]>;
  hiddenPumpsByFleet?: Record<number, string[]>;

  selectedChannelsByFleet?: Record<number, PumpChannel[]>;
  selectedTableChannelsByFleet?: Record<number, PumpChannel[]>;

  chartModeByPad?: Record<number, "combined" | "compare">;

  xAxisRangeByFleet?: Record<
    number,
    {
      min?: number;
      max?: number;
    }
  >;

  alarmSeverityFilter?: "critical" | "warning";

  // Blender state
  selectedBlenderId?: string;
  selectedBlenderChannels?: string[];
  blenderFromDate?: number | null;
  blenderToDate?: number | null;
}

interface AlarmReviewContext {
  alarm: any;
  fleetId: number;
  startTime: number;
  endTime: number;
  savedAt: number;
}

interface Pad {
  id: number;
  pad_name: string;
  fleet: number;
  status: string;
  job_number: number;
  organization_id: number;
  stage_count: number;
  well_count: number;
  start_ts: number | null;
  end_ts: number | null;
  completion_status?: string | null;
  completion_status_label?: string | null;
}

interface FleetGroup {
  fleet: number;
  latestPad: Pad;
}

interface WellActivityWell {
  wellId: number;
  wellName: string;
  stageNumber: number | null;
  pumpCount: number;
}

// A contiguous period over which the same SET of wells was being pumped. More than
// one well means simul-frac: on pad 14276, wells 811H and 813H ran concurrently
// for all 168 minutes measured, each with its own 14 pumps.
interface WellActivityBand {
  wells: WellActivityWell[];
  from: number;
  to: number;
}

interface Template {
  id: number;
  template_name: string;
  widget_type: string;
  channels: TemplateChannel[];
}

interface TemplateChannel {
  channel: string;
  display_position: string;
  min_value: number | null;
  max_value: number | null;
  axis: number | null;
}

interface PumpData {
  id: number;
  time_stamp: number;
  pump_name: string;
  [key: string]: any;
}

interface StageChannelData {
  id?: number;
  fleet: number;
  pad_id?: number | null;
  well_id?: number | null;
  stage_id?: number | null;
  time_stamp: number;
  surface_treating_pressure?: number | null;
  density?: number | null;
  clean_rate?: number | null;
  slurry_rate?: number | null;
}

interface PumpReengagementEvent {
  pump: string;
  alarm: string;
  graph_channel?: string | null;
  alarm_time: string | null;
  alarm_time_ms: number;
  neutral_time_ms: number;
  reengagement_time_ms: number;
  time_to_reengage_seconds: number;
  gear: number;
  discharge_pressure: number;
}

interface ChartAnnotation {
  id: string;
  fleet?: number;
  timestamp: number;
  comment: string;
  pump?: string;
  createdAt: number;
  createdBy?: string;
  createdByEmail?: string;
  canEdit?: boolean;
  canDelete?: boolean;
}

interface ChartAnnotationAudit {
  id?: number;
  annotationId?: string;
  annotation_id?: string;
  fleet?: number;
  pump?: string;
  pump_name?: string;
  annotationTime?: number;
  annotation_time?: number;
  actionType?: string;
  action_type?: string;
  oldComment?: string | null;
  old_comment?: string | null;
  newComment?: string | null;
  new_comment?: string | null;
  performedBy?: string;
  performed_by?: string;
  performedByEmail?: string;
  performed_by_email?: string;
  performedAt?: number;
  performed_at?: number;
}

type ThresholdSeverity = "normal" | "warning" | "critical";

interface PumpInterlockCondition {
  channel: string;
  operator: string;
  value: number;
}

interface PumpInterlockLimit {
  channel: string;
  severity: "warning" | "critical";
  operator: string;
  value: number;
  conditions?: PumpInterlockCondition[];
}

interface WellOption {
  id: number;
  well_name: string;
  api?: string;
  stage_count?: number;
  start_ts?: number | null;
  end_ts?: number | null;
}

// These two Power End pressure channels are not valid PadTrack telemetry
// channels. Keep them out of selectors, templates, persisted selections, and API
// requests so an old saved selection cannot trigger a backend "invalid channel"
// response. Keep the valid Power End Oil Pressure channel unchanged.
const UNSUPPORTED_POWER_END_PRESSURE_CHANNEL_KEYS = new Set([
  "powerend_oil_filter_inlet_pressure",
  "power_end_oil_filter_inlet_pressure",
  "pe_oil_filter_inlet_pressure",
  "powerend_oil_filter_outlet_pressure",
  "power_end_oil_filter_outlet_pressure",
  "pe_oil_filter_outlet_pressure",
]);

const normalizeUnsupportedChannelKey = (value: string): string =>
  String(value || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase()
    .replace(/_(psi|psig)$/g, "");

const isUnsupportedPowerEndPressureChannel = (value: string): boolean =>
  UNSUPPORTED_POWER_END_PRESSURE_CHANNEL_KEYS.has(
    normalizeUnsupportedChannelKey(value),
  );

const filterUnsupportedPumpChannels = (
  channels: PumpChannel[] | undefined | null,
): PumpChannel[] =>
  (channels || []).filter(
    (channel) =>
      Boolean(channel?.value) &&
      !isUnsupportedPowerEndPressureChannel(channel.value) &&
      !isUnsupportedPowerEndPressureChannel(channel.label),
  );

const ADDITIONAL_UNIVERSAL_CHANNELS: PumpChannel[] = [
  { value: "trip_discharge_pressure", label: "Trip Discharge Pressure (psi)" },
  { value: "pe_oil_level_low", label: "Power End: Oil Level Low" },
  {
    value: "powerend_oil_delivered_flow_rate",
    label: "Power End: Oil Delivered Flow Rate (gpm)",
  },
  {
    value: "powerend_oil_filter_differential_pressure",
    label: "Power End: Oil Filter Differential Pressure (psi)",
  },
  {
    value: "powerend_oil_heat_retention",
    label: "Power End: Oil Heat Retention (°F)",
  },
  {
    value: "powerend_oil_radiator_differential_temperature",
    label: "Power End: Oil Radiator Differential Temperature (°F)",
  },
  {
    value: "powerend_oil_bypass_flow_rate",
    label: "Power End: Oil Bypass Flow Rate (gpm)",
  },
  {
    value: "powerend_oil_inlet_temperature",
    label: "Power End: Oil Inlet Temperature (°F)",
  },
  {
    value: "powerend_oil_outlet_temperature",
    label: "Power End: Oil Outlet Temperature (°F)",
  },
  {
    value: "powerend_oil_pump_flow_rate",
    label: "Power End: Oil Pump Flow Rate (gpm)",
  },
  {
    value: "powerend_oil_radiator_temperature",
    label: "Power End: Oil Radiator Temperature (°F)",
  },
  {
    value: "powerend_oil_tank_temperature",
    label: "Power End: Oil Tank Temperature (°F)",
  },
];

const ADDITIONAL_DIESEL_CHANNELS: PumpChannel[] = [
  {
    value: "trans_oil_pressure",
    label: "Transmission: Oil Pressure (psi)",
  },
  {
    value: "engine_oil_pressure",
    label: "Engine: Oil Pressure (psi)",
  },
  { value: "df_displacement", label: "Engine: DF Displacement (%)" },
  {
    value: "transmission_input_speed",
    label: "Transmission: Input Speed (rpm)",
  },
  {
    value: "transmission_output_speed",
    label: "Transmission: Output Speed (rpm)",
  },
  { value: "engine_oil_temperature", label: "Engine: Oil Temperature (°F)" },
  { value: "engine_battery_voltage", label: "Engine: Battery Voltage (V)" },
  { value: "engine_fuel_pressure", label: "Engine: Fuel Pressure (psi)" },
  {
    value: "engine_intake_manifold_pressure",
    label: "Engine: Intake Manifold Pressure (psi)",
  },
  {
    value: "engine_torque_pct",
    label: "Engine: Torque (%)",
  },
  {
    value: "engine_df_supply_pressure",
    label: "Engine: DF Supply Pressure (psi)",
  },
  {
    value: "engine_oil_differential_pressure",
    label: "Engine: Oil Differential Pressure (psi)",
  },
  {
    value: "engine_def_level_pct",
    label: "Engine: DEF Level (%)",
  },
];

const ADDITIONAL_ELECTRIC_CHANNELS: PumpChannel[] = [
  {
    value: "calculated_discharge_rate",
    label: "Discharge Rate",
  },
  {
    value: "discharge_vfd_amps",
    label: "VFD Amps",
  },
  {
    value: "discharge_vfd_dc_link_volts",
    label: "VFD DC Link Volts",
  },
  {
    value: "discharge_motor_winding_a1_temp",
    label: "Winding A1 Temperature (°F)",
  },
  {
    value: "discharge_motor_winding_a2_temp",
    label: "Winding A2 Temperature (°F)",
  },
  {
    value: "discharge_motor_winding_b1_temp",
    label: "Winding B1 Temperature (°F)",
  },
  {
    value: "discharge_motor_winding_b2_temp",
    label: "Winding B2 Temperature (°F)",
  },
  {
    value: "discharge_motor_winding_c1_temp",
    label: "Winding C1 Temperature (°F)",
  },
  {
    value: "discharge_motor_winding_c2_temp",
    label: "Winding C2 Temperature (°F)",
  },
  {
    value: "discharge_motor_de_bearing_temp",
    label: "Drive-End Bearing Temperature (°F)",
  },
  {
    value: "discharge_motor_nde_bearing_temp",
    label: "Non-Drive-End Bearing Temperature (°F)",
  },
  {
    value: "cube_coolant_supply_pressure",
    label: "Cube: Coolant Supply Pressure (psi)",
  },
];

const ADDITIONAL_CHANNELS: PumpChannel[] = [
  ...ADDITIONAL_UNIVERSAL_CHANNELS,
  ...ADDITIONAL_DIESEL_CHANNELS,
  ...ADDITIONAL_ELECTRIC_CHANNELS,
];

const STAGE_CHANNEL_VALUES = new Set([
  "surface_treating_pressure",
  "density",
  "clean_rate",
  "slurry_rate",
]);

const isStageChannel = (channelValue: string): boolean =>
  STAGE_CHANNEL_VALUES.has(channelValue);

const pumpPadId = (pump: any): number | null => {
  const raw = pump?.pad_id ?? pump?.current_pad_id ?? pump?.padId;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const isElectricControllerType = (pump: any): boolean =>
  Number(pump?.controller_type) === 101;

const isDieselControllerType = (pump: any): boolean => {
  const controllerType = Number(pump?.controller_type);
  return controllerType === 100 || controllerType === 1;
};

const pumpListName = (pump: any): string =>
  String(pump?.pump_name || pump?.unit_number || "").trim();

type FleetListStatus = "active" | "lost_comms" | "expired" | "complete";

const COMPLETION_STATUS_BY_CODE: Record<string, FleetListStatus> = {
  a: "active",
  n: "complete",
  i: "lost_comms",
  e: "expired",
};

const normalizeCompletionStatus = (raw: unknown): FleetListStatus | null => {
  const value = String(raw ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!value) return null;

  // /active-stages.completion_status is the raw JDD code:
  // A=Active, N=Complete, I=Lost Comm, E=Expired, U=Unknown.
  if (COMPLETION_STATUS_BY_CODE[value]) {
    return COMPLETION_STATUS_BY_CODE[value];
  }

  if (
    (value.includes("lost") || value.includes("loss")) &&
    (value.includes("comm") || value.includes("communication"))
  ) {
    return "lost_comms";
  }
  if (value.includes("expired")) return "expired";
  if (
    value === "active" ||
    value.includes("pump") ||
    value.includes("progress")
  ) {
    return "active";
  }
  if (value.includes("complet")) return "complete";

  return null;
};

const extractActiveStageRows = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const obj = payload as Record<string, any>;
  for (const key of ["data", "stages", "active_stages", "results", "items"]) {
    if (Array.isArray(obj[key])) return obj[key];
  }

  const rows: any[] = [];
  Object.entries(obj).forEach(([key, value]) => {
    if (value == null || typeof value !== "object") return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (!item || typeof item !== "object") return;
        rows.push({
          ...item,
          fleet: item.fleet ?? item.fleet_id ?? Number(key),
        });
      });
      return;
    }

    const nested = value.stages ?? value.data;
    if (Array.isArray(nested)) {
      nested.forEach((item: any) => {
        rows.push({
          ...item,
          fleet: item?.fleet ?? item?.fleet_id ?? value.fleet ?? Number(key),
          completion_status:
            item?.completion_status ??
            item?.completionStatus ??
            item?.status ??
            value.completion_status ??
            value.status,
        });
      });
      return;
    }

    const fleet = Number(value.fleet ?? value.fleet_id ?? key);
    if (!Number.isFinite(fleet)) return;
    rows.push({
      ...value,
      fleet,
      completion_status:
        value.completion_status ?? value.completionStatus ?? value.status,
    });
  });
  return rows;
};

const completionStatusFromActiveStages = (
  stages: any[],
): Record<number, FleetListStatus> => {
  const latestByFleet = new Map<
    number,
    { status: FleetListStatus; timestamp: number }
  >();

  stages.forEach((stage) => {
    const fleet = Number(stage?.fleet ?? stage?.fleet_id);
    if (!Number.isFinite(fleet)) return;

    const status = normalizeCompletionStatus(
      stage?.completion_status ??
        stage?.completionStatus ??
        stage?.completion_status_label ??
        stage?.completionStatusLabel ??
        stage?.status,
    );
    if (!status) return;

    const timestamp = new Date(
      stage?.last_updated ?? stage?.lastUpdated ?? 0,
    ).getTime();
    const previous = latestByFleet.get(fleet);
    if (!previous || timestamp >= previous.timestamp) {
      latestByFleet.set(fleet, {
        status,
        timestamp: Number.isFinite(timestamp) ? timestamp : 0,
      });
    }
  });

  const result: Record<number, FleetListStatus> = {};
  latestByFleet.forEach((entry, fleet) => {
    result[fleet] = entry.status;
  });
  return result;
};

const splitChannelsBySource = (channels: PumpChannel[]) => ({
  pumpChannels: channels.filter((channel) => !isStageChannel(channel.value)),
  stageChannels: channels.filter((channel) => isStageChannel(channel.value)),
});

const appendUniquePumpChannels = (
  existingChannels: PumpChannel[],
  newChannels: PumpChannel[],
): PumpChannel[] => {
  const seen = new Set(existingChannels.map((channel) => channel.value));

  return [
    ...existingChannels,
    ...newChannels.filter((channel) => {
      if (seen.has(channel.value)) return false;
      seen.add(channel.value);
      return true;
    }),
  ];
};

const POWER_END_TEMPERATURE_CHANNEL_VALUES = new Set([
  "powerend_oil_temperature",
  "powerend_oil_inlet_temperature",
  "powerend_oil_outlet_temperature",
  "pe_temp",
  "pe_outlet_temp",
]);

const isPowerEndTemperatureChannelValue = (value: string): boolean =>
  POWER_END_TEMPERATURE_CHANNEL_VALUES.has(String(value || "").trim());

const LEGACY_POWER_END_OIL_TEMPERATURE_VALUES = new Set([
  "powerend_oil_temperature",
  "power_end_oil_temperature",
  "pe_oil_temperature",
  "pe_temp",
]);

const isLegacyPowerEndOilTemperatureChannel = (value: string): boolean =>
  LEGACY_POWER_END_OIL_TEMPERATURE_VALUES.has(
    String(value || "")
      .trim()
      .toLowerCase(),
  );

const CANONICAL_POWER_END_INLET_TEMPERATURE_CHANNEL: PumpChannel = {
  value: "powerend_oil_inlet_temperature",
  label: "Power End: Oil Inlet Temperature (°F)",
};

const normalizePowerEndTemperatureSelection = (
  channels: PumpChannel[] | undefined | null,
): PumpChannel[] => {
  const seen = new Set<string>();

  return (channels || [])
    .map((channel) =>
      isLegacyPowerEndOilTemperatureChannel(channel.value)
        ? CANONICAL_POWER_END_INLET_TEMPERATURE_CHANNEL
        : channel,
    )
    .filter((channel) => {
      if (!channel?.value || seen.has(channel.value)) {
        return false;
      }

      seen.add(channel.value);
      return true;
    });
};

const isPowerEndTemperatureSeriesName = (name: string): boolean => {
  const normalized = String(name || "").toLowerCase();

  return (
    normalized.includes("power end: oil temperature") ||
    normalized.includes("power end: oil inlet temperature") ||
    normalized.includes("power end: oil outlet temperature")
  );
};

const hasRealPowerEndTemperatureSeriesData = (data: unknown): boolean => {
  if (!Array.isArray(data) || data.length === 0) return false;

  return data.some((point: any) => {
    const value = Array.isArray(point) ? Number(point[1]) : Number(point?.y);

    return Number.isFinite(value) && value !== 0;
  });
};

const downsampledToHighcharts = (
  series: ReturnType<typeof buildDownsampledSeries>,
): Highcharts.SeriesOptionsType[] =>
  series
    .filter((s) => {
      if (!isPowerEndTemperatureSeriesName(String(s.name || ""))) {
        return true;
      }

      return hasRealPowerEndTemperatureSeriesData(s.data);
    })
    .map((s) => {
      const isGearSeries = /(^|\s|-)gear(\s|$)/i.test(String(s.name || ""));
      const pointCount = Array.isArray(s.data) ? s.data.length : 0;

      return {
        type: s.isSinglePoint ? "scatter" : "line",
        name: s.name,
        data: s.data as Array<[number, number]>,
        yAxis: s.yAxis,

        // Do not connect across missing telemetry. Connecting gaps can produce
        // misleading vertical blocks on short alarm-review windows.
        connectNulls: false,

        stickyTracking: false,
        animation: false,

        // Highcharts Boost/WebGL can render dense short line series as solid
        // color bands. Keep normal SVG rendering for alarm-review-sized series,
        // while retaining Boost for genuinely large Fleet datasets.
        boostThreshold: pointCount <= 10000 ? Number.MAX_SAFE_INTEGER : 10000,
        turboThreshold: 0,

        // Gear is discrete state data. A step line shows the actual transitions
        // instead of drawing diagonal interpolation between gear values.
        step: isGearSeries && !s.isSinglePoint ? "left" : undefined,

        marker: s.isSinglePoint
          ? {
              enabled: true,
              radius: 8,
              symbol: "circle",
              lineWidth: 2,
              lineColor: "#ffffff",
            }
          : {
              enabled: false,
              states: {
                hover: {
                  enabled: true,
                  radius: 4,
                },
              },
            },

        lineWidth: s.isSinglePoint ? 0 : 2,

        // Explicitly prevent any accidental area-style fill.
        fillColor: "transparent",
        fillOpacity: 0,

        visible: s.visible,
        zIndex: s.isSinglePoint ? 5 : 1,
      } as Highcharts.SeriesOptionsType;
    });

const collapseChannelBufferToOneSecond = (
  buffer: ChannelBuffer,
): ChannelBuffer => {
  const raw = buffer as any;
  const timestamps = raw.t;
  const values = raw.v ?? raw.y ?? raw.values;

  if (!timestamps?.length || !values?.length) return buffer;

  const latestBySecond = new Map<number, number>();

  for (let index = 0; index < timestamps.length; index += 1) {
    const timestamp = Number(timestamps[index]);
    const value = Number(values[index]);

    if (!Number.isFinite(timestamp) || !Number.isFinite(value)) continue;

    const secondTimestamp = Math.floor(timestamp / 1000) * 1000;
    latestBySecond.set(secondTimestamp, value);
  }

  const points = Array.from(latestBySecond.entries()).sort(
    (left, right) => left[0] - right[0],
  );

  const nextT = Float64Array.from(points.map(([timestamp]) => timestamp));
  const nextV = Float64Array.from(points.map(([, value]) => value));

  return {
    ...raw,
    t: nextT,
    ...(raw.v !== undefined ? { v: nextV } : {}),
    ...(raw.y !== undefined ? { y: nextV } : {}),
    ...(raw.values !== undefined ? { values: nextV } : {}),
  } as ChannelBuffer;
};

const collapseFleetBuffersToOneSecond = (
  buffers: FleetBuffers,
): FleetBuffers => {
  const collapsed: FleetBuffers = new Map();

  buffers.forEach((channelMap, pumpName) => {
    const collapsedChannels = new Map<string, ChannelBuffer>();

    channelMap.forEach((buffer, channelValue) => {
      collapsedChannels.set(
        channelValue,
        collapseChannelBufferToOneSecond(buffer),
      );
    });

    collapsed.set(pumpName, collapsedChannels);
  });

  return collapsed;
};

const buffersToSeedRows = (
  buffers: FleetBuffers,
  windowStart: number,
  windowEnd: number,
  fleetId: number,
): Record<string, any>[] => {
  const rows: Record<string, any>[] = [];
  buffers.forEach((channelMap, pumpName) => {
    const byTimestamp = new Map<number, Record<string, any>>();
    channelMap.forEach((buffer, channelValue) => {
      const windowed = sliceBuffer(buffer, windowStart, windowEnd);
      const points = minMaxDownsample(windowed, DEFAULT_MAX_POINTS);
      for (const [ts, value] of points) {
        let row = byTimestamp.get(ts);
        if (!row) {
          row = { pump_name: pumpName, time_stamp: ts, fleet: fleetId };
          byTimestamp.set(ts, row);
        }
        row[channelValue] = value;
      }
    });
    byTimestamp.forEach((row) => rows.push(row));
  });
  return rows;
};

type AssetPumpOption = {
  value: string;
  label: string;
  rawPumpNames: string[];
};

// One row of GET /blenders/list. last_seen is on the telemetry clock (runs
// ahead of real UTC), so never compare it against Date.now().
type BlenderListItem = {
  id: number;
  blender_name: string;
  fleet: number | null;
  is_electric: boolean | null;
  pad_id: number | null;
  first_seen: number;
  last_seen: number | null;
  added_at: string;
};

type BlenderAsset = {
  id: string;
  label: string;
  type: BlenderType;
  fleet: number | null;
  padId: number | null;
  lastSeen: number | null;
};

// Channels preselected when a blender is opened, matched by JDD label. Any
// that this blender doesn't report are simply skipped.
const DEFAULT_BLENDER_CHANNEL_LABELS = [
  "Auger 1: RPM",
  "Chemical 1: Rate",
  "Chemical 1: VFD Speed",
  "Discharge Engine: Speed",
  "Discharge Motor: Speed",
  "Discharge Pump: Rate",
  "Discharge: VFD Amps",
];
const DEFAULT_EXPANDED_BLENDER_GROUP_COUNT = 3;

const BLENDER_DATA_DEBOUNCE_MS = 500;
// Real-time streaming: poll /live once a second. The chart keeps a rolling
// window the length of the selected From/To span, between these bounds.
const BLENDER_LIVE_POLL_MS = 1000;
const BLENDER_LIVE_MIN_WINDOW_MS = 10 * 60 * 1000;
const BLENDER_LIVE_MAX_WINDOW_MS = 24 * 60 * 60 * 1000;
// /live serves at most 1 h on its first call; anything older is backfilled
// from /data at 1s resolution.
const BLENDER_LIVE_MAX_LOOKBACK_MS = 60 * 60 * 1000;
const BLENDER_LIVE_MAX_RETRY_MS = 10000;
// While streaming, the chart follows the newest reading and shows only this
// much of the rolling window, so each new second visibly moves the line
// (one second of a 6 h window is a fraction of a pixel). null = whole window.
const BLENDER_LIVE_VIEW_OPTIONS: Array<{ label: string; ms: number | null }> = [
  { label: "Last 5 min", ms: 5 * 60 * 1000 },
  { label: "Last 10 min", ms: 10 * 60 * 1000 },
  { label: "Last 30 min", ms: 30 * 60 * 1000 },
  { label: "Last 1 hour", ms: 60 * 60 * 1000 },
  { label: "Full window", ms: null },
];
const DEFAULT_BLENDER_LIVE_VIEW_MS = 10 * 60 * 1000;
// chart.update(options, redraw, oneToOne, animation). Kept as a constant:
// highcharts-react re-runs the update whenever this array's identity changes.
const BLENDER_CHART_UPDATE_ARGS: [boolean, boolean, boolean] = [
  true,
  true,
  false,
];
const BLENDER_TABLE_MAX_ROWS = 500;

interface AxisValueEditState {
  axisName: "left1" | "left2" | "right1" | "right2";
  axisTitle: string;
  clickedValue: number;
  ratioFromBottom: number;
  currentMin: number;
  currentMax: number;
  error?: string;
}
const Fleet: React.FC = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const fleetParam = new URLSearchParams(location.search).get("fleet");
  const initialSidebarOpen =
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true;
  const [showSidebar, setShowSidebar] = useState(initialSidebarOpen);
  const [activeTab, setActiveTab] = useState<
    "fleet" | "asset" | "blender" | "comments"
  >("fleet");

  const [blenderAssets, setBlenderAssets] = useState<BlenderAsset[]>([]);
  const [blenderListStatus, setBlenderListStatus] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  const [selectedBlenderId, setSelectedBlenderId] = useState("");
  const [recentBlenderIds, setRecentBlenderIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(RECENT_BLENDERS_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];

      return Array.from(
        new Set(
          parsed.map((value) => String(value || "").trim()).filter(Boolean),
        ),
      ).slice(0, 5);
    } catch (error) {
      console.warn("Unable to restore recently viewed Blenders:", error);
      return [];
    }
  });
  const pendingBlenderChannelsRef = useRef<{
    blenderId: string;
    channels: string[];
  } | null>(null);
  const blenderDeepLinkInitializedRef = useRef("");
  const [blenderChannelSearch, setBlenderChannelSearch] = useState("");
  // Channel keys (database column names) for the selected blender.
  const [selectedBlenderChannels, setSelectedBlenderChannels] = useState<
    string[]
  >([]);
  const [blenderChannelFavorites, setBlenderChannelFavorites] = useState<
    BlenderChannelFavorite[]
  >(() => {
    try {
      const stored = localStorage.getItem(BLENDER_CHANNEL_FAVORITES_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter(
          (item) =>
            item &&
            typeof item.name === "string" &&
            Array.isArray(item.channels),
        )
        .map((item) => ({
          id: String(item.id || Date.now()),
          name: String(item.name).trim(),
          channels: Array.from(
            new Set(
              item.channels
                .map((channel: unknown) => String(channel || "").trim())
                .filter(Boolean),
            ),
          ),
          createdAt: Number(item.createdAt) || Date.now(),
        }))
        .filter((item) => item.name && item.channels.length > 0);
    } catch (error) {
      console.warn("Unable to restore Blender channel favorites:", error);
      return [];
    }
  });
  const [expandedBlenderGroups, setExpandedBlenderGroups] = useState<
    Set<string>
  >(new Set());
  const [blenderChannelGroups, setBlenderChannelGroups] = useState<
    BlenderChannelGroup[]
  >([]);
  const [blenderChannelCount, setBlenderChannelCount] = useState(0);
  const [blenderChannelsStatus, setBlenderChannelsStatus] = useState<
    "idle" | "loading" | "loaded" | "not_found" | "error"
  >("idle");
  const [blenderFromDate, setBlenderFromDate] = useState<Date>(
    () => new Date(Date.now() - 6 * 60 * 60 * 1000),
  );
  const [blenderToDate, setBlenderToDate] = useState<Date>(() => new Date());
  // From/To being edited but not loaded yet; Load Data applies them to
  // blenderFromDate/blenderToDate, which drive the data request.
  const [blenderDraftDates, setBlenderDraftDates] = useState<{
    from?: Date;
    to?: Date;
  } | null>(null);
  // Bumped by Load Data when the range is unchanged, to reload it anyway.
  const [blenderReloadToken, setBlenderReloadToken] = useState(0);
  const [blenderData, setBlenderData] = useState<BlenderData | null>(null);
  const [blenderDataStatus, setBlenderDataStatus] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  const [blenderDataError, setBlenderDataError] = useState("");
  const [isBlenderStreaming, setIsBlenderStreaming] = useState(false);
  const [blenderLiveViewMs, setBlenderLiveViewMs] = useState<number | null>(
    DEFAULT_BLENDER_LIVE_VIEW_MS,
  );
  // From /live: `live` is the server's "data advanced in the last 30 s" flag,
  // which is reliable even though telemetry clocks run ahead of the browser.
  const [blenderLiveStatus, setBlenderLiveStatus] = useState<{
    live: boolean;
    lastAdvanceAgeMs: number | null;
    connectionError: boolean;
  } | null>(null);

  const selectedBlender = useMemo<BlenderAsset>(
    () =>
      blenderAssets.find((asset) => asset.id === selectedBlenderId) ?? {
        id: selectedBlenderId,
        label: selectedBlenderId,
        type: inferBlenderType(selectedBlenderId),
        fleet: null,
        padId: null,
        lastSeen: null,
      },
    [blenderAssets, selectedBlenderId],
  );

  const blenderAssetOptions = useMemo(
    () =>
      blenderAssets.map((asset) => ({
        value: asset.id,
        label: asset.label,
        type: asset.type,
      })),
    [blenderAssets],
  );

  const selectBlender = useCallback((nextAsset: BlenderAsset) => {
    pendingBlenderChannelsRef.current = null;
    setSelectedBlenderId(nextAsset.id);
    setBlenderChannelSearch("");
    // Channels and expanded groups are reset once this blender's channel
    // list arrives (see the /channels effect below).
  }, []);

  // five most recently opened real Blender assets.
  useEffect(() => {
    if (activeTab !== "blender" || !selectedBlenderId) return;

    setRecentBlenderIds((previous) => {
      const next = [
        selectedBlenderId,
        ...previous.filter((id) => id !== selectedBlenderId),
      ].slice(0, 5);

      const unchanged =
        previous.length === next.length &&
        previous.every((id, index) => id === next[index]);

      if (unchanged) return previous;

      try {
        localStorage.setItem(RECENT_BLENDERS_KEY, JSON.stringify(next));
      } catch (error) {
        console.warn("Unable to save recently viewed Blenders:", error);
      }

      return next;
    });
  }, [activeTab, selectedBlenderId]);

  const blenderChannelByKey = useMemo(() => {
    const byKey = new Map<string, BlenderChannel>();
    blenderChannelGroups.forEach((group) =>
      group.channels.forEach((channel) => byKey.set(channel.key, channel)),
    );
    return byKey;
  }, [blenderChannelGroups]);

  const visibleBlenderChannelGroups = useMemo(() => {
    const query = blenderChannelSearch.trim().toLowerCase();
    if (!query) return blenderChannelGroups;

    return blenderChannelGroups
      .map((group) => ({
        ...group,
        channels: group.channels.filter(
          (channel) =>
            channel.label.toLowerCase().includes(query) ||
            channel.key.toLowerCase().includes(query) ||
            group.group.toLowerCase().includes(query),
        ),
      }))
      .filter((group) => group.channels.length > 0);
  }, [blenderChannelGroups, blenderChannelSearch]);

  const persistBlenderChannelFavorites = useCallback(
    (favorites: BlenderChannelFavorite[]) => {
      setBlenderChannelFavorites(favorites);

      try {
        localStorage.setItem(
          BLENDER_CHANNEL_FAVORITES_KEY,
          JSON.stringify(favorites),
        );
      } catch (error) {
        console.warn("Unable to save Blender channel favorites:", error);
        toast.error("Unable to save Blender favorite.");
      }
    },
    [],
  );

  const saveBlenderChannelFavorite = useCallback(async () => {
    if (selectedBlenderChannels.length === 0) {
      toast.info("Select at least one Blender channel first.");
      return;
    }

    const result = await Swal.fire({
      title: "Save Channel Favorite",
      text: "Save the currently selected Blender channels for quick reuse.",
      input: "text",
      inputPlaceholder: "Favorite name",
      inputAttributes: {
        "aria-label": "Blender channel favorite name",
        maxLength: "60",
      },
      showCancelButton: true,
      confirmButtonText: "Save Favorite",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#8CC540",
      background: theme === "dark" ? "#303335" : "#ffffff",
      color: theme === "dark" ? "#ffffff" : "#1f2937",
      focusConfirm: false,

      didOpen: () => {
        const input = Swal.getInput();

        if (input) {
          input.style.width = "calc(100% - 48px)";
          input.style.maxWidth = "100%";
          input.style.margin = "16px auto 0";
          input.style.boxSizing = "border-box";
        }
      },

      inputValidator: (value) => {
        if (!value?.trim()) {
          return "Please enter a favorite name.";
        }
        return null;
      },
    });

    if (!result.isConfirmed) return;

    const favoriteName = String(result.value || "").trim();
    const existingFavorite = blenderChannelFavorites.find(
      (favorite) => favorite.name.toLowerCase() === favoriteName.toLowerCase(),
    );

    const nextFavorite: BlenderChannelFavorite = {
      id: existingFavorite?.id || `blender-favorite-${Date.now()}`,
      name: favoriteName,
      channels: [...selectedBlenderChannels],
      createdAt: Date.now(),
    };

    const nextFavorites = [
      nextFavorite,
      ...blenderChannelFavorites.filter(
        (favorite) => favorite.id !== existingFavorite?.id,
      ),
    ].slice(0, 10);

    persistBlenderChannelFavorites(nextFavorites);
    toast.success(
      existingFavorite
        ? `Blender favorite "${favoriteName}" updated.`
        : `Blender favorite "${favoriteName}" saved.`,
    );
  }, [
    selectedBlenderChannels,
    blenderChannelFavorites,
    persistBlenderChannelFavorites,
    theme,
  ]);

  const applyBlenderChannelFavorite = useCallback(
    (favorite: BlenderChannelFavorite) => {
      if (blenderChannelsStatus !== "loaded") {
        toast.info("Wait for the Blender channels to finish loading.");
        return;
      }

      const availableKeys = new Set(blenderChannelByKey.keys());
      const validChannels = favorite.channels.filter((channel) =>
        availableKeys.has(channel),
      );

      if (validChannels.length === 0) {
        toast.info(
          `"${favorite.name}" does not contain channels available on ${selectedBlenderId}.`,
        );
        return;
      }

      setSelectedBlenderChannels(validChannels);
      setExpandedBlenderGroups(
        new Set(
          blenderChannelGroups
            .filter((group) =>
              group.channels.some((channel) =>
                validChannels.includes(channel.key),
              ),
            )
            .map((group) => group.group),
        ),
      );

      if (validChannels.length < favorite.channels.length) {
        toast.info(
          `${validChannels.length} of ${favorite.channels.length} favorite channels are available on ${selectedBlenderId}.`,
        );
      } else {
        toast.success(`Applied "${favorite.name}".`);
      }
    },
    [
      blenderChannelsStatus,
      blenderChannelByKey,
      blenderChannelGroups,
      selectedBlenderId,
    ],
  );

  const deleteBlenderChannelFavorite = useCallback(
    async (favorite: BlenderChannelFavorite) => {
      const result = await Swal.fire({
        title: "Delete Channel Favorite?",
        text: `"${favorite.name}" will be removed from your Blender favorites.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
        background: theme === "dark" ? "#303335" : "#ffffff",
        color: theme === "dark" ? "#ffffff" : "#1f2937",
        reverseButtons: true,
      });

      if (!result.isConfirmed) return;

      persistBlenderChannelFavorites(
        blenderChannelFavorites.filter((current) => current.id !== favorite.id),
      );
      toast.success(`Deleted "${favorite.name}".`);
    },
    [blenderChannelFavorites, persistBlenderChannelFavorites, theme],
  );

  // Dashboard/share-link receiver for Blender historical views. The real asset
  // list and channel catalog may load after this runs, so preserve requested
  // channel keys in a ref until /channels confirms which keys are available.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const blenderParam = (
      params.get("blender") ||
      params.get("blender_id") ||
      ""
    ).trim();
    const requestedView = (params.get("tab") || params.get("view") || "")
      .trim()
      .toLowerCase();

    if (!blenderParam && requestedView !== "blender") return;

    const deepLinkKey = `${requestedView}|${blenderParam}|${
      params.get("start_time") || ""
    }|${params.get("end_time") || ""}|${params.get("channels") ?? "<unset>"}`;

    if (blenderDeepLinkInitializedRef.current === deepLinkKey) return;
    blenderDeepLinkInitializedRef.current = deepLinkKey;

    setActiveTab("blender");
    setExpandedFleets(new Set());
    setSelectedFleet(null);
    setPumpData([]);
    setAggregatedData({});
    setChartSeries([]);
    setXAxisRange({});

    if (blenderParam) {
      setSelectedBlenderId(blenderParam);
      setBlenderChannelSearch("");
    }

    const now = new Date();
    const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
    const parsedStart = Number(params.get("start_time"));
    const parsedEnd = Number(params.get("end_time"));

    const nextFrom =
      Number.isFinite(parsedStart) && parsedStart > 0
        ? new Date(parsedStart)
        : sixHoursAgo;
    const nextTo =
      Number.isFinite(parsedEnd) && parsedEnd > nextFrom.getTime()
        ? new Date(parsedEnd)
        : now;

    setBlenderDraftDates(null);
    setBlenderFromDate(nextFrom);
    setBlenderToDate(nextTo);

    const channelsParam = params.get("channels");
    if (blenderParam && channelsParam !== null) {
      const deepLinkChannels = channelsParam
        .split(",")
        .map((channel) => channel.trim())
        .filter(Boolean);

      pendingBlenderChannelsRef.current = {
        blenderId: blenderParam,
        channels: deepLinkChannels,
      };
      setSelectedBlenderChannels(deepLinkChannels);
    }
  }, [location.search]);

  const copyBlenderShareLink = useCallback(async () => {
    if (!selectedBlenderId) return;

    const params = new URLSearchParams();
    params.set("tab", "blender");
    params.set("blender", selectedBlenderId);
    params.set("start_time", String(blenderFromDate.getTime()));
    params.set("end_time", String(blenderToDate.getTime()));
    params.set("channels", selectedBlenderChannels.join(","));

    const shareUrl = `${window.location.origin}/fleet?${params.toString()}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Blender historical view link copied.");
    } catch (error) {
      console.error("Unable to copy Blender share link:", error);
      toast.error("Unable to copy Blender share link.");
    }
  }, [
    selectedBlenderId,
    selectedBlenderChannels,
    blenderFromDate,
    blenderToDate,
  ]);

  // Box-zoomed view of the Blender chart. Cleared when a different blender,
  // channel set, range or mode is loaded, but not on each live tick, so a
  // zoom made while streaming stays put.
  const [blenderChartView, setBlenderChartView] =
    useState<BlenderChartView | null>(null);
  const blenderChartDataKey = blenderData
    ? [
        blenderData.blenderId,
        blenderData.channels.join(","),
        isBlenderStreaming
          ? "live"
          : `${blenderData.startTime}-${blenderData.endTime}`,
      ].join("|")
    : "";
  useEffect(() => {
    setBlenderChartView(null);
  }, [blenderChartDataKey]);

  // A box zoom wins; otherwise, while streaming, follow the newest reading.
  const effectiveBlenderChartView = useMemo<BlenderChartView | null>(() => {
    if (blenderChartView) return blenderChartView;
    if (!isBlenderStreaming || !blenderData || blenderLiveViewMs === null) {
      return null;
    }
    return {
      xMin: blenderData.endTime - blenderLiveViewMs,
      xMax: blenderData.endTime,
    };
  }, [blenderChartView, isBlenderStreaming, blenderData, blenderLiveViewMs]);

  const blenderChartOptions = useMemo(
    () =>
      buildBlenderChartOptions(blenderData, blenderChannelByKey, {
        view: effectiveBlenderChartView,
        onSelectView: setBlenderChartView,
      }),
    [blenderData, blenderChannelByKey, effectiveBlenderChartView],
  );

  // Newest-first rows for the table, capped so a 3-day 1s load stays fast.
  const blenderTableRows = useMemo(() => {
    if (!blenderData) return [];
    const rows: number[] = [];
    for (
      let index = blenderData.t.length - 1;
      index >= 0 && rows.length < BLENDER_TABLE_MAX_ROWS;
      index -= 1
    ) {
      rows.push(index);
    }
    return rows;
  }, [blenderData]);
  const [fleetGroups, setFleetGroups] = useState<FleetGroup[]>([]);
  const fleetGroupsRef = useRef<FleetGroup[]>([]);
  fleetGroupsRef.current = fleetGroups;
  const [completionStatusByFleet, setCompletionStatusByFleet] = useState<
    Record<number, FleetListStatus>
  >({});
  const [fleetSearch, setFleetSearch] = useState("");
  const [selectedFleet, setSelectedFleet] = useState<FleetGroup | null>(null);
  const [expandedFleets, setExpandedFleets] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [isLoadDataLoading, setIsLoadDataLoading] = useState(false);

  const [isAssetTableLoading, setIsAssetTableLoading] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [loadingFleetIds, setLoadingFleetIds] = useState<Set<number>>(
    new Set(),
  );
  // Per-fleet loading flag for the coarse table pre-load. Kept separate from
  // loadingFleetIds (which tracks the primary chart load) so the table can show
  // its own non-blocking spinner after the chart has already rendered.
  const [tableLoadingByFleet, setTableLoadingByFleet] = useState<
    Record<number, boolean>
  >({});

  const [dateRefreshPendingFleetIds, setDateRefreshPendingFleetIds] = useState<
    Set<number>
  >(new Set());

  const [pendingDashboardSelection, setPendingDashboardSelection] = useState<{
    fleet: number;
    pump?: string;
    fromDate: Date;
    toDate: Date;
    channels?: PumpChannel[];
  } | null>(null);
  const [pumpListOptions, setPumpListOptions] = useState<AssetPumpOption[]>([]);
  const [selectedAssetPumps, setSelectedAssetPumps] = useState<
    AssetPumpOption[]
  >([]);
  const [validatingFleetIds, setValidatingFleetIds] = useState<Set<number>>(
    new Set(),
  );
  const validatingFleetIdsRef = useRef<Set<number>>(new Set());
  const [aggregationType, setAggregationType] = useState<
    "avg" | "sum" | "min" | "max"
  >("avg");
  const [streamingFleetIds, setStreamingFleetIds] = useState<Set<number>>(
    new Set(),
  );
  // Prevent repeated toggle clicks while live mode is starting/stopping and
  // historical data is being restored.
  const [streamingTransitionFleetIds, setStreamingTransitionFleetIds] =
    useState<Set<number>>(new Set());
  // Preserve the exact historical range the user had before enabling live mode.
  // Streaming temporarily replaces the date controls with its rolling window;
  // turning it off restores this saved range and reloads it automatically.
  const historicalRangeBeforeStreamingRef = useRef<
    Record<number, { from: Date; to: Date }>
  >({});
  const streamingIntervalsRef = useRef<Record<number, NodeJS.Timeout>>({});
  const streamingFleetIdsRef = useRef<Set<number>>(new Set());
  // Per-fleet guard: true while a 1s poll request is awaiting a response, so the
  // next tick is skipped instead of firing a duplicate concurrent request.
  const streamingPollInFlightRef = useRef<Record<number, boolean>>({});
  // Per-fleet last-applied snapshot pump_timestamp. The backend snapshot is
  // cache-backed and usually returns an identical payload across 1s polls, so
  // the steady poll skips re-processing (parse/merge/render) when this hasn't
  // advanced. Reset on (re)start/well-switch so a new selection always applies.
  const lastAppliedSnapshotTsRef = useRef<Record<number, number>>({});
  // Per-fleet cursor for the lossless incremental endpoint = the newest
  // time_stamp already applied. Each poll asks for rows STRICTLY newer than this,
  // so no live 1s sample is missed between polls (unlike the latest-only snapshot).
  const streamingCursorByFleetRef = useRef<Record<number, number>>({});
  // Per-fleet streaming window anchor = the user's selected start date (ms) when
  // streaming began. The live window is [max(anchor, liveEdge - 3h), liveEdge]:
  // it stays pinned to the chosen start and grows as data streams, but never
  // spans more than STREAMING_WINDOW_MS (3h) — after that the start slides.
  const streamingAnchorStartByFleetRef = useRef<Record<number, number>>({});
  // Only the latest chart-channel refresh may replace the live chart. This
  // prevents rapid channel changes from allowing an older backfill to win.
  const streamingChannelRefreshVersionRef = useRef<Record<number, number>>({});

  const isStreaming = streamingFleetIds.size > 0;
  const lastAlarmFetchRef = useRef<number>(0);
  // Monotonic request version for /pump-alarms. This is separate from the
  // streaming timestamp refs so an older alarm response can never overwrite
  // a newer Review Data request.
  const alarmFetchVersionRef = useRef<number>(0);

  // Tracks the fleet/time window that currently owns the visible alarm markers.
  // A historical chart load starts before /pump-alarms is requested, so without
  // this scope the previous fleet/range can remain visible for ~1s and a slower
  // stale load can start an old alarm request after the new load has already begun.
  const activeAlarmLoadScopeRef = useRef<{
    fleetId: number;
    startMs: number;
    endMs: number;
  } | null>(null);

  const lastAnnotationFetchRef = useRef<number>(0);
  const [latestValues, setLatestValues] = useState<Map<string, any>>(new Map());
  const [latestValuesByFleet, setLatestValuesByFleet] = useState<
    Record<number, Map<string, any>>
  >({});
  const latestValuesByFleetRef = useRef<Record<number, Map<string, any>>>({});
  latestValuesByFleetRef.current = latestValuesByFleet;
  const [fleetTypeByFleet, setFleetTypeByFleet] = useState<
    Record<number, "diesel" | "electric">
  >({});

  const chartRef = useRef<any>(null);
  const [chartSeries, setChartSeries] = useState<
    Highcharts.SeriesOptionsType[]
  >([]);
  const [xAxisRange, setXAxisRange] = useState<{ min?: number; max?: number }>(
    {},
  );

  const [availablePumps, setAvailablePumps] = useState<string[]>([]);
  const [selectedPumps, setSelectedPumps] = useState<string[]>([]);
  const [hiddenPumps, setHiddenPumps] = useState<Set<string>>(new Set());

  const [selectedAssetTablePumps, setSelectedAssetTablePumps] = useState<
    string[]
  >([]);
  const [assetLoadStatusText, setAssetLoadStatusText] = useState(
    "Loading selected pump data…",
  );
  const [assetLoadErrorText, setAssetLoadErrorText] = useState("");
  const [assetDetailStatusText, setAssetDetailStatusText] = useState("");
  const [assetHighResolutionLoading, setAssetHighResolutionLoading] =
    useState(false);
  // From/To of the last successful Asset load, used to show that the date
  // pickers have changed and Load Data needs to be pressed.
  const [assetLoadedRange, setAssetLoadedRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  // Asset chart zoom, handled like the Fleet chart: the zoomed window is
  // redrawn from the loaded rows, and only refetched (chart only) when the
  // API can serve it at a finer resolution than the loaded range.
  const [assetZoomRange, setAssetZoomRange] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const [assetZoomRows, setAssetZoomRows] = useState<PumpData[] | null>(null);
  const [assetZoomLoading, setAssetZoomLoading] = useState(false);

  const availableChannels = useMemo(
    () =>
      filterUnsupportedPumpChannels(
        appendUniquePumpChannels(ALL_PUMP_CHANNELS, ADDITIONAL_CHANNELS),
      ).filter(
        (channel) => !isLegacyPowerEndOilTemperatureChannel(channel.value),
      ),
    [],
  );

  const baseGroupedChannels = useMemo(() => {
    const normalizeGroupLabel = (label: string) =>
      label
        .toLowerCase()
        .replace(/channels?/g, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    const groups = GROUPED_PUMP_CHANNELS.map((group: any) => ({
      ...group,
      label: normalizeGroupLabel(String(group.label || ""))
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      options: filterUnsupportedPumpChannels([...(group.options || [])]).filter(
        (channel) => !isLegacyPowerEndOilTemperatureChannel(channel.value),
      ),
    }));

    const findGroup = (groupKey: "universal" | "diesel" | "electric") =>
      groups.find((group: any) =>
        normalizeGroupLabel(String(group.label || "")).includes(groupKey),
      );

    const addToGroup = (
      groupKey: "universal" | "diesel" | "electric",
      displayLabel: string,
      channels: PumpChannel[],
    ) => {
      let target = findGroup(groupKey);

      if (!target) {
        target = { label: displayLabel, options: [] };
        groups.push(target);
      } else {
        target.label = displayLabel;
      }

      const existingValues = new Set(
        target.options.map((channel: PumpChannel) => channel.value),
      );

      channels.forEach((channel) => {
        if (!existingValues.has(channel.value)) {
          target.options.push(channel);
          existingValues.add(channel.value);
        }
      });
    };

    // Merge added channels into the existing dropdown groups and keep the group
    // names as Universal, Diesel, and Electric only.
    addToGroup("universal", "Universal", ADDITIONAL_UNIVERSAL_CHANNELS);
    addToGroup("diesel", "Diesel", ADDITIONAL_DIESEL_CHANNELS);
    addToGroup("electric", "Electric", ADDITIONAL_ELECTRIC_CHANNELS);

    return groups as PumpChannelGroup[];
  }, []);

  const flattenChannelGroups = (groups: PumpChannelGroup[]): PumpChannel[] =>
    groups.flatMap((group: any) => group.options || []);

  const getGroupedChannelOptionsForFleet = (
    fleetId: number,
    currentFleetData: PumpData[] = [],
  ): PumpChannelGroup[] => {
    const latestRows = Array.from(
      (latestValuesByFleet[fleetId] || new Map()).values(),
    );

    const rowsToCheck = latestRows.length > 0 ? latestRows : currentFleetData;
    const isElectricFleet =
      fleetTypeByFleet[fleetId] === "electric" ||
      rowsToCheck.some(isElectricPumpRow);

    return baseGroupedChannels
      .map((group: any) => {
        const label = String(group.label || "").toLowerCase();

        if (isElectricFleet && label.includes("diesel")) return null;
        if (!isElectricFleet && label.includes("electric")) return null;

        return group;
      })
      .filter(Boolean) as PumpChannelGroup[];
  };

  // Fleet type for consumers that need a single answer (the chart pop-out's tab
  // order). Prefers the majority-based classification already resolved on fleet
  // expand, and only falls back to counting the latest rows.
  const isElectricFleetByMajority = (fleetId: number): boolean => {
    const resolvedType = fleetTypeByFleet[fleetId];
    if (resolvedType) return resolvedType === "electric";

    const latestRows = Array.from(
      (latestValuesByFleet[fleetId] || new Map()).values(),
    );
    const electricCount = latestRows.filter(isElectricPumpRow).length;

    return electricCount > latestRows.length - electricCount;
  };

  const isElectricPumpRow = (pump: any) => {
    const controllerType = Number(
      pump?.controller_type ?? pump?.Controller_Type ?? pump?.controllerType,
    );

    return controllerType === 101;
  };

  const getChannelOptionsForFleet = (
    fleetId: number,
    currentFleetData: PumpData[] = [],
  ): PumpChannel[] => {
    const latestRows = Array.from(
      (latestValuesByFleet[fleetId] || new Map()).values(),
    );

    const rowsToCheck = latestRows.length > 0 ? latestRows : currentFleetData;

    const isElectricFleet =
      fleetTypeByFleet[fleetId] === "electric" ||
      rowsToCheck.some(isElectricPumpRow);

    return appendUniquePumpChannels(
      appendUniquePumpChannels(
        ALL_PUMP_CHANNELS,
        ADDITIONAL_UNIVERSAL_CHANNELS,
      ),
      isElectricFleet
        ? ADDITIONAL_ELECTRIC_CHANNELS
        : ADDITIONAL_DIESEL_CHANNELS,
    );
  };
  const mainContentRef = useRef<HTMLDivElement | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const fleetFromDatePickerRefs = useRef<Record<number, DatePicker | null>>({});
  const fleetToDatePickerRefs = useRef<Record<number, DatePicker | null>>({});
  const [padWells, setPadWells] = useState<Record<number, WellOption[]>>({});
  // Preserve every historical pad/job returned by /pads. `fleetGroups` still
  // holds the current/latest pad for the fleet list, while this map lets a
  // historical From/To range resolve the pad/wells that were actually assigned
  // to that fleet at that time.
  const [padsByFleet, setPadsByFleet] = useState<Record<number, Pad[]>>({});
  // One contiguous run of one well on the pad, used to draw the "which well ran
  // when" bands behind the chart.
  const [wellActivityByPad, setWellActivityByPad] = useState<
    Record<number, WellActivityBand[]>
  >({});
  const wellActivityLoadedKeyRef = useRef<Record<number, string>>({});
  const [selectedWellIdsByPad, setSelectedWellIdsByPad] = useState<
    Record<number, number[]>
  >({});
  const [showAllWellsByPad, setShowAllWellsByPad] = useState<
    Record<number, boolean>
  >({});
  const [loadingWellSelectionByPad, setLoadingWellSelectionByPad] = useState<
    Record<number, boolean>
  >({});
  const [loadingWellLabelByPad, setLoadingWellLabelByPad] = useState<
    Record<number, string>
  >({});
  // Keep the current well selection available synchronously to streaming
  // callbacks. React state updates are asynchronous, so reading state immediately
  // after clicking another well can otherwise reuse the previous well filter.
  const selectedWellIdsByPadRef = useRef<Record<number, number[]>>({});
  const showAllWellsByPadRef = useRef<Record<number, boolean>>({});

  // Freeze the exact well filter used by each active real-time session.
  // These refs are read synchronously by validation, snapshot polling, SSE,
  // and well-switch callbacks so React state timing cannot reuse a stale well.
  const activeStreamingWellIdsByFleetRef = useRef<Record<number, number[]>>({});
  const activeStreamingShowAllByFleetRef = useRef<Record<number, boolean>>({});
  const streamingWellSignatureByFleetRef = useRef<Record<number, string>>({});
  const streamingWellSwitchVersionRef = useRef<Record<number, number>>({});
  const streamingWellSwitchTimerRef = useRef<Record<number, NodeJS.Timeout>>(
    {},
  );
  const [chartViewByPad, setChartViewByPad] = useState<
    Record<number, "stacked">
  >({});
  const [chartModeByPad, setChartModeByPad] = useState<
    Record<number, "combined" | "compare">
  >({});
  const [isModeSwitching, setIsModeSwitching] = useState(false);

  const defaultChannels: PumpChannel[] = [
    { value: "rpm", label: "RPM" },
    { value: "discharge_pressure", label: "Discharge Pressure" },
    { value: "engine_temp", label: "Engine Temp" },
  ];

  const defaultBoostChannel: PumpChannel = {
    value: "rpm",
    label: "Engine RPM",
  };

  const [selectedChannels, setSelectedChannels] = useState<PumpChannel[]>([
    defaultBoostChannel,
  ]);

  // Asset and Fleet must keep independent chart-channel selections.
  // `selectedChannels` remains the active chart selection used by shared chart
  // rendering code, while this state preserves the Asset selection when the
  // user switches between tabs.
  const [selectedAssetChannels, setSelectedAssetChannels] = useState<
    PumpChannel[]
  >([defaultBoostChannel]);

  const [selectedChannelsByFleet, setSelectedChannelsByFleet] = useState<
    Record<number, PumpChannel[]>
  >({});

  const [templates, setTemplates] = useState<Template[]>([]);

  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [selectedChartTemplateByFleet, setSelectedChartTemplateByFleet] =
    useState<Record<number, any>>({});

  const [templateModalContext, setTemplateModalContext] = useState<
    { view: "asset" } | { view: "fleet"; fleetId: number }
  >({ view: "asset" });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAxisModal, setShowAxisModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPumpDetailsModal, setShowPumpDetailsModal] = useState(false);
  const [alarmEvents, setAlarmEvents] = useState<any[]>([]);
  // Each expanded Fleet chart needs its own alarm list. A single global
  // `alarmEvents` array meant that reviewing an alarm on Fleet B immediately
  // replaced the markers rendered on an already-expanded Fleet A chart.
  const [alarmEventsByFleet, setAlarmEventsByFleet] = useState<
    Record<number, any[]>
  >({});
  const [reengagementEventsByFleet, setReengagementEventsByFleet] = useState<
    Record<number, PumpReengagementEvent[]>
  >({});
  const [
    reengagementEventsLoadingByFleet,
    setReengagementEventsLoadingByFleet,
  ] = useState<Record<number, boolean>>({});
  const [alarmSeverityFilter, setAlarmSeverityFilter] = useState<
    "critical" | "warning"
  >("critical");
  const [pumpInterlocks, setPumpInterlocks] = useState<
    Record<string, PumpInterlockLimit[]>
  >({});
  const [selectedAlarmEvents, setSelectedAlarmEvents] = useState<any[]>([]);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [alarmReviewNoTelemetryContext, setAlarmReviewNoTelemetryContext] =
    useState<{
      fleetId: number;
      pumpName: string;
      startTime: number;
      endTime: number;
      channels: string[];
    } | null>(null);

  // Keep the exact alarm the user clicked PER FLEET while Review Data may retry
  // from a pump-specific request to a fleet-wide telemetry request. A single
  // global ref caused Fleet B to overwrite Fleet A's review marker; returning to
  // Fleet A then cleared the marker even though its review range was still loaded.
  const alarmReviewSelectedAlarmByFleetRef = useRef<
    Record<number, AlarmReviewContext>
  >({});

  const getAlarmReviewContextForFleet = useCallback(
    (fleetId: number): AlarmReviewContext | undefined => {
      const inMemory = alarmReviewSelectedAlarmByFleetRef.current[fleetId];
      if (inMemory) return inMemory;

      try {
        // New format: one sessionStorage entry per fleet. Fall back to the old
        // single-entry key so an in-progress session survives this upgrade.
        const storedContext =
          sessionStorage.getItem(`${ALARM_REVIEW_CONTEXT_KEY}:${fleetId}`) ??
          sessionStorage.getItem(ALARM_REVIEW_CONTEXT_KEY);

        if (!storedContext) return undefined;

        const parsedContext = JSON.parse(storedContext);
        if (
          parsedContext?.alarm &&
          Number(parsedContext?.fleetId) === fleetId &&
          Number.isFinite(Number(parsedContext?.startTime)) &&
          Number.isFinite(Number(parsedContext?.endTime))
        ) {
          const restoredContext: AlarmReviewContext = {
            alarm: parsedContext.alarm,
            fleetId,
            startTime: Number(parsedContext.startTime),
            endTime: Number(parsedContext.endTime),
            savedAt: Number(parsedContext.savedAt) || 0,
          };

          alarmReviewSelectedAlarmByFleetRef.current = {
            ...alarmReviewSelectedAlarmByFleetRef.current,
            [fleetId]: restoredContext,
          };

          return restoredContext;
        }
      } catch (error) {
        console.warn(
          `Unable to restore alarm review context for fleet ${fleetId}:`,
          error,
        );
      }

      return undefined;
    },
    [],
  );

  const persistAlarmReviewContextForFleet = useCallback(
    (context: AlarmReviewContext) => {
      alarmReviewSelectedAlarmByFleetRef.current = {
        ...alarmReviewSelectedAlarmByFleetRef.current,
        [context.fleetId]: context,
      };

      try {
        sessionStorage.setItem(
          `${ALARM_REVIEW_CONTEXT_KEY}:${context.fleetId}`,
          JSON.stringify(context),
        );
      } catch (error) {
        console.warn(
          `Unable to persist alarm review context for fleet ${context.fleetId}:`,
          error,
        );
      }
    },
    [],
  );

  const [previousAlarmView, setPreviousAlarmView] = useState<{
    fleet: FleetGroup;
    from: Date;
    to: Date;
    pumps: string[];
    hiddenPumps: Set<string>;
  } | null>(null);

  const [templateName, setTemplateName] = useState("");

  const [chartAnnotations, setChartAnnotations] = useState<ChartAnnotation[]>(
    [],
  );
  const [selectedAnnotation, setSelectedAnnotation] =
    useState<ChartAnnotation | null>(null);
  const [selectedAnnotationGroup, setSelectedAnnotationGroup] = useState<
    ChartAnnotation[]
  >([]);
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);
  const [showAnnotationAuditModal, setShowAnnotationAuditModal] =
    useState(false);
  const [annotationAuditLoading, setAnnotationAuditLoading] = useState(false);
  const [selectedAuditAnnotation, setSelectedAuditAnnotation] =
    useState<ChartAnnotation | null>(null);
  const [annotationAuditRows, setAnnotationAuditRows] = useState<
    ChartAnnotationAudit[]
  >([]);

  const [annotationSearchResults, setAnnotationSearchResults] = useState<
    ChartAnnotation[]
  >([]);
  const [annotationSearchLoading, setAnnotationSearchLoading] = useState(false);
  const [annotationSearchText, setAnnotationSearchText] = useState("");
  const [annotationSearchFleet, setAnnotationSearchFleet] = useState("");
  const [annotationSearchPump, setAnnotationSearchPump] = useState("");
  const [annotationSearchUser, setAnnotationSearchUser] = useState("");
  const [annotationSearchFromDate, setAnnotationSearchFromDate] =
    useState<Date | null>(null);
  const [annotationSearchToDate, setAnnotationSearchToDate] =
    useState<Date | null>(null);
  const [openAnnotationActionMenuId, setOpenAnnotationActionMenuId] = useState<
    string | null
  >(null);

  const [axisSettings, setAxisSettings] = useState<{
    [axis: string]: {
      channels: string[];
      min: number | undefined;
      max: number | undefined;
    };
  }>({
    left1: { channels: ["rpm"], min: undefined, max: undefined },
    left2: { channels: ["discharge_pressure"], min: undefined, max: undefined },
    right1: { channels: ["engine_temp"], min: undefined, max: undefined },
    right2: { channels: [], min: undefined, max: undefined },
  });
  const [axisAutoScale, setAxisAutoScale] = useState<{
    [axis: string]: boolean;
  }>({
    left1: true,
    left2: true,
    right1: true,
    right2: true,
  });

  const [pumpData, setPumpData] = useState<PumpData[]>([]);
  const [aggregatedData, setAggregatedData] = useState<any>({});

  const [aggregatedDataByFleet, setAggregatedDataByFleet] = useState<
    Record<number, any>
  >({});
  const [pumpDataByFleet, setPumpDataByFleet] = useState<
    Record<number, PumpData[]>
  >({});
  const [stageDataByFleet, setStageDataByFleet] = useState<
    Record<number, StageChannelData[]>
  >({});

  const [stageSeriesByFleet, setStageSeriesByFleet] = useState<
    Record<number, Highcharts.SeriesOptionsType[]>
  >({});
  const [availablePumpsByFleet, setAvailablePumpsByFleet] = useState<
    Record<number, string[]>
  >({});
  const [selectedPumpsByFleet, setSelectedPumpsByFleet] = useState<
    Record<number, string[]>
  >({});
  const [hiddenPumpsByFleet, setHiddenPumpsByFleet] = useState<
    Record<number, Set<string>>
  >({});
  const [xAxisRangeByFleet, setXAxisRangeByFleet] = useState<
    Record<number, { min?: number; max?: number }>
  >({});
  // Preserve a user's manual zoom while real-time streaming continues to refresh.
  // Without this ref, every streaming tick replaces the x-axis with the rolling
  // three-hour range, so the chart snaps back about one second after zooming.
  const userZoomRangeByFleetRef = useRef<
    Record<number, { min: number; max: number }>
  >({});
  const [fromDateByFleet, setFromDateByFleet] = useState<
    Record<number, Date | null>
  >({});
  const [toDateByFleet, setToDateByFleet] = useState<
    Record<number, Date | null>
  >({});
  // Always-current mirror of fromDateByFleet/toDateByFleet for reads inside
  // Highcharts event handlers (e.g. a well-activity band click), which close
  // over these values at chart-render time and would otherwise see a stale
  // range when snapshotting "what to restore on Reset Graph".
  const fromDateByFleetRef = useRef(fromDateByFleet);
  const toDateByFleetRef = useRef(toDateByFleet);
  useEffect(() => {
    fromDateByFleetRef.current = fromDateByFleet;
  }, [fromDateByFleet]);
  useEffect(() => {
    toDateByFleetRef.current = toDateByFleet;
  }, [toDateByFleet]);

  // From/To being edited in a fleet's date pickers but not loaded yet. The
  // pickers show these; Load Data copies them into fromDateByFleet /
  // toDateByFleet, which is what the chart, table and auto-load effect use.
  // Date changes made in code (stage focus, Reset Graph, Review Data, ...)
  // still write fromDateByFleet/toDateByFleet directly and load immediately.
  const [draftDatesByFleet, setDraftDatesByFleet] = useState<
    Record<number, { from?: Date; to?: Date }>
  >({});

  // When a fleet's loaded range changes (Load Data, or a change made in code),
  // drop its draft so the pickers show the range that is actually loaded.
  const previousLoadedDatesRef = useRef({
    from: fromDateByFleet,
    to: toDateByFleet,
  });
  useEffect(() => {
    const previous = previousLoadedDatesRef.current;
    previousLoadedDatesRef.current = { from: fromDateByFleet, to: toDateByFleet };

    setDraftDatesByFleet((drafts) => {
      const changedFleetIds = Object.keys(drafts)
        .map(Number)
        .filter(
          (fleetId) =>
            previous.from[fleetId]?.getTime() !==
              fromDateByFleet[fleetId]?.getTime() ||
            previous.to[fleetId]?.getTime() !==
              toDateByFleet[fleetId]?.getTime(),
        );
      if (changedFleetIds.length === 0) return drafts;

      const next = { ...drafts };
      changedFleetIds.forEach((fleetId) => delete next[fleetId]);
      return next;
    });
  }, [fromDateByFleet, toDateByFleet]);
  // Set when a well-activity band is clicked to narrow a fleet's chart to one
  // stage's window. Holds the range to restore on "Reset Graph".
  const [stageSelectionByFleet, setStageSelectionByFleet] = useState<
    Record<number, { prevFrom: Date; prevTo: Date; label: string } | undefined>
  >({});
  const [selectedTableTemplate, setSelectedTableTemplate] = useState<any>(null);
  const lastStreamingUiUpdateRef = useRef<Record<number, number>>({});
  const [selectedTableTemplateByFleet, setSelectedTableTemplateByFleet] =
    useState<Record<number, any>>({});
  const [selectedTableChannels, setSelectedTableChannels] =
    useState<PumpChannel[]>(defaultChannels);
  const [showTableChannelNoticeByFleet, setShowTableChannelNoticeByFleet] =
    useState<Record<number, boolean>>({});
  const [selectedTableChannelsByFleet, setSelectedTableChannelsByFleet] =
    useState<Record<number, PumpChannel[]>>({});
  const [loadedChartChannelsByFleet, setLoadedChartChannelsByFleet] = useState<
    Record<number, string[]>
  >({});
  const [loadedTableChannelsByFleet, setLoadedTableChannelsByFleet] = useState<
    Record<number, string[]>
  >({});
  const selectedChannelsByFleetRef = useRef<Record<number, PumpChannel[]>>({});
  const selectedTableChannelsByFleetRef = useRef<Record<number, PumpChannel[]>>(
    {},
  );
  const tableChannelReloadTimerRef = useRef<Record<number, NodeJS.Timeout>>({});
  // Channel edits are collected while the dropdown is open. Historical data is
  // refreshed only after the dropdown closes, using the final selection.
  const chartChannelReloadTimerRef = useRef<Record<number, NodeJS.Timeout>>({});
  const chartChannelReloadInFlightRef = useRef<Record<number, boolean>>({});
  const chartChannelReloadQueuedRef = useRef<Record<number, boolean>>({});
  const loadedChartChannelsByFleetRef = useRef<Record<number, string[]>>({});
  const fleetChannelContainerRefsRef = useRef<
    Record<number, HTMLDivElement | null>
  >({});

  // Asset-view channel refresh state. Channel edits are collected while the
  // dropdown is open and one request is made after it closes.
  const assetChannelReloadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const assetChannelReloadInFlightRef = useRef(false);
  const assetChannelReloadQueuedRef = useRef(false);
  const loadedAssetChannelSignatureRef = useRef("");
  const assetChannelContainerRef = useRef<HTMLDivElement | null>(null);

  // Auto-load guards for the standup UX change. Fleet and Asset keep separate
  // signatures so switching tabs or changing filters cannot trigger duplicate
  // historical requests.
  const fleetAutoLoadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fleetAutoLoadSignatureRef = useRef("");
  const assetAutoLoadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const assetAutoLoadSignatureRef = useRef("");
  const assetZoomTimerRef = useRef<NodeJS.Timeout | null>(null);
  const assetZoomGenerationRef = useRef(0);
  const assetSlowLoadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const assetPumpSelectionGenerationRef = useRef(0);
  const assetTablePumpFilterInitializedRef = useRef(false);

  const fullResByFleetRef = useRef<Record<number, FleetBuffers>>({});

  const historicalZoomDetailActiveByFleetRef = useRef<Record<number, boolean>>(
    {},
  );
  const historicalZoomReloadTimerRef = useRef<Record<number, NodeJS.Timeout>>(
    {},
  );
  // Prebuilt, min/max-downsampled Highcharts series per fleet (combined mode).
  // When present, the chart renders these instead of holding/transforming raw
  // rows — this is what lets large fleets (e.g. 32 pumps / 669k rows) render.
  const [chartSeriesByFleet, setChartSeriesByFleet] = useState<
    Record<number, Highcharts.SeriesOptionsType[]>
  >({});

  const selectedPumpsRef = useRef<string[]>(selectedPumps);
  const selectedChannelsRef = useRef<PumpChannel[]>(selectedChannels);
  // Mirrors selectedFleet for async callbacks that must check the CURRENT fleet
  // without capturing a stale closure (e.g. the default-channel resolve on expand).
  const selectedFleetRef = useRef(selectedFleet);
  const selectedAssetChannelsRef = useRef<PumpChannel[]>(selectedAssetChannels);
  const dashboardLoadKeyRef = useRef<string>("");
  const dashboardInitKeyRef = useRef<string>("");

  const dashboardDeepLinkLoadInProgressRef = useRef(false);

  const hasRestoredFleetViewStateRef = useRef(false);

  /*
   * A View Chart request from the Flagged Re-engagement page can carry the
   * selected backend-qualified Critical alarm. The normal /pump-alarms call
   * remains the source of chart alarms. This fallback is merged only when that
   * endpoint does not return the exact selected alarm for some historical pump.
   */
  const flaggedAlarmFallbackRef = useRef<any | null>(null);

  /*
   * For a Flagged Re-engagement View Chart, channels and alarm markers must be
   * built from the same exact /pump-alarms response. Without this ref, Fleet
   * called /pump-alarms once to derive channels and again after loading chart
   * data; those two responses could differ and leave an extra channel in the
   * legend that had no corresponding visible alarm marker.
   */
  const flaggedResolvedAlarmsRef = useRef<any[]>([]);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  // Local development and deployed authentication use different storage keys.
  // Always use whichever valid token is available so historical /pumps requests
  // do not fail with 401 when only `jwt` is present.
  const getAuthToken = () =>
    localStorage.getItem("jwt") || localStorage.getItem("token");

  const [axisValueEdit, setAxisValueEdit] = useState<AxisValueEditState | null>(
    null,
  );
  const [axisValueInput, setAxisValueInput] = useState("");

  const openAxisValueEditor = useCallback(
    (payload: Omit<AxisValueEditState, "error">) => {
      setAxisValueEdit(payload);
      setAxisValueInput(String(Number(payload.clickedValue.toFixed(2))));
    },
    [],
  );

  const closeAxisValueEditor = useCallback(() => {
    setAxisValueEdit(null);
    setAxisValueInput("");
  }, []);

  const resetAxisValueToAuto = useCallback(() => {
    if (!axisValueEdit) return;

    setAxisSettings((prev) => ({
      ...prev,
      [axisValueEdit.axisName]: {
        ...prev[axisValueEdit.axisName],
        min: undefined,
        max: undefined,
      },
    }));

    setAxisAutoScale((prev) => ({
      ...prev,
      [axisValueEdit.axisName]: true,
    }));

    closeAxisValueEditor();
  }, [axisValueEdit, closeAxisValueEditor]);

  const applyAxisValueEdit = useCallback(() => {
    if (!axisValueEdit) return;

    const trimmed = axisValueInput.trim();

    if (!trimmed) {
      setAxisValueEdit((prev) =>
        prev ? { ...prev, error: "Enter a value or use Reset Auto." } : prev,
      );
      return;
    }

    const numericValue = Number(trimmed);

    if (!Number.isFinite(numericValue)) {
      setAxisValueEdit((prev) =>
        prev ? { ...prev, error: "Please enter a valid number." } : prev,
      );
      return;
    }

    const currentRange = axisValueEdit.currentMax - axisValueEdit.currentMin;

    if (!Number.isFinite(currentRange) || currentRange <= 0) {
      setAxisValueEdit((prev) =>
        prev
          ? { ...prev, error: "Unable to calculate the current axis range." }
          : prev,
      );
      return;
    }

    const nextMin = numericValue - axisValueEdit.ratioFromBottom * currentRange;
    const nextMax =
      numericValue + (1 - axisValueEdit.ratioFromBottom) * currentRange;

    if (nextMin >= nextMax) {
      setAxisValueEdit((prev) =>
        prev
          ? {
              ...prev,
              error: "Axis minimum must be less than axis maximum.",
            }
          : prev,
      );
      return;
    }

    setAxisSettings((prev) => ({
      ...prev,
      [axisValueEdit.axisName]: {
        ...prev[axisValueEdit.axisName],
        min: nextMin,
        max: nextMax,
      },
    }));

    setAxisAutoScale((prev) => ({
      ...prev,
      [axisValueEdit.axisName]: false,
    }));

    closeAxisValueEditor();
  }, [axisValueEdit, axisValueInput, closeAxisValueEditor]);

  const bindEditableYAxisLabels = useCallback(
    (chart: Highcharts.Chart) => {
      const container = chart.container as HTMLElement & {
        __padTrackAxisDblClickBound?: boolean;
      };

      if (!container || container.__padTrackAxisDblClickBound) return;

      container.__padTrackAxisDblClickBound = true;

      const markEditableLabels = () => {
        (chart.yAxis || []).forEach((axis: any) => {
          Object.values((axis as any).ticks || {}).forEach((tick: any) => {
            const labelElement = tick?.label?.element as SVGElement | undefined;
            if (!labelElement) return;

            labelElement.setAttribute("cursor", "pointer");
            labelElement.setAttribute(
              "title",
              "Double-click this axis value to rescale.",
            );
          });
        });
      };

      container.addEventListener("dblclick", (event: MouseEvent) => {
        const containerRect = container.getBoundingClientRect();
        const chartX = event.clientX - containerRect.left;
        const chartY = event.clientY - containerRect.top;

        let matchedAxis: Highcharts.Axis | null = null;
        let bestDistance = Number.POSITIVE_INFINITY;

        (chart.yAxis || []).forEach((axis: any) => {
          if (!axis.visible) return;

          const axisLeft = Number(axis.left ?? chart.plotLeft);
          const axisTop = Number(axis.top ?? chart.plotTop);
          const axisHeight = Number(axis.height ?? chart.plotHeight);
          const axisWidth = Number(axis.width ?? chart.plotWidth);
          const axisRight = axisLeft + axisWidth;
          const isLeftSide = axis.opposite !== true;

          const labelZoneLeft = isLeftSide ? axisLeft - 120 : axisRight - 20;
          const labelZoneRight = isLeftSide ? axisLeft + 35 : axisRight + 120;
          const labelZoneTop = axisTop - 25;
          const labelZoneBottom = axisTop + axisHeight + 25;

          const insideLabelZone =
            chartX >= labelZoneLeft &&
            chartX <= labelZoneRight &&
            chartY >= labelZoneTop &&
            chartY <= labelZoneBottom;

          if (!insideLabelZone) return;

          const axisEdgeX = isLeftSide ? axisLeft : axisRight;
          const distance = Math.abs(chartX - axisEdgeX);

          if (distance < bestDistance) {
            bestDistance = distance;
            matchedAxis = axis as Highcharts.Axis;
          }
        });

        if (!matchedAxis) return;

        event.preventDefault();
        event.stopPropagation();

        const axis = matchedAxis as Highcharts.Axis;
        const extremes = axis.getExtremes();

        if (
          !Number.isFinite(extremes.min) ||
          !Number.isFinite(extremes.max) ||
          extremes.max <= extremes.min
        ) {
          return;
        }

        const axisTop = Number((axis as any).top ?? chart.plotTop);
        const axisHeight = Number((axis as any).height ?? chart.plotHeight);
        const clampedY = Math.min(
          axisTop + axisHeight,
          Math.max(axisTop, chartY),
        );

        const ratioFromBottom = (axisTop + axisHeight - clampedY) / axisHeight;
        const currentRange = extremes.max - extremes.min;
        const clickedValue = extremes.min + ratioFromBottom * currentRange;

        const axisIndex = chart.yAxis.indexOf(axis);
        const axisNames = ["left1", "left2", "right1", "right2"] as const;
        const axisName = axisNames[axisIndex];

        if (!axisName) return;

        openAxisValueEditor({
          axisName,
          axisTitle:
            (axis.options?.title as any)?.text ||
            (axis as any).coll ||
            "Y Axis",
          clickedValue,
          ratioFromBottom,
          currentMin: extremes.min,
          currentMax: extremes.max,
        });
      });

      markEditableLabels();
      setTimeout(markEditableLabels, 0);
    },
    [openAxisValueEditor],
  );

  // One /pumps/list classification per fleet+pad, shared by every caller. Without
  // this the expand handler and the auto-load path each resolved it separately
  // and could disagree about the default channel.
  const defaultChannelsPromiseRef = useRef<
    Record<string, Promise<PumpChannel[]>>
  >({});

  // Monotonic per-fleet load counter. Every loadData claims the next value, and a
  // response is discarded if a newer load has claimed one since — otherwise a
  // slow response overwrites a fast newer one's chart, buffers, loaded-channel
  // list and pump selection.
  const chartLoadSequenceRef = useRef<Record<number, number>>({});

  const getDefaultChartChannelsForFleet = useCallback(
    (fleetId: number): Promise<PumpChannel[]> => {
      const padId =
        fleetGroupsRef.current.find((group) => group.fleet === fleetId)
          ?.latestPad?.id ?? 0;
      const cacheKey = `${fleetId}:${padId}`;
      const cached = defaultChannelsPromiseRef.current[cacheKey];
      if (cached) return cached;

      const rpmChannel = findChannelByValue("rpm") || {
        value: "rpm",
        label: "Engine RPM",
      };
      const vfdChannel = findChannelByValue("discharge_vfd_amps") || {
        value: "discharge_vfd_amps",
        label: "VFD Amps",
      };

      const resolution = (async (): Promise<PumpChannel[]> => {
        const token = getAuthToken();

        try {
          const params: Record<string, number> = { fleet: fleetId };
          if (padId) params.pad_id = padId;

          const response = await axios.get(`${backendUrl}/pumps/list`, {
            params,
            headers: { Authorization: `Bearer ${token}` },
            timeout: 20000,
          });

          const allPumps: any[] = Array.isArray(response.data)
            ? response.data
            : [];

          // /pumps/list?fleet= is the full configured inventory for that fleet
          // number (historical electric units still listed on a diesel pad).
          // Default channels must reflect the ACTIVE pad, not that roster.
          let padPumps = allPumps;
          let padScoped = false;

          const listHasPadIds = allPumps.some(
            (pump) => pumpPadId(pump) !== null,
          );
          if (padId && listHasPadIds) {
            const onPad = allPumps.filter((pump) => pumpPadId(pump) === padId);
            if (onPad.length > 0) {
              padPumps = onPad;
              padScoped = true;
            }
          }

          const latestMap = latestValuesByFleetRef.current[fleetId];
          if (latestMap && latestMap.size > 0) {
            const liveNames = new Set(
              Array.from(latestMap.keys())
                .map((name) => String(name).trim())
                .filter(Boolean),
            );
            const liveOnPad = padPumps.filter((pump) =>
              liveNames.has(pumpListName(pump)),
            );
            if (liveOnPad.length > 0) {
              padPumps = liveOnPad;
              padScoped = true;
            }
          }

          // Classify by MAJORITY of the pad-scoped set. Mixed fleets used to be
          // called electric by `.some()` on the full roster, so a mostly-diesel
          // pad defaulted to VFD Amps — a channel the diesel pumps don't report.
          const electricCount = padPumps.filter(
            isElectricControllerType,
          ).length;
          const dieselCount = padPumps.filter(isDieselControllerType).length;
          const isElectricFleet = electricCount > dieselCount;

          setFleetTypeByFleet((prev) => ({
            ...prev,
            [fleetId]: isElectricFleet ? "electric" : "diesel",
          }));

          // Both defaults only when we KNOW this pad has both pump types. If the
          // list could not be pad-scoped, majority-only avoids painting VFD Amps
          // onto a diesel pad that still has leftover electric units configured.
          const isMixedPad = padScoped && electricCount > 0 && dieselCount > 0;

          if (isMixedPad) {
            return isElectricFleet
              ? [vfdChannel, rpmChannel]
              : [rpmChannel, vfdChannel];
          }

          // Electric pumps do NOT report RPM (it comes back null), so a pure
          // electric pad must default to VFD Amps or its chart renders blank.
          return isElectricFleet ? [vfdChannel] : [rpmChannel];
        } catch (error) {
          console.error("Failed to resolve fleet type from pump list:", error);
          // Let a later caller retry rather than caching the failure forever.
          delete defaultChannelsPromiseRef.current[cacheKey];
          return [rpmChannel];
        }
      })();

      defaultChannelsPromiseRef.current[cacheKey] = resolution;
      return resolution;
    },
    [backendUrl],
  );

  const getDefaultChartChannelForFleet = useCallback(
    async (fleetId: number): Promise<PumpChannel> => {
      const channels = await getDefaultChartChannelsForFleet(fleetId);

      return (
        channels[0] ||
        findChannelByValue("rpm") || { value: "rpm", label: "Engine RPM" }
      );
    },
    [getDefaultChartChannelsForFleet],
  );

  // Apply a fleet's resolved default chart channels. Never clobbers an explicit
  // user choice or a chart that already loaded, so it is safe to call from both
  // the expand handler and the auto-load path.
  const ensureDefaultChannelsForFleet = useCallback(
    async (fleetId: number) => {
      const channels = await getDefaultChartChannelsForFleet(fleetId);
      if (channels.length === 0) return;

      const current = selectedChannelsByFleetRef.current[fleetId] || [];
      // The expand handler seeds RPM synchronously so the dropdown is never
      // blank; only that untouched seed (or nothing) may be replaced.
      const isUntouchedSeed =
        current.length === 0 ||
        (current.length === 1 && current[0].value === "rpm");
      const nothingLoaded =
        (loadedChartChannelsByFleetRef.current[fleetId] || []).length === 0;

      if (!isUntouchedSeed || !nothingLoaded) return;

      const currentValues = current.map((channel) => channel.value).join("|");
      const nextValues = channels.map((channel) => channel.value).join("|");
      if (currentValues === nextValues) return;

      selectedChannelsByFleetRef.current = {
        ...selectedChannelsByFleetRef.current,
        [fleetId]: channels,
      };
      setSelectedChannelsByFleet((prev) => ({ ...prev, [fleetId]: channels }));

      if (selectedFleetRef.current?.fleet === fleetId) {
        setSelectedChannels(channels);
        selectedChannelsRef.current = channels;
      }

      // RPM and VFD Amps are different units, so give VFD Amps its own axis when
      // a mixed fleet charts both. Only if the user has not assigned it already
      // and right2 is still free — never overwrite an explicit axis setup.
      if (
        channels.length > 1 &&
        channels.some((channel) => channel.value === "discharge_vfd_amps")
      ) {
        setAxisSettings((prev) => {
          const alreadyAssigned = Object.values(prev).some((axis) =>
            axis.channels?.includes("discharge_vfd_amps"),
          );

          if (alreadyAssigned || (prev.right2?.channels?.length ?? 0) > 0) {
            return prev;
          }

          return {
            ...prev,
            right2: { ...prev.right2, channels: ["discharge_vfd_amps"] },
          };
        });
      }
    },
    [getDefaultChartChannelsForFleet],
  );

  const dashboardSearchParams = new URLSearchParams(location.search);
  const isFlaggedReengagementView =
    dashboardSearchParams.get("flagged_reengagement_view") === "1";
  const dashboardPumpParam = dashboardSearchParams.get("pump");
  const dashboardFleetParam = dashboardSearchParams.get("fleet");
  const dashboardFleetNumber = dashboardFleetParam
    ? Number(dashboardFleetParam)
    : null;
  const dashboardAlarmRpmParam = dashboardSearchParams.get("alarm_rpm");
  const dashboardAlarmRpm = dashboardAlarmRpmParam
    ? Number(dashboardAlarmRpmParam)
    : null;

  const [flaggedChartMinimumRpm, setFlaggedChartMinimumRpm] = useState(
    dashboardSearchParams.get("minimum_rpm") || "",
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFlaggedChartMinimumRpm(params.get("minimum_rpm") || "");
  }, [location.search]);

  /*
   * A flagged re-engagement View Chart URL belongs only to the fleet in the
   * URL. Other expanded fleets must behave like normal Pump Data views.
   */
  const isDashboardPumpForFleet = useCallback(
    (fleetId?: number | null) =>
      Boolean(
        dashboardPumpParam &&
        fleetId != null &&
        Number.isFinite(dashboardFleetNumber) &&
        dashboardFleetNumber === fleetId,
      ),
    [dashboardPumpParam, dashboardFleetNumber],
  );

  const isDashboardPumpView = isDashboardPumpForFleet(selectedFleet?.fleet);
  const formatFleetStartDate = (ts: number) =>
    new Date(ts).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const getFleetListStatus = useCallback(
    (group: FleetGroup): FleetListStatus => {
      const fromStages = completionStatusByFleet[group.fleet];
      const fromPad = normalizeCompletionStatus(
        group.latestPad?.completion_status ??
          group.latestPad?.completion_status_label ??
          group.latestPad?.status,
      );

      // Lost Comms / Expired must not fall through into Completed. Prefer the
      // new /active-stages field, then the pad status already used on Pump Dashboard.
      if (fromStages === "lost_comms" || fromStages === "expired") {
        return fromStages;
      }
      if (fromPad === "lost_comms" || fromPad === "expired") {
        return fromPad;
      }
      if (fromStages === "active" || fromPad === "active") return "active";
      return "complete";
    },
    [completionStatusByFleet],
  );

  const fleetListStatusLabel = (status: FleetListStatus): string => {
    switch (status) {
      case "lost_comms":
        return "Lost Comms";
      case "expired":
        return "Expired";
      case "active":
        return "Active";
      default:
        return "Complete";
    }
  };

  const filteredFleetGroups = useMemo(() => {
    const q = fleetSearch.trim().toLowerCase();
    if (!q) return fleetGroups;
    return fleetGroups.filter((group) => {
      const pad = group.latestPad;
      const fleetNum = String(group.fleet);
      const padName = (pad?.pad_name ?? "").toLowerCase();
      const padStatus = (pad?.status ?? "").toLowerCase();
      const joinedStatus = fleetListStatusLabel(
        getFleetListStatus(group),
      ).toLowerCase();
      const stages = String(pad?.stage_count ?? "");
      const wells = String(pad?.well_count ?? "");
      return (
        fleetNum.includes(q) ||
        padName.includes(q) ||
        padStatus.includes(q) ||
        joinedStatus.includes(q) ||
        stages.includes(q) ||
        wells.includes(q)
      );
    });
  }, [fleetGroups, fleetSearch, getFleetListStatus]);

  // The fleet list is split into Active, Lost Comms, Expired, and Completed.
  // Lost Comms / Expired come from /active-stages completion_status when present,
  // otherwise from /pads status (same values Pump Dashboard already shows).
  const fleetRows = useMemo(() => {
    type FleetListVariant = FleetListStatus;
    type FleetListRow =
      | {
          kind: "header";
          key: string;
          label: string;
          count: number;
          variant: FleetListVariant;
        }
      | { kind: "fleet"; key: string; group: FleetGroup };

    const sections: Array<{
      key: FleetListVariant;
      label: string;
      groups: FleetGroup[];
    }> = [
      {
        key: "active",
        label: "Active Fleets",
        groups: filteredFleetGroups.filter(
          (group) => getFleetListStatus(group) === "active",
        ),
      },
      {
        key: "lost_comms",
        label: "Lost Comms",
        groups: filteredFleetGroups.filter(
          (group) => getFleetListStatus(group) === "lost_comms",
        ),
      },
      {
        key: "expired",
        label: "Expired",
        groups: filteredFleetGroups.filter(
          (group) => getFleetListStatus(group) === "expired",
        ),
      },
      {
        key: "complete",
        label: "Completed Fleets",
        groups: filteredFleetGroups.filter(
          (group) => getFleetListStatus(group) === "complete",
        ),
      },
    ];

    const rows: FleetListRow[] = [];

    sections.forEach((section) => {
      rows.push({
        kind: "header",
        key: `fleet-group-${section.key}`,
        label: section.label,
        count: section.groups.length,
        variant: section.key,
      });

      section.groups.forEach((group) => {
        rows.push({ kind: "fleet", key: `fleet-${group.fleet}`, group });
      });
    });

    return rows;
  }, [filteredFleetGroups, getFleetListStatus]);

  const getPadForFleetDateRange = useCallback(
    (
      group: FleetGroup | null | undefined,
      start: Date | null | undefined,
      end: Date | null | undefined,
    ): Pad | null => {
      if (!group) return null;

      const candidates = padsByFleet[group.fleet] || [];
      if (!start || !end || candidates.length === 0) {
        return group.latestPad ?? null;
      }

      const rangeStart = start.getTime();
      const rangeEnd = end.getTime();
      if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd)) {
        return group.latestPad ?? null;
      }

      // /pads timestamps are normally epoch milliseconds. Keep this tolerant of
      // epoch-seconds data so older records still resolve correctly.
      const toMillis = (value: number | null | undefined): number | null => {
        if (value == null || !Number.isFinite(Number(value))) return null;
        const numeric = Number(value);
        return Math.abs(numeric) < 100_000_000_000 ? numeric * 1000 : numeric;
      };

      let bestPad: Pad | null = null;
      let bestOverlap = -1;
      let bestStart = Number.NEGATIVE_INFINITY;

      candidates.forEach((pad) => {
        const padStart = toMillis(pad.start_ts) ?? Number.NEGATIVE_INFINITY;
        const padEnd = toMillis(pad.end_ts) ?? Number.POSITIVE_INFINITY;

        const overlap = Math.max(
          0,
          Math.min(rangeEnd, padEnd) - Math.max(rangeStart, padStart),
        );

        // Prefer the pad/job that owns the largest portion of the selected
        // historical window. For a tie, prefer the later assignment.
        if (
          overlap > bestOverlap ||
          (overlap === bestOverlap && padStart > bestStart)
        ) {
          bestPad = pad;
          bestOverlap = overlap;
          bestStart = padStart;
        }
      });

      if (bestPad && bestOverlap > 0) {
        return bestPad;
      }

      // If the requested window falls into a gap between pad records, use the
      // closest assignment rather than incorrectly jumping straight to latest.
      const anchor = rangeStart;
      let closestPad: Pad | null = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      candidates.forEach((pad) => {
        const padStart = toMillis(pad.start_ts) ?? Number.NEGATIVE_INFINITY;
        const padEnd = toMillis(pad.end_ts) ?? Number.POSITIVE_INFINITY;

        const distance =
          anchor < padStart
            ? padStart - anchor
            : anchor > padEnd
              ? anchor - padEnd
              : 0;

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPad = pad;
        }
      });

      return closestPad || group.latestPad || null;
    },
    [padsByFleet],
  );

  const openChartPopout = (
    fleet: number,
    startTime: number,
    endTime: number,
    channels: PumpChannel[],
    pumps: string[],
  ) => {
    const handoffKey = `${fleet}-${startTime}-${endTime}-${channels
      .map((ch) => ch.value)
      .join(",")}-${pumps.join(",")}`;

    const sourceChart =
      selectedFleet?.fleet === fleet ? chartRef.current?.chart : undefined;
    const axisNames = ["left1", "left2", "right1", "right2"] as const;
    const axisExtremes = axisNames.reduce<
      Record<string, { min?: number; max?: number }>
    >((result, axisName, axisIndex) => {
      const axis = sourceChart?.yAxis?.[axisIndex];
      const extremes = axis?.getExtremes?.();

      result[axisName] = {
        min: Number.isFinite(extremes?.min) ? extremes.min : undefined,
        max: Number.isFinite(extremes?.max) ? extremes.max : undefined,
      };

      return result;
    }, {});

    (window as any).__pumpChartHandoff = {
      key: handoffKey,
      // fullResByFleetRef is kept live + full-resolution during streaming (and
      // holds the loaded window for static), so it always reaches the current
      // edge — the popout seeds gap-free from it directly.
      buffers: fullResByFleetRef.current[fleet],
      // Rolling rows as a fallback so both charts start from the same dataset.
      rows: pumpDataByFleet[fleet] || [],
      axisSettings: JSON.parse(JSON.stringify(axisSettings)),
      axisAutoScale: { ...axisAutoScale },
      axisExtremes,
      channelOptions: availableChannels.map((channel) => ({ ...channel })),
    };

    const params = new URLSearchParams();

    params.set("fleet", String(fleet));
    params.set("start_time", String(startTime));
    params.set("end_time", String(endTime));
    params.set("channels", channels.map((ch) => ch.value).join(","));
    params.set("pumps", pumps.join(","));
    params.set("handoff", handoffKey);
    params.set("realtime", streamingFleetIdsRef.current.has(fleet) ? "1" : "0");
    // Drives the pop-out's channel-tab order. Classify by MAJORITY (never
    // `.some()`) so a mostly-diesel mixed fleet is not treated as electric.
    params.set(
      "fleet_type",
      isElectricFleetByMajority(fleet) ? "electric" : "diesel",
    );

    const fleetGroup = fleetGroups.find((group) => group.fleet === fleet);
    const pad = getPadForFleetDateRange(
      fleetGroup,
      new Date(startTime),
      new Date(endTime),
    );
    if (pad?.pad_name) {
      params.set("pad_name", pad.pad_name);
    }

    getSelectedWellIdsForPad(pad?.id).forEach((wellId) => {
      params.append("well_id", String(wellId));
      params.append("well_name", getWellNameById(pad?.id, wellId));
    });

    window.open(
      `/chart-popout?${params.toString()}`,
      "_blank",
      "width=1600,height=900,resizable=yes,scrollbars=yes",
    );
  };
  const openAssetChartPopout = (
    startTime: number,
    endTime: number,
    channels: PumpChannel[],
  ) => {
    if (selectedAssetPumps.length === 0 || channels.length === 0) {
      return;
    }

    const normalizedPumps = Array.from(
      new Set(
        selectedPumps.length > 0
          ? selectedPumps
          : selectedAssetPumps.map((pump) => pump.value),
      ),
    );
    const rawPumpNames = Array.from(
      new Set(
        selectedAssetPumps.flatMap((pump) =>
          pump.rawPumpNames?.length > 0 ? pump.rawPumpNames : [pump.value],
        ),
      ),
    );

    const handoffKey = `asset-${startTime}-${endTime}-${channels
      .map((channel) => channel.value)
      .join(",")}-${rawPumpNames.join(",")}`;

    (window as any).__pumpChartHandoff = {
      key: handoffKey,
      rows: pumpData,
      axisSettings: JSON.parse(JSON.stringify(axisSettings)),
      axisAutoScale: { ...axisAutoScale },
      channelOptions: availableChannels.map((channel) => ({ ...channel })),
    };

    const params = new URLSearchParams();
    params.set("mode", "asset");
    params.set("start_time", String(startTime));
    params.set("end_time", String(endTime));
    params.set("channels", channels.map((channel) => channel.value).join(","));
    params.set("pumps", normalizedPumps.join(","));
    params.set("handoff", handoffKey);
    params.set("realtime", "0");

    rawPumpNames.forEach((pumpName) => {
      params.append("pump_name", pumpName);
    });

    window.open(
      `/chart-popout?${params.toString()}`,
      "_blank",
      "width=1600,height=900,resizable=yes,scrollbars=yes",
    );
  };
  const openBlenderChartPopout = () => {
    if (!blenderData || blenderData.rowCount === 0) return;

    const handoffKey = buildBlenderHandoffKey(
      blenderData.blenderId,
      blenderData.startTime,
      blenderData.endTime,
      blenderData.channels,
    );

    // The pop-out reads this through window.opener and opens with the loaded
    // data immediately; it only refetches if the handoff is missing (e.g. the
    // pop-out is refreshed after this tab closes).
    const handoff: BlenderChartHandoff = {
      key: handoffKey,
      data: blenderData,
      channels: blenderData.channels
        .map((key) => blenderChannelByKey.get(key))
        .filter((channel): channel is BlenderChannel => !!channel),
    };
    (window as any).__blenderChartHandoff = handoff;

    const params = new URLSearchParams();
    params.set("blender", blenderData.blenderId);
    params.set("start_time", String(blenderData.startTime));
    params.set("end_time", String(blenderData.endTime));
    params.set("channels", blenderData.channels.join(","));
    params.set("type", selectedBlender.type);
    if (selectedBlender.fleet != null) {
      params.set("fleet", String(selectedBlender.fleet));
    }
    params.set("handoff", handoffKey);

    window.open(
      `/blender-chart-popout?${params.toString()}`,
      "_blank",
      "width=1600,height=900,resizable=yes,scrollbars=yes",
    );
  };
  const exportBlenderCsv = useCallback(() => {
    if (
      !blenderData ||
      blenderData.rowCount === 0 ||
      blenderData.t.length === 0
    ) {
      toast.info("No Blender data available to export.");
      return;
    }

    const escapeCsvValue = (value: unknown) => {
      const text = String(value ?? "");
      return `"${text.replace(/"/g, '""')}"`;
    };

    const channels = blenderData.channels;

    const headerRow = [
      "Timestamp",
      ...channels.map((key) => {
        const channel = blenderChannelByKey.get(key);
        const label = channel?.label ?? key;

        return channel?.unit ? `${label} (${channel.unit})` : label;
      }),
    ];

    const dataRows = Array.from(blenderData.t, (timestamp, rowIndex) => [
      formatBlenderTimestamp(timestamp),
      ...channels.map((key) => {
        const value = blenderData.values[key]?.[rowIndex];

        return value === null ||
          value === undefined ||
          !Number.isFinite(Number(value))
          ? ""
          : value;
      }),
    ]);

    const csvContent = [headerRow, ...dataRows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\r\n");

    const blob = new Blob([`\uFEFF${csvContent}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const safeBlenderId = blenderData.blenderId.replace(/[^a-zA-Z0-9_-]/g, "_");

    const fromDate = new Date(blenderData.startTime).toISOString().slice(0, 10);

    const toDate = new Date(blenderData.endTime).toISOString().slice(0, 10);

    link.href = url;
    link.download = `${safeBlenderId}-blender-data-${fromDate}-to-${toDate}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    toast.success("Blender data exported.");
  }, [blenderData, blenderChannelByKey]);
  // "Which well ran when" is derived from dbo.pumps.well_id — every pump row is
  // stamped with the well it was pumping, and that is ground truth.
  //
  // dbo.stages CANNOT answer this and must not be used for it: it holds exactly
  // one row per (well, stage_number) (unique constraint uq_stage_per_well), never
  // populates end_ts (0 of 286 rows on pad 14251), and therefore cannot represent
  // a stage that was pumped in more than one session. On that pad well B3 pumped
  // stage 56 in TWO sessions — 04:04–05:01 and 06:22–08:03 — and `stages` records
  // only the first, so deriving bands from it dropped B3's second run entirely and
  // let the two B1 stages either side of it merge across the gap.
  //
  // 1m resolution is ample for band edges and keeps this cheap: measured 11.5k
  // rows / 166KB / 2.8s for a 10.5h fleet-wide window. The named channel is
  // irrelevant (well_id and stage_id are base columns on every row) but one must
  // be named, or the backend returns all ~130 channels.
  const fetchWellActivity = useCallback(
    async (
      fleetId: number,
      padId: number,
      wells: WellOption[],
      windowStart: number,
      windowEnd: number,
    ) => {
      if (!fleetId || !padId || wells.length === 0) return;
      if (!(windowEnd > windowStart)) return;

      const cacheKey = `${fleetId}|${windowStart}|${windowEnd}`;
      if (wellActivityLoadedKeyRef.current[padId] === cacheKey) return;
      wellActivityLoadedKeyRef.current[padId] = cacheKey;

      try {
        const token = getAuthToken();
        if (!token) return;

        const params = new URLSearchParams({
          fleet: String(fleetId),
          start_time: String(windowStart),
          end_time: String(windowEnd),
          resolution: "1m",
          channels: "discharge_pressure",
        });

        const [activityResponse, perWellStages] = await Promise.all([
          axios.get(`${backendUrl}/pumps?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 120000,
          }),
          // stage_id → stage_number, so a band can still name its stage. Only
          // ?well_id= is usable: /stages?pad_id= returns HTTP 500 because
          // dbo.stages has no pad_id column.
          Promise.all(
            wells.map((well) =>
              axios
                .get(`${backendUrl}/stages`, {
                  params: { well_id: well.id },
                  headers: { Authorization: `Bearer ${token}` },
                  timeout: 30000,
                })
                .then((response) =>
                  Array.isArray(response.data) ? response.data : [],
                )
                .catch(() => [] as any[]),
            ),
          ),
        ]);

        const stageNumberById = new Map<number, number>();
        perWellStages.flat().forEach((stage: any) => {
          const stageId = Number(stage?.id);
          const stageNumber = Number(stage?.stage_number);
          if (Number.isFinite(stageId) && Number.isFinite(stageNumber)) {
            stageNumberById.set(stageId, stageNumber);
          }
        });

        const wellNameById = new Map<number, string>(
          wells.map((well) => [well.id, well.well_name]),
        );

        const payload = activityResponse.data;
        const rows: any[] = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

        // Per minute, collect EVERY well being pumped — not the majority one.
        // Simul-frac runs two (or more) wells at once, and taking a majority
        // silently hid the second well entirely.
        interface MinuteWell {
          pumps: Set<string>;
          stageVotes: Map<number, number>;
        }

        const byMinute = new Map<number, Map<number, MinuteWell>>();

        rows.forEach((row) => {
          const wellId = Number(row?.well_id);
          const timestamp = Number(row?.time_stamp);
          if (!Number.isFinite(wellId) || !Number.isFinite(timestamp)) return;

          const minute = Math.floor(timestamp / 60_000);
          let wellsThisMinute = byMinute.get(minute);
          if (!wellsThisMinute) {
            wellsThisMinute = new Map();
            byMinute.set(minute, wellsThisMinute);
          }

          let entry = wellsThisMinute.get(wellId);
          if (!entry) {
            entry = { pumps: new Set(), stageVotes: new Map() };
            wellsThisMinute.set(wellId, entry);
          }

          entry.pumps.add(String(row?.pump_name ?? ""));

          const stageId = Number(row?.stage_id);
          if (Number.isFinite(stageId)) {
            entry.stageVotes.set(
              stageId,
              (entry.stageVotes.get(stageId) ?? 0) + 1,
            );
          }
        });

        // A well counts for the minute only with real representation. One or two
        // stragglers still reporting the previous well at a handover must not
        // register as a concurrent well; a genuine simul-frac leg is ~half the
        // fleet (measured 13–14 pumps of 27).
        const qualifyingWells = (wellsThisMinute: Map<number, MinuteWell>) => {
          let total = 0;
          wellsThisMinute.forEach((entry) => {
            total += entry.pumps.size;
          });

          const floor = Math.max(2, Math.ceil(total * 0.12));
          const qualifying: number[] = [];

          wellsThisMinute.forEach((entry, wellId) => {
            if (entry.pumps.size >= floor) qualifying.push(wellId);
          });

          return qualifying.sort((a, b) => a - b);
        };

        interface BandAccumulator {
          key: string;
          wellIds: number[];
          pumpsByWell: Map<number, Set<string>>;
          stageVotesByWell: Map<number, Map<number, number>>;
          from: number;
          to: number;
        }

        const accumulators: BandAccumulator[] = [];

        Array.from(byMinute.keys())
          .sort((a, b) => a - b)
          .forEach((minute) => {
            const wellsThisMinute = byMinute.get(minute)!;
            const wellIds = qualifyingWells(wellsThisMinute);
            if (wellIds.length === 0) return;

            const key = wellIds.join("+");
            const minuteStart = minute * 60_000;
            const minuteEnd = minuteStart + 60_000;
            const previous = accumulators[accumulators.length - 1];

            // A new band starts when the SET of concurrent wells changes; a gap of
            // up to two minutes is reporting noise, not a handover.
            const isContinuation =
              previous &&
              previous.key === key &&
              minuteStart - previous.to <= 2 * 60_000;

            const target: BandAccumulator = isContinuation
              ? previous
              : {
                  key,
                  wellIds,
                  pumpsByWell: new Map(),
                  stageVotesByWell: new Map(),
                  from: minuteStart,
                  to: minuteEnd,
                };

            if (!isContinuation) accumulators.push(target);
            target.to = minuteEnd;

            wellIds.forEach((wellId) => {
              const entry = wellsThisMinute.get(wellId)!;

              let pumps = target.pumpsByWell.get(wellId);
              if (!pumps) {
                pumps = new Set();
                target.pumpsByWell.set(wellId, pumps);
              }
              entry.pumps.forEach((pump) => pumps!.add(pump));

              let stageVotes = target.stageVotesByWell.get(wellId);
              if (!stageVotes) {
                stageVotes = new Map();
                target.stageVotesByWell.set(wellId, stageVotes);
              }
              entry.stageVotes.forEach((count, stageId) => {
                stageVotes!.set(
                  stageId,
                  (stageVotes!.get(stageId) ?? 0) + count,
                );
              });
            });
          });

        const majorityStageNumber = (
          stageVotes: Map<number, number> | undefined,
        ) => {
          if (!stageVotes || stageVotes.size === 0) return null;

          let bestStageId: number | null = null;
          let bestCount = -1;
          stageVotes.forEach((count, stageId) => {
            if (count > bestCount) {
              bestCount = count;
              bestStageId = stageId;
            }
          });

          return bestStageId === null
            ? null
            : (stageNumberById.get(bestStageId) ?? null);
        };

        const bands: WellActivityBand[] = accumulators
          // Two minutes is the floor for a real run; shorter is a reporting
          // artefact at a handover boundary.
          .filter(
            (accumulator) => accumulator.to - accumulator.from >= 2 * 60_000,
          )
          .map((accumulator) => ({
            from: accumulator.from,
            to: accumulator.to,
            wells: accumulator.wellIds
              .map((wellId) => ({
                wellId,
                wellName: wellNameById.get(wellId) ?? `Well ${wellId}`,
                stageNumber: majorityStageNumber(
                  accumulator.stageVotesByWell.get(wellId),
                ),
                pumpCount: accumulator.pumpsByWell.get(wellId)?.size ?? 0,
              }))
              .sort((a, b) => a.wellName.localeCompare(b.wellName)),
          }));

        setWellActivityByPad((previous) => ({ ...previous, [padId]: bands }));
      } catch (error) {
        console.error("Failed to load well activity:", error);
        delete wellActivityLoadedKeyRef.current[padId];
      }
    },
    [backendUrl],
  );
  const fetchPadWells = useCallback(
    async (padId: number) => {
      try {
        // Wells are already loaded.
        // Do not reset the user's existing well selection.
        if (padWells[padId]) {
          return;
        }

        const token = getAuthToken();
        if (!token) return;

        const response = await axios.get(`${backendUrl}/pads/${padId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const wells: WellOption[] = response.data?.wells || [];

        setPadWells((prev) => ({
          ...prev,
          [padId]: wells,
        }));

        // Only initialize "Show all wells" when there is no saved/restored value.
        setShowAllWellsByPad((prev) => {
          if (prev[padId] !== undefined) {
            return prev;
          }

          return {
            ...prev,
            [padId]: true,
          };
        });

        // Only initialize selected wells when there is no saved/restored selection.
        setSelectedWellIdsByPad((prev) => {
          if (prev[padId] !== undefined) {
            return prev;
          }

          return {
            ...prev,
            [padId]: wells.map((well) => well.id),
          };
        });

        setChartModeByPad((prev) => ({
          ...prev,
          [padId]: prev[padId] ?? "combined",
        }));
      } catch (error) {
        console.error("Error fetching pad wells:", error);
        toast.error("Failed to fetch wells");
      }
    },
    [backendUrl, dashboardPumpParam, padWells],
  );

  // Load the well-activity timeline only AFTER the fleet's chart has loaded. This
  // keeps its 1m request out of the initial fan-out, which the backend serialises
  // (concurrent /pumps queries contend and the chart is what the user is waiting
  // for), and guarantees the pad's wells are known by the time it runs.
  useEffect(() => {
    if (activeTab !== "fleet") return;

    const fleetId = selectedFleet?.fleet;
    if (fleetId == null) return;

    if ((loadedChartChannelsByFleet[fleetId] || []).length === 0) return;

    const from = fromDateByFleet[fleetId];
    const to = toDateByFleet[fleetId];
    if (!from || !to || to.getTime() <= from.getTime()) return;

    const historicalPad = getPadForFleetDateRange(selectedFleet, from, to);
    const padId = historicalPad?.id;
    if (padId == null) return;

    const wells = padWells[padId];
    if (!wells || wells.length === 0) return;

    void fetchWellActivity(fleetId, padId, wells, from.getTime(), to.getTime());
  }, [
    activeTab,
    selectedFleet,
    padWells,
    fromDateByFleet,
    toDateByFleet,
    loadedChartChannelsByFleet,
    fetchWellActivity,
    getPadForFleetDateRange,
  ]);

  const currentUserEmail = localStorage.getItem("email");
  const userRoles = JSON.parse(localStorage.getItem("roles") || "[]");
  const isManager =
    userRoles.includes("Manager") || userRoles.includes("Admin");
  const canManageAnnotation = (annotation: ChartAnnotation) => {
    return isManager || annotation.createdByEmail === currentUserEmail;
  };

  const handleChartChannelChange = (sel: PumpChannel[]) => {
    setSelectedTemplate(null);

    const normalizedSelection = normalizePowerEndTemperatureSelection(sel);

    if (selectedPumps.length > 1 && normalizedSelection.length > 1) {
      const latestSelected =
        normalizedSelection[normalizedSelection.length - 1];

      setSelectedAssetChannels([latestSelected]);
      selectedAssetChannelsRef.current = [latestSelected];

      setSelectedChannels([latestSelected]);
      selectedChannelsRef.current = [latestSelected];

      toast.info(
        "Only one channel can be displayed when multiple pumps are selected.",
      );

      if (activeTab === "asset") {
        scheduleAssetChartChannelReload();
      }

      return;
    }

    setSelectedAssetChannels(normalizedSelection);
    selectedAssetChannelsRef.current = normalizedSelection;

    setSelectedChannels(normalizedSelection);
    selectedChannelsRef.current = normalizedSelection;

    if (normalizedSelection.length === 0) {
      setChartSeries([]);
      loadedAssetChannelSignatureRef.current = "";
      return;
    }

    if (activeTab === "asset") {
      scheduleAssetChartChannelReload();
    }
  };

  const fetchPads = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const [padsResponse, stagesResponse] = await Promise.all([
        axios.get(`${backendUrl}/pads`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(`${backendUrl}/active-stages`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch((error) => {
            console.error(
              "Failed to fetch active-stages completion status:",
              error,
            );
            return null;
          }),
      ]);
      const padsData: Pad[] = padsResponse.data;

      const historicalPadsByFleet = padsData.reduce<Record<number, Pad[]>>(
        (result, pad) => {
          if (!pad.fleet) return result;
          if (!result[pad.fleet]) result[pad.fleet] = [];
          result[pad.fleet].push(pad);
          return result;
        },
        {},
      );

      Object.values(historicalPadsByFleet).forEach((pads) => {
        pads.sort(
          (left, right) =>
            Number(left.start_ts ?? 0) - Number(right.start_ts ?? 0),
        );
      });

      setPadsByFleet(historicalPadsByFleet);

      const fleetMap = new Map<number, Pad>();
      const isCurrentJob = (pad: Pad) => {
        const status = normalizeCompletionStatus(pad.status);
        return (
          status === "active" || status === "lost_comms" || status === "expired"
        );
      };

      padsData.forEach((pad) => {
        if (!pad.fleet) return;
        const existing = fleetMap.get(pad.fleet);
        if (!existing) {
          fleetMap.set(pad.fleet, pad);
          return;
        }

        const padCurrent = isCurrentJob(pad);
        const existingCurrent = isCurrentJob(existing);
        if (padCurrent && !existingCurrent) {
          fleetMap.set(pad.fleet, pad);
        } else if (padCurrent === existingCurrent) {
          if (
            pad.start_ts &&
            existing.start_ts &&
            pad.start_ts > existing.start_ts
          ) {
            fleetMap.set(pad.fleet, pad);
          }
        }
      });

      const groups: FleetGroup[] = Array.from(fleetMap.entries())
        .map(([fleet, pad]) => ({
          fleet,
          latestPad: pad,
        }))
        .sort((a, b) => a.fleet - b.fleet);

      const stages = extractActiveStageRows(stagesResponse?.data);
      setCompletionStatusByFleet(completionStatusFromActiveStages(stages));
      setFleetGroups(groups);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pads:", error);
      toast.error("Failed to fetch pad data");
      setLoading(false);
    }
  }, [backendUrl, fetchPadWells]);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await axios.get(`${backendUrl}/widget-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const templateRows: Template[] = Array.isArray(response.data)
        ? response.data
        : [];

      // TABLE channels are saved with position_N. Sort on read because an API/DB
      // response is not guaranteed to preserve child-row order.
      setTemplates(
        templateRows.map((template) => ({
          ...template,
          channels: [...(template.channels || [])].sort((left, right) => {
            if (template.widget_type !== "TABLE") return 0;

            const leftPosition = Number(
              String(left.display_position || "").match(/position_(\d+)/i)?.[1],
            );
            const rightPosition = Number(
              String(right.display_position || "").match(
                /position_(\d+)/i,
              )?.[1],
            );

            if (
              !Number.isFinite(leftPosition) ||
              !Number.isFinite(rightPosition)
            ) {
              return 0;
            }

            return leftPosition - rightPosition;
          }),
        })),
      );
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  }, [backendUrl]);

  const fetchPumpList = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await axios.get(`${backendUrl}/pumps/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const pumpRows = Array.isArray(response.data) ? response.data : [];

      const pumpByNormalizedName = new Map<string, AssetPumpOption>();

      pumpRows.forEach((pump: any) => {
        const rawPumpName = String(pump?.pump_name || "").trim();

        if (!rawPumpName) {
          return;
        }

        // Temporary frontend normalization until historical database pump names
        // are updated. Both 53Q-11662 and 53Q11662 display as 53Q11662.
        const normalizedPumpName = rawPumpName.replace(/-/g, "").toUpperCase();

        const existingPump = pumpByNormalizedName.get(normalizedPumpName);

        if (existingPump) {
          if (!existingPump.rawPumpNames.includes(rawPumpName)) {
            existingPump.rawPumpNames.push(rawPumpName);
          }

          return;
        }

        pumpByNormalizedName.set(normalizedPumpName, {
          value: normalizedPumpName,
          label: normalizedPumpName,
          rawPumpNames: [rawPumpName],
        });
      });

      const options = Array.from(pumpByNormalizedName.values()).sort(
        (first, second) =>
          first.label.localeCompare(second.label, undefined, {
            numeric: true,
            sensitivity: "base",
          }),
      );

      setPumpListOptions(options);
    } catch (error) {
      console.error("Error fetching pump list:", error);
    }
  }, [backendUrl]);

  // Asset can be restored directly from sessionStorage after a browser refresh.
  // In that path the Asset button's onClick handler never runs, so From/To and
  // the pump list were previously left blank. Initialize only missing dates to
  // the same 6-hour default as Fleet and load the pump list whenever Asset is active.
  useEffect(() => {
    if (activeTab !== "asset") return;

    const now = new Date();
    const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);

    setFromDate((previous) => previous ?? sixHoursAgo);
    setToDate((previous) => previous ?? now);

    void fetchPumpList();
  }, [activeTab, fetchPumpList]);

  const fetchBlenderList = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    setBlenderListStatus("loading");
    try {
      // No query params: the asset view lists every blender on every fleet.
      const response = await axios.get(`${backendUrl}/blenders/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rows: BlenderListItem[] = Array.isArray(response.data)
        ? response.data
        : [];

      const assets: BlenderAsset[] = rows
        .filter((row) => String(row?.blender_name || "").trim())
        .map((row) => {
          const name = String(row.blender_name).trim();
          return {
            id: name,
            label: name,
            type: inferBlenderType(name, row.is_electric),
            fleet: row.fleet ?? null,
            padId: row.pad_id ?? null,
            lastSeen: row.last_seen ?? null,
          };
        });

      setBlenderAssets(assets);
      setBlenderListStatus("loaded");
    } catch (error) {
      console.error("Error fetching blender list:", error);
      setBlenderListStatus("error");
    }
  }, [backendUrl]);

  // The blender list changes only when a new unit appears in the field, so
  // fetch once when the Blender tab is first opened (and retry after an error).
  useEffect(() => {
    if (activeTab !== "blender") return;
    if (blenderListStatus !== "idle" && blenderListStatus !== "error") return;

    void fetchBlenderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchBlenderList]);

  // Keep the selection valid once the list arrives: default to the first
  // blender, or clear the selection when the list is empty.
  useEffect(() => {
    if (blenderListStatus !== "loaded") return;
    if (blenderAssets.some((asset) => asset.id === selectedBlenderId)) return;

    if (blenderAssets.length > 0) {
      selectBlender(blenderAssets[0]);
    } else {
      setSelectedBlenderId("");
    }
  }, [blenderAssets, blenderListStatus, selectedBlenderId, selectBlender]);

  // Load the selected blender's channel catalog. Groups and channels differ
  // per unit (diesel vs electric, and by what each unit has reported), so the
  // side pane is driven entirely by this response.
  useEffect(() => {
    if (!selectedBlenderId) {
      setBlenderChannelGroups([]);
      setBlenderChannelCount(0);
      setSelectedBlenderChannels([]);
      setBlenderChannelsStatus("idle");
      return;
    }

    const token = getAuthToken();
    if (!token) return;

    let cancelled = false;
    const pendingSelection =
      pendingBlenderChannelsRef.current?.blenderId === selectedBlenderId
        ? pendingBlenderChannelsRef.current.channels
        : null;

    setBlenderChannelsStatus("loading");
    setBlenderChannelGroups([]);
    setBlenderChannelCount(0);
    setSelectedBlenderChannels(pendingSelection ?? []);

    axios
      .get<BlenderChannelsResponse>(
        `${backendUrl}/blenders/${encodeURIComponent(selectedBlenderId)}/channels`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((response) => {
        if (cancelled) return;

        const groups = Array.isArray(response.data?.groups)
          ? response.data.groups.filter(
              (group) => Array.isArray(group.channels) && group.channels.length,
            )
          : [];
        const channels = groups.flatMap((group) => group.channels);
        const defaultKeys = DEFAULT_BLENDER_CHANNEL_LABELS.map(
          (label) => channels.find((channel) => channel.label === label)?.key,
        ).filter((key): key is string => !!key);

        setBlenderChannelGroups(groups);
        setBlenderChannelCount(
          typeof response.data?.channel_count === "number"
            ? response.data.channel_count
            : channels.length,
        );

        const availableChannelKeys = new Set(
          channels.map((channel) => channel.key),
        );
        const queuedSelection =
          pendingBlenderChannelsRef.current?.blenderId === selectedBlenderId
            ? pendingBlenderChannelsRef.current.channels
            : null;
        const nextSelectedChannels =
          queuedSelection !== null
            ? queuedSelection.filter((key) => availableChannelKeys.has(key))
            : defaultKeys;

        if (queuedSelection !== null) {
          pendingBlenderChannelsRef.current = null;
        }

        setSelectedBlenderChannels(nextSelectedChannels);
        setExpandedBlenderGroups(
          new Set(
            groups
              .slice(0, DEFAULT_EXPANDED_BLENDER_GROUP_COUNT)
              .map((group) => group.group),
          ),
        );
        setBlenderChannelsStatus("loaded");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching blender channels:", error);
        setBlenderChannelsStatus(
          axios.isAxiosError(error) && error.response?.status === 404
            ? "not_found"
            : "error",
        );
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl, selectedBlenderId]);

  // What the Blender date pickers show: the unsaved draft while editing,
  // otherwise the loaded range.
  const blenderPickerFromDate = blenderDraftDates?.from ?? blenderFromDate;
  const blenderPickerToDate = blenderDraftDates?.to ?? blenderToDate;
  const isBlenderDateRangePending =
    blenderPickerFromDate.getTime() !== blenderFromDate.getTime() ||
    blenderPickerToDate.getTime() !== blenderToDate.getTime();
  const blenderHasInvalidDateRange =
    blenderPickerToDate.getTime() <= blenderPickerFromDate.getTime();

  // Blender "Load Data": applies the edited From/To; reloads the same range
  // when nothing changed.
  const handleBlenderLoadData = () => {
    if (blenderHasInvalidDateRange) return;
    setBlenderDraftDates(null);
    if (isBlenderDateRangePending) {
      setBlenderFromDate(blenderPickerFromDate);
      setBlenderToDate(blenderPickerToDate);
    } else {
      setBlenderReloadToken((token) => token + 1);
    }
  };

  // Load chart/table data whenever the blender, its selected channels or the
  // loaded range change (date edits only count once Load Data applies them). Debounced so ticking several checkboxes or editing a date
  // sends one request; a newer request aborts the one in flight.
  useEffect(() => {
    if (activeTab !== "blender") return;
    // While streaming, the live effect below owns blenderData. Turning
    // streaming off re-runs this effect and reloads the From/To range.
    if (isBlenderStreaming) return;

    // Only send keys that belong to the loaded catalog, so a stale selection
    // from the previous blender never reaches the API as CHANNEL_INVALID.
    const channelKeys =
      blenderChannelsStatus === "loaded"
        ? selectedBlenderChannels.filter((key) => blenderChannelByKey.has(key))
        : [];

    if (!selectedBlenderId || channelKeys.length === 0) {
      setBlenderData(null);
      setBlenderDataStatus("idle");
      setBlenderDataError("");
      return;
    }

    const startTime = blenderFromDate.getTime();
    const endTime = blenderToDate.getTime();
    if (!(startTime < endTime)) {
      setBlenderData(null);
      setBlenderDataStatus("error");
      setBlenderDataError("The From time must be earlier than the To time.");
      return;
    }

    const token = getAuthToken();
    if (!token) return;

    const controller = new AbortController();
    setBlenderDataStatus("loading");
    setBlenderDataError("");

    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({
        start_time: String(startTime),
        end_time: String(endTime),
        channels: channelKeys.join(","),
        resolution: "auto",
        format: "columnar",
      });

      axios
        .get<BlenderDataResponse>(
          `${backendUrl}/blenders/${encodeURIComponent(selectedBlenderId)}/data?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        )
        .then((response) => {
          setBlenderData(
            parseBlenderDataResponse(response.data, {
              blenderId: selectedBlenderId,
              startTime,
              endTime,
              channels: channelKeys,
            }),
          );
          setBlenderDataStatus("loaded");
        })
        .catch((error) => {
          if (axios.isCancel(error)) return;
          console.error("Error fetching blender data:", error);

          setBlenderData(null);
          setBlenderDataStatus("error");
          setBlenderDataError(
            describeBlenderDataError(
              axios.isAxiosError(error) ? error.response?.data : null,
            ),
          );
        });
    }, BLENDER_DATA_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    backendUrl,
    selectedBlenderId,
    selectedBlenderChannels,
    blenderChannelByKey,
    blenderChannelsStatus,
    blenderFromDate,
    blenderToDate,
    isBlenderStreaming,
    blenderReloadToken,
  ]);

  // Real-time streaming. Seed with the last part of the window from /live
  // (measured back from the blender's newest reading, so the telemetry clock
  // offset doesn't matter), backfill anything older than /live's 1 h lookback
  // from /data, then poll /live every second with the since cursor and append.
  useEffect(() => {
    if (!isBlenderStreaming || activeTab !== "blender") return;

    const channelKeys =
      blenderChannelsStatus === "loaded"
        ? selectedBlenderChannels.filter((key) => blenderChannelByKey.has(key))
        : [];
    if (!selectedBlenderId || channelKeys.length === 0) {
      setBlenderData(null);
      setBlenderDataStatus("idle");
      setBlenderLiveStatus(null);
      return;
    }

    const token = getAuthToken();
    if (!token) return;

    const blenderId = selectedBlenderId;
    const windowMs = Math.min(
      BLENDER_LIVE_MAX_WINDOW_MS,
      Math.max(
        BLENDER_LIVE_MIN_WINDOW_MS,
        blenderToDate.getTime() - blenderFromDate.getTime(),
      ),
    );
    const lookbackMs = Math.min(windowMs, BLENDER_LIVE_MAX_LOOKBACK_MS);
    const headers = { Authorization: `Bearer ${token}` };
    const controller = new AbortController();
    const blenderPath = `${backendUrl}/blenders/${encodeURIComponent(blenderId)}`;

    let cancelled = false;
    let timer: number | undefined;
    let since: number | null = null;
    let consecutiveFailures = 0;
    let current: BlenderData | null = null;

    const parse = (body: BlenderDataResponse | undefined) =>
      parseBlenderDataResponse(body, {
        blenderId,
        startTime: 0,
        endTime: 0,
        channels: channelKeys,
      });

    const fetchLive = (cursor: number | null) => {
      const params = new URLSearchParams({
        channels: channelKeys.join(","),
        format: "columnar",
      });
      if (cursor !== null) params.set("since", String(cursor));
      else params.set("lookback_ms", String(lookbackMs));
      return axios.get<BlenderLiveResponse>(
        `${blenderPath}/live?${params.toString()}`,
        { headers, signal: controller.signal },
      );
    };

    const updateLiveStatus = (body: BlenderLiveResponse | undefined) =>
      setBlenderLiveStatus({
        live: body?.live === true,
        lastAdvanceAgeMs:
          typeof body?.last_advance_age_ms === "number"
            ? body.last_advance_age_ms
            : null,
        connectionError: false,
      });

    // Keeps the newest reading at the right edge of the window.
    const withWindow = (data: BlenderData, newest: number): BlenderData => ({
      ...data,
      resolution: "1s",
      bucketMs: 1000,
      startTime: newest - windowMs,
      endTime: newest,
    });

    const schedule = (delayMs: number) => {
      if (cancelled) return;
      timer = window.setTimeout(poll, delayMs);
    };

    const handleFailure = (error: unknown) => {
      if (cancelled || axios.isCancel(error)) return false;
      console.error("Blender live stream error:", error);

      const status = axios.isAxiosError(error) ? error.response?.status : null;
      // A bad request or unknown blender won't fix itself: stop streaming.
      if (status === 400 || status === 404) {
        setBlenderDataStatus("error");
        setBlenderDataError(
          describeBlenderDataError(
            axios.isAxiosError(error) ? error.response?.data : null,
          ),
        );
        setIsBlenderStreaming(false);
        return false;
      }

      // Network blips and 5xx: keep the chart and retry with backoff.
      consecutiveFailures += 1;
      setBlenderLiveStatus((previous) => ({
        live: false,
        lastAdvanceAgeMs: previous?.lastAdvanceAgeMs ?? null,
        connectionError: true,
      }));
      return true;
    };

    const poll = async () => {
      const started = Date.now();
      try {
        const response = await fetchLive(since);
        if (cancelled) return;

        // When row_count is 0 the cursor hasn't moved; keep the same since.
        if (typeof response.data?.max_time_stamp === "number") {
          since = response.data.max_time_stamp;
        }

        const incoming = parse(response.data);
        if (current && incoming.t.length > 0) {
          const merged = mergeBlenderLiveRows(current, incoming, windowMs);
          if (merged !== current) {
            current = withWindow(merged, merged.t[merged.t.length - 1]);
            setBlenderData(current);
            setBlenderDataStatus("loaded");
          }
        }
        updateLiveStatus(response.data);
        consecutiveFailures = 0;
        schedule(Math.max(0, BLENDER_LIVE_POLL_MS - (Date.now() - started)));
      } catch (error) {
        if (!handleFailure(error)) return;
        schedule(
          Math.min(
            BLENDER_LIVE_MAX_RETRY_MS,
            BLENDER_LIVE_POLL_MS * 2 ** consecutiveFailures,
          ),
        );
      }
    };

    const seed = async () => {
      setBlenderDataStatus("loading");
      setBlenderDataError("");
      setBlenderLiveStatus(null);
      try {
        const first = await fetchLive(null);
        if (cancelled) return;

        since =
          typeof first.data?.max_time_stamp === "number"
            ? first.data.max_time_stamp
            : null;
        let data = parse(first.data);

        // /live's first call covers at most 1 h; fill the rest of the window
        // from /data so the chart opens with the whole span.
        if (since !== null && windowMs > lookbackMs) {
          const backfillStart = since - windowMs;
          const backfillEnd = data.t.length > 0 ? data.t[0] - 1 : since;
          if (backfillStart < backfillEnd) {
            const params = new URLSearchParams({
              start_time: String(backfillStart),
              end_time: String(backfillEnd),
              channels: channelKeys.join(","),
              resolution: "1s",
              format: "columnar",
            });
            const history = await axios.get<BlenderDataResponse>(
              `${blenderPath}/data?${params.toString()}`,
              { headers, signal: controller.signal },
            );
            if (cancelled) return;
            data = mergeBlenderLiveRows(parse(history.data), data, windowMs);
          }
        }

        const newest =
          since ?? (data.t.length > 0 ? data.t[data.t.length - 1] : Date.now());
        current = withWindow({ ...data, rowCount: data.t.length }, newest);
        setBlenderData(current);
        setBlenderDataStatus("loaded");
        updateLiveStatus(first.data);
        schedule(BLENDER_LIVE_POLL_MS);
      } catch (error) {
        if (!handleFailure(error)) return;
        // Couldn't seed yet: retry the whole seed with backoff.
        timer = window.setTimeout(
          seed,
          Math.min(
            BLENDER_LIVE_MAX_RETRY_MS,
            BLENDER_LIVE_POLL_MS * 2 ** consecutiveFailures,
          ),
        );
      }
    };

    void seed();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isBlenderStreaming,
    activeTab,
    backendUrl,
    selectedBlenderId,
    selectedBlenderChannels,
    blenderChannelByKey,
    blenderChannelsStatus,
    blenderFromDate,
    blenderToDate,
  ]);

  const fetchPumpInterlocks = useCallback(async () => {
    try {
      const token = getAuthToken();

      if (!token) {
        return;
      }

      const response = await axios.get(`${backendUrl}/pump-interlocks/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rulesFromApi: PumpInterlockLimit[] = response.data?.rules || [];

      const fuelPressureFallbackRules: PumpInterlockLimit[] = [
        {
          channel: "engine_fuel_pressure",
          label: "Engine Fuel Pressure Low Warning",
          severity: "warning",
          operator: "<",
          value: 110,
          conditions: [],
        } as any,
        {
          channel: "engine_fuel_pressure",
          label: "Engine Fuel Pressure Low Critical",
          severity: "critical",
          operator: "<",
          value: 100,
          conditions: [],
        } as any,
      ];

      const hasFuelPressureRules = rulesFromApi.some(
        (rule) =>
          rule?.channel &&
          fuelPressureAliases.includes(rule.channel.trim().toLowerCase()),
      );

      const rules: PumpInterlockLimit[] = hasFuelPressureRules
        ? rulesFromApi
        : [...rulesFromApi, ...fuelPressureFallbackRules];

      const groupedRules = rules.reduce<Record<string, PumpInterlockLimit[]>>(
        (acc, rule) => {
          if (!rule?.channel) return acc;
          const key = rule.channel.trim().toLowerCase();
          acc[key] = [...(acc[key] || []), rule];
          return acc;
        },
        {},
      );

      setPumpInterlocks(groupedRules);
    } catch (error) {
      console.error("Error fetching pump interlock thresholds:", error);
      setPumpInterlocks({});
    }
  }, [backendUrl]);

  const compareThreshold = (
    value: any,
    operator?: string,
    limit?: number,
  ): boolean => {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue) || limit === null || limit === undefined) {
      return false;
    }

    switch (operator) {
      case ">":
        return numericValue > limit;
      case ">=":
        return numericValue >= limit;
      case "<":
        return numericValue < limit;
      case "<=":
        return numericValue <= limit;
      case "=":
      case "==":
        return numericValue === limit;
      default:
        return false;
    }
  };

  const isInterlockRuleActive = (
    rowData: any,
    conditions?: PumpInterlockCondition[],
  ): boolean => {
    if (!conditions?.length) return true;

    return conditions.every((condition) =>
      compareThreshold(
        getChannelValue(rowData, condition.channel),
        condition.operator,
        condition.value,
      ),
    );
  };
  const fuelPressureAliases = [
    "engine_fuel_pressure",
    "engine_fuel_pressure_psi",
    "engine_fuel_press",
    "fuel_pressure",
    "fuel_press",
    "fuel_pressure_psi",
    "eng_fuel_pressure",
    "atlas_engine_fuel_pressure",
    "engine_fuel_delivery_pressure",
    "engine_fuel_pressure_psig",
  ];

  const CHANNEL_ALIASES: Record<string, string[]> = {
    rpm: [
      "rpm",
      "engine_rpm",
      "engine_speed",
      "engine_speed_rpm",
      "eng_rpm",
      "motor_speed",
      "pump_rpm",
    ],
    calculated_discharge_rate: [
      "calculated_discharge_rate",
      "discharge_rate",
      "calculated_rate",
    ],
    discharge_vfd_amps: [
      "discharge_vfd_amps",
      "motor_current",
      "vfd_amps",
      "vfd_current",
    ],
    discharge_vfd_dc_link_volts: [
      "discharge_vfd_dc_link_volts",
      "dc_link_volts",
      "vfd_dc_link_volts",
      "dc_link_voltage",
    ],
    discharge_motor_winding_a1_temp: [
      "discharge_motor_winding_a1_temp",
      "winding_a1_temp",
      "winding_a_temp",
    ],
    discharge_motor_winding_a2_temp: [
      "discharge_motor_winding_a2_temp",
      "winding_a2_temp",
      "winding_a_temp",
    ],
    discharge_motor_winding_b1_temp: [
      "discharge_motor_winding_b1_temp",
      "winding_b1_temp",
      "winding_b_temp",
    ],
    discharge_motor_winding_b2_temp: [
      "discharge_motor_winding_b2_temp",
      "winding_b2_temp",
      "winding_b_temp",
    ],
    discharge_motor_winding_c1_temp: [
      "discharge_motor_winding_c1_temp",
      "winding_c1_temp",
      "winding_c_temp",
    ],
    discharge_motor_winding_c2_temp: [
      "discharge_motor_winding_c2_temp",
      "winding_c2_temp",
      "winding_c_temp",
    ],
    discharge_motor_de_bearing_temp: [
      "discharge_motor_de_bearing_temp",
      "de_bearing_temp",
      "drive_end_bearing_temp",
    ],
    discharge_motor_nde_bearing_temp: [
      "discharge_motor_nde_bearing_temp",
      "nde_bearing_temp",
      "non_drive_end_bearing_temp",
    ],
    cube_coolant_supply_pressure: [
      "cube_coolant_supply_pressure",
      "coolant_supply_pressure",
      "cube_coolant_supply_press",
      "coolant_supply_press",
      "cube_supply_pressure",
    ],
    pe_oil_level_low: [
      "pe_oil_level_low",
      "powerend_oil_level_low",
      "power_end_oil_level_low",
      "atlas_oil_tank_level_low",
      "atlas_oil_tank_critical_level",
    ],
    engine_temp: ["engine_temp", "engine_coolant_temperature"],
    engine_coolant_temperature: ["engine_temp", "engine_coolant_temperature"],
    trip_discharge_pressure: [
      "trip_discharge_pressure",
      "trip_discharge_pressure_psi",
      "trip_discharge_press",
    ],
    powerend_oil_delivered_flow_rate: [
      "powerend_oil_delivered_flow_rate",
      "power_end_oil_delivered_flow_rate",
      "pe_oil_delivered_flow_rate",
      "powerend_oil_delivered_flow",
    ],
    powerend_oil_filter_differential_pressure: [
      "powerend_oil_filter_differential_pressure",
      "power_end_oil_filter_differential_pressure",
      "pe_oil_filter_differential_pressure",
      "powerend_oil_filter_diff_pressure",
      "pe_oil_filter_diff_pressure",
    ],
    powerend_oil_heat_retention: [
      "powerend_oil_heat_retention",
      "power_end_oil_heat_retention",
      "pe_oil_heat_retention",
    ],
    powerend_oil_radiator_differential_temperature: [
      "powerend_oil_radiator_differential_temperature",
      "power_end_oil_radiator_differential_temperature",
      "pe_oil_radiator_differential_temperature",
      "powerend_oil_radiator_diff_temp",
      "pe_oil_radiator_diff_temp",
    ],
    powerend_oil_bypass_flow_rate: [
      "powerend_oil_bypass_flow_rate",
      "power_end_oil_bypass_flow_rate",
      "pe_oil_bypass_flow_rate",
    ],
    powerend_oil_temperature: [
      "powerend_oil_temperature",
      "power_end_oil_temperature",
      "pe_oil_temperature",
      "pe_temp",
    ],
    powerend_oil_inlet_temperature: [
      "powerend_oil_inlet_temperature",
      "power_end_oil_inlet_temperature",
      "pe_oil_inlet_temperature",

      // Older pumps call the inlet reading simply "Power End Oil Temperature"
      "powerend_oil_temperature",
      "power_end_oil_temperature",
      "pe_oil_temperature",
      "pe_temp",
    ],
    powerend_oil_outlet_temperature: [
      "powerend_oil_outlet_temperature",
      "power_end_oil_outlet_temperature",
      "pe_oil_outlet_temperature",
      "pe_outlet_temp",
    ],
    powerend_oil_pump_flow_rate: [
      "powerend_oil_pump_flow_rate",
      "power_end_oil_pump_flow_rate",
      "pe_oil_pump_flow_rate",
    ],
    powerend_oil_radiator_temperature: [
      "powerend_oil_radiator_temperature",
      "power_end_oil_radiator_temperature",
      "pe_oil_radiator_temperature",
    ],
    powerend_oil_tank_temperature: [
      "powerend_oil_tank_temperature",
      "power_end_oil_tank_temperature",
      "pe_oil_tank_temperature",
    ],
    trans_oil_pressure: [
      "trans_oil_pressure",
      "trans_pressure",
      "transmission_oil_pressure",
      "transmission_pressure",
      "trans_oil_press",
      "transmission_oil_press",
    ],
    transmission_input_speed: [
      "transmission_input_speed",
      "trans_input_speed",
      "trans_input_rpm",
      "transmission_input_rpm",
    ],
    transmission_output_speed: [
      "transmission_output_speed",
      "trans_output_speed",
      "trans_output_rpm",
      "transmission_output_rpm",
    ],
    engine_oil_temperature: [
      "engine_oil_temperature",
      "engine_oil_temp",
      "eng_oil_temperature",
      "eng_oil_temp",
    ],
    engine_intake_manifold_pressure: ["engine_intake_manifold_pressure"],
    engine_torque_pct: ["engine_torque_pct"],
    engine_df_supply_pressure: ["engine_df_supply_pressure"],
    engine_oil_differential_pressure: [
      "engine_oil_differential_pressure",
      "engine_air_filter_diff",
    ],
    engine_def_level_pct: ["engine_def_level_pct"],
    engine_battery_voltage: [
      "engine_battery_voltage",
      "battery_voltage",
      "engine_battery_volts",
      "eng_battery_voltage",
    ],
    engine_fuel_pressure: fuelPressureAliases,
    engine_fuel_pressure_psi: fuelPressureAliases,
    engine_fuel_press: fuelPressureAliases,
    fuel_pressure: fuelPressureAliases,
    fuel_press: fuelPressureAliases,
    fuel_pressure_psi: fuelPressureAliases,
    eng_fuel_pressure: fuelPressureAliases,
    atlas_engine_fuel_pressure: fuelPressureAliases,
    engine_hours: ["engine_hours", "engine_total_hours", "engine_hour_meter"],
    fe_hours: ["fe_hours", "fluid_end_hours"],
    pe_hours: ["pe_hours", "powerend_hours", "power_end_hours"],
    gear: ["gear", "current_gear", "trans_gear", "transmission_gear"],
    engine_load: [
      "engine_load",
      "engine_percent_load",
      "engine_load_percent",
      "eng_load",
    ],
    engine_oil_pressure: [
      "engine_oil_pressure",
      "engine_oil_press",
      "engine_oil_pressure_psi",
      "oil_pressure",
      "eng_oil_pressure",
    ],
    suction_pressure: [
      "suction_pressure",
      "fe_suction_pressure",
      "atlas_secondary_pump_suction_pressure",
      "suction_press",
      "fluid_end_suction_pressure",
    ],
    power_end_oil_pressure: [
      "power_end_oil_pressure",
      "powerend_oil_pressure",
      "power_end_lube_pressure",
      "powerend_lube_pressure",
      "pe_pressure",
      "pe_lube_pressure",
      "pe_lube_press",
    ],
    powerend_oil_pressure: [
      "power_end_oil_pressure",
      "powerend_oil_pressure",
      "power_end_lube_pressure",
      "powerend_lube_pressure",
      "pe_pressure",
      "pe_lube_pressure",
      "pe_lube_press",
    ],
    inst_fuel_disp: [
      "inst_fuel_disp",
      "df_displacement",
      "dual_fuel_displacement",
    ],
    df_displacement: [
      "df_displacement",
      "inst_fuel_disp",
      "dual_fuel_displacement",
    ],
  };

  const normalizeChannelKey = (channel: string) =>
    channel
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/[\s-]+/g, "_")
      .replace(/_+/g, "_")
      .toLowerCase();

  const getAlarmGraphChannelValue = (alarm: any): string | null => {
    const explicitChannel = String(
      alarm?.graph_channel ??
        alarm?.alarm_graph_channel ??
        alarm?.graphChannel ??
        "",
    ).trim();

    if (explicitChannel) {
      return isLegacyPowerEndOilTemperatureChannel(explicitChannel)
        ? "powerend_oil_inlet_temperature"
        : explicitChannel;
    }

    const alarmName = String(
      alarm?.alarm ?? alarm?.alarm_name ?? alarm?.name ?? "",
    )
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();

    const mappings: Array<{ terms: string[]; channel: string }> = [
      {
        terms: [
          "transmissionoilpressure",
          "transmissionoilpress",
          "transmissionpressure",
        ],
        channel: "trans_oil_pressure",
      },
      {
        terms: ["engineoilpressure", "engineoilpress"],
        channel: "engine_oil_pressure",
      },
      {
        terms: ["suctionpressure", "suctionpress"],
        channel: "suction_pressure",
      },
      {
        terms: [
          "powerendoilpressure",
          "powerendlubepressure",
          "powerendpressure",
          "peoilpressure",
          "pelubepressure",
          "pepressure",
        ],
        channel: "pe_pressure",
      },
      {
        terms: [
          "transmissionoiltemperature",
          "transmissionoiltemp",
          "transmissiontemperature",
          "transtemp",
        ],
        channel: "trans_temp",
      },
      {
        terms: ["engineoiltemperature", "engineoiltemp"],
        channel: "engine_oil_temperature",
      },
      {
        terms: [
          "enginecoolanttemperature",
          "enginecoolanttemp",
          "enginetemperature",
          "enginetemp",
        ],
        channel: "engine_temp",
      },
      {
        terms: [
          "powerendoiltemperature",
          "powerendoiltemp",
          "powerendtemperature",
          "petemp",
        ],
        channel: "powerend_oil_inlet_temperature",
      },
      {
        terms: ["enginebatteryvoltage", "enginevoltage", "batteryvoltage"],
        channel: "engine_battery_voltage",
      },
      {
        terms: ["enginefuelpressure", "fuelpressure", "fuelpress"],
        channel: "engine_fuel_pressure",
      },
      {
        terms: ["transmissioninputspeed", "transmissioninputrpm"],
        channel: "transmission_input_speed",
      },
      {
        terms: ["transmissionoutputspeed", "transmissionoutputrpm"],
        channel: "transmission_output_speed",
      },
      {
        terms: [
          "trippressureexceeded",
          "trippressure",
          "tripdischargepressure",
        ],
        channel: "trip_discharge_pressure",
      },
      { terms: ["dischargepressure"], channel: "discharge_pressure" },
      { terms: ["enginespeed", "enginerpm"], channel: "rpm" },
      { terms: ["transmissiongear", "currentgear"], channel: "gear" },
      { terms: ["engineload", "enginepercentload"], channel: "engine_load" },
      {
        terms: ["dfdisplacement", "dualfueldisplacement"],
        channel: "df_displacement",
      },
      {
        terms: [
          "dischargevfdamps",
          "vfdamps",
          "vfdcurrent",
          "motorcurrent",
          "dischargemotorcurrent",
        ],
        channel: "discharge_vfd_amps",
      },
      {
        terms: [
          "isowyeavgvoltage",
          "isowyeaveragevoltage",
          "wyeavgvoltage",
          "wyeaveragevoltage",
        ],
        channel: "discharge_vfd_dc_link_volts",
      },
      {
        terms: [
          "dischargemotorwindinga1temp",
          "windinga1temp",
          "windinga1temperature",
        ],
        channel: "discharge_motor_winding_a1_temp",
      },
      {
        terms: [
          "dischargemotorwindinga2temp",
          "windinga2temp",
          "windinga2temperature",
        ],
        channel: "discharge_motor_winding_a2_temp",
      },
      {
        terms: [
          "dischargemotorwindingb1temp",
          "windingb1temp",
          "windingb1temperature",
        ],
        channel: "discharge_motor_winding_b1_temp",
      },
      {
        terms: [
          "dischargemotorwindingb2temp",
          "windingb2temp",
          "windingb2temperature",
        ],
        channel: "discharge_motor_winding_b2_temp",
      },
      {
        terms: [
          "dischargemotorwindingc1temp",
          "windingc1temp",
          "windingc1temperature",
        ],
        channel: "discharge_motor_winding_c1_temp",
      },
      {
        terms: [
          "dischargemotorwindingc2temp",
          "windingc2temp",
          "windingc2temperature",
        ],
        channel: "discharge_motor_winding_c2_temp",
      },
      {
        terms: [
          "dischargemotordebearingtemp",
          "driveendbearingtemp",
          "driveendbearingtemperature",
          "debearingtemp",
        ],
        channel: "discharge_motor_de_bearing_temp",
      },
      {
        terms: [
          "dischargemotorndebearingtemp",
          "nondriveendbearingtemp",
          "nondriveendbearingtemperature",
          "ndebearingtemp",
        ],
        channel: "discharge_motor_nde_bearing_temp",
      },
      {
        terms: [
          "atlasoiltankcriticallevel",
          "atlasoiltanklevel",
          "powerendoiltankcriticallevel",
          "powerendoiltanklevel",
          "peoillevellow",
          "peoillevel",
          "atlasdtcmonitoringbox4powerendtank",
          "atlasdtcmonitoringbox4powerend",
        ],
        channel: "pe_oil_level_low",
      },
      {
        terms: [
          "cubecoolantsupplypressure",
          "coolantsupplypressure",
          "cubecoolantpressure",
        ],
        channel: "cube_coolant_supply_pressure",
      },
      {
        terms: ["calculateddischargerate", "dischargerate"],
        channel: "calculated_discharge_rate",
      },
    ];

    const mapping = mappings.find(({ terms }) =>
      terms.some((term) => alarmName.includes(term)),
    );

    return mapping?.channel ?? null;
  };

  /*
   * Flagged re-engagement charts can need more than the alarm's single
   * graph_channel. A Trip Pressure Exceeded/Kickout event must show both the
   * configured trip-pressure threshold and the pump's actual discharge pressure
   * so the operator can compare what the pump was making against the kickout.
   */
  const getFlaggedAlarmGraphChannelValues = (alarm: any): string[] => {
    const channelValues = new Set<string>();
    const graphChannel = getAlarmGraphChannelValue(alarm);

    if (graphChannel) {
      channelValues.add(graphChannel);
    }

    const normalizedAlarmName = String(
      alarm?.alarm ?? alarm?.alarm_name ?? alarm?.name ?? "",
    )
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();
    const normalizedGraphChannel = graphChannel
      ? normalizeChannelKey(graphChannel)
      : "";

    const isTripPressureAlarm =
      normalizedGraphChannel === "trip_discharge_pressure" ||
      normalizedAlarmName.includes("trippressureexceeded") ||
      normalizedAlarmName.includes("trippressurekickout") ||
      normalizedAlarmName.includes("tripdischargepressure");

    if (isTripPressureAlarm) {
      channelValues.add("trip_discharge_pressure");
      channelValues.add("discharge_pressure");
    }

    return Array.from(channelValues);
  };

  const toPumpChannel = useCallback(
    (value: string): PumpChannel =>
      findChannelByValue(value) ||
      availableChannels.find((channel) => channel.value === value) || {
        value,
        label: value
          .replace(/_/g, " ")
          .replace(/\b\w/g, (character) => character.toUpperCase()),
      },
    [availableChannels],
  );

  const getRowValueByNormalizedKey = (rowData: any, candidateKey: string) => {
    if (!rowData || typeof rowData !== "object") return undefined;

    if (
      Object.prototype.hasOwnProperty.call(rowData, candidateKey) &&
      rowData[candidateKey] !== null &&
      rowData[candidateKey] !== undefined
    ) {
      return rowData[candidateKey];
    }

    const normalizedCandidate = normalizeChannelKey(candidateKey);

    for (const [rowKey, rowValue] of Object.entries(rowData)) {
      if (
        rowValue !== null &&
        rowValue !== undefined &&
        normalizeChannelKey(rowKey) === normalizedCandidate
      ) {
        return rowValue;
      }
    }

    return undefined;
  };

  const getChannelValue = (rowData: any, channel: string) => {
    const normalized = normalizeChannelKey(channel);
    const aliases = CHANNEL_ALIASES[normalized] || [normalized];

    for (const key of aliases) {
      const value = getRowValueByNormalizedKey(rowData, key);

      if (value !== null && value !== undefined) {
        return value;
      }
    }

    return getRowValueByNormalizedKey(rowData, channel);
  };

  const getThresholdSeverity = (
    value: any,
    channel: string,
    rowData?: any,
  ): ThresholdSeverity => {
    const normalizedChannel = normalizeChannelKey(channel);
    const aliases = CHANNEL_ALIASES[normalizedChannel] || [normalizedChannel];

    const rules = aliases.flatMap((key) => pumpInterlocks[key] || []);

    if (!rules.length) return "normal";

    const valueToCheck =
      value !== null && value !== undefined
        ? value
        : getChannelValue(rowData || {}, channel);

    const applicableRules = rules.filter((rule) =>
      isInterlockRuleActive(rowData || {}, rule.conditions),
    );

    const hasCritical = applicableRules.some(
      (rule) =>
        String(rule.severity).toLowerCase() === "critical" &&
        compareThreshold(valueToCheck, rule.operator, rule.value),
    );

    if (hasCritical) return "critical";

    const hasWarning = applicableRules.some(
      (rule) =>
        String(rule.severity).toLowerCase() === "warning" &&
        compareThreshold(valueToCheck, rule.operator, rule.value),
    );

    return hasWarning ? "warning" : "normal";
  };
  const getThresholdCellStyle = (severity: ThresholdSeverity) => {
    if (severity === "critical") {
      return {
        backgroundColor: "#dc3545",
        color: "#ffffff",
        fontWeight: 700,
        boxShadow: "inset 0 0 0 9999px rgba(220, 53, 69, 0.95)",
      };
    }

    if (severity === "warning") {
      return {
        backgroundColor: "#ffc107",
        color: "#1f2933",
        fontWeight: 700,
        boxShadow: "inset 0 0 0 9999px rgba(255, 193, 7, 0.95)",
      };
    }

    return {};
  };
  const formatLocalDateTimeForApi = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds(),
    )}`;
  };
  const fetchPumpAlarms = useCallback(
    async (
      pumpNames: string[],
      start: Date | null,
      end: Date | null,
      fleetId?: number,
    ) => {
      const requestedStartMs = start?.getTime();
      const requestedEndMs = end?.getTime();

      // A slower historical load from the previous fleet/range can reach this
      // function after a newer load has already claimed the chart. Ignore it
      // before it can become the newest alarm request.
      const activeAlarmScope = activeAlarmLoadScopeRef.current;
      if (
        fleetId != null &&
        activeAlarmScope &&
        (activeAlarmScope.fleetId !== fleetId ||
          activeAlarmScope.startMs !== requestedStartMs ||
          activeAlarmScope.endMs !== requestedEndMs)
      ) {
        return;
      }

      // Only the newest alarm request is allowed to update marker state. A broad
      // fleet request can still be finishing when Review Data starts its focused
      // request; without this guard the older response can overwrite the review
      // alarms a moment later and make markers appear to change/disappear.
      const alarmFetchVersion = alarmFetchVersionRef.current + 1;
      alarmFetchVersionRef.current = alarmFetchVersion;

      // Restore only this fleet's Review Data context. Review Data on another
      // fleet must never overwrite or suppress the marker for this fleet.
      const reviewAlarmContext =
        fleetId != null ? getAlarmReviewContextForFleet(fleetId) : undefined;

      // The clicked Review Data alarm belongs to the review WINDOW, not to the
      // telemetry rows that happen to be returned. If the target pump has no
      // telemetry, Review Data intentionally retries fleet-wide. In that fallback
      // `pumpNames` may not contain the alarm pump at all, so requiring a pump-name
      // match here would drop the exact clicked alarm and its red marker even though
      // the chart is still showing that alarm's review window.
      const isExactReviewWindow = Boolean(
        reviewAlarmContext &&
        requestedStartMs === reviewAlarmContext.startTime &&
        requestedEndMs === reviewAlarmContext.endTime &&
        (fleetId == null || fleetId === reviewAlarmContext.fleetId),
      );

      try {
        if (!pumpNames.length || !start || !end) {
          if (alarmFetchVersion !== alarmFetchVersionRef.current) return;

          const nextAlarmEvents =
            isExactReviewWindow && reviewAlarmContext
              ? [reviewAlarmContext.alarm]
              : [];

          setAlarmEvents(nextAlarmEvents);
          if (fleetId != null) {
            setAlarmEventsByFleet((previous) => ({
              ...previous,
              [fleetId]: nextAlarmEvents,
            }));
          }
          return;
        }

        const token = getAuthToken();

        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        const ALARM_CHUNK_MS = 30 * ONE_DAY_MS;
        const ALARM_MAX_CONCURRENCY = 3;

        const startMs = start.getTime();
        const endMs = end.getTime();
        const spanMs = endMs - startMs;

        const ranges: Array<{ start: Date; end: Date }> = [];

        if (spanMs > ALARM_CHUNK_MS) {
          let cursor = startMs;

          while (cursor < endMs) {
            const chunkEnd = Math.min(cursor + ALARM_CHUNK_MS, endMs);

            ranges.push({
              start: new Date(cursor),
              end: new Date(chunkEnd),
            });

            // The alarm API date range may be inclusive. Move forward 1 ms to
            // avoid duplicate boundary alarms between adjacent chunks.
            cursor = chunkEnd + 1;
          }
        } else {
          ranges.push({ start, end });
        }

        const responses: any[][] = new Array(ranges.length);
        let nextRangeIndex = 0;

        const worker = async () => {
          while (true) {
            const rangeIndex = nextRangeIndex;
            nextRangeIndex += 1;

            if (rangeIndex >= ranges.length) {
              return;
            }

            const range = ranges[rangeIndex];

            const response = await axios.post(
              `${backendUrl}/pump-alarms`,
              {
                pump_names: pumpNames,
                start_time: formatLocalDateTimeForApi(range.start),
                end_time: formatLocalDateTimeForApi(range.end),
              },
              {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 180000,
              },
            );

            responses[rangeIndex] = Array.isArray(response.data)
              ? response.data
              : [];
          }
        };

        await Promise.all(
          Array.from(
            {
              length: Math.min(ALARM_MAX_CONCURRENCY, ranges.length),
            },
            () => worker(),
          ),
        );

        const allAlarms = responses.flatMap((alarms) =>
          Array.isArray(alarms) ? alarms : [],
        );

        /*
         * Alarm timestamps can represent the same instant in different string
         * formats, for example:
         *   2026-07-31T17:18:26
         *   2026-07-31T22:18:26.000Z
         *
         * Normalize only the timestamp representation before building the exact
         * duplicate key. Pump and alarm name remain part of the key, so two
         * different alarms at the same instant are still preserved.
         */
        const getAlarmTimestampMs = (alarm: any): number | string => {
          const rawTimestamp =
            alarm?.alarm_time_ms ??
            alarm?.alarm_time ??
            alarm?.time_stamp ??
            alarm?.timestamp ??
            "";

          if (typeof rawTimestamp === "number") {
            return rawTimestamp < 1_000_000_000_000
              ? rawTimestamp * 1000
              : rawTimestamp;
          }

          const parsedTimestamp = new Date(rawTimestamp).getTime();
          return Number.isFinite(parsedTimestamp)
            ? parsedTimestamp
            : String(rawTimestamp);
        };

        const getExactAlarmKey = (alarm: any): string =>
          [
            String(
              alarm?.pump ?? alarm?.source ?? alarm?.pump_name ?? "",
            ).trim(),
            String(alarm?.alarm ?? alarm?.alarm_name ?? "").trim(),
            getAlarmTimestampMs(alarm),
          ].join("::");

        const exactUniqueAlarms = Array.from(
          new Map(
            allAlarms.map((alarm: any) => [getExactAlarmKey(alarm), alarm]),
          ).values(),
        );

        const appendAlarmIfMissing = (candidate: any) => {
          if (!candidate) return;

          const candidateTime = getAlarmTimestampMs(candidate);
          const numericCandidateTime = Number(candidateTime);

          // A fallback belongs on this chart only when its timestamp is inside
          // the exact range currently being fetched.
          if (
            Number.isFinite(numericCandidateTime) &&
            (numericCandidateTime < startMs || numericCandidateTime > endMs)
          ) {
            return;
          }

          const candidateKey = getExactAlarmKey(candidate);
          const alreadyPresent = exactUniqueAlarms.some(
            (existingAlarm: any) =>
              getExactAlarmKey(existingAlarm) === candidateKey,
          );

          if (!alreadyPresent) {
            exactUniqueAlarms.push(candidate);
          }
        };

        // A Flagged Re-engagement deep link carries the backend-selected effective
        // alarm timestamp. For Trip Pressure this can be the telemetry threshold
        // crossing rather than the later source-row timestamp. Replace that one
        // source marker with the effective marker so PadTrack lines up with JDD,
        // while preserving every other real alarm in the chart window.
        if (isFlaggedReengagementView && isDashboardPumpForFleet(fleetId)) {
          const flaggedAlarm = flaggedAlarmFallbackRef.current;

          if (flaggedAlarm) {
            const normalizeAlarmIdentity = (value: unknown) =>
              String(value ?? "")
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "");

            const flaggedPumpKey = normalizeAlarmIdentity(
              flaggedAlarm?.pump ??
                flaggedAlarm?.source ??
                flaggedAlarm?.pump_name,
            );
            const flaggedAlarmKey = normalizeAlarmIdentity(
              flaggedAlarm?.alarm ?? flaggedAlarm?.alarm_name,
            );
            const sourceAlarmTimeMs = Number(
              flaggedAlarm?.source_alarm_time_ms,
            );
            const effectiveAlarmTimeMs = Number(
              getAlarmTimestampMs(flaggedAlarm),
            );

            if (
              Number.isFinite(sourceAlarmTimeMs) &&
              Number.isFinite(effectiveAlarmTimeMs) &&
              Math.abs(sourceAlarmTimeMs - effectiveAlarmTimeMs) > 1000
            ) {
              for (
                let index = exactUniqueAlarms.length - 1;
                index >= 0;
                index -= 1
              ) {
                const existingAlarm = exactUniqueAlarms[index];
                const existingPumpKey = normalizeAlarmIdentity(
                  existingAlarm?.pump ??
                    existingAlarm?.source ??
                    existingAlarm?.pump_name,
                );
                const existingAlarmKey = normalizeAlarmIdentity(
                  existingAlarm?.alarm ?? existingAlarm?.alarm_name,
                );
                const existingTimeMs = Number(
                  getAlarmTimestampMs(existingAlarm),
                );

                const isExactSourceAlarm =
                  existingPumpKey === flaggedPumpKey &&
                  existingAlarmKey === flaggedAlarmKey &&
                  Number.isFinite(existingTimeMs) &&
                  Math.abs(existingTimeMs - sourceAlarmTimeMs) <= 1000;

                if (isExactSourceAlarm) {
                  exactUniqueAlarms.splice(index, 1);
                }
              }
            }

            appendAlarmIfMissing(flaggedAlarm);
          }
        }

        // Normal Pump Data Review Data can fall back from one pump to fleet-wide
        // telemetry. Keep the alarm that opened Review Data so its red marker
        // remains visible after that fallback reload.
        if (isExactReviewWindow && reviewAlarmContext) {
          appendAlarmIfMissing(reviewAlarmContext.alarm);
        }

        const currentAlarmScope = activeAlarmLoadScopeRef.current;
        if (
          fleetId != null &&
          currentAlarmScope &&
          (currentAlarmScope.fleetId !== fleetId ||
            currentAlarmScope.startMs !== requestedStartMs ||
            currentAlarmScope.endMs !== requestedEndMs)
        ) {
          return;
        }

        if (alarmFetchVersion !== alarmFetchVersionRef.current) {
          return;
        }

        /*
         * Keep alarm-marker behavior identical to normal Pump Data.
         * Flagged Re-engagement affects the selected pump, time range, and
         * auto-loaded channels only. It must NOT suppress other real critical
         * alarms for the same pump/window (for example Engine Oil Pressure).
         */
        setAlarmEvents(exactUniqueAlarms);
        if (fleetId != null) {
          setAlarmEventsByFleet((previous) => ({
            ...previous,
            [fleetId]: exactUniqueAlarms,
          }));
        }
      } catch (error) {
        console.error("Error fetching pump alarms:", error);

        const currentAlarmScope = activeAlarmLoadScopeRef.current;
        if (
          fleetId != null &&
          currentAlarmScope &&
          (currentAlarmScope.fleetId !== fleetId ||
            currentAlarmScope.startMs !== requestedStartMs ||
            currentAlarmScope.endMs !== requestedEndMs)
        ) {
          return;
        }

        if (alarmFetchVersion !== alarmFetchVersionRef.current) {
          return;
        }

        const nextAlarmEvents =
          isExactReviewWindow && reviewAlarmContext
            ? [reviewAlarmContext.alarm]
            : [];

        setAlarmEvents(nextAlarmEvents);
        if (fleetId != null) {
          setAlarmEventsByFleet((previous) => ({
            ...previous,
            [fleetId]: nextAlarmEvents,
          }));
        }
      }
    },
    [
      backendUrl,
      getAlarmReviewContextForFleet,
      isDashboardPumpForFleet,
      isFlaggedReengagementView,
    ],
  );

  const resolveFlaggedChartChannels = useCallback(
    async (
      pumpName: string,
      start: Date,
      end: Date,
    ): Promise<PumpChannel[]> => {
      const channelValues = new Set<string>(["gear", "rpm"]);

      const isCriticalAlarm = (alarm: any) => {
        const severity = String(alarm?.severity || "")
          .trim()
          .toLowerCase();
        const alarmName = String(
          alarm?.alarm ?? alarm?.alarm_name ?? alarm?.name ?? "",
        )
          .replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase();

        return (
          severity.includes("critical") ||
          alarmName.includes("critical") ||
          alarmName.includes("trippressureexceeded") ||
          alarmName.includes("trippressurekickout") ||
          alarmName.includes("tripdischargepressure")
        );
      };

      const addAlarmGraphChannel = (alarm: any) => {
        if (!isCriticalAlarm(alarm)) return;

        const graphChannels = getFlaggedAlarmGraphChannelValues(alarm);

        if (graphChannels.length > 0) {
          graphChannels.forEach((channelValue) =>
            channelValues.add(channelValue),
          );
        } else {
          console.warn(
            "No graph channel mapping found for Critical alarm:",
            alarm,
          );
        }
      };

      try {
        const token = getAuthToken();
        const response = await axios.post(
          `${backendUrl}/pump-alarms`,
          {
            pump_names: [pumpName],
            start_time: formatLocalDateTimeForApi(start),
            end_time: formatLocalDateTimeForApi(end),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 180000,
          },
        );

        const alarms = Array.isArray(response.data) ? response.data : [];
        alarms.forEach(addAlarmGraphChannel);
      } catch (error) {
        console.error(
          "Failed to resolve Critical alarm channels for flagged chart:",
          error,
        );
      }

      // Preserve the exact triggering alarm as a fallback if the alarm API
      // misses a boundary row or is temporarily unavailable.
      const selectedFlaggedAlarm = flaggedAlarmFallbackRef.current;
      if (selectedFlaggedAlarm) {
        addAlarmGraphChannel(selectedFlaggedAlarm);
      }

      return Array.from(channelValues).map(toPumpChannel);
    },
    [backendUrl, toPumpChannel],
  );

  const fetchPumpReengagementEvents = useCallback(
    async (fleet: number, start: Date | null, end: Date | null) => {
      if (!start || !end) return;

      setReengagementEventsLoadingByFleet((prev) => ({
        ...prev,
        [fleet]: true,
      }));

      try {
        const token = getAuthToken();
        const response = await axios.get(
          `${backendUrl}/pump-reengagement-events`,
          {
            params: {
              fleet,
              start_time: start.getTime(),
              end_time: end.getTime(),
            },
            headers: { Authorization: `Bearer ${token}` },
            timeout: 180000,
          },
        );

        setReengagementEventsByFleet((prev) => ({
          ...prev,
          [fleet]: response.data?.events || [],
        }));
      } catch (error) {
        console.error("Error fetching pump re-engagement events:", error);
        setReengagementEventsByFleet((prev) => ({
          ...prev,
          [fleet]: [],
        }));
        toast.error("Failed to load flagged re-engagement events");
      } finally {
        setReengagementEventsLoadingByFleet((prev) => ({
          ...prev,
          [fleet]: false,
        }));
      }
    },
    [backendUrl],
  );

  const getAlarmDate = (alarm: any): Date | null => {
    const rawAlarmTime =
      alarm?.alarm_time_ms ??
      alarm?.alarm_time ??
      alarm?.time_stamp ??
      alarm?.timestamp;

    if (
      rawAlarmTime === null ||
      rawAlarmTime === undefined ||
      rawAlarmTime === ""
    ) {
      return null;
    }

    let alarmTimeMs =
      typeof rawAlarmTime === "number"
        ? rawAlarmTime
        : new Date(rawAlarmTime).getTime();

    if (Number.isFinite(alarmTimeMs) && alarmTimeMs < 1_000_000_000_000) {
      alarmTimeMs *= 1000;
    }

    return Number.isFinite(alarmTimeMs) ? new Date(alarmTimeMs) : null;
  };

  const formatReengagementDelay = (seconds: number) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleViewReengagementChart = async (
    event: PumpReengagementEvent,
    fleetGroup: FleetGroup,
  ) => {
    const reviewFrom = new Date(event.alarm_time_ms - 5 * 60 * 1000);
    const reviewTo = new Date(event.reengagement_time_ms + 5 * 60 * 1000);

    const graphChannels = getFlaggedAlarmGraphChannelValues(event);
    const reviewChannels: PumpChannel[] = [
      toPumpChannel("gear"),
      toPumpChannel("rpm"),
      ...graphChannels.map(toPumpChannel),
    ].filter(
      (channel, index, channels) =>
        channels.findIndex((item) => item.value === channel.value) === index,
    );

    setSelectedFleet(fleetGroup);
    setFromDateByFleet((prev) => ({
      ...prev,
      [fleetGroup.fleet]: reviewFrom,
    }));
    setToDateByFleet((prev) => ({
      ...prev,
      [fleetGroup.fleet]: reviewTo,
    }));
    setSelectedPumpsByFleet((prev) => ({
      ...prev,
      [fleetGroup.fleet]: [event.pump],
    }));
    setHiddenPumpsByFleet((prev) => ({
      ...prev,
      [fleetGroup.fleet]: new Set<string>(),
    }));
    setSelectedChannelsByFleet((prev) => ({
      ...prev,
      [fleetGroup.fleet]: reviewChannels,
    }));
    selectedChannelsByFleetRef.current = {
      ...selectedChannelsByFleetRef.current,
      [fleetGroup.fleet]: reviewChannels,
    };

    setSelectedPumps([event.pump]);
    selectedPumpsRef.current = [event.pump];
    setHiddenPumps(new Set<string>());
    setSelectedChannels(reviewChannels);
    selectedChannelsRef.current = reviewChannels;

    await loadData(
      reviewFrom,
      reviewTo,
      fleetGroup,
      event.pump,
      true,
      reviewChannels,
      undefined,
      true, // keep flagged-event list while reviewing one event
      true, // no table preload needed for the focused chart
    );

    requestAnimationFrame(() => {
      document
        .getElementById(`fleet-${fleetGroup.fleet}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleReviewAlarmData = async (alarm: any) => {
    const pumpName = alarm?.pump ?? alarm?.source ?? alarm?.pump_name;
    const alarmDate = getAlarmDate(alarm);

    if (!pumpName || !alarmDate) {
      toast.warning("Alarm pump or timestamp is unavailable");
      return;
    }

    const fleetGroup = selectedFleet;

    if (!fleetGroup) {
      toast.warning("Fleet is unavailable for this alarm");
      return;
    }

    const fleetId = fleetGroup.fleet;
    const isFlaggedReviewForFleet =
      isFlaggedReengagementView && isDashboardPumpForFleet(fleetId);

    /*
     * A normal Pump Data alarm review uses a compact 5-min-before /
     * 10-min-after window. A Flagged Re-engagement trigger is different: the
     * neutral/re-engagement can happen more than 10 minutes after the alarm.
     * In that case keep the flagged event's full lifecycle visible so Review
     * Data does not cut the chart off before telemetry/re-engagement begins.
     */
    const normalizedReviewPump = String(pumpName).trim().toLowerCase();
    const normalizedReviewAlarm = String(
      alarm?.alarm ?? alarm?.alarm_name ?? alarm?.name ?? "",
    )
      .trim()
      .toLowerCase();

    const matchingFlaggedEvent = isFlaggedReviewForFleet
      ? (reengagementEventsByFleet[fleetId] || [])
          .filter(
            (event) =>
              String(event.pump).trim().toLowerCase() === normalizedReviewPump,
          )
          .sort(
            (left, right) =>
              Math.abs(left.alarm_time_ms - alarmDate.getTime()) -
              Math.abs(right.alarm_time_ms - alarmDate.getTime()),
          )
          .find((event) => {
            const eventAlarm = String(event.alarm || "")
              .trim()
              .toLowerCase();
            const sameAlarm =
              !normalizedReviewAlarm ||
              !eventAlarm ||
              eventAlarm === normalizedReviewAlarm;
            const closeToClickedAlarm =
              Math.abs(event.alarm_time_ms - alarmDate.getTime()) <=
              2 * 60 * 1000;

            return sameAlarm && closeToClickedAlarm;
          })
      : undefined;

    const reviewFrom = new Date(alarmDate.getTime() - 5 * 60 * 1000);
    const reviewTo = new Date(
      matchingFlaggedEvent
        ? matchingFlaggedEvent.reengagement_time_ms + 5 * 60 * 1000
        : alarmDate.getTime() + 10 * 60 * 1000,
    );

    // Preserve the exact clicked alarm across the two Review Data telemetry
    // attempts (pump-specific first, then fleet-wide fallback). Seed the marker
    // state immediately so the alarm never disappears while telemetry reloads.
    const alarmReviewContext = {
      alarm,
      fleetId,
      startTime: reviewFrom.getTime(),
      endTime: reviewTo.getTime(),
      savedAt: Date.now(),
    };

    persistAlarmReviewContextForFleet(alarmReviewContext);

    const mergeClickedReviewAlarm = (previousAlarms: any[]) => {
      const alarmsInsideReviewWindow = previousAlarms.filter(
        (existingAlarm) => {
          const existingAlarmDate = getAlarmDate(existingAlarm);
          if (!existingAlarmDate) return false;

          const timestamp = existingAlarmDate.getTime();
          return (
            timestamp >= reviewFrom.getTime() && timestamp <= reviewTo.getTime()
          );
        },
      );

      const clickedAlarmPump = String(
        alarm?.pump ?? alarm?.source ?? alarm?.pump_name ?? "",
      ).trim();
      const clickedAlarmName = String(
        alarm?.alarm ?? alarm?.alarm_name ?? alarm?.name ?? "",
      ).trim();
      const clickedAlarmKey = `${clickedAlarmPump}::${clickedAlarmName}::${alarmDate.getTime()}`;

      const alreadyPresent = alarmsInsideReviewWindow.some((existingAlarm) => {
        const existingAlarmDate = getAlarmDate(existingAlarm);
        if (!existingAlarmDate) return false;

        const existingPump = String(
          existingAlarm?.pump ??
            existingAlarm?.source ??
            existingAlarm?.pump_name ??
            "",
        ).trim();
        const existingName = String(
          existingAlarm?.alarm ??
            existingAlarm?.alarm_name ??
            existingAlarm?.name ??
            "",
        ).trim();

        return (
          `${existingPump}::${existingName}::${existingAlarmDate.getTime()}` ===
          clickedAlarmKey
        );
      });

      return alreadyPresent
        ? alarmsInsideReviewWindow
        : [...alarmsInsideReviewWindow, alarm];
    };

    setAlarmEvents((previousAlarms) => mergeClickedReviewAlarm(previousAlarms));
    setAlarmEventsByFleet((previous) => ({
      ...previous,
      [fleetId]: mergeClickedReviewAlarm(previous[fleetId] ?? []),
    }));

    setShowAlarmModal(false);
    setAlarmReviewNoTelemetryContext(null);

    // Review Data performs its own focused chart request. Mark this exact
    // fleet/date range as already handled BEFORE updating the date state so the
    // normal historical auto-load effect does not fire a second request for the
    // same range. That duplicate request used to clear the focused pump and emit
    // the generic "No data for the selected window" toast after the alarm-specific
    // telemetry message.
    const reviewAutoLoadSignature = `${fleetId}|${reviewFrom.getTime()}|${reviewTo.getTime()}`;
    fleetAutoLoadSignatureRef.current = reviewAutoLoadSignature;

    if (fleetAutoLoadTimerRef.current) {
      clearTimeout(fleetAutoLoadTimerRef.current);
      fleetAutoLoadTimerRef.current = null;
    }

    /*
     * When Review Data is opened from a Flagged Re-engagement chart, keep the
     * re-engagement context and chart EVERY Critical alarm channel in the new
     * review window. This means an Engine Oil Pressure Critical marker inside a
     * Suction Pressure flagged event produces both channels on the graph, plus
     * the permanent Gear + RPM context channels.
     */
    if (isFlaggedReviewForFleet) {
      const reviewChannels = await resolveFlaggedChartChannels(
        String(pumpName),
        reviewFrom,
        reviewTo,
      );

      setFromDateByFleet((prev) => ({
        ...prev,
        [fleetId]: reviewFrom,
      }));
      setToDateByFleet((prev) => ({
        ...prev,
        [fleetId]: reviewTo,
      }));
      setSelectedPumpsByFleet((prev) => ({
        ...prev,
        [fleetId]: [String(pumpName)],
      }));
      setHiddenPumpsByFleet((prev) => ({
        ...prev,
        [fleetId]: new Set<string>(),
      }));
      setSelectedChannelsByFleet((prev) => ({
        ...prev,
        [fleetId]: reviewChannels,
      }));

      selectedChannelsByFleetRef.current = {
        ...selectedChannelsByFleetRef.current,
        [fleetId]: reviewChannels,
      };

      setSelectedPumps([String(pumpName)]);
      selectedPumpsRef.current = [String(pumpName)];
      setHiddenPumps(new Set<string>());
      setSelectedChannels(reviewChannels);
      selectedChannelsRef.current = reviewChannels;

      // First try the exact pump from the flagged alarm. Some historical alarm
      // rows are not associated with the currently selected well/pump telemetry
      // slice, so this focused request can legitimately return zero rows even
      // though fleet telemetry exists in the same review window.
      const focusedFlaggedAlarmHasData = await loadData(
        reviewFrom,
        reviewTo,
        fleetGroup,
        String(pumpName),
        false,
        reviewChannels,
        undefined,
        true, // keep flagged re-engagement list/context
        true, // focused alarm chart does not need table preload
        undefined,
        undefined,
        undefined,
        false,
        undefined,
        false,
        false,
        {
          suppressNoDataToast: true,
        },
      );

      // Match the behavior that previously appeared only after browser
      // Back/Forward: if the exact pump/well filter has no telemetry, retry the
      // same alarm window fleet-wide. The clicked alarm marker is preserved by
      // alarmReviewSelectedAlarmByFleetRef/sessionStorage, so the user still sees which
      // alarm opened Review Data while available fleet telemetry is displayed.
      let flaggedAlarmHasData = focusedFlaggedAlarmHasData;

      if (!focusedFlaggedAlarmHasData) {
        console.info(
          "Flagged Review Data target query returned no telemetry; retrying fleet-wide",
          {
            fleet: fleetId,
            pump: String(pumpName),
            startTime: reviewFrom.getTime(),
            endTime: reviewTo.getTime(),
            channels: reviewChannels.map((channel) => channel.value),
          },
        );

        flaggedAlarmHasData = await loadData(
          reviewFrom,
          reviewTo,
          fleetGroup,
          undefined, // fleet-wide fallback
          false,
          reviewChannels,
          undefined,
          true, // keep flagged re-engagement list/context
          true, // focused alarm chart does not need table preload
          undefined,
          undefined,
          undefined,
          false,
          undefined,
          false,
          false,
          {
            forceFleetWidePumpQuery: true,
            suppressNoDataToast: true,
          },
        );
      }

      if (!flaggedAlarmHasData) {
        setAlarmReviewNoTelemetryContext({
          fleetId,
          pumpName: String(pumpName),
          startTime: reviewFrom.getTime(),
          endTime: reviewTo.getTime(),
          channels: reviewChannels.map((channel) => channel.value).sort(),
        });

        toast.info("No telemetry is available around this alarm timestamp.");
      }

      const nextParams = new URLSearchParams(location.search);
      nextParams.set("start_time", String(reviewFrom.getTime()));
      nextParams.set("end_time", String(reviewTo.getTime()));
      nextParams.set(
        "channels",
        reviewChannels.map((channel) => channel.value).join(","),
      );
      window.history.replaceState(null, "", `/fleet?${nextParams.toString()}`);
      return;
    }

    // Normal Pump Data alarm review remains alarm-specific.
    const graphChannelValue = getAlarmGraphChannelValue(alarm);

    if (!graphChannelValue) {
      toast.warning("No graph channel is mapped for this alarm");
      return;
    }

    const reviewChannel = toPumpChannel(graphChannelValue);
    const reviewChannels = [reviewChannel];

    setFromDateByFleet((prev) => ({
      ...prev,
      [fleetId]: reviewFrom,
    }));
    setToDateByFleet((prev) => ({
      ...prev,
      [fleetId]: reviewTo,
    }));
    setSelectedPumpsByFleet((prev) => ({
      ...prev,
      [fleetId]: [String(pumpName)],
    }));
    setHiddenPumpsByFleet((prev) => ({
      ...prev,
      [fleetId]: new Set<string>(),
    }));
    setSelectedChannelsByFleet((prev) => ({
      ...prev,
      [fleetId]: reviewChannels,
    }));

    selectedChannelsByFleetRef.current = {
      ...selectedChannelsByFleetRef.current,
      [fleetId]: reviewChannels,
    };

    setSelectedPumps([String(pumpName)]);
    selectedPumpsRef.current = [String(pumpName)];
    setHiddenPumps(new Set<string>());
    setSelectedChannels(reviewChannels);
    selectedChannelsRef.current = reviewChannels;

    // First try the normal single-pump historical query.
    const focusedAlarmHasData = await loadData(
      reviewFrom,
      reviewTo,
      fleetGroup,
      String(pumpName),
      false,
      reviewChannels,
      undefined,
      false,
      true,
      undefined,
      undefined,
      undefined,
      false,
      undefined,
      false,
      false,
      {
        suppressNoDataToast: true,
      },
    );

    if (focusedAlarmHasData) {
      return;
    }

    // The alarm-specific pump filter can return no rows even though the same
    // review window has fleet telemetry. Retry fleet-wide so Review Data shows
    // the available channel context immediately instead of requiring browser
    // Back/Forward navigation to trigger the normal fleet load.
    console.info(
      "Alarm Review Data target query returned no telemetry; retrying fleet-wide",
      {
        fleet: fleetId,
        pump: String(pumpName),
        startTime: reviewFrom.getTime(),
        endTime: reviewTo.getTime(),
        channels: reviewChannels.map((channel) => channel.value),
      },
    );

    const fallbackAlarmHasData = await loadData(
      reviewFrom,
      reviewTo,
      fleetGroup,
      undefined, // fleet-wide fallback: show available telemetry in this window
      false,
      reviewChannels,
      undefined,
      false,
      true,
      undefined,
      undefined,
      undefined,
      false,
      undefined,
      false,
      false,
      {
        forceFleetWidePumpQuery: true,
        suppressNoDataToast: true,
      },
    );

    if (!fallbackAlarmHasData) {
      setAlarmReviewNoTelemetryContext({
        fleetId,
        pumpName: String(pumpName),
        startTime: reviewFrom.getTime(),
        endTime: reviewTo.getTime(),
        channels: reviewChannels.map((channel) => channel.value).sort(),
      });

      toast.info("No telemetry is available around this alarm timestamp.");
    }
  };

  const handleExportAlarmData = async (alarm: any) => {
    const pumpName = alarm?.pump ?? alarm?.source ?? alarm?.pump_name;

    const alarmDate = getAlarmDate(alarm);

    if (!pumpName || !alarmDate) {
      toast.warning("Alarm pump or timestamp is unavailable");
      return;
    }

    const token = getAuthToken();

    if (!token) {
      toast.error("Authentication required");
      return;
    }

    const exportFrom = alarmDate.getTime() - 5 * 60 * 1000;
    const exportTo = alarmDate.getTime() + 10 * 60 * 1000;

    const fleetId = selectedFleet?.fleet;

    const channelsForExport =
      fleetId != null && selectedChannelsByFleet[fleetId]?.length > 0
        ? selectedChannelsByFleet[fleetId]
        : selectedChannels.length > 0
          ? selectedChannels
          : defaultChannels;

    const params = new URLSearchParams();

    params.set("resolution", "1sec");
    params.set("start_time", String(exportFrom));
    params.set("end_time", String(exportTo));
    params.set("pump_name", pumpName);
    params.set(
      "channels",
      channelsForExport.map((channel) => channel.value).join(","),
    );

    try {
      const response = await axios.get(
        `${backendUrl}/pumps/export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
          timeout: 180000,
        },
      );

      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = `${pumpName}_alarm_1sec_${exportFrom}_${exportTo}.csv`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);

      toast.success("1-second alarm data exported");
    } catch (error) {
      console.error("Alarm data export failed:", error);
      toast.error("Failed to export alarm data");
    }
  };

  const fetchChartAnnotations = useCallback(
    async (fleet: number, start: Date, end: Date) => {
      try {
        const token = getAuthToken();
        if (!token) return;

        const response = await axios.get(`${backendUrl}/chart-annotations`, {
          params: {
            fleet,
            start_time: start.getTime(),
            end_time: end.getTime(),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setChartAnnotations(response.data || []);
      } catch (error) {
        console.error("Error fetching chart annotations:", error);
        setChartAnnotations([]);
      }
    },
    [backendUrl],
  );

  const searchChartAnnotations = useCallback(async () => {
    try {
      setAnnotationSearchLoading(true);

      const token = getAuthToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const params: any = {};

      if (annotationSearchText.trim()) {
        params.q = annotationSearchText.trim();
      }

      if (annotationSearchFleet.trim()) {
        params.fleet = Number(annotationSearchFleet.trim());
      }

      if (annotationSearchPump.trim()) {
        params.pump = annotationSearchPump.trim();
      }

      if (annotationSearchUser.trim()) {
        params.user_email = annotationSearchUser.trim();
      }

      if (annotationSearchFromDate) {
        params.start_time = annotationSearchFromDate.getTime();
      }

      if (annotationSearchToDate) {
        params.end_time = annotationSearchToDate.getTime();
      }

      const response = await axios.get(
        `${backendUrl}/chart-annotations/search`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setAnnotationSearchResults(response.data || []);
    } catch (error) {
      console.error("Error searching chart annotations:", error);
      toast.error("Failed to search comments");
    } finally {
      setAnnotationSearchLoading(false);
    }
  }, [
    backendUrl,
    annotationSearchText,
    annotationSearchFleet,
    annotationSearchPump,
    annotationSearchUser,
    annotationSearchFromDate,
    annotationSearchToDate,
  ]);

  const fetchAnnotationAudit = async (annotation: ChartAnnotation) => {
    if (!isManager) {
      toast.warning("Only managers/admins can view audit history");
      return;
    }

    try {
      setSelectedAuditAnnotation(annotation);
      setAnnotationAuditRows([]);
      setShowAnnotationAuditModal(true);
      setAnnotationAuditLoading(true);

      const token = getAuthToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const response = await axios.get(
        `${backendUrl}/chart-annotations/${annotation.id}/audit`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setAnnotationAuditRows(response.data || []);
    } catch (error) {
      console.error("Error fetching annotation audit history:", error);
      toast.error("Failed to fetch audit history");
    } finally {
      setAnnotationAuditLoading(false);
    }
  };

  const getAuditValue = (
    row: ChartAnnotationAudit,
    camelKey: string,
    snakeKey: string,
  ) => {
    return (row as any)[camelKey] ?? (row as any)[snakeKey];
  };

  const getAutoResolutionConfig = useCallback(
    (start: Date | null, end: Date | null) => {
      if (isStreaming) {
        return { code: "1s", label: "1 Sec", bucketMs: 1000 };
      }

      if (!start || !end) {
        return { code: "1m", label: "1 Min", bucketMs: 60 * 1000 };
      }

      const diffMs = end.getTime() - start.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      const diffDays = diffHours / 24;

      //   <= 3 days   -> 1 second
      //   <= 180 days -> 1 minute
      //   > 180 days  -> 1 hour (up to the overall 6-month limit)
      if (diffDays <= 3) {
        return { code: "1s", label: "1 Sec", bucketMs: 1000 };
      }

      if (diffDays <= 180) {
        return { code: "1m", label: "1 Min", bucketMs: 60 * 1000 };
      }

      return { code: "1h", label: "1 Hour", bucketMs: 60 * 60 * 1000 };
    },
    [isStreaming],
  );

  const getAutoResolutionLabel = useCallback(
    (start: Date | null, end: Date | null) => {
      return getAutoResolutionConfig(start, end).label;
    },
    [getAutoResolutionConfig],
  );

  const getSelectedWellIdsForPad = useCallback(
    (padId?: number | null) => {
      if (!padId) return [] as number[];

      const wells = padWells[padId] || [];
      const selectedIds =
        selectedWellIdsByPadRef.current[padId] ??
        selectedWellIdsByPad[padId] ??
        [];
      const showAll =
        showAllWellsByPadRef.current[padId] ??
        showAllWellsByPad[padId] ??
        false;

      if (showAll && wells.length > 0) {
        return wells.map((well) => well.id);
      }

      return selectedIds;
    },
    [padWells, selectedWellIdsByPad, showAllWellsByPad],
  );

  const getWellNameById = useCallback(
    (padId: number | null | undefined, wellId: number) => {
      if (!padId) return `Well ${wellId}`;
      return (
        padWells[padId]?.find((well) => well.id === wellId)?.well_name ||
        `Well ${wellId}`
      );
    },
    [padWells],
  );

  const getWellLoadingLabel = useCallback(
    (padId: number, selectedIds: number[], showAll: boolean) => {
      const wells = padWells[padId] || [];

      if (showAll) {
        return "Loading all wells…";
      }

      if (selectedIds.length === 1) {
        const wellName =
          wells.find((well) => well.id === selectedIds[0])?.well_name ||
          "selected well";
        return `Loading ${wellName} data…`;
      }

      if (selectedIds.length > 1) {
        return `Loading ${selectedIds.length} selected wells…`;
      }

      return "Loading well data…";
    },
    [padWells],
  );

  const beginWellSelectionLoading = useCallback(
    (padId: number, selectedIds: number[], showAll: boolean) => {
      const fleetGroup = fleetGroups.find(
        (group) => group.latestPad.id === padId,
      );

      // Historical mode does not reload until Load Data is pressed. Only show
      // the blocking well-switch state when an actual real-time reload starts.
      if (!fleetGroup || !streamingFleetIdsRef.current.has(fleetGroup.fleet)) {
        return;
      }

      setLoadingWellSelectionByPad((previous) => ({
        ...previous,
        [padId]: true,
      }));
      setLoadingWellLabelByPad((previous) => ({
        ...previous,
        [padId]: getWellLoadingLabel(padId, selectedIds, showAll),
      }));
    },
    [fleetGroups, getWellLoadingLabel],
  );

  const finishWellSelectionLoading = useCallback((padId: number) => {
    setLoadingWellSelectionByPad((previous) => ({
      ...previous,
      [padId]: false,
    }));
    setLoadingWellLabelByPad((previous) => ({
      ...previous,
      [padId]: "",
    }));
  }, []);

  const toggleWellSelection = useCallback(
    (padId: number, wellId: number) => {
      if (loadingWellSelectionByPad[padId]) return;

      const showAll =
        showAllWellsByPadRef.current[padId] ??
        showAllWellsByPad[padId] ??
        false;
      const current =
        selectedWellIdsByPadRef.current[padId] ??
        selectedWellIdsByPad[padId] ??
        [];

      // Clicking the only currently selected well is a no-op. Returning before
      // updating either refs or state prevents the real-time well-change effect
      // from clearing and reloading the same dataset, which caused a blank chart.
      if (!showAll && current.length === 1 && current[0] === wellId) {
        return;
      }

      // First click while "Show all wells together" is active narrows the view
      // to that well. After that, each click adds/removes a well so users can
      // compare any multi-well subset while real-time streaming remains ON.
      let nextIds: number[];
      if (showAll) {
        nextIds = [wellId];
      } else if (current.includes(wellId)) {
        nextIds = current.filter((id) => id !== wellId);
      } else {
        nextIds = [...current, wellId];
      }

      nextIds = Array.from(new Set(nextIds));

      beginWellSelectionLoading(padId, nextIds, false);

      // Update refs before React state so the debounced streaming restart uses
      // the complete latest multi-well selection, even during rapid clicks.
      showAllWellsByPadRef.current = {
        ...showAllWellsByPadRef.current,
        [padId]: false,
      };
      selectedWellIdsByPadRef.current = {
        ...selectedWellIdsByPadRef.current,
        [padId]: nextIds,
      };

      setShowAllWellsByPad((prev) => ({ ...prev, [padId]: false }));
      setSelectedWellIdsByPad((prev) => ({ ...prev, [padId]: nextIds }));
    },
    [
      beginWellSelectionLoading,
      loadingWellSelectionByPad,
      selectedWellIdsByPad,
      showAllWellsByPad,
    ],
  );

  const handleShowAllWellsChange = useCallback(
    (padId: number, checked: boolean) => {
      if (loadingWellSelectionByPad[padId]) return;

      const wells = padWells[padId] || [];
      const nextIds = checked
        ? wells.map((well) => well.id)
        : wells.length > 0
          ? [wells[0].id]
          : [];

      beginWellSelectionLoading(padId, nextIds, checked);

      showAllWellsByPadRef.current = {
        ...showAllWellsByPadRef.current,
        [padId]: checked,
      };
      selectedWellIdsByPadRef.current = {
        ...selectedWellIdsByPadRef.current,
        [padId]: nextIds,
      };

      setShowAllWellsByPad((prev) => ({ ...prev, [padId]: checked }));
      setSelectedWellIdsByPad((prev) => ({ ...prev, [padId]: nextIds }));
    },
    [beginWellSelectionLoading, loadingWellSelectionByPad, padWells],
  );

  useEffect(() => {
    loadingFleetIds.forEach((fleetId) => {
      fleetFromDatePickerRefs.current[fleetId]?.setOpen(false);
      fleetToDatePickerRefs.current[fleetId]?.setOpen(false);
    });
  }, [loadingFleetIds]);

  useEffect(() => {
    selectedWellIdsByPadRef.current = selectedWellIdsByPad;
  }, [selectedWellIdsByPad]);

  useEffect(() => {
    showAllWellsByPadRef.current = showAllWellsByPad;
  }, [showAllWellsByPad]);

  const handleBackToTop = () => {
    mainContentRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const scrollContainer = mainContentRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setShowBackToTop(scrollContainer.scrollTop > 300);
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  // Use the browser's native scrolling. A global wheel listener previously
  // forwarded every wheel event through React/JavaScript. During large chart
  // updates that handler could not run, which made the page appear locked.

  useEffect(() => {
    const scrollContainer = mainContentRef.current;
    if (!scrollContainer) return;

    // This page owns its scrolling inside mainContentStyle. Explicitly restore
    // that element whenever the component mounts, even if a previously closed
    // SweetAlert/modal left body or html overflow locked.
    scrollContainer.style.overflowY = "auto";
    scrollContainer.style.overflowX = "hidden";
    scrollContainer.style.minHeight = "0";
    scrollContainer.style.height = "100%";

    return () => {
      // Do not mutate body/html here; other pages may use their own layout.
    };
  }, []);

  useEffect(() => {
    const isAnyModalOpen =
      showAlarmModal ||
      showPumpDetailsModal ||
      showTemplateModal ||
      showExportModal;

    if (!isAnyModalOpen) return;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [
    showAlarmModal,
    showPumpDetailsModal,
    showTemplateModal,
    showExportModal,
  ]);
  useEffect(() => {
    if (!showAlarmModal) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showAlarmModal]);

  useEffect(() => {
    fetchPads();
    fetchTemplates();
    fetchPumpInterlocks();
  }, [fetchPads, fetchTemplates, fetchPumpInterlocks]);

  useEffect(() => {
    if (!fleetGroups.length) return;
    if (hasRestoredFleetViewStateRef.current) return;

    const urlParams = new URLSearchParams(location.search);

    const requestedView = (urlParams.get("tab") || urlParams.get("view") || "")
      .trim()
      .toLowerCase();

    if (
      urlParams.get("fleet") ||
      urlParams.get("pump") ||
      urlParams.get("blender") ||
      urlParams.get("blender_id") ||
      requestedView === "blender" ||
      urlParams.get("flagged_reengagement_view") === "1"
    ) {
      hasRestoredFleetViewStateRef.current = true;
      return;
    }

    hasRestoredFleetViewStateRef.current = true;

    try {
      const rawState = sessionStorage.getItem(FLEET_VIEW_STATE_KEY);

      if (!rawState) {
        return;
      }

      const saved: PersistedFleetViewState = JSON.parse(rawState);

      if (saved.activeTab) {
        setActiveTab(saved.activeTab);
      }

      if (saved.expandedFleetIds?.length) {
        setExpandedFleets(new Set(saved.expandedFleetIds));
      }

      if (saved.selectedFleetId != null) {
        const restoredFleet = fleetGroups.find(
          (group) => group.fleet === saved.selectedFleetId,
        );

        if (restoredFleet) {
          setSelectedFleet(restoredFleet);
          selectedFleetRef.current = restoredFleet;

          fetchPadWells(restoredFleet.latestPad.id);
        }
      }

      if (saved.fromDateByFleet) {
        const restoredFromDates = Object.fromEntries(
          Object.entries(saved.fromDateByFleet).map(([fleetId, timestamp]) => [
            Number(fleetId),
            timestamp != null ? new Date(timestamp) : null,
          ]),
        );

        setFromDateByFleet(restoredFromDates);
      }

      if (saved.toDateByFleet) {
        const restoredToDates = Object.fromEntries(
          Object.entries(saved.toDateByFleet).map(([fleetId, timestamp]) => [
            Number(fleetId),
            timestamp != null ? new Date(timestamp) : null,
          ]),
        );

        setToDateByFleet(restoredToDates);
      }

      if (saved.selectedWellIdsByPad) {
        setSelectedWellIdsByPad(saved.selectedWellIdsByPad);
        selectedWellIdsByPadRef.current = saved.selectedWellIdsByPad;
      }

      if (saved.showAllWellsByPad) {
        setShowAllWellsByPad(saved.showAllWellsByPad);
        showAllWellsByPadRef.current = saved.showAllWellsByPad;
      }

      if (saved.selectedPumpsByFleet) {
        setSelectedPumpsByFleet(saved.selectedPumpsByFleet);
      }

      if (saved.hiddenPumpsByFleet) {
        const restoredHiddenPumps = Object.fromEntries(
          Object.entries(saved.hiddenPumpsByFleet).map(
            ([fleetId, pumpNames]) => [
              Number(fleetId),
              new Set<string>(pumpNames),
            ],
          ),
        );

        setHiddenPumpsByFleet(restoredHiddenPumps);
      }

      if (saved.selectedChannelsByFleet) {
        const restoredChartChannels = Object.fromEntries(
          Object.entries(saved.selectedChannelsByFleet).map(
            ([fleetId, channels]) => [
              Number(fleetId),
              normalizePowerEndTemperatureSelection(
                filterUnsupportedPumpChannels(channels),
              ),
            ],
          ),
        ) as Record<number, PumpChannel[]>;

        setSelectedChannelsByFleet(restoredChartChannels);
        selectedChannelsByFleetRef.current = restoredChartChannels;
      }

      if (saved.selectedTableChannelsByFleet) {
        const restoredTableChannels = Object.fromEntries(
          Object.entries(saved.selectedTableChannelsByFleet).map(
            ([fleetId, channels]) => [
              Number(fleetId),
              normalizePowerEndTemperatureSelection(
                filterUnsupportedPumpChannels(channels),
              ),
            ],
          ),
        ) as Record<number, PumpChannel[]>;

        setSelectedTableChannelsByFleet(restoredTableChannels);
        selectedTableChannelsByFleetRef.current = restoredTableChannels;
      }

      if (saved.chartModeByPad) {
        setChartModeByPad(saved.chartModeByPad);
      }

      if (saved.xAxisRangeByFleet) {
        setXAxisRangeByFleet(saved.xAxisRangeByFleet);
      }

      if (saved.alarmSeverityFilter) {
        setAlarmSeverityFilter(saved.alarmSeverityFilter);
      }
      const restoredBlenderId = String(saved.selectedBlenderId || "").trim();
      if (restoredBlenderId) {
        setSelectedBlenderId(restoredBlenderId);
      }

      if (restoredBlenderId && Array.isArray(saved.selectedBlenderChannels)) {
        const restoredBlenderChannels = saved.selectedBlenderChannels
          .map((channel) => String(channel || "").trim())
          .filter(Boolean);

        pendingBlenderChannelsRef.current = {
          blenderId: restoredBlenderId,
          channels: restoredBlenderChannels,
        };
        setSelectedBlenderChannels(restoredBlenderChannels);
      }

      if (
        saved.blenderFromDate != null &&
        Number.isFinite(saved.blenderFromDate)
      ) {
        setBlenderFromDate(new Date(saved.blenderFromDate));
      }

      if (saved.blenderToDate != null && Number.isFinite(saved.blenderToDate)) {
        setBlenderToDate(new Date(saved.blenderToDate));
      }
      if (saved.selectedFleetId != null) {
        const fleetId = saved.selectedFleetId;

        const restoredChannels = normalizePowerEndTemperatureSelection(
          filterUnsupportedPumpChannels(
            saved.selectedChannelsByFleet?.[fleetId],
          ),
        );

        if (restoredChannels.length) {
          setSelectedChannels(restoredChannels);
          selectedChannelsRef.current = restoredChannels;
        }

        const restoredPumps = saved.selectedPumpsByFleet?.[fleetId];

        if (restoredPumps?.length) {
          setSelectedPumps(restoredPumps);
          selectedPumpsRef.current = restoredPumps;
        }

        const restoredHidden = saved.hiddenPumpsByFleet?.[fleetId];

        if (restoredHidden) {
          setHiddenPumps(new Set(restoredHidden));
        }

        const restoredXAxis = saved.xAxisRangeByFleet?.[fleetId];

        if (restoredXAxis) {
          setXAxisRange(restoredXAxis);
        }
      }
    } catch (error) {
      console.error("Unable to restore Fleet view state:", error);

      sessionStorage.removeItem(FLEET_VIEW_STATE_KEY);
    }
  }, [fleetGroups, location.search, fetchPadWells]);

  useEffect(() => {
    if (!hasRestoredFleetViewStateRef.current) {
      return;
    }

    try {
      const serializedHiddenPumps: Record<number, string[]> = {};

      Object.entries(hiddenPumpsByFleet).forEach(([fleetId, pumps]) => {
        serializedHiddenPumps[Number(fleetId)] = Array.from(pumps);
      });

      const serializedFromDates: Record<number, number | null> = {};

      Object.entries(fromDateByFleet).forEach(([fleetId, date]) => {
        serializedFromDates[Number(fleetId)] =
          date instanceof Date ? date.getTime() : null;
      });

      const serializedToDates: Record<number, number | null> = {};

      Object.entries(toDateByFleet).forEach(([fleetId, date]) => {
        serializedToDates[Number(fleetId)] =
          date instanceof Date ? date.getTime() : null;
      });

      const stateToSave: PersistedFleetViewState = {
        activeTab,

        selectedFleetId: selectedFleet?.fleet ?? null,

        expandedFleetIds: Array.from(expandedFleets),

        fromDateByFleet: serializedFromDates,
        toDateByFleet: serializedToDates,

        selectedWellIdsByPad,
        showAllWellsByPad,

        selectedPumpsByFleet,
        hiddenPumpsByFleet: serializedHiddenPumps,

        selectedChannelsByFleet: Object.fromEntries(
          Object.entries(selectedChannelsByFleet).map(([fleetId, channels]) => [
            Number(fleetId),
            normalizePowerEndTemperatureSelection(
              filterUnsupportedPumpChannels(channels),
            ),
          ]),
        ),
        selectedTableChannelsByFleet: Object.fromEntries(
          Object.entries(selectedTableChannelsByFleet).map(
            ([fleetId, channels]) => [
              Number(fleetId),
              normalizePowerEndTemperatureSelection(
                filterUnsupportedPumpChannels(channels),
              ),
            ],
          ),
        ),

        chartModeByPad,

        xAxisRangeByFleet,

        alarmSeverityFilter,
        // Blender state
        selectedBlenderId,
        selectedBlenderChannels,
        blenderFromDate: blenderFromDate.getTime(),
        blenderToDate: blenderToDate.getTime(),
      };

      sessionStorage.setItem(FLEET_VIEW_STATE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Unable to save Fleet view state:", error);
    }
  }, [
    activeTab,
    selectedFleet,
    expandedFleets,
    fromDateByFleet,
    toDateByFleet,
    selectedWellIdsByPad,
    showAllWellsByPad,
    selectedPumpsByFleet,
    hiddenPumpsByFleet,
    selectedChannelsByFleet,
    selectedTableChannelsByFleet,
    chartModeByPad,
    xAxisRangeByFleet,
    alarmSeverityFilter,
    selectedBlenderId,
    selectedBlenderChannels,
    blenderFromDate,
    blenderToDate,
  ]);

  useEffect(() => {
    if (!fleetGroups.length) return;

    const params = new URLSearchParams(location.search);
    const fleetParam = params.get("fleet");
    const pumpParam = params.get("pump");
    const startTimeParam = params.get("start_time");
    const endTimeParam = params.get("end_time");
    const channelsParam = params.get("channels");
    const flaggedAlarmParam = params.get("flagged_alarm");
    const flaggedGraphChannelParam = params.get("flagged_graph_channel");
    const flaggedAlarmTimeParam = params.get("flagged_alarm_time");
    const flaggedSourceAlarmTimeParam = params.get("flagged_source_alarm_time");
    const flaggedAlarmRpmParam = params.get("alarm_rpm");
    const flaggedAlarmSeverityParam =
      params.get("flagged_alarm_severity") || "Critical";

    if (!fleetParam) return;

    const fleetNumber = Number(fleetParam);
    const matchedFleet = fleetGroups.find((f) => f.fleet === fleetNumber);

    if (!matchedFleet) return;

    const now = new Date();
    const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);

    const parsedStartTime = Number(startTimeParam);
    const parsedEndTime = Number(endTimeParam);

    const dashboardFrom =
      startTimeParam && Number.isFinite(parsedStartTime) && parsedStartTime > 0
        ? new Date(parsedStartTime)
        : sixHoursAgo;

    const dashboardTo =
      endTimeParam &&
      Number.isFinite(parsedEndTime) &&
      parsedEndTime > dashboardFrom.getTime()
        ? new Date(parsedEndTime)
        : now;

    const parsedFlaggedAlarmTime = Number(flaggedAlarmTimeParam);
    const parsedFlaggedSourceAlarmTime = Number(flaggedSourceAlarmTimeParam);

    flaggedAlarmFallbackRef.current =
      pumpParam &&
      flaggedAlarmParam &&
      Number.isFinite(parsedFlaggedAlarmTime) &&
      parsedFlaggedAlarmTime > 0
        ? {
            pump: pumpParam,
            alarm: flaggedAlarmParam,
            graph_channel: flaggedGraphChannelParam || null,
            alarm_time: new Date(parsedFlaggedAlarmTime).toISOString(),
            alarm_time_ms: parsedFlaggedAlarmTime,
            source_alarm_time_ms:
              Number.isFinite(parsedFlaggedSourceAlarmTime) &&
              parsedFlaggedSourceAlarmTime > 0
                ? parsedFlaggedSourceAlarmTime
                : parsedFlaggedAlarmTime,
            alarm_rpm:
              flaggedAlarmRpmParam &&
              Number.isFinite(Number(flaggedAlarmRpmParam))
                ? Number(flaggedAlarmRpmParam)
                : null,
            severity: flaggedAlarmSeverityParam,
          }
        : null;

    const dashboardChannels: PumpChannel[] =
      normalizePowerEndTemperatureSelection(
        (channelsParam || "")
          .split(",")
          .map((channel) => channel.trim())
          .filter(Boolean)
          .map(
            (channel) =>
              findChannelByValue(channel) || {
                value: channel,
                label: channel
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (value) => value.toUpperCase()),
              },
          ),
      );
    // This effect can run again when fetchPadWells changes identity after wells
    // are loaded. Do not clear the pump/channel selection for the same View Chart
    // URL after its initial dashboard load has already completed.
    const dashboardSelectionKey = `${matchedFleet.fleet}-${pumpParam || ""}-${dashboardFrom.getTime()}-${dashboardTo.getTime()}-${dashboardChannels.map((channel) => channel.value).join(",")}-${flaggedAlarmParam || ""}-${flaggedGraphChannelParam || ""}-${flaggedAlarmTimeParam || ""}-${flaggedSourceAlarmTimeParam || ""}`;

    if (dashboardInitKeyRef.current === dashboardSelectionKey) {
      return;
    }
    dashboardInitKeyRef.current = dashboardSelectionKey;
    dashboardDeepLinkLoadInProgressRef.current = true;

    // Cancel a normal Fleet auto-load that may have been queued before this
    // deep-link initialization effect ran.
    if (fleetAutoLoadTimerRef.current) {
      clearTimeout(fleetAutoLoadTimerRef.current);
      fleetAutoLoadTimerRef.current = null;
    }

    // Remove any retained/default chart content before showing the target fleet.
    // The user sees one loading state instead of an RPM-only or stale chart flash.
    if (matchedFleet.fleet in fullResByFleetRef.current) {
      const nextBuffers = { ...fullResByFleetRef.current };
      delete nextBuffers[matchedFleet.fleet];
      fullResByFleetRef.current = nextBuffers;
    }

    setChartSeriesByFleet((prev) => {
      if (!(matchedFleet.fleet in prev)) return prev;
      const next = { ...prev };
      delete next[matchedFleet.fleet];
      return next;
    });

    setPumpDataByFleet((prev) => {
      if (!(matchedFleet.fleet in prev)) return prev;
      const next = { ...prev };
      delete next[matchedFleet.fleet];
      return next;
    });

    setLoadedChartChannelsByFleet((prev) => {
      if (!(matchedFleet.fleet in prev)) return prev;
      const next = { ...prev };
      delete next[matchedFleet.fleet];
      return next;
    });

    loadedChartChannelsByFleetRef.current = {
      ...loadedChartChannelsByFleetRef.current,
      [matchedFleet.fleet]: [],
    };

    setChartSeries([]);
    setPumpData([]);
    setAvailablePumps([]);
    setSelectedPumps([]);
    selectedPumpsRef.current = [];
    setHiddenPumps(new Set());

    setActiveTab("fleet");
    setExpandedFleets(new Set([matchedFleet.fleet]));
    setSelectedFleet(matchedFleet);

    setSelectedChannels([]);
    selectedChannelsRef.current = [];

    setSelectedChannelsByFleet((prev) => {
      const next = { ...prev };
      delete next[matchedFleet.fleet];
      return next;
    });
    selectedChannelsByFleetRef.current = {
      ...selectedChannelsByFleetRef.current,
      [matchedFleet.fleet]: [],
    };

    setFromDate(dashboardFrom);
    setToDate(dashboardTo);

    setFromDateByFleet((prev) => ({
      ...prev,
      [matchedFleet.fleet]: dashboardFrom,
    }));

    setToDateByFleet((prev) => ({
      ...prev,
      [matchedFleet.fleet]: dashboardTo,
    }));

    fetchPadWells(matchedFleet.latestPad.id);

    setPendingDashboardSelection({
      fleet: matchedFleet.fleet,
      pump: pumpParam || undefined,
      fromDate: dashboardFrom,
      toDate: dashboardTo,
      channels: dashboardChannels.length > 0 ? dashboardChannels : undefined,
    });

    // Wait until React has rendered the expanded accordion before scrolling.
    window.setTimeout(() => {
      document
        .getElementById(`fleet-${matchedFleet.fleet}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  }, [
    fleetGroups,
    location.search,
    fetchPadWells,
    getDefaultChartChannelForFleet,
  ]);

  useEffect(() => {
    if (!pendingDashboardSelection || !selectedFleet) return;

    if (selectedFleet.fleet !== pendingDashboardSelection.fleet) return;

    const loadKey = `${pendingDashboardSelection.fleet}-${pendingDashboardSelection.pump || ""}-${pendingDashboardSelection.fromDate.getTime()}-${pendingDashboardSelection.toDate.getTime()}-${(pendingDashboardSelection.channels || []).map((channel) => channel.value).join(",")}`;

    if (dashboardLoadKeyRef.current === loadKey) {
      return;
    }

    dashboardLoadKeyRef.current = loadKey;
    dashboardDeepLinkLoadInProgressRef.current = true;

    const runDashboardLoad = async () => {
      const fleetId = pendingDashboardSelection.fleet;
      const pumpName = pendingDashboardSelection.pump;

      // Prevent the date/fleet auto-load effect from scheduling the same request
      // after this deep-link load completes.
      const fleetAutoLoadSignature = `${fleetId}|${pendingDashboardSelection.fromDate.getTime()}|${pendingDashboardSelection.toDate.getTime()}`;
      fleetAutoLoadSignatureRef.current = fleetAutoLoadSignature;

      if (fleetAutoLoadTimerRef.current) {
        clearTimeout(fleetAutoLoadTimerRef.current);
        fleetAutoLoadTimerRef.current = null;
      }

      // Show one loading state while alarm channels are being resolved. Do not
      // render a default RPM chart during this phase.
      setLoadingFleetIds((prev) => new Set(prev).add(fleetId));

      try {
        if (pumpName) {
          setSelectedPumps([pumpName]);
          selectedPumpsRef.current = [pumpName];
          setSelectedPumpsByFleet((prev) => ({
            ...prev,
            [fleetId]: [pumpName],
          }));
          setHiddenPumps(new Set());
          setHiddenPumpsByFleet((prev) => ({
            ...prev,
            [fleetId]: new Set<string>(),
          }));
        }

        const isFlaggedReengagementView =
          new URLSearchParams(location.search).get(
            "flagged_reengagement_view",
          ) === "1";

        /*
         * Resolve the complete channel list BEFORE the first chart-data request.
         * For a transmission critical event this resolves:
         *   Gear + RPM + Transmission Oil Pressure
         * and loadData receives all three together.
         */
        const channelsToLoad =
          isFlaggedReengagementView && pumpName
            ? await resolveFlaggedChartChannels(
                pumpName,
                pendingDashboardSelection.fromDate,
                pendingDashboardSelection.toDate,
              )
            : pendingDashboardSelection.channels?.length
              ? pendingDashboardSelection.channels
              : [
                  await getDefaultChartChannelForFleet(
                    pendingDashboardSelection.fleet,
                  ),
                ];

        setSelectedChannelsByFleet((prev) => ({
          ...prev,
          [fleetId]: channelsToLoad,
        }));
        selectedChannelsByFleetRef.current = {
          ...selectedChannelsByFleetRef.current,
          [fleetId]: channelsToLoad,
        };

        setSelectedChannels(channelsToLoad);
        selectedChannelsRef.current = channelsToLoad;

        // One and only one chart-data request for this View Chart navigation.
        await loadData(
          pendingDashboardSelection.fromDate,
          pendingDashboardSelection.toDate,
          selectedFleet,
          pumpName,
          false,
          channelsToLoad,
          undefined,
          false,
        );
      } finally {
        setPendingDashboardSelection(null);
        dashboardDeepLinkLoadInProgressRef.current = false;

        // loadData also removes this flag. This cleanup covers channel-resolution
        // failures that happen before loadData starts.
        setLoadingFleetIds((prev) => {
          const next = new Set(prev);
          next.delete(fleetId);
          return next;
        });
      }
    };

    void runDashboardLoad();
  }, [
    pendingDashboardSelection,
    selectedFleet,
    location.search,
    resolveFlaggedChartChannels,
    getDefaultChartChannelForFleet,
  ]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (showAxisModal) setShowAxisModal(false);
        if (showTemplateModal) setShowTemplateModal(false);
        if (showExportModal) setShowExportModal(false);
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showAxisModal, showTemplateModal, showExportModal]);

  useEffect(() => {
    const handleApplyTemplate = (event: any) => {
      const template = event.detail as Template;
      if (!template) return;

      const templateOption = {
        value: String(template.id),
        label: template.template_name,
        template,
      };

      // ManageTemplatesModal applies templates through this window event.
      // Preserve the Fleet/Asset context that opened the modal so Apply updates
      // the same controls the user is looking at.
      if (templateModalContext.view === "fleet") {
        const fleetId = templateModalContext.fleetId;
        const fleetGroup = fleetGroups.find((group) => group.fleet === fleetId);

        if (!fleetGroup) {
          console.warn(
            `Could not apply template ${template.template_name}: Fleet ${fleetId} was not found.`,
          );
          return;
        }

        if (template.widget_type === "CHART") {
          setSelectedChartTemplateByFleet((previous) => ({
            ...previous,
            [fleetId]: templateOption,
          }));
        } else if (template.widget_type === "TABLE") {
          setSelectedTableTemplateByFleet((previous) => ({
            ...previous,
            [fleetId]: templateOption,
          }));
        }

        applyTemplate(template, fleetGroup);
        return;
      }

      if (template.widget_type === "CHART") {
        setSelectedTemplate(templateOption);
      } else if (template.widget_type === "TABLE") {
        setSelectedTableTemplate(templateOption);
      }

      applyTemplate(template);
    };

    window.addEventListener("applyTemplate", handleApplyTemplate);

    return () => {
      window.removeEventListener("applyTemplate", handleApplyTemplate);
    };
  }, [availableChannels, axisSettings, fleetGroups, templateModalContext]);

  const chartAxisNames = ["left1", "left2", "right1", "right2"] as const;

  const resolveAxisNameForChannel = useCallback(
    (
      channelValue: string,
      channelsToUse: PumpChannel[],
      settingsToUse: typeof axisSettings,
    ): (typeof chartAxisNames)[number] => {
      const hasPowerEndTemperatureSelection = channelsToUse.some((channel) =>
        isPowerEndTemperatureChannelValue(channel.value),
      );

      if (channelsToUse.length <= 1) return "left1";

      // Inlet and Outlet are the same unit and must be compared on the same
      // Power End Temperature axis: whichever axis the axis-settings effect
      // gave them (their own axis when one is free). left1 is only the
      // fallback before the settings have caught up with a new selection.
      if (
        hasPowerEndTemperatureSelection &&
        isPowerEndTemperatureChannelValue(channelValue)
      ) {
        return (
          chartAxisNames.find((axisName) =>
            (settingsToUse[axisName]?.channels || []).some(
              isPowerEndTemperatureChannelValue,
            ),
          ) ?? "left1"
        );
      }

      const configuredAxis = chartAxisNames.find((axisName) =>
        (settingsToUse[axisName]?.channels || []).includes(channelValue),
      );

      if (configuredAxis) return configuredAxis;

      const channelIndex = Math.max(
        0,
        channelsToUse.findIndex((channel) => channel.value === channelValue),
      );

      return chartAxisNames[channelIndex % chartAxisNames.length];
    },
    [],
  );

  useEffect(() => {
    const selectedValues = new Set(
      selectedChannels.map((channel) => channel.value),
    );

    setAxisSettings((previous) => {
      const next = chartAxisNames.reduce<typeof previous>(
        (result, axisName) => {
          result[axisName] = {
            ...previous[axisName],
            channels: (previous[axisName]?.channels || []).filter((channel) =>
              selectedValues.has(channel),
            ),
          };
          return result;
        },
        {} as typeof previous,
      );

      // Keep Power End Inlet + Outlet Temperature on the SAME Y-axis, and give
      // that axis to them alone when possible. They used to be forced onto
      // left1, which also holds RPM by default, so the temperature was drawn
      // on the RPM scale and had no axis of its own.
      const selectedPowerEndTemperatureValues = selectedChannels
        .map((channel) => channel.value)
        .filter(isPowerEndTemperatureChannelValue);

      // An axis that currently holds only Power End temperatures (e.g. one the
      // user picked in Configure Axes) keeps them there.
      const existingPowerEndAxis =
        selectedPowerEndTemperatureValues.length > 0
          ? chartAxisNames.find(
              (axisName) =>
                next[axisName].channels.length > 0 &&
                next[axisName].channels.every(isPowerEndTemperatureChannelValue),
            )
          : undefined;

      // Pull the temperatures off every axis; they are placed again below,
      // after the other channels, so they can take a free axis.
      if (selectedPowerEndTemperatureValues.length > 0) {
        chartAxisNames.forEach((axisName) => {
          next[axisName] = {
            ...next[axisName],
            channels: next[axisName].channels.filter(
              (channelValue) =>
                !isPowerEndTemperatureChannelValue(channelValue),
            ),
          };
        });
      }

      const assigned = new Set(
        chartAxisNames.flatMap((axisName) => next[axisName].channels),
      );

      selectedChannels.forEach((channel) => {
        if (isPowerEndTemperatureChannelValue(channel.value)) return;
        if (assigned.has(channel.value)) return;

        // Don't hand the Power End temperatures' own axis to another channel.
        const candidateAxes = chartAxisNames.filter(
          (axisName) => axisName !== existingPowerEndAxis,
        );
        const targetAxis = candidateAxes.reduce((bestAxis, axisName) =>
          next[axisName].channels.length < next[bestAxis].channels.length
            ? axisName
            : bestAxis,
        );

        next[targetAxis] = {
          ...next[targetAxis],
          channels: [...next[targetAxis].channels, channel.value],
        };
        assigned.add(channel.value);
      });

      // Inlet + Outlet go together on: the axis they already had to
      // themselves, else the first empty axis, else the least-used axis
      // (only when all 4 axes are taken do they share with another channel).
      if (selectedPowerEndTemperatureValues.length > 0) {
        const powerEndAxis =
          existingPowerEndAxis ??
          chartAxisNames.find(
            (axisName) => next[axisName].channels.length === 0,
          ) ??
          chartAxisNames.reduce((bestAxis, axisName) =>
            next[axisName].channels.length < next[bestAxis].channels.length
              ? axisName
              : bestAxis,
          );

        next[powerEndAxis] = {
          ...next[powerEndAxis],
          channels: Array.from(
            new Set([
              ...next[powerEndAxis].channels,
              ...selectedPowerEndTemperatureValues,
            ]),
          ),
        };
      }

      const unchanged = chartAxisNames.every(
        (axisName) =>
          previous[axisName]?.channels.join("|") ===
          next[axisName]?.channels.join("|"),
      );

      return unchanged ? previous : next;
    });
  }, [selectedChannels]);

  useEffect(() => {
    selectedPumpsRef.current = selectedPumps;
  }, [selectedPumps]);

  useEffect(() => {
    selectedChannelsRef.current = selectedChannels;
  }, [selectedChannels]);

  useEffect(() => {
    selectedAssetChannelsRef.current = selectedAssetChannels;
  }, [selectedAssetChannels]);

  useEffect(() => {
    selectedFleetRef.current = selectedFleet;
  }, [selectedFleet]);

  useEffect(() => {
    if (activeTab !== "fleet" || !selectedFleet) return;

    const fleetId = selectedFleet.fleet;
    const hasStoredFleetAlarms = Object.prototype.hasOwnProperty.call(
      alarmEventsByFleet,
      fleetId,
    );

    if (hasStoredFleetAlarms) {
      setAlarmEvents(alarmEventsByFleet[fleetId] ?? []);
      return;
    }

    const reviewAlarmContext = getAlarmReviewContextForFleet(fleetId);
    const fleetStartMs = fromDateByFleet[fleetId]?.getTime();
    const fleetEndMs = toDateByFleet[fleetId]?.getTime();
    const isExactReviewWindow = Boolean(
      reviewAlarmContext &&
      fleetStartMs === reviewAlarmContext.startTime &&
      fleetEndMs === reviewAlarmContext.endTime,
    );

    const restoredAlarms =
      isExactReviewWindow && reviewAlarmContext
        ? [reviewAlarmContext.alarm]
        : [];

    setAlarmEvents(restoredAlarms);

    if (restoredAlarms.length > 0) {
      setAlarmEventsByFleet((previous) => ({
        ...previous,
        [fleetId]: restoredAlarms,
      }));
    }
  }, [
    activeTab,
    selectedFleet,
    alarmEventsByFleet,
    fromDateByFleet,
    toDateByFleet,
    getAlarmReviewContextForFleet,
  ]);

  // Restore the correct independent channel selection whenever the user moves
  // between Fleet and Asset. Fleet selections remain stored per fleet; Asset
  // retains its own selection and is never overwritten by Fleet changes.
  useEffect(() => {
    if (activeTab === "asset") {
      setSelectedChannels(selectedAssetChannelsRef.current);
      selectedChannelsRef.current = selectedAssetChannelsRef.current;
      return;
    }

    /*
     * A deep-link View Chart owns its initial channel selection. Waiting here
     * prevents the normal Fleet fallback from briefly inserting RPM before
     * resolveFlaggedChartChannels returns Gear/RPM/alarm channels.
     */
    if (dashboardDeepLinkLoadInProgressRef.current) {
      return;
    }

    if (activeTab === "fleet" && selectedFleet) {
      const fleetChannels = selectedChannelsByFleetRef.current[
        selectedFleet.fleet
      ] || [defaultBoostChannel];
      setSelectedChannels(fleetChannels);
      selectedChannelsRef.current = fleetChannels;
    }
  }, [activeTab, selectedFleet]);

  useEffect(() => {
    selectedChannelsByFleetRef.current = selectedChannelsByFleet;
  }, [selectedChannelsByFleet]);

  useEffect(() => {
    loadedChartChannelsByFleetRef.current = loadedChartChannelsByFleet;
  }, [loadedChartChannelsByFleet]);

  useEffect(() => {
    selectedTableChannelsByFleetRef.current = selectedTableChannelsByFleet;
  }, [selectedTableChannelsByFleet]);

  const buildStageChannelSeries = useCallback(
    (
      data: StageChannelData[],
      channelsToUse: PumpChannel[],
      axisSettingsToUse: typeof axisSettings,
    ): Highcharts.SeriesOptionsType[] => {
      const axisIndexMap: Record<string, number> = {
        left1: 0,
        left2: 1,
        right1: 2,
        right2: 3,
      };

      return channelsToUse
        .filter((channel) => isStageChannel(channel.value))
        .map((channel) => {
          const points = data
            .map((row) => {
              const value = getChannelValue(row, channel.value);

              if (
                value === null ||
                value === undefined ||
                Number.isNaN(Number(value))
              ) {
                return null;
              }

              return [Number(row.time_stamp), Number(value)] as [
                number,
                number,
              ];
            })
            .filter((point): point is [number, number] => point !== null)
            .sort((first, second) => first[0] - second[0]);

          if (points.length === 0) {
            return null;
          }

          const axisName = resolveAxisNameForChannel(
            channel.value,
            channelsToUse,
            axisSettingsToUse,
          );

          const uniqueByTimestamp = Array.from(
            new Map(
              points.map(([timestamp, value]) => [
                timestamp,
                [timestamp, value] as [number, number],
              ]),
            ).values(),
          );

          const STAGE_MAX_RENDER_POINTS = 2000;

          const sampleStep = Math.max(
            1,
            Math.ceil(uniqueByTimestamp.length / STAGE_MAX_RENDER_POINTS),
          );

          const renderData =
            sampleStep === 1
              ? uniqueByTimestamp
              : uniqueByTimestamp.filter(
                  (_point, index) =>
                    index % sampleStep === 0 ||
                    index === uniqueByTimestamp.length - 1,
                );

          const isSinglePoint = renderData.length <= 1;

          return {
            type: isSinglePoint ? "scatter" : "line",
            name: channel.label,
            data: renderData,
            yAxis: axisIndexMap[axisName] ?? 0,

            stickyTracking: false,
            connectNulls: false,
            boostThreshold: Number.MAX_SAFE_INTEGER,
            turboThreshold: 0,

            lineWidth: isSinglePoint ? 0 : 2,
            marker: isSinglePoint
              ? {
                  enabled: true,
                  radius: 7,
                  symbol: "diamond",
                }
              : {
                  enabled: false,
                },
            visible: true,
            zIndex: 6,
          } as Highcharts.SeriesOptionsType;
        })
        .filter(
          (series): series is Highcharts.SeriesOptionsType => series !== null,
        );
    },
    [resolveAxisNameForChannel],
  );

  useEffect(() => {
    // Fleet charts render from chartSeriesByFleet/fullResByFleetRef. Rebuilding
    // the legacy global chartSeries on every Fleet channel click scans the full
    // historical row set on the browser main thread and can make the page appear
    // unresponsive. Keep this row-based rebuild only for the Asset tab, and wait
    // until the Asset request has completely finished so state updates do not
    // trigger the same expensive work multiple times.
    if (activeTab !== "asset" || isLoadDataLoading) {
      return;
    }

    // When zoomed, re-downsample only the zoomed window (from finer zoom rows
    // when they have been fetched), so zooming reveals real detail instead of
    // magnifying the full-range downsample.
    const sourceRows = assetZoomRange ? (assetZoomRows ?? pumpData) : pumpData;
    const rowsForChart = assetZoomRange
      ? sourceRows.filter(
          (row) =>
            row.time_stamp >= assetZoomRange.min &&
            row.time_stamp <= assetZoomRange.max,
        )
      : sourceRows;

    const pumpSeries =
      rowsForChart.length > 0
        ? buildChartSeriesWithChannels(
            rowsForChart,
            selectedPumps,
            hiddenPumps,
            selectedChannels,
            axisSettings,
          )
        : [];

    setChartSeries(pumpSeries);
  }, [
    pumpData,
    selectedPumps,
    hiddenPumps,
    selectedChannels,
    axisSettings,
    selectedFleet,
    activeTab,
    isLoadDataLoading,
    assetZoomRange,
    assetZoomRows,
  ]);

  const handleRemovePump = (pumpName: string) => {
    setSelectedPumps((prev) => prev.filter((p) => p !== pumpName));
  };

  const handleAddPump = (pumpName: string) => {
    if (!selectedPumps.includes(pumpName)) {
      setSelectedPumps((prev) => [...prev, pumpName].sort());
    }
  };

  const handleLegendClick = (pumpName: string) => {
    setHiddenPumps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pumpName)) {
        newSet.delete(pumpName);
      } else {
        newSet.add(pumpName);
      }
      return newSet;
    });
  };

  const handleFleetSelect = (group: FleetGroup) => {
    const isCurrentlyExpanded = expandedFleets.has(group.fleet);
    if (isCurrentlyExpanded) {
      setExpandedFleets((prev) => {
        const next = new Set(prev);
        next.delete(group.fleet);
        return next;
      });
      if (group.fleet in fullResByFleetRef.current) {
        const nextBuffers = { ...fullResByFleetRef.current };
        delete nextBuffers[group.fleet];
        fullResByFleetRef.current = nextBuffers;
      }
      setChartSeriesByFleet((prev) => {
        if (!(group.fleet in prev)) return prev;
        const next = { ...prev };
        delete next[group.fleet];
        return next;
      });
      if (selectedFleet?.fleet === group.fleet) {
        setSelectedFleet(null);
        setPumpData([]);
        setAggregatedData({});
        setChartSeries([]);
        setXAxisRange({});
        setFromDate(null);
        setToDate(null);
      }
    } else {
      setExpandedFleets((prev) => new Set(prev).add(group.fleet));
      setSelectedFleet(group);

      const existingFleetChannels =
        selectedChannelsByFleetRef.current[group.fleet];

      /*
       * Do not seed a newly opened fleet with RPM while its real default channel
       * is still being resolved. Majority-electric fleets do not report RPM, so
       * the automatic first request could return no chart data and only recover
       * after the user touched a date field. Leaving the initial selection empty
       * lets loadData resolve the correct fleet default (VFD Amps for electric,
       * RPM for diesel) before the first /pumps request is sent.
       */
      const channelsToUse =
        existingFleetChannels?.length > 0 ? existingFleetChannels : [];

      setSelectedChannelsByFleet((prev) => {
        if (prev[group.fleet]?.length > 0) {
          return prev;
        }

        return {
          ...prev,
          [group.fleet]: channelsToUse,
        };
      });

      selectedChannelsByFleetRef.current = {
        ...selectedChannelsByFleetRef.current,
        [group.fleet]: channelsToUse,
      };

      setSelectedChannels(channelsToUse);
      selectedChannelsRef.current = channelsToUse;

      setPumpData([]);
      setAggregatedData({});
      setChartSeries([]);
      setXAxisRange({});
      setAvailablePumps([]);
      setSelectedPumps([]);
      setHiddenPumps(new Set());

      const now = new Date();
      const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      setFromDateByFleet((prev) =>
        prev[group.fleet] ? prev : { ...prev, [group.fleet]: sixHoursAgo },
      );
      setToDateByFleet((prev) =>
        prev[group.fleet] ? prev : { ...prev, [group.fleet]: now },
      );

      fetchPadWells(group.latestPad.id);
    }
  };

  useEffect(() => {
    const fleetId = selectedFleet?.fleet;
    if (fleetId == null) return;
    if (streamingFleetIds.has(fleetId)) return;

    const selectedFleetChannels = selectedChannelsByFleet[fleetId] ?? [];
    const loaded = loadedChartChannelsByFleet[fleetId] ?? [];

    const channelsToRender = selectedFleetChannels.filter((channel) =>
      loaded.includes(channel.value),
    );

    if (channelsToRender.length === 0) {
      /*
       * Only an EMPTY selection means the user cleared all chart channels; that
       * is the case where the retained prebuilt series must go.
       *
       * A non-empty selection with nothing loaded for it is a different thing
       * entirely: the load for those channels is still in flight, or a stale
       * response overwrote `loadedChartChannelsByFleet` with an older channel
       * set. Wiping here is what made a fleet report "No data available for the
       * selected range" right after its data had actually arrived. Leave the
       * existing chart alone and let the load settle it.
       */
      if (selectedFleetChannels.length > 0) return;

      setChartSeriesByFleet((previous) => ({
        ...previous,
        [fleetId]: [],
      }));

      if (selectedFleet?.fleet === fleetId) {
        setChartSeries([]);
      }

      return;
    }

    const pumpChannels = channelsToRender;

    const buffers = fullResByFleetRef.current[fleetId];
    let rebuiltPumpSeries: Highcharts.SeriesOptionsType[] = [];

    if (buffers && buffers.size > 0 && pumpChannels.length > 0) {
      // Build a series for every pump in the retained buffer. Pump selection and
      // legend visibility control only the series visibility, not whether the
      // underlying series exists.
      const bufferPumps = Array.from(buffers.keys());
      const selected = selectedPumpsByFleet[fleetId];
      // A selection that does not overlap the buffered pumps at all is stale, not
      // a real subset — a mixed fleet persists its diesel pumps from an RPM load
      // and then buffers only electric pumps for a VFD Amps load, so intersecting
      // naively hid EVERY series (chart drawn, whole legend greyed, no lines).
      // Mirror resolvePersistedPumpSelection in loadData: fall back to
      // all-visible rather than all-hidden.
      const bufferPumpSet = new Set(bufferPumps);
      const keptSelection = (selected ?? []).filter((pumpName) =>
        bufferPumpSet.has(pumpName),
      );
      const selectedSet =
        keptSelection.length > 0 ? new Set(keptSelection) : null;
      const explicitlyHidden = hiddenPumpsByFleet[fleetId] ?? new Set<string>();
      const effectiveHidden = new Set<string>(explicitlyHidden);

      if (selectedSet) {
        bufferPumps.forEach((pumpName) => {
          if (!selectedSet.has(pumpName)) {
            effectiveHidden.add(pumpName);
          }
        });
      }

      const buffersForRender =
        isFlaggedReengagementView && isDashboardPumpForFleet(fleetId)
          ? collapseFleetBuffersToOneSecond(buffers)
          : buffers;

      rebuiltPumpSeries = downsampledToHighcharts(
        buildDownsampledSeries(
          buffersForRender,
          bufferPumps,
          effectiveHidden,
          pumpChannels,
          axisSettings,
        ),
      );
    }

    const rebuilt: Highcharts.SeriesOptionsType[] = [...rebuiltPumpSeries];

    setChartSeriesByFleet((previous) => ({
      ...previous,
      [fleetId]: rebuilt,
    }));

    if (selectedFleet?.fleet === fleetId) {
      setChartSeries(rebuilt);
    }
  }, [
    selectedPumpsByFleet,
    hiddenPumpsByFleet,
    selectedChannelsByFleet,
    axisSettings,
    selectedFleet,
    loadedChartChannelsByFleet,
    streamingFleetIds,
  ]);

  const loadData = async (
    startDate?: Date,
    endDate?: Date,
    targetFleet?: FleetGroup,
    targetPumpName?: string,
    // Deprecated: previously toggled a blocking "Loading Data" modal. The modal
    // was removed in favor of non-blocking indicators (button spinner + chart
    // overlay); the param is retained for call-site positional compatibility.
    showModal: boolean = false,
    forcedChartChannels?: PumpChannel[],
    // Target chart mode. Passed by the mode-switch handler because its
    // setChartModeByPad state hasn't committed when it calls loadData, so reading
    // chartModeByPad here would see the stale mode.
    chartModeOverride?: "combined" | "compare",
    // Keep the existing flagged-event grid when View Chart narrows the chart
    // to one pump and a small time window.
    skipReengagementRefresh: boolean = false,
    // Skip the coarse all-channel table pre-load. The streaming back-fill only
    // needs the chart columnar data; the streaming table is fed by the 1s
    // snapshot poll, so the ~130-channel 1m table fetch would be wasted work.
    skipTableLoad: boolean = false,
    // Used only by real-time well switching. If the user selects another well
    // before this request finishes, the older response must not overwrite the
    // chart for the newer selection.
    wellSwitchVersion?: number,
    // Explicit well selection for real-time well switching. React state may not
    // have committed yet when the debounced reload starts, so the request must
    // use the exact selection that triggered this switch.
    wellIdsOverride?: number[],
    showAllWellsOverride?: boolean,
    // Channel-only historical refreshes fetch only newly added channels and
    // merge them into the retained buffers instead of reloading RPM and every
    // already-loaded channel for all pumps.
    mergeWithExistingChartData: boolean = false,
    // Optional UX-specific success message. Used when leaving real-time mode so
    // the user clearly understands that historical auto-refresh is active.
    successToastMessage?: string,
    // Automatic Fleet expansion/date refresh should be quiet. The inline chart
    // status is enough confirmation; explicit user actions keep existing toasts.
    suppressSuccessToast: boolean = false,
    preserveSelectedDateRange: boolean = false,
    requestOptions?: {
      noDataToastMessage?: string;
      onNoData?: () => void;
      // Alarm Review Data can retry the same window without backend pump/well
      // filters when the alarm-specific pump query returns no telemetry.
      forceFleetWidePumpQuery?: boolean;
      suppressNoDataToast?: boolean;
    },
  ) => {
    const fleet = targetFleet ?? selectedFleet;
    const fleetKey = fleet?.fleet;
    let effectiveFromDate =
      startDate ||
      (fleetKey != null ? (fromDateByFleet[fleetKey] ?? null) : null) ||
      fromDate;
    let effectiveToDate =
      endDate ||
      (fleetKey != null ? (toDateByFleet[fleetKey] ?? null) : null) ||
      toDate;

    if (!effectiveFromDate || !effectiveToDate) {
      const now = new Date();
      const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      effectiveFromDate = sixHoursAgo;
      effectiveToDate = now;
      if (fleetKey != null) {
        setFromDateByFleet((prev) => ({ ...prev, [fleetKey]: sixHoursAgo }));
        setToDateByFleet((prev) => ({ ...prev, [fleetKey]: now }));
      } else {
        setFromDate(sixHoursAgo);
        setToDate(now);
      }
    }

    if (!fleet || !effectiveFromDate || !effectiveToDate) {
      toast.warning("Please select a fleet and date range");
      return;
    }

    // Claim this load's place in the fleet's order. Checked once the response
    // lands, so an older in-flight load can no longer overwrite a newer one.
    const loadSequence = (chartLoadSequenceRef.current[fleet.fleet] ?? 0) + 1;
    chartLoadSequenceRef.current[fleet.fleet] = loadSequence;

    const diffMs = effectiveToDate.getTime() - effectiveFromDate.getTime();
    const maxAllowedEndDate = new Date(effectiveFromDate);
    maxAllowedEndDate.setMonth(maxAllowedEndDate.getMonth() + 6);

    if (diffMs <= 0) {
      toast.warning("End date must be after start date");
      return;
    }

    // Validate the maximum supported range BEFORE showing the resolution message.
    // If the range is invalid, show only the 6-month warning and stop loading.
    if (effectiveToDate.getTime() > maxAllowedEndDate.getTime()) {
      toast.warning("Please select a date range within 6 months");
      return;
    }

    // Only show the 1-hour resolution message for a VALID date range.
    if (!isStreaming) {
      const resolutionConfig = getAutoResolutionConfig(
        effectiveFromDate,
        effectiveToDate,
      );

      if (resolutionConfig.code === "1h") {
        toast.warning("Large date range selected — using 1 Hour resolution");
      }
    }

    /*
     * A normal historical load changes the alarm scope before the telemetry XHR
     * finishes. Clear the previous fleet/range markers immediately so users never
     * see the old alarm flash while the new chart is loading.
     *
     * Review Data is the exception: handleReviewAlarmData seeds the exact clicked
     * alarm before calling loadData, so keep that one marker while its telemetry
     * request is in flight.
     *
     * Zoom/channel-merge refreshes stay inside the same logical chart context and
     * should not blank the already-correct markers.
     */
    if (!mergeWithExistingChartData && !preserveSelectedDateRange) {
      const requestedStartMs = effectiveFromDate.getTime();
      const requestedEndMs = effectiveToDate.getTime();
      const reviewAlarmContext = getAlarmReviewContextForFleet(fleet.fleet);
      const isExactReviewLoad = Boolean(
        reviewAlarmContext &&
        reviewAlarmContext.fleetId === fleet.fleet &&
        reviewAlarmContext.startTime === requestedStartMs &&
        reviewAlarmContext.endTime === requestedEndMs,
      );

      activeAlarmLoadScopeRef.current = {
        fleetId: fleet.fleet,
        startMs: requestedStartMs,
        endMs: requestedEndMs,
      };

      // Invalidate any /pump-alarms request that belonged to the previous chart
      // before the new telemetry request even starts.
      alarmFetchVersionRef.current += 1;

      const nextAlarmEvents =
        isExactReviewLoad && reviewAlarmContext
          ? [reviewAlarmContext.alarm]
          : [];

      setAlarmEvents(nextAlarmEvents);
      setAlarmEventsByFleet((previous) => ({
        ...previous,
        [fleet.fleet]: nextAlarmEvents,
      }));

      if (!isExactReviewLoad) {
        setSelectedAlarmEvents([]);
      }
    }

    try {
      // Non-blocking loading UX: the Load Data button shows an inline spinner
      // (driven by loadingFleetIds) and the chart shows its own overlay, so the
      // page stays fully interactive during the load. No blocking modal.
      setLoadingFleetIds((prev) => new Set(prev).add(fleet.fleet));

      // Flip the table + re-engagement sections into their loading state right
      // away so they don't flash "no data"/"none found" while the chart loads.
      // Their real fetches run after the chart (below) and clear these flags in
      // their own finally blocks; the early-return and catch paths clear them.
      if (!skipTableLoad) {
        setTableLoadingByFleet((prev) => ({ ...prev, [fleet.fleet]: true }));
      }
      if (!skipReengagementRefresh) {
        setReengagementEventsLoadingByFleet((prev) => ({
          ...prev,
          [fleet.fleet]: true,
        }));
      }

      const token = getAuthToken();

      const effectivePad =
        getPadForFleetDateRange(fleet, effectiveFromDate, effectiveToDate) ||
        fleet.latestPad;

      // Load the historical pad's well names/IDs before the user interacts with
      // its well buttons. The first all-well request can still run fleet-only;
      // subsequent individual-well selections use these historical IDs.
      if (effectivePad?.id) {
        await fetchPadWells(effectivePad.id);
      }

      const selectedWellIds =
        wellIdsOverride !== undefined
          ? wellIdsOverride
          : getSelectedWellIdsForPad(effectivePad.id);

      // "All wells selected" == the whole pad active in the selected historical
      // range, so filter by fleet only. A `well_id IN (...)` predicate has no
      // supporting index and turns a ~10s fleet scan into a multi-minute query.
      const padWellsForFleet = padWells[effectivePad.id] || [];
      const isAllWells =
        (showAllWellsOverride !== undefined
          ? showAllWellsOverride
          : showAllWellsByPadRef.current[effectivePad.id] === true ||
            showAllWellsByPad[effectivePad.id] === true) ||
        (padWellsForFleet.length > 0 &&
          selectedWellIds.length >= padWellsForFleet.length);
      const wellIdsToSend = isAllWells ? [] : selectedWellIds;

      let channelsForLoad = filterUnsupportedPumpChannels(
        forcedChartChannels && forcedChartChannels.length > 0
          ? forcedChartChannels
          : selectedChannelsByFleet[fleet.fleet] &&
              selectedChannelsByFleet[fleet.fleet].length > 0
            ? selectedChannelsByFleet[fleet.fleet]
            : selectedChannels,
      );

      // Guard the empty-channel race. On a freshly expanded fleet the default
      // chart channel is resolved ASYNCHRONOUSLY (getDefaultChartChannelForFleet
      // → /pumps/list). If Load Data is clicked before it resolves, channelsForLoad
      // is empty → the /pumps request omits `channels` → the backend returns ALL
      // ~130 channels (a huge payload) and parseColumnarToBuffers extracts nothing
      // for the (empty) selection → "No data available" until a retry. Resolve the
      // default channel here so the first load always fetches a real channel.
      if (channelsForLoad.length === 0) {
        const defaultChannel = await getDefaultChartChannelForFleet(
          fleet.fleet,
        );
        channelsForLoad = [defaultChannel];
        setSelectedChannelsByFleet((prev) =>
          prev[fleet.fleet]?.length
            ? prev
            : { ...prev, [fleet.fleet]: [defaultChannel] },
        );
        selectedChannelsByFleetRef.current = {
          ...selectedChannelsByFleetRef.current,
          [fleet.fleet]: [defaultChannel],
        };

        if (selectedFleetRef.current?.fleet === fleet.fleet) {
          setSelectedChannels([defaultChannel]);
          selectedChannelsRef.current = [defaultChannel];
        }
      }

      const {
        pumpChannels: pumpChartChannels,
        stageChannels: stageChartChannels,
      } = splitChannelsBySource(channelsForLoad);

      const chartChannels = pumpChartChannels.map((channel) => channel.value);

      const stageTableChannels = availableChannels.filter((channel) =>
        isStageChannel(channel.value),
      );

      const stageChannelValues = Array.from(
        new Set([
          ...stageChartChannels.map((channel) => channel.value),
          ...(!skipTableLoad
            ? stageTableChannels.map((channel) => channel.value)
            : []),
        ]),
      );

      const tableChannelsForLoad = filterUnsupportedPumpChannels(
        selectedTableChannelsByFleetRef.current[fleet.fleet] ||
          selectedTableChannelsByFleet[fleet.fleet] ||
          selectedTableChannels,
      );

      const tableChannels = tableChannelsForLoad.map(
        (channel) => channel.value,
      );

      // Fallback aliases so getChannelValue resolves a channel regardless of the
      // backend's column naming; calculated_discharge_rate is needed by the
      // table's AVG gating. Included in both fetches.
      const FALLBACK_ALIASES = [
        "calculated_discharge_rate",
        "engine_coolant_temperature",
        "engine_fuel_pressure",
        "engine_fuel_pressure_psi",
        "engine_fuel_press",
        "fuel_pressure",
        "fuel_press",
        "fuel_pressure_psi",
        "eng_fuel_pressure",
        "atlas_engine_fuel_pressure",
      ];

      const buildPumpParams = (
        channels: string[],
        resolution: string,
        format?: string,
        rangeStart: Date = effectiveFromDate,
        rangeEnd: Date = effectiveToDate,
      ) => {
        const p = new URLSearchParams();
        p.append("fleet", String(fleet.fleet));
        p.append("start_time", String(rangeStart.getTime()));
        p.append("end_time", String(rangeEnd.getTime()));
        p.append("resolution", resolution);
        if (!requestOptions?.forceFleetWidePumpQuery) {
          wellIdsToSend.forEach((id) => p.append("well_id", String(id)));
          if (targetPumpName) p.append("pump_name", targetPumpName);
        }
        if (format) p.append("format", format);
        const chs = Array.from(new Set(channels));
        if (chs.length > 0) p.append("channels", chs.join(","));
        return p;
      };

      const buildTreatmentParams = () => {
        const params = new URLSearchParams();

        params.append("fleet", String(fleet.fleet));
        params.append("start_time", String(effectiveFromDate.getTime()));
        params.append("end_time", String(effectiveToDate.getTime()));

        wellIdsToSend.forEach((wellId) => {
          params.append("well_id", String(wellId));
        });

        return params;
      };

      // Chart fetches only its selected channels + the exact backend columns
      // getChannelValue may read for them (their CHANNEL_ALIASES) — not the broad
      // fallback set, most of which the chart never reads but would pull at 1s.
      const chartFetchChannels = Array.from(
        new Set(
          chartChannels.flatMap(
            (ch) => CHANNEL_ALIASES[normalizeChannelKey(ch)] || [ch],
          ),
        ),
      );
      // Load only the channels currently selected in the table. Fetching every
      // available channel made the background table request very large and could
      // leave the chart populated while aggregatedDataByFleet stayed empty.
      const selectedPumpTableChannels = tableChannelsForLoad.filter(
        (channel) => !isStageChannel(channel.value),
      );
      const tableFetchChannels = Array.from(
        new Set([
          ...selectedPumpTableChannels.flatMap(
            (channel) =>
              CHANNEL_ALIASES[normalizeChannelKey(channel.value)] || [
                channel.value,
              ],
          ),
          // Needed by the existing AVG/active-pump gating logic.
          "calculated_discharge_rate",
        ]),
      );
      // Base the batched decision on what's actually sent: fleet-only (all wells)
      // and single-well loads go to the single /pumps query (≤6h ⇒ <1M rows ⇒ no
      // truncation), faster than sequential batched chunks. Only a real multi-well
      // subset uses the batched endpoint.
      //
      // NOTE: streaming must NOT force batched. The streaming 3h back-fill is a
      // fleet-only combined load that wants the fast single columnar /pumps query.
      // /pumps/batched has no format=columnar support (returns legacy rows), so
      // routing streaming here made a 3h seed take ~40s of sequential chunks AND
      // dropped the columnar buffers the seed depends on — while also starving the
      // 1s snapshot polls of backend workers.
      // A real multi-well subset must be fetched one well at a time and merged.
      // Sending repeated well_id values through /pumps/batched can return an empty
      // payload even though each selected well contains data. The single /pumps
      // endpoint reliably supports one well filter, so use parallel per-well
      // requests and merge their rows on the client.
      const isMultiWellSubset = wellIdsToSend.length > 1;

      // The single /pumps path serves the heavy 6h @ 1s loads, so give every
      // request the same 180s headroom.
      const authConfig = {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000,
      };

      const hasColumnarSeries = (payload: any): boolean => {
        if (!payload?.series || typeof payload.series !== "object") {
          return false;
        }

        return Object.values(payload.series).some((pumpSeries: any) => {
          if (!pumpSeries || typeof pumpSeries !== "object") {
            return false;
          }

          return Object.values(pumpSeries).some((channelData: any) => {
            if (Array.isArray(channelData)) {
              return channelData.length > 0;
            }

            return (
              Array.isArray(channelData?.timestamps) &&
              channelData.timestamps.length > 0
            );
          });
        });
      };

      const fetchPumpPayload = async (
        channels: string[],
        resolution: string,
        format?: string,
        rangeStart: Date = effectiveFromDate,
        rangeEnd: Date = effectiveToDate,
      ) => {
        const normalizePumpPayload = (rawPayload: any) => {
          if (Array.isArray(rawPayload)) {
            return {
              data: rawPayload,
              row_count: rawPayload.length,
              truncated: false,
              channels: [],
              series: undefined,
            };
          }

          // Some environments wrap the actual pump response inside `data`.
          // Preserve both row-based and columnar response shapes.
          const nestedPayload =
            rawPayload?.data &&
            !Array.isArray(rawPayload.data) &&
            typeof rawPayload.data === "object"
              ? rawPayload.data
              : null;

          const effectivePayload = nestedPayload || rawPayload || {};

          const rows = Array.isArray(effectivePayload?.data)
            ? effectivePayload.data
            : Array.isArray(effectivePayload?.rows)
              ? effectivePayload.rows
              : Array.isArray(effectivePayload?.results)
                ? effectivePayload.results
                : Array.isArray(effectivePayload?.items)
                  ? effectivePayload.items
                  : [];

          const series =
            effectivePayload?.series &&
            typeof effectivePayload.series === "object"
              ? effectivePayload.series
              : rawPayload?.series && typeof rawPayload.series === "object"
                ? rawPayload.series
                : undefined;

          return {
            ...rawPayload,
            ...effectivePayload,
            data: rows,
            series,
            row_count:
              Number(effectivePayload?.row_count) ||
              Number(rawPayload?.row_count) ||
              Number(effectivePayload?.count) ||
              Number(rawPayload?.count) ||
              rows.length,
            truncated: Boolean(
              effectivePayload?.truncated ?? rawPayload?.truncated,
            ),
            channels: Array.isArray(effectivePayload?.channels)
              ? effectivePayload.channels
              : Array.isArray(rawPayload?.channels)
                ? rawPayload.channels
                : [],
          };
        };

        const fetchOneWell = async (wellId?: number) => {
          const requestParams = buildPumpParams(
            channels,
            resolution,
            format,
            rangeStart,
            rangeEnd,
          );
          requestParams.delete("well_id");
          if (wellId !== undefined) {
            requestParams.append("well_id", String(wellId));
          }

          const response = await axios.get(
            `${backendUrl}/pumps?${requestParams.toString()}`,
            authConfig,
          );

          return normalizePumpPayload(response.data);
        };

        if (!isMultiWellSubset) {
          return fetchOneWell(wellIdsToSend[0]);
        }

        // Multi-well subset: request each selected well independently and merge.
        // Combined mode fetches columnar (format=columnar) → merge the per-well
        // `series` maps (wells have disjoint pumps) so the render takes the
        // efficient columnar path. Compare mode fetches legacy rows → merge the
        // `data` arrays (each row keeps well_id for the per-well split).
        const payloads = await Promise.all(
          wellIdsToSend.map((wellId) => fetchOneWell(wellId)),
        );

        const mergedSeries: Record<string, any> = {};
        payloads.forEach((payload) => {
          if (payload?.series && typeof payload.series === "object") {
            Object.assign(mergedSeries, payload.series);
          }
        });
        const hasMergedSeries = Object.keys(mergedSeries).length > 0;

        return {
          data: payloads.flatMap((payload) =>
            Array.isArray(payload?.data) ? payload.data : [],
          ),
          series: hasMergedSeries ? mergedSeries : undefined,
          row_count: payloads.reduce(
            (total, payload) =>
              total +
              (Number(payload?.row_count) ||
                (Array.isArray(payload?.data) ? payload.data.length : 0)),
            0,
          ),
          truncated: payloads.some((payload) => Boolean(payload?.truncated)),
          channels: Array.from(
            new Set(
              payloads.flatMap((payload) =>
                Array.isArray(payload?.channels) ? payload.channels : [],
              ),
            ),
          ),
        };
      };

      const fetchChartPumpPayload = async (
        channels: string[],
        resolution: string,
        format?: string,
      ) => {
        const spanMs = effectiveToDate.getTime() - effectiveFromDate.getTime();

        const ONE_HOUR_MS = 60 * 60 * 1000;
        const ONE_DAY_MS = 24 * ONE_HOUR_MS;

        let graphChunkMs: number | null = null;

        if (resolution === "1s" && spanMs > 6 * ONE_HOUR_MS) {
          graphChunkMs = 6 * ONE_HOUR_MS;
        } else if (resolution === "1m" && spanMs > 7 * ONE_DAY_MS) {
          graphChunkMs = 4 * ONE_DAY_MS;
        }

        const GRAPH_MAX_CONCURRENCY = 3;

        if (
          !graphChunkMs ||
          format !== "columnar" ||
          targetPumpName ||
          isMultiWellSubset
        ) {
          return fetchPumpPayload(channels, resolution, format);
        }

        const ranges: Array<{ start: Date; end: Date }> = [];

        let cursor = effectiveFromDate.getTime();
        const finalEnd = effectiveToDate.getTime();

        while (cursor < finalEnd) {
          const chunkEnd = Math.min(cursor + graphChunkMs, finalEnd);

          ranges.push({
            start: new Date(cursor),
            end: new Date(chunkEnd),
          });

          cursor = chunkEnd + 1;
        }

        const payloads: any[] = new Array(ranges.length);
        let nextRangeIndex = 0;

        const worker = async () => {
          while (true) {
            const rangeIndex = nextRangeIndex;
            nextRangeIndex += 1;

            if (rangeIndex >= ranges.length) {
              return;
            }

            const { start, end } = ranges[rangeIndex];

            payloads[rangeIndex] = await fetchPumpPayload(
              channels,
              resolution,
              format,
              start,
              end,
            );
          }
        };

        await Promise.all(
          Array.from(
            {
              length: Math.min(GRAPH_MAX_CONCURRENCY, ranges.length),
            },
            () => worker(),
          ),
        );

        const mergedSeries: Record<string, any> = {};

        payloads.forEach((payload) => {
          const series = payload?.series;

          if (!series || typeof series !== "object") {
            return;
          }

          Object.entries(series).forEach(([pumpName, rawPumpSeries]) => {
            const pumpSeries = rawPumpSeries as Record<string, any>;

            if (!mergedSeries[pumpName]) {
              mergedSeries[pumpName] = {};
            }

            Object.entries(pumpSeries).forEach(([key, values]) => {
              if (!Array.isArray(values)) {
                return;
              }

              if (!Array.isArray(mergedSeries[pumpName][key])) {
                mergedSeries[pumpName][key] = [];
              }

              mergedSeries[pumpName][key].push(...values);
            });
          });
        });

        return {
          data: [],
          series: mergedSeries,

          row_count: payloads.reduce(
            (total, payload) => total + (Number(payload?.row_count) || 0),
            0,
          ),

          truncated: payloads.some((payload) => Boolean(payload?.truncated)),

          channels: Array.from(
            new Set(
              payloads.flatMap((payload) =>
                Array.isArray(payload?.channels) ? payload.channels : [],
              ),
            ),
          ),

          chunk_count: payloads.length,
        };
      };

      // Chart/table split: the CHART fetches only its channels at full (1s)
      const autoCfg = getAutoResolutionConfig(
        effectiveFromDate,
        effectiveToDate,
      );
      const chartResolution = autoCfg.code;

      const LONG_RANGE_TABLE_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

      const tableResolution =
        diffMs > LONG_RANGE_TABLE_THRESHOLD_MS
          ? "1h"
          : autoCfg.code === "1s"
            ? "1m"
            : autoCfg.code;
      const newRange = {
        min: effectiveFromDate.getTime(),
        max: effectiveToDate.getTime(),
      };
      const tableChannelsForFleet =
        selectedTableChannelsByFleetRef.current[fleet.fleet] ||
        selectedTableChannelsByFleet[fleet.fleet] ||
        defaultChannels;

      // Combined view uses the compact columnar payload (typed arrays) so large
      // fleets don't materialize hundreds of MB of row objects. Compare mode keeps
      // legacy rows — it filters per well and columnar drops well_id.
      const chartMode =
        chartModeOverride ?? (chartModeByPad[effectivePad.id] || "combined");

      // Combined mode always uses columnar (typed buffers), even for a well
      // SUBSET. The backend's well-filtered columnar now returns full-resolution
      // data (verified), so a multi-well subset is fetched per-well as columnar
      // and its series merged — avoiding the heavy legacy row-object merge that
      // blanked simul-frac (2+ well) combined loads. Compare mode keeps legacy
      // rows (it needs per-row well_id for its per-well split).
      const useColumnar = chartMode === "combined";

      // TABLE: coarse-resolution load, kicked off only AFTER the chart has
      // rendered (see runTableLoad() call below) so its ~130-channel 1m fetch
      // never contends with the slow 1s chart fetch for backend workers.
      // Fire-and-forget with its own error handling so a table failure never
      // blocks the chart. Skipped for the streaming back-fill — the streaming
      // table is driven by the 1s snapshot poll, so this fetch would be wasted.
      const runTableLoad = () => {
        setTableLoadingByFleet((prev) => ({ ...prev, [fleet.fleet]: true }));
        fetchPumpPayload(tableFetchChannels, tableResolution)
          .then((tablePayload) => {
            const tableRows: PumpData[] = Array.isArray(tablePayload?.data)
              ? tablePayload.data
              : [];
            // Aggregate exactly the currently selected table channels. A later
            // table-channel change uses the existing table reload path.
            const fleetAggregations = computeAggregations(
              tableRows,
              selectedPumpTableChannels,
            );

            // Replace pump aggregates for the newly loaded range.
            setAggregatedDataByFleet((prev) => {
              if (selectedFleet?.fleet === fleet.fleet) {
                setAggregatedData(fleetAggregations);
              }

              return {
                ...prev,
                [fleet.fleet]: fleetAggregations,
              };
            });
            const loadedTableValues = tableChannelsForFleet.map(
              (channel) => channel.value,
            );
            setLoadedTableChannelsByFleet((prev) => ({
              ...prev,
              [fleet.fleet]: loadedTableValues,
            }));
            setShowTableChannelNoticeByFleet((prev) => ({
              ...prev,
              [fleet.fleet]: false,
            }));
          })
          .catch((tableError) => {
            console.error("Error loading table data:", tableError);
          })
          .finally(() => {
            setTableLoadingByFleet((prev) => ({
              ...prev,
              [fleet.fleet]: false,
            }));
          });
      };

      // CHART: the primary load — drives the button spinner and toasts.
      const pumpChartRequest =
        chartFetchChannels.length > 0
          ? fetchChartPumpPayload(
              chartFetchChannels,
              chartResolution,
              useColumnar ? "columnar" : undefined,
            ).then((data) => ({ data }))
          : Promise.resolve({
              data: {
                row_count: 0,
                truncated: false,
                channels: [],
                series: {},
                data: [],
              },
            });

      const stageChartRequest =
        stageChannelValues.length > 0
          ? axios.get(
              `${backendUrl}/treatment/channels?${buildTreatmentParams().toString()}`,
              authConfig,
            )
          : Promise.resolve({
              data: {
                row_count: 0,
                data: [],
                unavailable_channels: [],
              },
            });

      const [chartResp, stageResp] = await Promise.all([
        pumpChartRequest,
        stageChartRequest,
      ]);

      // A newer load for this fleet has started, so this response is stale.
      // Applying it is what let the initial RPM load land AFTER the VFD Amps load
      // and wipe the chart while persisting the wrong (diesel) pump selection.
      // The newer load owns the loading flags, so leave those to it.
      if (chartLoadSequenceRef.current[fleet.fleet] !== loadSequence) {
        return;
      }

      // A slower response for a previously selected well must never replace the
      // chart after the user has already switched to another well.
      if (
        wellSwitchVersion !== undefined &&
        streamingWellSwitchVersionRef.current[fleet.fleet] !== wellSwitchVersion
      ) {
        setLoadingFleetIds((prev) => {
          const next = new Set(prev);
          next.delete(fleet.fleet);
          return next;
        });
        // This response was superseded; the table/re-engagement fetches below
        // won't run, so clear the loading flags we set at the start.
        setTableLoadingByFleet((prev) => ({ ...prev, [fleet.fleet]: false }));
        setReengagementEventsLoadingByFleet((prev) => ({
          ...prev,
          [fleet.fleet]: false,
        }));
        return;
      }

      const payload = chartResp.data;
      const responseHasColumnarData = hasColumnarSeries(payload);

      console.info("Historical Load Data response", {
        fleet: fleet.fleet,
        requestedChannels: chartFetchChannels,
        requestedWellIds: wellIdsToSend,
        requestedColumnar: useColumnar,
        hasSeries: responseHasColumnarData,
        rowCount: Number(payload?.row_count) || 0,
        legacyRows: Array.isArray(payload?.data) ? payload.data.length : 0,
        payloadKeys:
          payload && typeof payload === "object" ? Object.keys(payload) : [],
      });

      const fetchedStageRows: StageChannelData[] = Array.isArray(
        stageResp.data?.data,
      )
        ? stageResp.data.data
        : [];
      const stageRows: StageChannelData[] =
        mergeWithExistingChartData && fetchedStageRows.length === 0
          ? stageDataByFleet[fleet.fleet] || []
          : fetchedStageRows;
      const stageChannelsForRender = mergeWithExistingChartData
        ? (selectedChannelsByFleetRef.current[fleet.fleet] || []).filter(
            (channel) => isStageChannel(channel.value),
          )
        : stageChartChannels;

      const stageSeries = buildStageChannelSeries(
        stageRows,
        stageChannelsForRender,
        axisSettings,
      );

      setStageDataByFleet((previous) => ({
        ...previous,
        [fleet.fleet]: stageRows,
      }));

      setStageSeriesByFleet((previous) => ({
        ...previous,
        [fleet.fleet]: stageSeries,
      }));

      if (!skipTableLoad) {
        const stageAggregations = computeStageAggregations(
          stageRows,
          stageTableChannels,
        );

        setAggregatedDataByFleet((prev) => {
          const currentRows = prev[fleet.fleet] || {};
          const currentPumpNames = Object.keys(currentRows);
          const selectedFleetPumps = selectedPumpsByFleet[fleet.fleet] || [];

          // Option 1: treatment/stage values are presented on the selected pump
          // row instead of exposing an internal synthetic "stage" row. When the
          // fleet view contains multiple selected pumps, the shared treatment
          // values are copied to each visible pump row.
          const targetPumpNames = targetPumpName
            ? [targetPumpName]
            : selectedFleetPumps.length > 0
              ? selectedFleetPumps
              : currentPumpNames;

          const merged = { ...currentRows } as Record<string, any>;

          targetPumpNames.forEach((pumpName) => {
            merged[pumpName] = {
              ...(merged[pumpName] || {}),
              ...stageAggregations,
            };
          });

          if (selectedFleet?.fleet === fleet.fleet) {
            setAggregatedData(merged);
          }

          return {
            ...prev,
            [fleet.fleet]: merged,
          };
        });
      }

      if (stageResp.data?.unavailable_channels?.length > 0) {
        console.warn(
          "Unavailable treatment channels:",
          stageResp.data.unavailable_channels,
        );
      }

      // Preserve a user-chosen pump subset across loads instead of resetting the
      // chart to every returned pump. Intersect the prior selection with the
      // pumps that actually came back; fall back to all returned pumps only when
      // there is no prior subset (first load) or none of it is present.
      const resolvePersistedPumpSelection = (
        previousSelection: string[] | undefined,
        returnedPumps: string[],
      ): string[] => {
        if (targetPumpName) return [targetPumpName];
        if (previousSelection && previousSelection.length > 0) {
          const available = new Set(returnedPumps);
          const kept = previousSelection.filter((pump) => available.has(pump));
          if (kept.length > 0) return kept;
        }
        return returnedPumps;
      };

      const setCommonChartState = (uniquePumps: string[]) => {
        setAvailablePumpsByFleet((prev) => ({
          ...prev,
          [fleet.fleet]: uniquePumps,
        }));

        setSelectedPumpsByFleet((prev) => ({
          ...prev,
          [fleet.fleet]: targetPumpName
            ? [targetPumpName]
            : resolvePersistedPumpSelection(prev[fleet.fleet], uniquePumps),
        }));

        setHiddenPumpsByFleet((prev) => ({
          ...prev,
          [fleet.fleet]: prev[fleet.fleet] ?? new Set<string>(),
        }));

        const isStreamingFleet = streamingFleetIdsRef.current.has(fleet.fleet);

        const shouldResetHistoricalXAxis =
          !preserveSelectedDateRange &&
          !mergeWithExistingChartData &&
          !isStreamingFleet;

        if (shouldResetHistoricalXAxis) {
          const pendingZoomTimer =
            historicalZoomReloadTimerRef.current[fleet.fleet];

          if (pendingZoomTimer) {
            clearTimeout(pendingZoomTimer);
            delete historicalZoomReloadTimerRef.current[fleet.fleet];
          }

          delete userZoomRangeByFleetRef.current[fleet.fleet];

          historicalZoomDetailActiveByFleetRef.current[fleet.fleet] = false;

          setXAxisRangeByFleet((prev) => ({
            ...prev,
            [fleet.fleet]: newRange,
          }));
        }

        if (!preserveSelectedDateRange) {
          setFromDateByFleet((prev) => ({
            ...prev,
            [fleet.fleet]: effectiveFromDate,
          }));

          setToDateByFleet((prev) => ({
            ...prev,
            [fleet.fleet]: effectiveToDate,
          }));
        }

        const requestedChannelValues = channelsForLoad.map(
          (channel) => channel.value,
        );

        const loadedChannelValues = mergeWithExistingChartData
          ? Array.from(
              new Set([
                ...(loadedChartChannelsByFleetRef.current[fleet.fleet] || []),
                ...requestedChannelValues,
              ]),
            )
          : requestedChannelValues;

        loadedChartChannelsByFleetRef.current = {
          ...loadedChartChannelsByFleetRef.current,
          [fleet.fleet]: loadedChannelValues,
        };

        setLoadedChartChannelsByFleet((prev) => ({
          ...prev,
          [fleet.fleet]: loadedChannelValues,
        }));
      };
      let rowCount = 0;

      if (responseHasColumnarData) {
        // Columnar: parse to typed buffers, resolve each selected channel to the
        // backend column that actually carries data (via CHANNEL_ALIASES), retain
        // the buffers for zoom, and hand Highcharts prebuilt downsampled series.
        // No raw row objects are held for the chart.
        // Let the browser paint the inline loading state and handle scrolling
        // before parsing the returned channel payload.
        await yieldToBrowser();

        const rawBuffers = parseColumnarToBuffers(payload, chartFetchChannels);
        const fetchedBuffers: FleetBuffers = new Map();
        rawBuffers.forEach((colMap, pumpName) => {
          const resolved = new Map<string, ChannelBuffer>();
          channelsForLoad.forEach((ch) => {
            const aliases = CHANNEL_ALIASES[normalizeChannelKey(ch.value)] || [
              ch.value,
            ];
            for (const alias of aliases) {
              const buf = colMap.get(alias);
              if (buf && buf.t.length > 0) {
                resolved.set(ch.value, buf);
                break;
              }
            }
          });
          if (resolved.size > 0) fetchedBuffers.set(pumpName, resolved);
        });

        const buffers: FleetBuffers = mergeWithExistingChartData
          ? new Map(fullResByFleetRef.current[fleet.fleet] || [])
          : new Map();

        fetchedBuffers.forEach((fetchedChannelMap, pumpName) => {
          const mergedChannelMap = new Map(buffers.get(pumpName) || []);
          fetchedChannelMap.forEach((buffer, channelValue) => {
            mergedChannelMap.set(channelValue, buffer);
          });
          buffers.set(pumpName, mergedChannelMap);
        });

        fullResByFleetRef.current = {
          ...fullResByFleetRef.current,
          [fleet.fleet]: buffers,
        };

        const uniquePumps = Array.from(buffers.keys()).sort();

        const matchedTargetPumpName = targetPumpName
          ? uniquePumps.find(
              (pumpName) =>
                pumpName.trim().toUpperCase() ===
                String(targetPumpName).trim().toUpperCase(),
            )
          : undefined;

        const targetPumpHasData = matchedTargetPumpName
          ? Array.from(buffers.get(matchedTargetPumpName)?.values() || []).some(
              (buffer) => buffer.t.length > 0,
            )
          : false;

        // A fleet-wide fallback can contain telemetry for many other pumps.
        // For Review Data, success means the requested alarm pump itself has data.
        rowCount = targetPumpName
          ? targetPumpHasData
            ? 1
            : 0
          : (payload?.row_count ?? uniquePumps.length);

        const pumpsForSeries = targetPumpName
          ? matchedTargetPumpName
            ? [matchedTargetPumpName]
            : []
          : uniquePumps;
        // Honor a user-chosen pump subset: build every returned pump's series
        // but mark the ones outside the selection hidden (matches the reactive
        // rebuild effect's "series exist, selection controls visibility" model).
        const resolvedPumpSelection = resolvePersistedPumpSelection(
          selectedPumpsByFleet[fleet.fleet],
          uniquePumps,
        );
        const initialHiddenPumps = targetPumpName
          ? new Set<string>()
          : new Set<string>(
              uniquePumps.filter(
                (pump) => !resolvedPumpSelection.includes(pump),
              ),
            );

        // Build the final chart from RPM plus every channel currently selected
        // by the user. Already-loaded channels are reused from retained buffers.
        const selectedChannelsForRender =
          selectedChannelsByFleetRef.current[fleet.fleet] || channelsForLoad;
        const pumpChannelsForRender = selectedChannelsForRender.filter(
          (channel) => !isStageChannel(channel.value),
        );

        await yieldToBrowser();

        const isFlaggedReengagementChart =
          isFlaggedReengagementView &&
          isDashboardPumpForFleet(fleet.fleet) &&
          Boolean(targetPumpName);

        const buffersForRender = isFlaggedReengagementChart
          ? collapseFleetBuffersToOneSecond(buffers)
          : buffers;

        const pumpSeries = downsampledToHighcharts(
          buildDownsampledSeries(
            buffersForRender,
            pumpsForSeries,
            initialHiddenPumps,
            pumpChannelsForRender,
            axisSettings,
          ),
        );

        const hcSeries: Highcharts.SeriesOptionsType[] = [...pumpSeries];

        void fetchPumpAlarms(
          targetPumpName ? [targetPumpName] : uniquePumps,
          effectiveFromDate,
          effectiveToDate,
          fleet.fleet,
        );

        fetchChartAnnotations(fleet.fleet, effectiveFromDate, effectiveToDate);

        setChartSeriesByFleet((prev) => ({ ...prev, [fleet.fleet]: hcSeries }));
        setPumpDataByFleet((prev) => ({ ...prev, [fleet.fleet]: [] }));
        setCommonChartState(uniquePumps);

        if (selectedFleet?.fleet === fleet.fleet) {
          const previousSelected = selectedPumpsByFleet[fleet.fleet] || [];

          const validPreviousSelected = previousSelected.filter((pumpName) =>
            uniquePumps.includes(pumpName),
          );

          const pumpsToKeep = targetPumpName
            ? [targetPumpName]
            : validPreviousSelected.length > 0
              ? validPreviousSelected
              : uniquePumps;

          const hiddenToKeep =
            hiddenPumpsByFleet[fleet.fleet] ?? new Set<string>();

          const rangeToKeep =
            xAxisRangeByFleet[fleet.fleet]?.min !== undefined &&
            xAxisRangeByFleet[fleet.fleet]?.max !== undefined
              ? xAxisRangeByFleet[fleet.fleet]
              : newRange;

          setPumpData([]);
          setChartSeries(hcSeries);
          setAvailablePumps(uniquePumps);

          setSelectedPumps(pumpsToKeep);
          selectedPumpsRef.current = pumpsToKeep;

          setHiddenPumps(new Set(hiddenToKeep));

          setXAxisRange(rangeToKeep);
        }
      } else {
        // Legacy rows (compare mode / multi-well subset). Drop any prebuilt
        // columnar series + buffers so the row-based render takes over cleanly.
        const allRows: PumpData[] = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.rows)
              ? payload.rows
              : Array.isArray(payload?.results)
                ? payload.results
                : [];
        const normalizedRows: PumpData[] = allRows
          .map((row: any, index: number) => {
            const rawTimestamp =
              getRowValueByNormalizedKey(row, "time_stamp") ??
              getRowValueByNormalizedKey(row, "timestamp") ??
              getRowValueByNormalizedKey(row, "pump_timestamp") ??
              getRowValueByNormalizedKey(row, "event_time") ??
              getRowValueByNormalizedKey(row, "datetime");

            let normalizedTimestamp =
              typeof rawTimestamp === "string"
                ? new Date(rawTimestamp).getTime()
                : Number(rawTimestamp);

            if (
              Number.isFinite(normalizedTimestamp) &&
              normalizedTimestamp < 1_000_000_000_000
            ) {
              normalizedTimestamp *= 1000;
            }

            const pumpName = String(
              getRowValueByNormalizedKey(row, "pump_name") ??
                getRowValueByNormalizedKey(row, "pump") ??
                getRowValueByNormalizedKey(row, "source") ??
                "",
            ).trim();

            const canonicalRow: Record<string, any> = {
              ...row,
              id: row?.id ?? normalizedTimestamp + index,
              time_stamp: normalizedTimestamp,
              pump_name: pumpName,
            };

            channelsForLoad.forEach((channel) => {
              const value = getChannelValue(row, channel.value);
              if (value !== null && value !== undefined) {
                canonicalRow[channel.value] = value;
              }
            });

            return canonicalRow as PumpData;
          })
          .filter(
            (row) =>
              Boolean(row.pump_name) && Number.isFinite(Number(row.time_stamp)),
          );

        const normalizedTargetPumpName = targetPumpName
          ? String(targetPumpName).trim().toUpperCase()
          : "";

        const rows: PumpData[] = targetPumpName
          ? normalizedRows.filter(
              (row) =>
                String(row.pump_name).trim().toUpperCase() ===
                normalizedTargetPumpName,
            )
          : normalizedRows;
        const uniquePumps = Array.from(
          new Set(rows.map((item) => item.pump_name)),
        ).sort();
        // Honor a user-chosen pump subset (see the columnar path above).
        const resolvedPumpSelection = resolvePersistedPumpSelection(
          selectedPumpsByFleet[fleet.fleet],
          uniquePumps,
        );
        const initialHiddenPumps = targetPumpName
          ? new Set<string>()
          : new Set<string>(
              uniquePumps.filter(
                (pump) => !resolvedPumpSelection.includes(pump),
              ),
            );
        const legacyPumpSeries = buildChartSeriesWithChannels(
          rows,
          targetPumpName ? [targetPumpName] : uniquePumps,
          initialHiddenPumps,
          pumpChartChannels,
          axisSettings,
        );

        const combinedLegacySeries: Highcharts.SeriesOptionsType[] = [
          ...legacyPumpSeries,
        ];
        rowCount = rows.length;

        console.info("Load Data legacy chart response", {
          fleet: fleet.fleet,
          requestedWellIds: wellIdsToSend,
          requestedChannels: chartFetchChannels,
          payloadRowCount: Number(payload?.row_count) || 0,
          normalizedRowCount: rows.length,
          uniquePumpCount: uniquePumps.length,
          range: {
            from: effectiveFromDate.getTime(),
            to: effectiveToDate.getTime(),
          },
        });

        setChartSeriesByFleet((previous) => ({
          ...previous,
          [fleet.fleet]: combinedLegacySeries,
        }));
        if (fleet.fleet in fullResByFleetRef.current) {
          const nextBuffers = { ...fullResByFleetRef.current };
          delete nextBuffers[fleet.fleet];
          fullResByFleetRef.current = nextBuffers;
        }

        const isFlaggedReengagementChart =
          isFlaggedReengagementView &&
          isDashboardPumpForFleet(fleet.fleet) &&
          Boolean(targetPumpName);

        void fetchPumpAlarms(
          targetPumpName ? [targetPumpName] : uniquePumps,
          effectiveFromDate,
          effectiveToDate,
          fleet.fleet,
        );

        fetchChartAnnotations(fleet.fleet, effectiveFromDate, effectiveToDate);

        setPumpDataByFleet((prev) => ({ ...prev, [fleet.fleet]: rows }));
        setCommonChartState(uniquePumps);

        if (selectedFleet?.fleet === fleet.fleet) {
          const previousSelected = selectedPumpsByFleet[fleet.fleet] || [];

          const validPreviousSelected = previousSelected.filter((pumpName) =>
            uniquePumps.includes(pumpName),
          );

          const pumpsToKeep = targetPumpName
            ? [targetPumpName]
            : validPreviousSelected.length > 0
              ? validPreviousSelected
              : uniquePumps;

          const hiddenToKeep =
            hiddenPumpsByFleet[fleet.fleet] ?? new Set<string>();

          const rangeToKeep =
            xAxisRangeByFleet[fleet.fleet]?.min !== undefined &&
            xAxisRangeByFleet[fleet.fleet]?.max !== undefined
              ? xAxisRangeByFleet[fleet.fleet]
              : newRange;

          setPumpData(rows);
          setChartSeries(combinedLegacySeries);
          setAvailablePumps(uniquePumps);

          setSelectedPumps(pumpsToKeep);
          selectedPumpsRef.current = pumpsToKeep;

          setHiddenPumps(new Set(hiddenToKeep));

          setXAxisRange(rangeToKeep);
        }
      }

      const secondaryLoadDelayMs =
        chartResolution === "1m" && diffMs > 14 * 24 * 60 * 60 * 1000
          ? 1500
          : 0;

      if (!skipTableLoad) {
        window.setTimeout(() => runTableLoad(), secondaryLoadDelayMs);
      }

      const MAX_AUTO_REENGAGEMENT_RANGE_MS = 14 * 24 * 60 * 60 * 1000;

      const shouldLoadReengagementEvents =
        !skipReengagementRefresh && diffMs <= MAX_AUTO_REENGAGEMENT_RANGE_MS;

      if (shouldLoadReengagementEvents) {
        window.setTimeout(() => {
          void fetchPumpReengagementEvents(
            fleet.fleet,
            effectiveFromDate,
            effectiveToDate,
          );
        }, secondaryLoadDelayMs);
      } else {
        setReengagementEventsLoadingByFleet((prev) => ({
          ...prev,
          [fleet.fleet]: false,
        }));
      }

      if (payload?.truncated) {
        toast.warning(
          "Showing partial results. Pick a coarser resolution or narrower range.",
        );
      } else if (rowCount === 0) {
        requestOptions?.onNoData?.();

        if (!requestOptions?.suppressNoDataToast) {
          toast.info(
            requestOptions?.noDataToastMessage ||
              "No data for the selected window.",
          );
        }
      } else if (!suppressSuccessToast) {
        toast.success(successToastMessage || "Data loaded successfully");
      }
      setLoadingFleetIds((prev) => {
        const next = new Set(prev);
        next.delete(fleet.fleet);
        return next;
      });

      // Return the result of this exact request. React state updates above are
      // asynchronous, so callers must not inspect pumpDataByFleet/chartSeries
      // immediately after awaiting loadData.
      return rowCount > 0;
    } catch (error) {
      console.error("Error loading pump data:", error);
      const code = axios.isAxiosError(error) && error.response?.data?.code;
      if (code === "RANGE_TOO_LARGE") {
        toast.error(
          "Range too wide for this resolution. Pick a coarser resolution or narrower window.",
        );
      } else {
        toast.error("Failed to load pump data");
      }
      setLoadingFleetIds((prev) => {
        const next = new Set(prev);
        next.delete(fleet.fleet);
        return next;
      });
      // The chart request failed, so the table/re-engagement fetches below never
      // ran — clear their loading flags so they don't spin forever.
      setTableLoadingByFleet((prev) => ({ ...prev, [fleet.fleet]: false }));
      setReengagementEventsLoadingByFleet((prev) => ({
        ...prev,
        [fleet.fleet]: false,
      }));

      return false;
    }
  };

  // Reload only the chart for a historical zoom. Keep table/re-engagement data
  // and the user's selected date range untouched. loadData will automatically pick
  // 1s / 1m / 1h from the zoomed interval via getAutoResolutionConfig().
  const reloadCombinedHistoricalZoomRange = (
    fleet: FleetGroup,
    rangeStart: Date,
    rangeEnd: Date,
    channels: PumpChannel[],
    preserveSelectedDateRange: boolean,
  ) =>
    loadData(
      rangeStart,
      rangeEnd,
      fleet,
      undefined, // targetPumpName
      false, // showModal
      channels,
      "combined",
      true, // skipReengagementRefresh
      true, // skipTableLoad
      undefined, // wellSwitchVersion
      undefined, // wellIdsOverride
      undefined, // showAllWellsOverride
      false, // mergeWithExistingChartData
      undefined, // successToastMessage
      true, // suppressSuccessToast
      preserveSelectedDateRange,
    );

  const resetCombinedHistoricalZoom = (fleetId: number) => {
    const fleetForZoom = fleetGroups.find((group) => group.fleet === fleetId);

    const baseFrom = fromDateByFleet[fleetId];
    const baseTo = toDateByFleet[fleetId];

    if (!fleetForZoom || !baseFrom || !baseTo) {
      return;
    }

    const pendingTimer = historicalZoomReloadTimerRef.current[fleetId];

    if (pendingTimer) {
      clearTimeout(pendingTimer);
      delete historicalZoomReloadTimerRef.current[fleetId];
    }

    // Clear the current manual zoom.
    delete userZoomRangeByFleetRef.current[fleetId];

    historicalZoomDetailActiveByFleetRef.current[fleetId] = false;

    // Restore the original From / To range on the X axis.
    setXAxisRangeByFleet((prev) => ({
      ...prev,
      [fleetId]: {
        min: baseFrom.getTime(),
        max: baseTo.getTime(),
      },
    }));

    const channelsForReset =
      selectedChannelsByFleetRef.current[fleetId] || selectedChannels;

    // Reload original range using its normal automatic resolution.
    void reloadCombinedHistoricalZoomRange(
      fleetForZoom,
      baseFrom,
      baseTo,
      channelsForReset,
      false,
    );
  };

  // Restore the date range that was active before a well-activity band was
  // clicked, and drop the stage focus. Backs the "Reset Graph" button in the
  // chart actions row.
  const resetStageSelection = useCallback(
    (fleetId: number) => {
      const selection = stageSelectionByFleet[fleetId];
      if (!selection) return;

      // The auto-load effect keys off selectedFleet, so make sure this fleet
      // is the selected one or the restored range would not reload.
      const fleetForStage = fleetGroups.find(
        (group) => group.fleet === fleetId,
      );
      if (fleetForStage) setSelectedFleet(fleetForStage);

      setDateRefreshPendingFleetIds((previous) =>
        new Set(previous).add(fleetId),
      );
      setFromDateByFleet((prev) => ({
        ...prev,
        [fleetId]: selection.prevFrom,
      }));
      setToDateByFleet((prev) => ({ ...prev, [fleetId]: selection.prevTo }));
      setStageSelectionByFleet((prev) => {
        const next = { ...prev };
        delete next[fleetId];
        return next;
      });
    },
    [stageSelectionByFleet, fleetGroups],
  );

  const getChannelSelectionSignature = (channels: PumpChannel[] | string[]) =>
    channels
      .map((channel) =>
        typeof channel === "string" ? channel : String(channel.value),
      )
      .sort()
      .join("|");

  const scheduleHistoricalChartChannelReload = (fleetGroup: FleetGroup) => {
    const fleetId = fleetGroup.fleet;

    if (streamingFleetIdsRef.current.has(fleetId)) {
      return;
    }

    const existingTimer = chartChannelReloadTimerRef.current[fleetId];
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    chartChannelReloadTimerRef.current[fleetId] = setTimeout(async () => {
      delete chartChannelReloadTimerRef.current[fleetId];

      const latestChannels = selectedChannelsByFleetRef.current[fleetId] || [];

      const latestSignature = getChannelSelectionSignature(latestChannels);

      const loadedSignature = getChannelSelectionSignature(
        loadedChartChannelsByFleetRef.current[fleetId] || [],
      );

      const currentFrom = fromDateByFleetRef.current[fleetId];
      const currentTo = toDateByFleetRef.current[fleetId];

      if (
        !currentFrom ||
        !currentTo ||
        currentTo.getTime() <= currentFrom.getTime()
      ) {
        return;
      }

      const loadedValues = new Set(
        loadedChartChannelsByFleetRef.current[fleetId] || [],
      );

      const missingChannels = latestChannels.filter(
        (channel) => !loadedValues.has(channel.value),
      );

      if (
        !latestSignature ||
        latestSignature === loadedSignature ||
        missingChannels.length === 0
      ) {
        chartChannelReloadQueuedRef.current[fleetId] = false;
        return;
      }

      // Stage/treatment channels share a separate endpoint...
      // Stage/treatment channels share a separate endpoint and aggregation path.
      // Keep their existing full refresh behavior; pump channels use the lighter
      // incremental merge path below.
      const containsStageChannel = missingChannels.some((channel) =>
        isStageChannel(channel.value),
      );

      // Do not stack duplicate historical requests. If another dropdown close
      // happens while one request is running, remember only the latest selection
      // and evaluate it once after the current request completes.
      if (chartChannelReloadInFlightRef.current[fleetId]) {
        chartChannelReloadQueuedRef.current[fleetId] = true;
        return;
      }

      chartChannelReloadInFlightRef.current[fleetId] = true;
      chartChannelReloadQueuedRef.current[fleetId] = false;

      const frozenChannels = (
        containsStageChannel ? latestChannels : missingChannels
      ).map((channel) => ({ ...channel }));
      const selectedPumpForLoad = isDashboardPumpForFleet(fleetId)
        ? dashboardPumpParam || undefined
        : undefined;

      try {
        // loadData updates the chart only after the response is ready, so the
        // previously loaded chart remains visible throughout this refresh.
        await loadData(
          currentFrom,
          currentTo,
          fleetGroup,
          selectedPumpForLoad,
          false,
          frozenChannels,
          undefined,
          false,
          true,
          undefined,
          undefined,
          undefined,
          !containsStageChannel,
        );
      } finally {
        chartChannelReloadInFlightRef.current[fleetId] = false;

        if (chartChannelReloadQueuedRef.current[fleetId]) {
          chartChannelReloadQueuedRef.current[fleetId] = false;
          scheduleHistoricalChartChannelReload(fleetGroup);
        }
      }
    }, 150);
  };

  const handleHistoricalChannelDropdownBlur = (
    event: React.FocusEvent<HTMLDivElement>,
    fleetGroup: FleetGroup,
  ) => {
    const container = event.currentTarget;
    const nextTarget = event.relatedTarget as Node | null;

    if (nextTarget && container.contains(nextTarget)) {
      return;
    }

    // Let the custom select finish its close state first. Internal option clicks
    // can briefly move focus; aria-expanded prevents those from being treated as
    // a completed dropdown close.
    window.setTimeout(() => {
      if (container.contains(document.activeElement)) {
        return;
      }

      scheduleHistoricalChartChannelReload(fleetGroup);
    }, 0);
  };

  useEffect(() => {
    const handleFleetChannelClose = (event: MouseEvent | KeyboardEvent) => {
      const isEscape = event instanceof KeyboardEvent && event.key === "Escape";
      const clickTarget =
        event instanceof MouseEvent ? (event.target as Node | null) : null;

      Object.entries(fleetChannelContainerRefsRef.current).forEach(
        ([fleetIdText, container]) => {
          if (!container) return;

          const fleetId = Number(fleetIdText);
          const clickedOutside =
            event instanceof MouseEvent &&
            Boolean(clickTarget) &&
            !container.contains(clickTarget);

          if (!isEscape && !clickedOutside) return;

          const group = fleetGroups.find((item) => item.fleet === fleetId);
          if (!group) return;

          const selectedSignature = getChannelSelectionSignature(
            selectedChannelsByFleetRef.current[fleetId] || [],
          );
          const loadedSignature = getChannelSelectionSignature(
            loadedChartChannelsByFleetRef.current[fleetId] || [],
          );

          if (selectedSignature && selectedSignature !== loadedSignature) {
            scheduleHistoricalChartChannelReload(group);
          }
        },
      );
    };

    document.addEventListener("mousedown", handleFleetChannelClose, true);
    document.addEventListener("keydown", handleFleetChannelClose, true);

    return () => {
      document.removeEventListener("mousedown", handleFleetChannelClose, true);
      document.removeEventListener("keydown", handleFleetChannelClose, true);
    };
  }, [fleetGroups]);

  const handleMainLoad = async (
    targetFleet?: FleetGroup,
    startDate?: Date,
    endDate?: Date,
  ) => {
    const fleet = targetFleet ?? selectedFleet;

    if (!fleet) {
      toast.warning("Please select a fleet");
      return;
    }

    const fleetId = fleet.fleet;

    /*
     * Use the URL pump only for the fleet opened from Flagged Re-engagement.
     * Reusing it for another fleet caused requests such as Fleet 41 +
     * a Fleet 30 pump, which returned an empty chart.
     */
    const selectedPumpForLoad = isDashboardPumpForFleet(fleetId)
      ? dashboardPumpParam || undefined
      : undefined;

    // Read the REF first: ensureDefaultChannelsForFleet may have just applied the
    // resolved defaults, and the corresponding state update is not visible in
    // this closure yet. Using state here loaded the stale RPM seed instead.
    const channelsForLoad =
      (fleetId != null && selectedChannelsByFleetRef.current[fleetId]?.length
        ? selectedChannelsByFleetRef.current[fleetId]
        : undefined) ||
      (fleetId != null && selectedChannelsByFleet[fleetId]?.length
        ? selectedChannelsByFleet[fleetId]
        : undefined) ||
      (selectedChannels.length ? selectedChannels : undefined);

    await loadData(
      startDate,
      endDate,
      fleet,
      selectedPumpForLoad,
      false, // Normal Fleet expansion/date refresh uses inline loading only.
      channelsForLoad,
      undefined,
      false,
      false,
      undefined,
      undefined,
      undefined,
      false,
      undefined,
      true, // Do not show "Data loaded successfully" for automatic loads.
    );
  };

  // Fleet "Load Data": date picker edits are only drafts until this is
  // pressed. A changed range is committed to fromDateByFleet/toDateByFleet and
  // loaded by the historical auto-load effect (the same path a stage click or
  // Reset Graph uses); an unchanged range is reloaded directly.
  const handleFleetLoadData = (fleet: FleetGroup) => {
    const fleetId = fleet.fleet;
    const draft = draftDatesByFleet[fleetId];
    const loadedFrom = fromDateByFleet[fleetId] ?? null;
    const loadedTo = toDateByFleet[fleetId] ?? null;
    const nextFrom = draft?.from ?? loadedFrom;
    const nextTo = draft?.to ?? loadedTo;

    if (!nextFrom || !nextTo || nextTo.getTime() <= nextFrom.getTime()) return;

    const rangeChanged =
      nextFrom.getTime() !== loadedFrom?.getTime() ||
      nextTo.getTime() !== loadedTo?.getTime();

    setDraftDatesByFleet((previous) => {
      if (!previous[fleetId]) return previous;
      const next = { ...previous };
      delete next[fleetId];
      return next;
    });
    setDateRefreshPendingFleetIds((previous) => new Set(previous).add(fleetId));

    if (rangeChanged) {
      // The auto-load effect keys off selectedFleet.
      setSelectedFleet(fleet);
      setFromDateByFleet((previous) => ({ ...previous, [fleetId]: nextFrom }));
      setToDateByFleet((previous) => ({ ...previous, [fleetId]: nextTo }));
      return;
    }

    void handleMainLoad(fleet, nextFrom, nextTo).finally(() => {
      setDateRefreshPendingFleetIds((previous) => {
        const next = new Set(previous);
        next.delete(fleetId);
        return next;
      });
    });
  };

  const scheduleAssetChartChannelReload = () => {
    if (assetChannelReloadTimerRef.current) {
      clearTimeout(assetChannelReloadTimerRef.current);
    }

    assetChannelReloadTimerRef.current = setTimeout(async () => {
      assetChannelReloadTimerRef.current = null;

      // CustomMultiSelect can keep focus inside its menu and may not emit a
      // normal blur. Wait until aria-expanded is false so the request happens
      // only after the dropdown has actually closed.
      const container = assetChannelContainerRef.current;
      if (container?.querySelector('[aria-expanded="true"]')) {
        scheduleAssetChartChannelReload();
        return;
      }

      const latestChannels = selectedAssetChannelsRef.current;
      const latestSignature = getChannelSelectionSignature(latestChannels);

      // Clear All is handled immediately in handleChartChannelChange and should
      // never make a backend request.
      if (!latestSignature) {
        assetChannelReloadQueuedRef.current = false;
        return;
      }

      if (
        !selectedAssetPumps.length ||
        !fromDate ||
        !toDate ||
        latestSignature === loadedAssetChannelSignatureRef.current
      ) {
        assetChannelReloadQueuedRef.current = false;
        return;
      }

      if (assetChannelReloadInFlightRef.current) {
        assetChannelReloadQueuedRef.current = true;
        return;
      }

      assetChannelReloadInFlightRef.current = true;
      assetChannelReloadQueuedRef.current = false;

      try {
        await loadAssetData(fromDate, toDate, undefined, "chart");
      } finally {
        assetChannelReloadInFlightRef.current = false;
        if (assetChannelReloadQueuedRef.current) {
          assetChannelReloadQueuedRef.current = false;
          scheduleAssetChartChannelReload();
        }
      }
    }, 200);
  };

  const handleAssetChannelDropdownBlur = () => {
    // onChange also schedules this. Blur is retained as a second close signal
    // for keyboard users and custom-select implementations that move focus.
    scheduleAssetChartChannelReload();
  };

  const clearAssetSlowLoadTimer = () => {
    if (assetSlowLoadTimerRef.current) {
      clearTimeout(assetSlowLoadTimerRef.current);
      assetSlowLoadTimerRef.current = null;
    }
  };

  const clearAssetLoadedResults = () => {
    clearAssetSlowLoadTimer();

    if (assetAutoLoadTimerRef.current) {
      clearTimeout(assetAutoLoadTimerRef.current);
      assetAutoLoadTimerRef.current = null;
    }

    assetAutoLoadSignatureRef.current = "";
    loadedAssetChannelSignatureRef.current = "";

    // Invalidate any alarm request still running for the previous Asset result.
    alarmFetchVersionRef.current += 1;

    setPumpData([]);
    setAggregatedData({});
    setChartSeries([]);
    setXAxisRange({});

    setAvailablePumps([]);
    setSelectedPumps([]);
    selectedPumpsRef.current = [];
    setHiddenPumps(new Set<string>());

    setSelectedAssetTablePumps([]);
    assetTablePumpFilterInitializedRef.current = false;

    setAlarmEvents([]);
    setSelectedAlarmEvents([]);

    setIsLoadDataLoading(false);
    setIsAssetTableLoading(false);
    setAssetLoadStatusText("Loading selected pump data…");
    setAssetLoadErrorText("");
    setAssetDetailStatusText("");
    setAssetHighResolutionLoading(false);
  };

  const handleAssetPumpSelectionChange = (next: AssetPumpOption[]) => {
    // Any response that started for the previous top-level pump selection is stale.
    assetPumpSelectionGenerationRef.current += 1;
    setSelectedAssetPumps(next);

    // Always clear the old chart/grid immediately. This also fixes the empty
    // selection case where the old Asset data previously stayed on screen.
    clearAssetLoadedResults();
  };

  const loadAssetData = async (
    startDate?: Date,
    endDate?: Date,
    forcedTableChannels?: PumpChannel[],
    loadingTarget: "chart" | "table" | "both" = "both",
  ) => {
    const effectiveFromDate = startDate || fromDate;
    const effectiveToDate = endDate || toDate;

    if (!effectiveFromDate || !effectiveToDate) {
      toast.warning("Please select a date range");
      return false;
    }

    if (selectedAssetPumps.length === 0) {
      return false;
    }

    const diffMs = effectiveToDate.getTime() - effectiveFromDate.getTime();
    const maxAllowedEndDate = new Date(effectiveFromDate);
    maxAllowedEndDate.setMonth(maxAllowedEndDate.getMonth() + 6);

    if (diffMs <= 0) {
      toast.warning("End date must be after start date");
      return false;
    }

    if (effectiveToDate.getTime() > maxAllowedEndDate.getTime()) {
      toast.warning("Please select a date range within 6 months");
      return false;
    }

    const showChartLoading =
      loadingTarget === "chart" || loadingTarget === "both";
    const showTableLoading =
      loadingTarget === "table" || loadingTarget === "both";

    // Every new Asset load owns a new generation. This invalidates an older
    // date/channel/pump request and any 1-second background refinement it started,
    // so stale detail can never overwrite a newer user selection.
    const pumpSelectionGeneration = ++assetPumpSelectionGenerationRef.current;

    // A new chart load replaces the data a zoom was drawn from.
    if (showChartLoading) {
      clearAssetZoom();
    }

    clearAssetSlowLoadTimer();
    setAssetLoadStatusText("Loading selected pump data…");
    setAssetLoadErrorText("");
    setAssetDetailStatusText("");
    setAssetHighResolutionLoading(false);

    assetSlowLoadTimerRef.current = setTimeout(() => {
      if (assetPumpSelectionGenerationRef.current === pumpSelectionGeneration) {
        setAssetLoadStatusText(
          "Still checking this time range… Some selected pumps may not have telemetry.",
        );
      }
    }, 6000);

    try {
      if (showChartLoading) {
        setIsLoadDataLoading(true);

        // Do not leave alarm markers from the previous Asset request on screen.
        alarmFetchVersionRef.current += 1;
        setAlarmEvents([]);
        setSelectedAlarmEvents([]);
      }

      if (showTableLoading) {
        setIsAssetTableLoading(true);
      }

      const token = getAuthToken();

      if (!token) {
        toast.error("Authentication required");
        return false;
      }

      const channelsForAssetLoad = filterUnsupportedPumpChannels(
        selectedAssetChannelsRef.current,
      );
      const tableChannelsForAssetLoad = filterUnsupportedPumpChannels(
        forcedTableChannels ?? selectedTableChannels,
      );

      if (channelsForAssetLoad.length === 0) {
        setChartSeries([]);
        return false;
      }

      const params = new URLSearchParams();
      params.append("start_time", String(effectiveFromDate.getTime()));
      params.append("end_time", String(effectiveToDate.getTime()));

      const assetResolution = getAutoResolutionConfig(
        effectiveFromDate,
        effectiveToDate,
      ).code;
      params.append("resolution", assetResolution);

      const assetPumpNamesForRequest = Array.from(
        new Set(
          selectedAssetPumps.flatMap((pump) =>
            pump.rawPumpNames?.length > 0 ? pump.rawPumpNames : [pump.value],
          ),
        ),
      );

      assetPumpNamesForRequest.forEach((pumpName) => {
        params.append("pump_name", pumpName);
      });

      // Asset selections come directly from /pumps/list raw names, so the backend
      // can use an index-friendly exact pump_name predicate instead of wrapping the
      // telemetry column in REPLACE/UPPER for every row. This is especially helpful
      // for pumps that have no telemetry in the requested window.
      params.append("pump_name_match", "exact");

      // Send canonical UI channel names only. The backend owns the historical
      // alias/coalesce mapping, so sending every alias as a separate requested
      // column made each raw 1-second row unnecessarily wide.
      const assetDisplayChannels = Array.from(
        new Set([
          ...channelsForAssetLoad.map((channel) => channel.value),
          ...tableChannelsForAssetLoad.map((channel) => channel.value),
        ]),
      );

      const assetFetchChannels = Array.from(
        new Set([...assetDisplayChannels, "calculated_discharge_rate"]),
      );

      params.append("channels", assetFetchChannels.join(","));
      params.append("non_null_channels", assetDisplayChannels.join(","));

      // Asset only needs pump_name, time_stamp and requested channels. Tell the
      // backend not to return id/pad/well/stage/fleet on every telemetry row.
      params.append("asset_compact", "1");

      // For the required <=3-day 1-second Asset load, first do a very small
      // 1-minute existence probe. If the rollup table can represent every
      // selected display channel and confirms there is no telemetry, skip the
      // expensive raw 1-second scan. When data exists, the real request below
      // still uses 1-second resolution, so the client resolution requirement
      // remains unchanged.
      if (assetResolution === "1s") {
        const probeParams = new URLSearchParams();
        probeParams.append("start_time", String(effectiveFromDate.getTime()));
        probeParams.append("end_time", String(effectiveToDate.getTime()));
        probeParams.append("resolution", "1m");

        assetPumpNamesForRequest.forEach((pumpName) => {
          probeParams.append("pump_name", pumpName);
        });

        probeParams.append("pump_name_match", "exact");
        probeParams.append("channels", assetFetchChannels.join(","));
        probeParams.append("non_null_channels", assetDisplayChannels.join(","));
        probeParams.append("asset_compact", "1");
        probeParams.append("availability_only", "1");

        try {
          const probeResponse = await axios.get(
            `${backendUrl}/pumps?${probeParams.toString()}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 20000,
            },
          );

          if (
            assetPumpSelectionGenerationRef.current !== pumpSelectionGeneration
          ) {
            return false;
          }

          const probeSupported = probeResponse.data?.supported === true;
          const probeHasData = probeResponse.data?.has_data === true;

          if (probeSupported && !probeHasData) {
            setPumpData([]);
            setChartSeries([]);
            setAggregatedData({});
            setAvailablePumps([]);
            setSelectedPumps([]);
            selectedPumpsRef.current = [];
            setHiddenPumps(new Set<string>());
            setSelectedAssetTablePumps([]);
            assetTablePumpFilterInitializedRef.current = false;
            setAlarmEvents([]);
            setSelectedAlarmEvents([]);
            setXAxisRange({
              min: effectiveFromDate.getTime(),
              max: effectiveToDate.getTime(),
            });
            setAssetLoadedRange({
              start: effectiveFromDate.getTime(),
              end: effectiveToDate.getTime(),
            });
            setAssetLoadErrorText("");

            loadedAssetChannelSignatureRef.current =
              getChannelSelectionSignature(channelsForAssetLoad);

            toast.info(
              "No telemetry found for the selected pumps in this time range.",
            );
            return false;
          }
        } catch (probeError) {
          // The probe is an optimization only. If it is unavailable or times out,
          // preserve correctness by continuing with the required 1-second query.
          console.warn(
            "Asset availability probe failed; continuing with 1-second load:",
            probeError,
          );
        }
      }

      let response: { data: any };

      if (assetResolution === "1s") {
        // 1-second loads go straight to raw 1s data (chart and table channels
        // together), with no 1-minute preview. Up to 6 h is a single request;
        // a longer range (up to the 3-day 1s limit) is split into 6 h pieces,
        // the same piece size the Fleet chart uses, so no single request gets
        // near the backend's 1,000,000-row cap. Pieces run 3 at a time.
        const ASSET_1S_CHUNK_MS = 6 * 60 * 60 * 1000;
        const ASSET_1S_MAX_CONCURRENCY = 3;

        const chunkRanges: Array<{ start: number; end: number }> = [];
        const finalEnd = effectiveToDate.getTime();
        let chunkStart = effectiveFromDate.getTime();
        while (chunkStart < finalEnd) {
          const chunkEnd = Math.min(chunkStart + ASSET_1S_CHUNK_MS, finalEnd);
          chunkRanges.push({ start: chunkStart, end: chunkEnd });
          chunkStart = chunkEnd + 1;
        }

        const chunkPayloads: any[] = new Array(chunkRanges.length);
        let nextChunkIndex = 0;
        const worker = async () => {
          while (nextChunkIndex < chunkRanges.length) {
            const chunkIndex = nextChunkIndex;
            nextChunkIndex += 1;

            const chunkParams = new URLSearchParams(params);
            chunkParams.set("start_time", String(chunkRanges[chunkIndex].start));
            chunkParams.set("end_time", String(chunkRanges[chunkIndex].end));

            const chunkResponse = await axios.get(
              `${backendUrl}/pumps?${chunkParams.toString()}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 180000,
              },
            );
            chunkPayloads[chunkIndex] = chunkResponse.data;
          }
        };

        await Promise.all(
          Array.from(
            {
              length: Math.min(ASSET_1S_MAX_CONCURRENCY, chunkRanges.length),
            },
            () => worker(),
          ),
        );

        response = {
          data: {
            data: chunkPayloads.flatMap((chunkPayload) =>
              Array.isArray(chunkPayload?.data) ? chunkPayload.data : [],
            ),
            truncated: chunkPayloads.some((chunkPayload) =>
              Boolean(chunkPayload?.truncated),
            ),
          },
        };
      } else {
        response = await axios.get(`${backendUrl}/pumps?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        });
      }

      // The top Pumps selection changed while this request was in flight.
      if (assetPumpSelectionGenerationRef.current !== pumpSelectionGeneration) {
        return false;
      }

      setAssetLoadErrorText("");

      const payload = response.data;
      const rows: PumpData[] = (
        Array.isArray(payload?.data) ? payload.data : []
      ).map((row: PumpData) => ({
        ...row,
        pump_name: String(row.pump_name || "")
          .replace(/-/g, "")
          .toUpperCase(),
      }));

      // Give the browser one frame to paint the loading state before the heavier
      // aggregation and chart state updates run.
      await new Promise<void>((resolve) =>
        window.requestAnimationFrame(() => resolve()),
      );

      if (assetPumpSelectionGenerationRef.current !== pumpSelectionGeneration) {
        return false;
      }

      setPumpData(rows);

      const uniquePumps = Array.from(
        new Set(rows.map((item) => item.pump_name).filter(Boolean)),
      ).sort();

      // selectedPumps now represents all loaded Asset pumps for chart rendering.
      // Individual chart visibility is handled by the Highcharts legend.
      setAvailablePumps(uniquePumps);
      setSelectedPumps(uniquePumps);
      selectedPumpsRef.current = uniquePumps;
      setHiddenPumps(new Set<string>());

      // Initialize the table pump filter once for a newly selected top-level pump
      // set. Later date/channel reloads preserve pumps the user intentionally hid,
      // while automatically adding a pump that becomes available for the first time.
      if (!assetTablePumpFilterInitializedRef.current) {
        setSelectedAssetTablePumps(uniquePumps);
        assetTablePumpFilterInitializedRef.current = uniquePumps.length > 0;
      } else {
        setSelectedAssetTablePumps((previous) => {
          const nextAvailableSet = new Set(uniquePumps);
          const previousAvailableSet = new Set(availablePumps);

          const stillSelected = previous.filter((pump) =>
            nextAvailableSet.has(pump),
          );
          const newlyAvailable = uniquePumps.filter(
            (pump) => !previousAvailableSet.has(pump),
          );

          return Array.from(
            new Set([...stillSelected, ...newlyAvailable]),
          ).sort();
        });
      }

      setAggregatedData(computeAggregations(rows, tableChannelsForAssetLoad));

      setXAxisRange({
        min: effectiveFromDate.getTime(),
        max: effectiveToDate.getTime(),
      });
      setAssetLoadedRange({
        start: effectiveFromDate.getTime(),
        end: effectiveToDate.getTime(),
      });

      if (payload?.truncated) {
        toast.warning(
          "Showing partial results. Pick a coarser resolution or narrower range.",
        );
      } else if (rows.length === 0) {
        toast.info(
          "No telemetry found for the selected pumps in this time range.",
        );
      } else {
        const returnedPumpSet = new Set(uniquePumps);
        const pumpsWithoutData = selectedAssetPumps
          .map((pump) => pump.value)
          .filter((pump) => !returnedPumpSet.has(pump));

        if (pumpsWithoutData.length > 0) {
          toast.info(
            `${pumpsWithoutData.length} selected ${
              pumpsWithoutData.length === 1 ? "pump has" : "pumps have"
            } no data in this time range.`,
          );
        } else {
          toast.success("Asset data loaded successfully");
        }
      }

      loadedAssetChannelSignatureRef.current =
        getChannelSelectionSignature(channelsForAssetLoad);

      // Telemetry is the primary Asset result. Do not make chart/table rendering
      // wait on /pump-alarms; load alarm markers in the background instead.
      if (uniquePumps.length > 0) {
        void fetchPumpAlarms(uniquePumps, effectiveFromDate, effectiveToDate);
      } else {
        setAlarmEvents([]);
        setSelectedAlarmEvents([]);
      }

      return rows.length > 0;
    } catch (error) {
      if (assetPumpSelectionGenerationRef.current !== pumpSelectionGeneration) {
        return false;
      }

      console.error("Error loading asset pump data:", error);

      const isTimeout =
        axios.isAxiosError(error) && error.code === "ECONNABORTED";
      const code = axios.isAxiosError(error) && error.response?.data?.code;

      if (isTimeout) {
        const message =
          "Unable to load Asset data because the request timed out.";
        setAssetLoadErrorText(message);
        toast.error(message);
      } else if (code === "RANGE_TOO_LARGE") {
        const message =
          "Range too wide for this resolution. Pick a coarser resolution or narrower window.";
        setAssetLoadErrorText(message);
        toast.error(message);
      } else {
        const message = "Failed to load asset pump data.";
        setAssetLoadErrorText(message);
        toast.error(message);
      }

      return false;
    } finally {
      if (assetPumpSelectionGenerationRef.current === pumpSelectionGeneration) {
        clearAssetSlowLoadTimer();

        if (showChartLoading) {
          setIsLoadDataLoading(false);
        }
        if (showTableLoading) {
          setIsAssetTableLoading(false);
        }
      }
    }
  };

  // Fleet historical data loads automatically on initial expansion and whenever
  // the selected fleet date range changes. Channel-only changes continue to use
  // the existing independent channel reload path.
  useEffect(() => {
    if (activeTab !== "fleet" || !selectedFleet) return;

    /*
     * The pending-dashboard effect performs the initial View Chart request.
     * Running this normal auto-load at the same time creates the RPM flash,
     * duplicate success toast, and delayed second channel load.
     */
    if (
      pendingDashboardSelection ||
      dashboardDeepLinkLoadInProgressRef.current
    ) {
      return;
    }

    const fleetId = selectedFleet.fleet;
    const start = fromDateByFleet[fleetId];
    const end = toDateByFleet[fleetId];

    if (!start || !end || end.getTime() <= start.getTime()) return;
    if (streamingFleetIdsRef.current.has(fleetId)) return;
    if (!expandedFleets.has(fleetId)) return;

    const signature = `${fleetId}|${start.getTime()}|${end.getTime()}`;
    if (fleetAutoLoadSignatureRef.current === signature) return;

    if (fleetAutoLoadTimerRef.current) {
      clearTimeout(fleetAutoLoadTimerRef.current);
    }

    fleetAutoLoadTimerRef.current = setTimeout(async () => {
      fleetAutoLoadTimerRef.current = null;
      fleetAutoLoadSignatureRef.current = signature;

      try {
        // Resolve the fleet's default channels BEFORE the first request. The
        // expand handler seeds RPM synchronously, so without this the initial
        // load fired on RPM (blank for electric pumps) and was then raced by the
        // VFD Amps upgrade — two overlapping loads whose responses arrived out
        // of order and overwrote each other's chart state.
        await ensureDefaultChannelsForFleet(fleetId);

        const historicalPad = getPadForFleetDateRange(
          selectedFleet,
          start,
          end,
        );
        if (historicalPad?.id) {
          await fetchPadWells(historicalPad.id);
        }

        await handleMainLoad(selectedFleet, start, end);
      } finally {
        setDateRefreshPendingFleetIds((previous) => {
          const next = new Set(previous);
          next.delete(fleetId);
          return next;
        });
      }
    }, 500);

    return () => {
      if (fleetAutoLoadTimerRef.current) {
        clearTimeout(fleetAutoLoadTimerRef.current);
        fleetAutoLoadTimerRef.current = null;
      }
    };
  }, [
    activeTab,
    selectedFleet,
    fromDateByFleet,
    toDateByFleet,
    expandedFleets,
    pendingDashboardSelection,
    getPadForFleetDateRange,
    fetchPadWells,
  ]);

  // Asset historical data loads automatically when the pump selection changes.
  // Date/time edits do NOT reload: the user sets From/To and presses Load Data,
  // so a half-edited range never fires a request. The 500 ms debounce avoids
  // duplicate requests while pumps are still being picked.
  useEffect(() => {
    if (activeTab !== "asset") return;
    if (!fromDate || !toDate || toDate.getTime() <= fromDate.getTime()) return;
    if (selectedAssetPumps.length === 0) return;

    const signature = selectedAssetPumps
      .map((pump) => pump.value)
      .sort()
      .join(",");

    if (assetAutoLoadSignatureRef.current === signature) return;

    if (assetAutoLoadTimerRef.current) {
      clearTimeout(assetAutoLoadTimerRef.current);
    }

    assetAutoLoadTimerRef.current = setTimeout(async () => {
      assetAutoLoadTimerRef.current = null;
      assetAutoLoadSignatureRef.current = signature;
      await loadAssetData(fromDate, toDate, undefined, "both");
    }, 500);

    return () => {
      if (assetAutoLoadTimerRef.current) {
        clearTimeout(assetAutoLoadTimerRef.current);
        assetAutoLoadTimerRef.current = null;
      }
    };
  }, [activeTab, selectedAssetPumps, fromDate, toDate]);

  const handleAssetLoadData = () => {
    if (!fromDate || !toDate) return;
    void loadAssetData(fromDate, toDate, undefined, "both");
  };

  // Drops the zoom and any finer-resolution rows fetched for it. Any zoom
  // request still in flight is ignored when it returns.
  const clearAssetZoom = () => {
    assetZoomGenerationRef.current += 1;
    if (assetZoomTimerRef.current) {
      clearTimeout(assetZoomTimerRef.current);
      assetZoomTimerRef.current = null;
    }
    setAssetZoomRange(null);
    setAssetZoomRows(null);
    setAssetZoomLoading(false);
  };

  // Chart-only fetch of a zoomed window at a finer resolution. The table and
  // the loaded From/To range are left untouched, the same as the Fleet chart.
  const fetchAssetZoomDetail = async (
    zoomMin: number,
    zoomMax: number,
    resolution: string,
    zoomGeneration: number,
  ) => {
    const token = getAuthToken();
    const chartChannels = filterUnsupportedPumpChannels(
      selectedAssetChannelsRef.current,
    ).map((channel) => channel.value);
    if (!token || chartChannels.length === 0) return;

    const params = new URLSearchParams();
    params.append("start_time", String(Math.floor(zoomMin)));
    params.append("end_time", String(Math.ceil(zoomMax)));
    params.append("resolution", resolution);
    Array.from(
      new Set(
        selectedAssetPumps.flatMap((pump) =>
          pump.rawPumpNames?.length > 0 ? pump.rawPumpNames : [pump.value],
        ),
      ),
    ).forEach((pumpName) => params.append("pump_name", pumpName));
    params.append("pump_name_match", "exact");
    params.append(
      "channels",
      Array.from(new Set([...chartChannels, "calculated_discharge_rate"])).join(
        ",",
      ),
    );
    params.append("non_null_channels", chartChannels.join(","));
    params.append("asset_compact", "1");

    const baseGeneration = assetPumpSelectionGenerationRef.current;
    setAssetZoomLoading(true);

    try {
      const response = await axios.get(
        `${backendUrl}/pumps?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        },
      );

      // Ignore if the user zoomed again, reset, or loaded new data meanwhile.
      if (
        assetZoomGenerationRef.current !== zoomGeneration ||
        assetPumpSelectionGenerationRef.current !== baseGeneration
      ) {
        return;
      }

      const rows: PumpData[] = (
        Array.isArray(response.data?.data) ? response.data.data : []
      ).map((row: PumpData) => ({
        ...row,
        pump_name: String(row.pump_name || "")
          .replace(/-/g, "")
          .toUpperCase(),
      }));

      // Keep the loaded rows if the finer query came back empty, so the
      // zoom still shows something.
      if (rows.length > 0) setAssetZoomRows(rows);
    } catch (error) {
      if (assetZoomGenerationRef.current !== zoomGeneration) return;
      console.error("Asset zoom detail load failed:", error);
      toast.error(
        "Could not load more detail for the zoomed range. Showing the loaded data.",
      );
    } finally {
      if (assetZoomGenerationRef.current === zoomGeneration) {
        setAssetZoomLoading(false);
      }
    }
  };

  // Called with the box the user dragged on the Asset chart. The zoomed
  // window is drawn straight away from the loaded rows; when the API can serve
  // it at a finer resolution (e.g. a 1-minute 7-day load zoomed to a few
  // hours), that detail is fetched after a short debounce, like Fleet.
  const handleAssetChartZoom = (zoomMin: number, zoomMax: number) => {
    if (!(zoomMax > zoomMin)) return;

    const zoomGeneration = ++assetZoomGenerationRef.current;
    if (assetZoomTimerRef.current) {
      clearTimeout(assetZoomTimerRef.current);
      assetZoomTimerRef.current = null;
    }

    setAssetZoomRange({ min: zoomMin, max: zoomMax });
    setAssetZoomRows(null);
    setAssetZoomLoading(false);

    if (!assetLoadedRange) return;

    const resolutionRank: Record<string, number> = {
      "1h": 0,
      "1m": 1,
      "1s": 2,
    };
    const loadedResolution = getAutoResolutionConfig(
      new Date(assetLoadedRange.start),
      new Date(assetLoadedRange.end),
    ).code;
    const zoomResolution = getAutoResolutionConfig(
      new Date(zoomMin),
      new Date(zoomMax),
    ).code;

    if (resolutionRank[zoomResolution] <= resolutionRank[loadedResolution]) {
      return;
    }

    assetZoomTimerRef.current = setTimeout(() => {
      assetZoomTimerRef.current = null;
      void fetchAssetZoomDetail(zoomMin, zoomMax, zoomResolution, zoomGeneration);
    }, 200);
  };

  // Highcharts keeps the handlers from when the chart was created; route them
  // through refs so they always see the latest state.
  const handleAssetChartZoomRef = useRef(handleAssetChartZoom);
  handleAssetChartZoomRef.current = handleAssetChartZoom;
  const clearAssetZoomRef = useRef(clearAssetZoom);
  clearAssetZoomRef.current = clearAssetZoom;

  const getYAxisIndex = (axisName: string): number => {
    const axisMap: { [key: string]: number } = {
      left1: 0,
      left2: 1,
      right1: 2,
      right2: 3,
    };
    return axisMap[axisName] || 0;
  };

  const configureYAxes = useCallback(
    (
      channelsToUse: PumpChannel[] = selectedChannels,
    ): Highcharts.YAxisOptions[] => {
      const selectedChannelValues = new Set(
        channelsToUse.map((channel) => channel.value),
      );

      const axisNames = ["left1", "left2", "right1", "right2"] as const;
      const forcePrimaryAxis = channelsToUse.length === 1;
      const singleChannel = channelsToUse[0];

      const visibleAxisNames = axisNames.filter((axisName) => {
        if (forcePrimaryAxis) {
          return axisName === "left1" && Boolean(singleChannel);
        }

        return (axisSettings[axisName]?.channels || []).some((channelValue) =>
          selectedChannelValues.has(channelValue),
        );
      });

      // Only one Y-axis should draw the horizontal grid lines.
      const gridAxisName = visibleAxisNames[0] || "left1";

      return axisNames.map((axisName) => {
        const activeChannels = forcePrimaryAxis
          ? axisName === "left1" && singleChannel
            ? [singleChannel.value]
            : []
          : channelsToUse
              .filter(
                (channel) =>
                  resolveAxisNameForChannel(
                    channel.value,
                    channelsToUse,
                    axisSettings,
                  ) === axisName,
              )
              .map((channel) => channel.value);

        const channelNames = activeChannels.map(
          (channelValue) =>
            availableChannels.find((channel) => channel.value === channelValue)
              ?.label || channelValue,
        );

        const isVisible = activeChannels.length > 0;
        const opposite = axisName === "right1" || axisName === "right2";

        const escapeAxisTitle = (value: string) =>
          value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\"/g, "&quot;")
            .replace(/'/g, "&#039;");

        const axisContainsOnlyPowerEndTemperature =
          activeChannels.length > 0 &&
          activeChannels.every(isPowerEndTemperatureChannelValue);

        const axisTitleText = axisContainsOnlyPowerEndTemperature
          ? "Power End Temperature (°F)"
          : channelNames.length === 0
            ? undefined
            : channelNames.length === 1
              ? channelNames[0]
              : `${channelNames[0]} +${channelNames.length - 1}`;

        const axisTitle = axisTitleText
          ? `<span title="${escapeAxisTitle(channelNames.join(", "))}" style="cursor:default;">${escapeAxisTitle(axisTitleText)}</span>`
          : undefined;

        const axisOffset =
          axisName === "left2" || axisName === "right2" ? 72 : 0;

        return {
          title: {
            text: isVisible ? axisTitle : undefined,
            useHTML: true,
            rotation: opposite ? 90 : 270,
            margin: 18,
            style: {
              fontSize: "10px",
              fontWeight: "500",
            },
          },

          opposite,
          offset: axisOffset,

          min:
            forcePrimaryAxis && axisName !== "left1"
              ? undefined
              : axisSettings[axisName]?.min,

          max:
            forcePrimaryAxis && axisName !== "left1"
              ? undefined
              : axisSettings[axisName]?.max,

          visible: isVisible,

          labels: {
            enabled: isVisible,
            reserveSpace: isVisible,
            x: opposite ? 10 : -8,
            style: {
              fontSize: "10px",
              textOverflow: "ellipsis",
            },
          },

          lineWidth: isVisible ? 1 : 0,
          tickWidth: isVisible ? 1 : 0,

          // Only the first visible axis draws grid lines; the others are
          // tick-aligned to it, so every axis's labels sit on those lines.
          ...channelAxisGridOptions({
            drawsGrid: isVisible && axisName === gridAxisName,
            channelValues: activeChannels,
            isDarkTheme: theme === "dark",
          }),
        } as Highcharts.YAxisOptions;
      });
    },
    [
      axisSettings,
      availableChannels,
      selectedChannels,
      theme,
      resolveAxisNameForChannel,
    ],
  );

  // Left and right chart margins are kept identical so the plot area sits
  // centered. The value only has to clear the widest axis stack in use:
  // configureYAxes returns [left1, left2, right1, right2], and the outer
  // axes (left2/right2) sit at offset 72 plus their labels and title.
  const getAxisGutter = useCallback(
    (channelsToUse?: PumpChannel[]) => {
      const axes = configureYAxes(channelsToUse ?? selectedChannels);
      const hasOuterAxis = Boolean(axes[1]?.visible || axes[3]?.visible);
      return hasOuterAxis ? 138 : 66;
    },
    [configureYAxes, selectedChannels],
  );

  const buildChartSeries = useCallback(
    (
      data: PumpData[],
      pumpsToShow?: string[],
      hiddenSet?: Set<string>,
      channelsToUse?: PumpChannel[],
    ) => {
      const activePumps = pumpsToShow || selectedPumps;
      const hiddenForVisibility = hiddenSet || hiddenPumps;

      return buildChartSeriesWithChannels(
        data,
        activePumps,
        hiddenForVisibility,
        channelsToUse ?? selectedChannels,
        axisSettings,
      );
    },
    [axisSettings, hiddenPumps, selectedChannels, selectedPumps, alarmEvents],
  );
  const handleCreateAnnotation = useCallback(
    async (timestamp: number) => {
      const result = await Swal.fire({
        title: "Add Chart Comment",
        input: "textarea",
        inputLabel: new Date(timestamp).toLocaleString(),
        inputPlaceholder: "Enter comment for this point...",
        inputAttributes: {
          "aria-label": "Chart comment",
        },
        showCancelButton: true,
        confirmButtonText: "Save Comment",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#8CC540",
        background: theme === "dark" ? "#303335" : "#ffffff",
        color: theme === "dark" ? "#e5e5e5" : "#333333",
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return "Please enter a comment";
          }
          return null;
        },
      });

      if (!result.isConfirmed || !result.value) return;

      const token = getAuthToken();
      if (!token) return;

      const response = await axios.post(
        `${backendUrl}/chart-annotations`,
        {
          fleet: selectedFleet?.fleet,
          pump: selectedPumps[0],
          timestamp,
          comment: result.value.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const savedAnnotation: ChartAnnotation = {
        id: response.data.id,
        timestamp: response.data.timestamp,
        comment: response.data.comment,
        pump: response.data.pump,
        createdAt: response.data.createdAt,
        createdBy: response.data.createdBy,
        createdByEmail: response.data.createdByEmail,
      };

      setChartAnnotations((prev) => {
        const updated = [...prev, savedAnnotation];

        setTimeout(() => {
          chartRef.current?.chart?.redraw();
        }, 100);

        return updated;
      });

      toast.success("Comment added");
    },
    [backendUrl, selectedFleet, selectedPumps, theme],
  );

  const handleEditAnnotation = async (annotation: ChartAnnotation) => {
    setShowAnnotationModal(false);
    setSelectedAnnotation(null);

    const result = await Swal.fire({
      title: "Edit Comment",
      input: "textarea",
      inputValue: annotation.comment,
      showCancelButton: true,
      confirmButtonText: "Update",
      confirmButtonColor: "#8CC540",
    });

    if (!result.isConfirmed || !result.value?.trim()) return;

    try {
      const token = getAuthToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const updatedComment = result.value.trim();

      await axios.put(
        `${backendUrl}/chart-annotations/${annotation.id}`,
        { comment: updatedComment },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setChartAnnotations((prev) =>
        prev.map((item) =>
          item.id === annotation.id
            ? { ...item, comment: updatedComment }
            : item,
        ),
      );

      setAnnotationSearchResults((prev) =>
        prev.map((item) =>
          item.id === annotation.id
            ? { ...item, comment: updatedComment }
            : item,
        ),
      );

      setSelectedAnnotationGroup((prev) =>
        prev.map((item) =>
          item.id === annotation.id
            ? { ...item, comment: updatedComment }
            : item,
        ),
      );

      toast.success("Comment updated");
    } catch (error) {
      console.error("Error updating annotation:", error);
      toast.error("Failed to update comment");
    }
  };

  const handleDeleteAnnotation = async (annotation: ChartAnnotation) => {
    setShowAnnotationModal(false);
    setSelectedAnnotation(null);

    const result = await Swal.fire({
      title: "Delete comment?",
      text: "This comment will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    try {
      const token = getAuthToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      await axios.delete(`${backendUrl}/chart-annotations/${annotation.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setChartAnnotations((prev) =>
        prev.filter((item) => item.id !== annotation.id),
      );

      setAnnotationSearchResults((prev) =>
        prev.filter((item) => item.id !== annotation.id),
      );

      setSelectedAnnotationGroup((prev) => {
        const updated = prev.filter((item) => item.id !== annotation.id);

        if (updated.length === 0) {
          setShowAnnotationModal(false);
          setSelectedAnnotation(null);
        }

        return updated;
      });

      toast.success("Comment deleted");
    } catch (error) {
      console.error("Error deleting annotation:", error);
      toast.error("Failed to delete comment");
    }
  };

  type AlarmSeverity = "normal" | "warning" | "critical";

  const normalizeAlarmText = (value: any) =>
    String(value || "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_:-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const normalizeAlarmSeverity = (alarm: any): AlarmSeverity => {
    const severity = normalizeAlarmText(alarm?.severity);

    if (severity.includes("critical")) return "critical";
    if (severity.includes("warning")) return "warning";

    const alarmText = normalizeAlarmText(alarm?.alarm || alarm?.name);

    if (
      alarmText.includes("critical") ||
      alarmText.includes("allstop") ||
      alarmText.includes("all stop") ||
      alarmText.includes("stop button") ||
      alarmText.includes("shutdown") ||
      alarmText.includes("trip") ||
      alarmText.includes("over max")
    ) {
      return "critical";
    }

    if (
      alarmText.includes("warning") ||
      alarmText.includes("warn") ||
      alarmText.includes("low") ||
      alarmText.includes("high") ||
      alarmText.includes("alarm") ||
      alarmText.includes("failure") ||
      alarmText.includes("loss") ||
      alarmText.includes("disconnected") ||
      alarmText.includes("detected") ||
      alarmText.includes("voltage") ||
      alarmText.includes("pressure") ||
      alarmText.includes("lamp") ||
      alarmText.includes("communication")
    ) {
      return "warning";
    }

    return "normal";
  };

  const getAlarmSeverityMeta = (severity: AlarmSeverity) => {
    switch (severity) {
      case "critical":
        return {
          label: "Critical",
          color: "#ef4444",
          icon: <FiAlertCircle />,
          marker: "⬣",
        };
      case "warning":
        return {
          label: "Warning",
          color: "#f59e0b",
          icon: <FiAlertTriangle />,
          marker: "⚠",
        };
      default:
        return {
          label: "Normal",
          color: "#22c55e",
          icon: <FiCheckCircle />,
          marker: "●",
        };
    }
  };

  const getHighestAlarmSeverity = (alarms: any[]): AlarmSeverity => {
    if (alarms.some((alarm) => normalizeAlarmSeverity(alarm) === "critical")) {
      return "critical";
    }

    if (alarms.some((alarm) => normalizeAlarmSeverity(alarm) === "warning")) {
      return "warning";
    }

    return "normal";
  };

  const getAlarmMarkers = useCallback(
    (sourceAlarmEvents?: any[]): [number, any[]][] => {
      const markersMap: Map<number, any[]> = new Map();
      const eventsForChart = (sourceAlarmEvents ?? alarmEvents).filter(
        (alarm) => normalizeAlarmSeverity(alarm) === alarmSeverityFilter,
      );

      eventsForChart.forEach((alarm) => {
        const alarmPump = alarm.pump || alarm.source || alarm.pump_name;
        const rawAlarmTime =
          alarm.alarm_time_ms ??
          alarm.alarm_time ??
          alarm.time_stamp ??
          alarm.timestamp;

        /*
         * Alarm markers represent all alarm events in this fleet/time window,
         * even when a pump series is currently hidden in the legend. The source
         * array is already scoped by fetchPumpAlarms() to this chart's fleet.
         */
        if (
          rawAlarmTime === null ||
          rawAlarmTime === undefined ||
          rawAlarmTime === "" ||
          !alarmPump
        ) {
          return;
        }

        let timeMs =
          typeof rawAlarmTime === "number"
            ? rawAlarmTime
            : new Date(rawAlarmTime).getTime();

        if (Number.isFinite(timeMs) && timeMs < 1_000_000_000_000) {
          timeMs *= 1000;
        }

        if (!Number.isFinite(timeMs)) return;

        const bucketKey = timeMs;

        if (!markersMap.has(bucketKey)) {
          markersMap.set(bucketKey, []);
        }

        markersMap.get(bucketKey)!.push({
          ...alarm,
          pump: alarmPump,
          alarm_time_ms: timeMs,
          alarm_time: alarm.alarm_time ?? new Date(timeMs).toISOString(),
        });
      });

      return Array.from(markersMap.entries()).sort((a, b) => a[0] - b[0]);
    },
    [alarmEvents, alarmSeverityFilter],
  );
  const renderAlarmMarkers = useCallback(
    function (
      chart: Highcharts.Chart,
      activePumps: string[] = selectedPumps,
      sourceAlarmEvents?: any[],
    ) {
      const chartWithMarkers = chart as Highcharts.Chart & {
        customAlarmMarkers?: Highcharts.SVGElement[];
        customAlarmTooltips?: Highcharts.SVGElement[];
      };

      chartWithMarkers.customAlarmMarkers?.forEach((marker) =>
        marker.destroy(),
      );
      chartWithMarkers.customAlarmMarkers = [];

      const markers = getAlarmMarkers(sourceAlarmEvents);
      if (!markers.length || !chart.xAxis?.[0]) return;

      const thresholdText = flaggedChartMinimumRpm.trim();
      const minimumAlarmRpm = Number(thresholdText);
      const shouldFilterAlarmMarkers =
        isFlaggedReengagementView &&
        thresholdText !== "" &&
        Number.isFinite(minimumAlarmRpm) &&
        minimumAlarmRpm >= 0;

      const getAlarmRpmFromChart = (alarm: any, alarmTimeMs: number) => {
        const rawExplicitAlarmRpm = alarm?.alarm_rpm;
        if (
          rawExplicitAlarmRpm !== null &&
          rawExplicitAlarmRpm !== undefined &&
          rawExplicitAlarmRpm !== ""
        ) {
          const explicitAlarmRpm = Number(rawExplicitAlarmRpm);
          if (Number.isFinite(explicitAlarmRpm)) return explicitAlarmRpm;
        }

        const pumpName = String(
          alarm?.pump || alarm?.source || alarm?.pump_name || "",
        )
          .trim()
          .toLowerCase();

        const rpmSeries = chart.series.find((series: any) => {
          const seriesName = String(series?.name || "");
          const normalizedSeriesName = seriesName.toLowerCase();
          const isRpmSeries =
            /(^|[\s-])rpm(\s|$)/i.test(seriesName) ||
            normalizedSeriesName.includes("engine rpm");
          const matchesPump =
            !pumpName || normalizedSeriesName.includes(pumpName);

          return isRpmSeries && matchesPump;
        }) as any;

        if (!rpmSeries) return null;

        let closestRpm: number | null = null;
        let closestDistance = Number.POSITIVE_INFINITY;

        (rpmSeries.points || []).forEach((point: any) => {
          const pointTime = Number(point?.x);
          const pointRpm = Number(point?.y);
          if (!Number.isFinite(pointTime) || !Number.isFinite(pointRpm)) return;

          if (pointTime > alarmTimeMs) return;

          const age = alarmTimeMs - pointTime;
          if (age < closestDistance) {
            closestDistance = age;
            closestRpm = pointRpm;
          }
        });

        // Use the latest one-second RPM sample at/before the alarm. Avoid
        // carrying an old value across a telemetry gap.
        return closestDistance <= 5000 ? closestRpm : null;
      };

      markers.forEach(([bucketKey, alarms]) => {
        const visibleAlarms = shouldFilterAlarmMarkers
          ? alarms.filter((alarm) => {
              const alarmRpm = getAlarmRpmFromChart(alarm, bucketKey);

              // With a threshold applied, show only alarms whose alarm-time RPM
              // is available and meets the configured minimum.
              return alarmRpm != null && alarmRpm >= minimumAlarmRpm;
            })
          : alarms;

        if (!visibleAlarms.length) return;

        const xValue = bucketKey;
        const x = chart.xAxis[0].toPixels(xValue);
        const y = chart.plotTop - 14;

        // Skip when the axis has no usable pixel mapping yet (empty series / not
        // laid out): toPixels returns NaN, and the range check below can't catch
        // it (NaN comparisons are always false), so it would draw <rect x="NaN">.
        if (!Number.isFinite(x)) return;

        if (x < chart.plotLeft || x > chart.plotLeft + chart.plotWidth) {
          return;
        }

        const openAlarmModal = () => {
          setSelectedAlarmEvents(visibleAlarms);
          setShowAlarmModal(true);
        };

        const severity = getHighestAlarmSeverity(visibleAlarms);
        const severityMeta = getAlarmSeverityMeta(severity);

        const diamond = chart.renderer
          .text(severityMeta.marker, x - 7, y)
          .css({
            color: severityMeta.color,
            fontSize: "20px",
            fontWeight: "700",
            textShadow: "0 0 3px #111827",
            cursor: "pointer",
            pointerEvents: "auto",
          } as any)
          .attr({ zIndex: 20 })
          .add();

        const hitBox = chart.renderer
          .rect(x - 12, y - 18, 28, 28, 4)
          .attr({
            fill: "rgba(255,255,255,0.001)",
            zIndex: 21,
            cursor: "pointer",
          })
          .add();

        const destroyAlarmTooltips = () => {
          (chart as any).customAlarmTooltips?.forEach((tooltip: any) =>
            tooltip.destroy(),
          );
          (chart as any).customAlarmTooltips = [];
        };

        const showAlarmTooltip = () => {
          destroyAlarmTooltips();

          (chart as any).tooltip?.hide(0);

          const tooltipLabel = chart.renderer
            .label(
              `<b>${severityMeta.label} Alarms</b><br/>${visibleAlarms.length} alarm(s)<br/>Click to view details`,
              x + 12,
              y - 42,
              "callout",
              x,
              y,
              true,
            )
            .attr({
              fill: theme === "dark" ? "rgba(48, 51, 53, 0.95)" : "#ffffff",
              stroke: theme === "dark" ? "#5C646B" : "#92d13d",
              "stroke-width": 1,
              padding: 8,
              zIndex: 10010,
            })
            .css({
              color: theme === "dark" ? "#ffffff" : "#111827",
              fontSize: "12px",
              pointerEvents: "none",
            } as any)
            .add();

          (chart as any).customAlarmTooltips = [tooltipLabel];
        };

        const hideAlarmTooltip = () => {
          destroyAlarmTooltips();
          (chart as any).tooltip?.hide(0);
        };

        diamond.element.classList.add("chart-alarm-marker");
        hitBox.element.classList.add("chart-alarm-marker");

        diamond.on("click", (event: any) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();
          destroyAlarmTooltips();
          openAlarmModal();
        });

        hitBox.on("click", (event: any) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();
          destroyAlarmTooltips();
          openAlarmModal();
        });
        diamond.on("mouseover", (event: any) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();
          showAlarmTooltip();
        });

        hitBox.on("mouseover", (event: any) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();
          showAlarmTooltip();
        });

        diamond.on("mouseout", hideAlarmTooltip);
        hitBox.on("mouseout", hideAlarmTooltip);

        chartWithMarkers.customAlarmMarkers = [
          ...(chartWithMarkers.customAlarmMarkers || []),
          diamond,
          hitBox,
        ];
      });
    },
    [
      getAlarmMarkers,
      selectedPumps,
      theme,
      flaggedChartMinimumRpm,
      isFlaggedReengagementView,
    ],
  );

  const renderAnnotationMarkers = useCallback(
    function (chart: Highcharts.Chart) {
      const chartWithAnnotations = chart as Highcharts.Chart & {
        customAnnotationMarkers?: Highcharts.SVGElement[];
        customAnnotationTooltips?: Highcharts.SVGElement[];
      };

      chartWithAnnotations.customAnnotationMarkers?.forEach((marker) =>
        marker.destroy(),
      );
      chartWithAnnotations.customAnnotationTooltips?.forEach((tooltip) =>
        tooltip.destroy(),
      );

      chartWithAnnotations.customAnnotationMarkers = [];
      chartWithAnnotations.customAnnotationTooltips = [];

      if (!chartAnnotations.length || !chart.xAxis?.[0]) return;

      const bucketMinutes = 1;

      const annotationBuckets = chartAnnotations.reduce((map, annotation) => {
        const bucketKey = Math.floor(
          annotation.timestamp / (bucketMinutes * 60 * 1000),
        );

        if (!map.has(bucketKey)) {
          map.set(bucketKey, []);
        }

        map.get(bucketKey)!.push(annotation);
        return map;
      }, new Map<number, ChartAnnotation[]>());

      Array.from(annotationBuckets).forEach(([bucketKey, annotations]) => {
        const xValue = bucketKey * bucketMinutes * 60 * 1000;
        const x = chart.xAxis[0].toPixels(xValue);
        const y = chart.plotTop + chart.plotHeight - 28;

        const annotation = annotations[0];

        // Skip when the axis has no usable pixel mapping yet (toPixels → NaN);
        // the range check below can't catch NaN, so it would draw <rect x="NaN">.
        if (!Number.isFinite(x)) return;

        if (x < chart.plotLeft || x > chart.plotLeft + chart.plotWidth) return;

        const note = chart.renderer
          .rect(x - 7, y - 7, 14, 14, 2)
          .attr({
            fill: "#facc15",
            stroke: "#92400e",
            "stroke-width": 1,
            zIndex: 10002,
            cursor: "pointer",
          })
          .add();

        const foldedCorner = chart.renderer
          .path([
            ["M", x + 2, y - 7],
            ["L", x + 7, y - 7],
            ["L", x + 7, y - 2],
            ["Z"],
          ] as any)
          .attr({
            fill: "#fde68a",
            zIndex: 10003,
            cursor: "pointer",
          })
          .add();

        if (annotations.length > 1) {
          const countLabel = chart.renderer
            .text(String(annotations.length), x, y + 4)
            .css({
              color: "#111827",
              fontSize: "10px",
              fontWeight: "700",
              textAnchor: "middle",
              pointerEvents: "none",
            } as any)
            .attr({ zIndex: 10005 })
            .add();

          chartWithAnnotations.customAnnotationMarkers?.push(countLabel);
        }
        const hitBox = chart.renderer
          .rect(x - 14, y - 14, 32, 32, 4)
          .attr({
            fill: "rgba(255,255,255,0.001)",
            zIndex: 10006,
            cursor: "pointer",
          })
          .add();

        const openAnnotation = (event?: any) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();

          setSelectedAnnotation(annotation);
          setSelectedAnnotationGroup(annotations);
          setShowAnnotationModal(true);
        };

        const bindAnnotationEvents = (element: Highcharts.SVGElement) => {
          element.element.classList.add("chart-annotation-marker");

          element.css({
            pointerEvents: "auto",
            cursor: "pointer",
          } as any);

          element.element.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            openAnnotation(event);
          });
        };
        bindAnnotationEvents(note);
        bindAnnotationEvents(foldedCorner);
        bindAnnotationEvents(hitBox);

        const showTooltip = () => {
          chartWithAnnotations.customAnnotationTooltips?.forEach((tooltip) =>
            tooltip.destroy(),
          );
          chartWithAnnotations.customAnnotationTooltips = [];

          const tooltip = chart.renderer
            .label(
              `<b>Comments</b><br/>${annotations.length} comment(s)<br/><span style="font-size:11px;">Click to view</span>`,
              x + 12,
              y + 4,
              "callout",
              x,
              y,
              true,
            )
            .attr({
              fill: theme === "dark" ? "rgba(48,51,53,0.96)" : "#ffffff",
              stroke: "#facc15",
              "stroke-width": 1,
              padding: 8,
              zIndex: 10004,
            })
            .css({
              color: theme === "dark" ? "#ffffff" : "#111827",
              fontSize: "12px",
              pointerEvents: "none",
            } as any)
            .add();

          chartWithAnnotations.customAnnotationTooltips?.push(tooltip);
        };

        const hideTooltip = () => {
          chartWithAnnotations.customAnnotationTooltips?.forEach((tooltip) =>
            tooltip.destroy(),
          );
          chartWithAnnotations.customAnnotationTooltips = [];
        };

        note.on("mouseover", showTooltip);
        foldedCorner.on("mouseover", showTooltip);
        hitBox.on("mouseover", showTooltip);

        note.on("mouseout", hideTooltip);
        foldedCorner.on("mouseout", hideTooltip);
        hitBox.on("mouseout", hideTooltip);

        chartWithAnnotations.customAnnotationMarkers?.push(
          note,
          foldedCorner,
          hitBox,
        );
      });
    },
    [chartAnnotations, theme],
  );

  const shouldIgnoreChartClick = (event: any): boolean => {
    const target = (event?.browserEvent?.target ||
      event?.originalEvent?.target ||
      event?.target) as Element | null;

    return !!(
      target?.closest?.(".chart-annotation-marker") ||
      target?.closest?.(".chart-alarm-marker") ||
      target?.closest?.(".chart-well-activity-label") ||
      target?.closest?.(".highcharts-button") ||
      target?.closest?.(".highcharts-reset-zoom") ||
      target?.closest?.(".highcharts-legend") ||
      target?.closest?.(".highcharts-axis-labels") ||
      target?.closest?.(".highcharts-tooltip")
    );
  };

  const chartOptions = useMemo((): Highcharts.Options => {
    const isDarkTheme = theme === "dark";
    const assetAxisGutter = getAxisGutter();
    const channelAxes = configureYAxes();
    const showEmptyGrid =
      chartSeries.length === 0 || !channelAxes.some((axis) => axis.visible);

    return {
      title: {
        text: isDashboardPumpForFleet(selectedFleet?.fleet)
          ? `Pump Data - ${dashboardPumpParam}`
          : "Pump Data - Fleet " + (selectedFleet?.fleet || ""),
        y: 16,
        margin: ALARM_BAND_HEIGHT,
        style: {
          color: isDarkTheme ? "#e5e5e5" : "#333333",
          fontSize: "18px",
          fontWeight: "600",
        },
      },
      xAxis: {
        type: "datetime",
        title: {
          text: "Time",
          style: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
          },
        },
        labels: {
          style: {
            color: isDarkTheme ? "#e5e5e5" : "#666666",
          },
        },
        min: xAxisRange.min,
        max: xAxisRange.max,
        events: {
          afterSetExtremes: function (e: any) {
            if (!isStreaming) {
              if (e.trigger === "zoom") {
                if (activeTab === "fleet" && selectedFleet) {
                  loadData(new Date(e.min), new Date(e.max));
                } else if (
                  activeTab === "asset" &&
                  selectedAssetPumps.length > 0
                ) {
                  loadAssetData(
                    new Date(e.min),
                    new Date(e.max),
                    undefined,
                    "both",
                  );
                }
              } else if (e.trigger === "resetZoom") {
                if (activeTab === "fleet" && selectedFleet) {
                  loadData(fromDate!, toDate!);
                } else if (
                  activeTab === "asset" &&
                  selectedAssetPumps.length > 0
                ) {
                  loadAssetData(fromDate!, toDate!, undefined, "both");
                }
              }
            }
          },
        },
      },
      yAxis: [
        ...channelAxes.map((axis) => ({
          ...axis,
          title: {
            ...axis.title,
            style: {
              color: isDarkTheme ? "#e5e5e5" : "#333333",
            },
          },
          labels: {
            ...axis.labels,
            style: {
              color: isDarkTheme ? "#e5e5e5" : "#666666",
            },
          },
        })),
        emptyChartGridAxis(isDarkTheme, showEmptyGrid),
        {
          min: 0,
          max: 1.15,
          visible: false,
        },
      ],
      series: chartSeries,
      credits: { enabled: false },
      chart: {
        zooming: {
          type: "x",
        },
        height: chartSeries.length > 0 ? 600 : 180,
        spacingTop: 8,
        spacingRight: 24,
        spacingBottom: 8,
        marginLeft: assetAxisGutter,
        marginRight: assetAxisGutter,
        backgroundColor: isDarkTheme ? "#303335" : "#ffffff",
        animation: false,
        events: {
          render: function () {
            const chart = this as Highcharts.Chart;
            renderAlarmMarkers(chart, selectedPumps);
            renderAnnotationMarkers(chart);
          },
        },
      },
      tooltip: {
        shared: false,
        outside: true,
        xDateFormat: "%Y-%m-%d %H:%M:%S",
        valueDecimals: 2,
        backgroundColor: isDarkTheme ? "#303335" : "#ffffff",
        style: {
          color: isDarkTheme ? "#e5e5e5" : "#333333",
        },
        borderColor: isDarkTheme ? "#5C646B" : "#e6e6e6",
      },

      legend: {
        enabled: true,
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        alignColumns: false,
        maxHeight: 96,
        x: 0,
        floating: false,
        itemDistance: 14,
        itemMarginTop: 2,
        itemMarginBottom: 2,
        padding: 8,
        itemStyle: {
          color: isDarkTheme ? "#e5e5e5" : "#333333",
          fontSize: "10px",
          fontWeight: "400",
          textOverflow: "ellipsis",
        },
        itemHoverStyle: {
          color: "#92d13d",
        },
        itemHiddenStyle: {
          color: isDarkTheme ? "#666666" : "#cccccc",
        },
        navigation: {
          enabled: true,
          activeColor: "#92d13d",
          inactiveColor: "#6b7280",
          style: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
            fontSize: "11px",
          },
        },
        backgroundColor: isDarkTheme
          ? "rgba(48, 51, 53, 0.8)"
          : "rgba(255, 255, 255, 0.8)",
        borderRadius: 4,
        borderColor: isDarkTheme ? "#3d4247" : "#e6e6e6",
        borderWidth: 1,
      },
      responsive: {
        rules: [
          {
            condition: { maxWidth: 900 },
            chartOptions: {
              chart: {
                marginLeft: 72,
                marginRight: 72,
                height: 560,
              },
              legend: {
                maxHeight: 88,
              },
            },
          },
        ],
      },
      plotOptions: {
        series: {
          stickyTracking: false,
          animation: false,
          boostThreshold: 10000,
          turboThreshold: 0,

          events: {
            click: function (event: any) {
              if (shouldIgnoreChartClick(event)) return;

              const timestamp = Math.round(event?.point?.x);

              if (!timestamp) return;

              handleCreateAnnotation(timestamp);
            },
          },

          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },

        line: {
          lineWidth: 2,
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4,
              },
            },
          },
        },

        scatter: {
          marker: {
            enabled: true,
            radius: 6,
            symbol: "circle",
          },
        },
      },
      boost: {
        enabled: true,
        useGPUTranslations: true,
        // false: preallocation sizes the WebGL buffer from the FIRST series and
        // requires all series to have the same point count. Our min/max-downsampled
        // series have DIFFERENT lengths (flat pumps ~1000 pts, varying ~2000), so
        // true made the longer/varying series silently fail to render — leaving
        // only the overlapping flat line on multi-pump fleets.
        usePreallocated: false,
        seriesThreshold: 50,
      },
    };
  }, [
    theme,
    selectedFleet,
    chartSeries,
    xAxisRange,
    configureYAxes,
    getAxisGutter,
    renderAlarmMarkers,
    renderAnnotationMarkers,
    handleCreateAnnotation,
    selectedPumps,
  ]);

  // Asset chart options. Memoized so the chart is not rebuilt on unrelated
  // re-renders (it used to be an inline object, which recreated the chart on
  // every render and lost any zoom). Zoom works like the Fleet chart: the
  // dragged window is redrawn from loaded data (refetched at finer resolution
  // only when needed), and an on-chart Reset zoom button restores From/To.
  const assetChartOptions = useMemo((): Highcharts.Options => {
    const baseRender = (chartOptions.chart?.events as any)?.render as
      | ((this: Highcharts.Chart, event: Event) => void)
      | undefined;

    return {
      ...chartOptions,
      title: {
        ...(chartOptions.title || {}),
        text: "Pump Data - Asset View",
        y: 16,
        margin: ALARM_BAND_HEIGHT,
      },
      xAxis: {
        ...(chartOptions.xAxis as Highcharts.XAxisOptions),
        min: assetZoomRange?.min ?? xAxisRange.min,
        max: assetZoomRange?.max ?? xAxisRange.max,
        // Zoom is handled by the selection event below, not by refetching
        // the whole range on every Highcharts extremes change.
        events: {},
      },
      chart: {
        ...(chartOptions.chart || {}),
        spacingTop: 8,
        events: {
          ...(chartOptions.chart?.events || {}),
          selection: function (event: Highcharts.SelectEventObject) {
            const xSelection = event.xAxis?.[0];
            if (xSelection) {
              handleAssetChartZoomRef.current(xSelection.min, xSelection.max);
            }
            // We redraw the chart for the zoomed window ourselves.
            return false;
          },
          render: function (this: Highcharts.Chart, event: Event) {
            baseRender?.call(this, event);

            const chart = this as Highcharts.Chart & {
              __assetResetZoomButton?: Highcharts.SVGElement;
            };
            // Same look and place as Highcharts' native Reset zoom button on
            // the Fleet chart: default button theme, top-right inside the plot.
            const buttonPosition = {
              align: "right" as const,
              x: -10,
              y: 10,
            };

            if (assetZoomRange) {
              if (!chart.__assetResetZoomButton) {
                chart.__assetResetZoomButton = (chart.renderer as any)
                  .button("Reset zoom", 0, 0, () => {
                    // Defer: clearing the zoom rebuilds the chart, and doing
                    // that inside Highcharts' own click handler destroyed the
                    // chart mid-click ("reading 'hoverPoint'" crash).
                    window.setTimeout(() => clearAssetZoomRef.current(), 0);
                  })
                  .attr({ zIndex: 7 })
                  .add();
              }
              chart.__assetResetZoomButton?.align(
                buttonPosition,
                false,
                "plotBox",
              );
            } else if (chart.__assetResetZoomButton) {
              chart.__assetResetZoomButton.destroy();
              delete chart.__assetResetZoomButton;
            }
          },
        },
      },
      boost: {
        ...(chartOptions.boost || {}),
        // Same as the Fleet chart: series are already downsampled, so keep
        // normal selections on SVG (see the combined chart builder).
        seriesThreshold: 150,
      },
    };
  }, [chartOptions, assetZoomRange, xAxisRange]);

  // "ARENA ROJA FED UNIT 811H" + "…813H" → "ARENA ROJA FED UNIT 811H + 813H".
  // Simul-frac wells on a pad share almost their whole name, so repeating it makes
  // the label far too wide to fit in a band.
  const formatWellNames = (names: string[]) => {
    if (names.length === 0) return "";
    if (names.length === 1) return names[0];

    const first = names[0];
    let prefixLength = 0;

    scan: for (let index = 0; index < first.length; index += 1) {
      for (const name of names) {
        if (name[index] !== first[index]) break scan;
      }
      prefixLength = index + 1;
    }

    // Never cut mid-token — back up to the last space.
    while (prefixLength > 0 && first[prefixLength - 1] !== " ") {
      prefixLength -= 1;
    }

    if (prefixLength < 4) return names.join(" + ");

    return `${first.slice(0, prefixLength)}${names
      .map((name) => name.slice(prefixLength))
      .join(" + ")}`;
  };

  // Empty when no stage could be resolved — a band still names its wells.
  const wellActivityStageText = (band: WellActivityBand) => {
    const stages = Array.from(
      new Set(
        band.wells
          .map((well) => well.stageNumber)
          .filter((stage): stage is number => stage !== null),
      ),
    ).sort((a, b) => a - b);

    if (stages.length === 0) return "";
    return `St ${stages.join("/")}`;
  };

  const isSimulFracBand = (band: WellActivityBand) => band.wells.length > 1;

  // Clip a pad's well-activity bands to a time window, extending the newest one
  // to the window edge. Shared by the band fills and the band labels so the two
  // can never disagree about where a run starts and ends.
  const visibleWellActivityBands = useCallback(
    (
      padId: number | null | undefined,
      windowStart: number,
      windowEnd: number,
    ) => {
      const bands = padId != null ? wellActivityByPad[padId] : undefined;
      if (!bands || bands.length === 0) return [];

      const lastIndex = bands.length - 1;

      return bands
        .map((band, index) => {
          // The newest stage has no successor and no end_ts, so its end was
          // stamped "now" at fetch time. Extend it to the window edge instead,
          // or a streaming chart's final band stops growing.
          const rawTo =
            index === lastIndex && Number.isFinite(windowEnd)
              ? Math.max(band.to, windowEnd)
              : band.to;

          return {
            ...band,
            from: Math.max(band.from, windowStart),
            to: Math.min(rawTo, windowEnd),
            // The run's true extent, before clipping to the visible window —
            // clicking a label zooms to the whole run, not just the visible slice.
            fullFrom: band.from,
            fullTo: rawTo,
          };
        })
        .filter((band) => band.to > band.from);
    },
    [wellActivityByPad],
  );

  // Well-activity bands are CHROME, not a series: they sit behind up to ~60
  // coloured pump lines, so they must not compete for attention. Hence a neutral
  // two-tone wash that alternates on ADJACENCY (guaranteeing every boundary is
  // visible) with the well identity carried by the band's text label.
  //
  // Deliberately NOT a colour-per-well encoding: validated with the dataviz
  // palette validator against this chart's own surfaces, four categorical slots
  // fail the all-pairs gate in both modes (dark: worst pair CVD ΔE 1.6, normal
  // vision 11.6 — below the 15 floor), and pads routinely run more than three
  // wells. Labels carry identity instead, so nothing depends on hue.
  const buildWellActivityPlotBands = useCallback(
    (
      padId: number | null | undefined,
      range: { min?: number; max?: number },
      isDarkTheme: boolean,
    ): Highcharts.XAxisPlotBandsOptions[] => {
      const windowStart = Number.isFinite(range.min)
        ? (range.min as number)
        : -Infinity;
      const windowEnd = Number.isFinite(range.max)
        ? (range.max as number)
        : Infinity;

      // Fills and borders only. The text is drawn by renderWellActivityLabels,
      // which can measure real pixel widths — a plotBand label cannot, so it
      // could neither centre itself in the band nor fit the name to the space.
      return visibleWellActivityBands(padId, windowStart, windowEnd).map(
        (band, index) =>
          ({
            from: band.from,
            to: band.to,
            color:
              index % 2 === 0
                ? isDarkTheme
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(17, 17, 17, 0.035)"
                : isDarkTheme
                  ? "rgba(255, 255, 255, 0.10)"
                  : "rgba(17, 17, 17, 0.075)",
            borderColor: isDarkTheme
              ? "rgba(255, 255, 255, 0.18)"
              : "rgba(17, 17, 17, 0.14)",
            borderWidth: 1,
          }) as Highcharts.XAxisPlotBandsOptions,
      );
    },
    [visibleWellActivityBands],
  );

  // Band labels, drawn with the renderer so each one can be centred in its band
  // and fitted to the pixels actually available: the full well name when it fits,
  // an ellipsised name when it doesn't, and the stage alone when even that is too
  // wide. Hovering a label reveals the complete name.
  const renderWellActivityLabels = useCallback(
    (
      chart: Highcharts.Chart,
      padId: number | null,
      fleetGroupForBands: FleetGroup | null,
    ) => {
      const chartWithLabels = chart as Highcharts.Chart & {
        customWellActivityLabels?: Highcharts.SVGElement[];
        customWellActivityTooltip?: Highcharts.SVGElement | null;
      };

      chartWithLabels.customWellActivityLabels?.forEach((label) =>
        label.destroy(),
      );
      chartWithLabels.customWellActivityLabels = [];

      const destroyTooltip = () => {
        chartWithLabels.customWellActivityTooltip?.destroy();
        chartWithLabels.customWellActivityTooltip = null;
      };

      destroyTooltip();

      const xAxis = chart.xAxis?.[0];
      if (!xAxis) return;

      const isDarkTheme = theme === "dark";
      const extremes = xAxis.getExtremes();
      const windowStart = Number.isFinite(extremes.min)
        ? extremes.min
        : -Infinity;
      const windowEnd = Number.isFinite(extremes.max) ? extremes.max : Infinity;

      visibleWellActivityBands(padId, windowStart, windowEnd).forEach(
        (band) => {
          const left = Math.max(
            chart.plotLeft,
            xAxis.toPixels(band.from, false),
          );
          const right = Math.min(
            chart.plotLeft + chart.plotWidth,
            xAxis.toPixels(band.to, false),
          );

          if (!Number.isFinite(left) || !Number.isFinite(right)) return;

          // Leave a gutter so a label never touches its band's border.
          const available = right - left - 12;
          if (available < 22) return;

          const stageText = wellActivityStageText(band);
          const isSimulFrac = isSimulFracBand(band);
          const wellsText = formatWellNames(
            band.wells.map((well) => well.wellName),
          );
          // The stage and the SIMUL-FRAC tag sit in the suffix so that fitting trims
          // the well names and never drops the fact that it IS a simul-frac.
          const suffixParts = [
            stageText,
            isSimulFrac ? "SIMUL-FRAC" : "",
          ].filter(Boolean);
          const suffix = suffixParts.length
            ? ` · ${suffixParts.join(" · ")}`
            : "";
          const fullText = `${wellsText}${suffix}`;
          const centreX = (left + right) / 2;
          const y = chart.plotTop + 14;

          const label = chart.renderer
            .text(fullText, centreX, y)
            .attr({ align: "center", zIndex: 15 })
            .css({
              color: isDarkTheme ? "#c3c2b7" : "#52514e",
              fontSize: "10px",
              fontWeight: "600",
              cursor: "pointer",
              pointerEvents: "auto",
              whiteSpace: "nowrap",
            } as any)
            .add();

          // Keeps a label click from also reaching the annotation-creating click
          // handler, the same way the alarm markers opt out.
          label.element.classList.add("chart-well-activity-label");

          const widthOf = () => {
            try {
              return label.getBBox().width;
            } catch {
              return 0;
            }
          };

          let width = widthOf();

          // Fit to the available pixels. Estimate from the measured average glyph
          // width rather than trimming one character at a time — `render` fires on
          // every redraw, so this must not cost a measurement per character.
          // Everything the label must never lose, however narrow the band gets.
          const essential = suffix.replace(/^ · /, "") || wellsText;

          if (width > available && width > 0) {
            const perChar = width / fullText.length;
            const budget = Math.floor(available / perChar);
            const nameBudget = budget - suffix.length - 1;

            if (nameBudget >= 3) {
              label.attr({
                text: `${wellsText.slice(0, nameBudget)}…${suffix}`,
              });
              width = widthOf();

              // One corrective trim covers the estimate being slightly optimistic.
              if (width > available && nameBudget > 3) {
                label.attr({
                  text: `${wellsText.slice(0, nameBudget - 2)}…${suffix}`,
                });
                width = widthOf();
              }
            } else {
              label.attr({ text: essential });
              width = widthOf();
            }

            // Last resort before giving up: the stage and the SIMUL-FRAC tag alone.
            if (width > available) {
              label.attr({ text: essential });
              width = widthOf();
            }

            if (width > available) {
              label.destroy();
              return;
            }
          }

          // Clicking a band narrows this fleet's date range to the stage's
          // period — updating the on-page pickers, reloading the chart for
          // just that window, and priming "Open Graph" to pop out only this
          // stage. "Reset Graph" (in the chart actions row) restores whatever
          // range was active before the click. No well_id filter is applied
          // (unindexed and very slow); the time window alone isolates the
          // run, and a simul-frac band's window legitimately covers both
          // wells at once.
          const focusOnBand = () => {
            destroyTooltip();

            if (!fleetGroupForBands) return;
            const fleetId = fleetGroupForBands.fleet;

            const axis = chart.xAxis?.[0];
            // Clamp to the run's full extent, but never past the data
            // actually loaded — a run can start before the loaded window.
            const loaded = axis?.getExtremes();
            const from =
              axis && Number.isFinite(loaded?.dataMin)
                ? Math.max(band.fullFrom, loaded!.dataMin)
                : band.fullFrom;
            const to =
              axis && Number.isFinite(loaded?.dataMax)
                ? Math.min(band.fullTo, loaded!.dataMax)
                : band.fullTo;

            if (!(to > from)) return;

            const newFrom = new Date(from);
            const newTo = new Date(to);
            const stageLabel =
              wellActivityStageText(band) ||
              formatWellNames(band.wells.map((well) => well.wellName));

            setStageSelectionByFleet((prev) => {
              if (prev[fleetId]) {
                // Already viewing a stage for this fleet — keep the
                // ORIGINAL range so Reset Graph always returns to the true
                // unfiltered view, just relabel to the newly clicked stage.
                return {
                  ...prev,
                  [fleetId]: { ...prev[fleetId]!, label: stageLabel },
                };
              }

              const priorFrom = fromDateByFleetRef.current[fleetId];
              const priorTo = toDateByFleetRef.current[fleetId];
              if (!priorFrom || !priorTo) return prev;

              return {
                ...prev,
                [fleetId]: {
                  prevFrom: priorFrom,
                  prevTo: priorTo,
                  label: stageLabel,
                },
              };
            });

            // Setting the dates is all that is needed: the historical
            // auto-load effect reloads this fleet for the new range and is
            // the ONLY place that clears dateRefreshPendingFleetIds. Calling
            // loadData here as well would fetch the same window twice and
            // would bypass that effect's streaming / deep-link guards.
            setActiveTab("fleet");
            setExpandedFleets((prev) => new Set(prev).add(fleetId));
            setSelectedFleet(fleetGroupForBands);
            setDateRefreshPendingFleetIds((previous) =>
              new Set(previous).add(fleetId),
            );
            setFromDateByFleet((prev) => ({ ...prev, [fleetId]: newFrom }));
            setToDateByFleet((prev) => ({ ...prev, [fleetId]: newTo }));
          };

          const showFullName = () => {
            destroyTooltip();
            chart.tooltip?.hide(0);

            const tooltipX = Math.min(
              chart.chartWidth - 300,
              Math.max(chart.plotLeft, centreX + 10),
            );

            // One line per well so a simul-frac shows both wells with their own
            // stage and pump count — the band label can only abbreviate.
            const heading = isSimulFrac
              ? `<b>Simul-frac — ${band.wells.length} wells</b><br/>`
              : "";
            const wellLines = band.wells
              .map((well) => {
                const parts = [well.wellName];
                if (well.stageNumber !== null)
                  parts.push(`St ${well.stageNumber}`);
                if (well.pumpCount > 0) {
                  parts.push(
                    `${well.pumpCount} ${well.pumpCount === 1 ? "pump" : "pumps"}`,
                  );
                }
                return isSimulFrac
                  ? parts.join(" · ")
                  : `<b>${well.wellName}</b>${parts.length > 1 ? `<br/>${parts.slice(1).join(" · ")}` : ""}`;
              })
              .join("<br/>");

            chartWithLabels.customWellActivityTooltip = chart.renderer
              .label(
                `${heading}${wellLines}<br/>Click to view just this stage`,
                tooltipX,
                y + 14,
                "callout",
                centreX,
                y + 2,
                true,
              )
              .attr({
                fill: isDarkTheme
                  ? "rgba(48, 51, 53, 0.97)"
                  : "rgba(255, 255, 255, 0.98)",
                stroke: isDarkTheme ? "#5C646B" : "#d1d5db",
                "stroke-width": 1,
                padding: 8,
                r: 4,
                zIndex: 25,
              })
              .css({
                color: isDarkTheme ? "#ffffff" : "#111827",
                fontSize: "11px",
                pointerEvents: "none",
              } as any)
              .add();
          };

          label.on("mouseover", showFullName);
          label.on("mouseout", destroyTooltip);
          label.on("click", focusOnBand);

          chartWithLabels.customWellActivityLabels = [
            ...(chartWithLabels.customWellActivityLabels || []),
            label,
          ];
        },
      );
    },
    [
      visibleWellActivityBands,
      theme,
      setActiveTab,
      setExpandedFleets,
      setSelectedFleet,
      setDateRefreshPendingFleetIds,
      setFromDateByFleet,
      setToDateByFleet,
      setStageSelectionByFleet,
    ],
  );

  const buildCombinedChartOptions = useCallback(
    (
      data: PumpData[],
      titleText: string,
      overrides?: {
        selectedPumps?: string[];
        hiddenPumps?: Set<string>;
        selectedChannels?: PumpChannel[];
        xAxisRange?: { min?: number; max?: number };
        prebuiltSeries?: Highcharts.SeriesOptionsType[];
        fleetId?: number;
      },
    ): Highcharts.Options => {
      const isDarkTheme = theme === "dark";
      const combinedAxisGutter = getAxisGutter(overrides?.selectedChannels);
      const xRange = overrides?.xAxisRange ?? xAxisRange;
      const zoomFleetId = overrides?.fleetId;
      const chartAlarmEvents =
        zoomFleetId != null
          ? (alarmEventsByFleet[zoomFleetId] ?? [])
          : alarmEvents;
      const isFlaggedReviewChart =
        isFlaggedReengagementView && isDashboardPumpForFleet(zoomFleetId);

      // Flagged Re-engagement review only: draw the user-entered minimum RPM as
      // a visual reference on the RPM axis. The threshold can hide alarm markers
      // whose alarm-time RPM is below the minimum, but it never changes telemetry
      // and does not force the RPM axis to expand when the threshold is outside
      // the currently visible range.
      const chartChannelsForThreshold =
        overrides?.selectedChannels ?? selectedChannels;
      const flaggedRpmThreshold = Number(flaggedChartMinimumRpm);
      const hasFlaggedRpmThreshold =
        isFlaggedReviewChart &&
        flaggedChartMinimumRpm.trim() !== "" &&
        Number.isFinite(flaggedRpmThreshold) &&
        flaggedRpmThreshold >= 0 &&
        chartChannelsForThreshold.some((channel) => channel.value === "rpm");
      const flaggedRpmAxisIndex = hasFlaggedRpmThreshold
        ? getYAxisIndex(
            resolveAxisNameForChannel(
              "rpm",
              chartChannelsForThreshold,
              axisSettings,
            ),
          )
        : -1;

      const hasUserZoom =
        zoomFleetId != null &&
        Boolean(userZoomRangeByFleetRef.current[zoomFleetId]);
      // While Highcharts owns a manual zoom, do not push controlled min/max back
      // through React. Doing so makes the zoomed window look like the base range
      // and Highcharts removes its Reset zoom button on the next streaming tick.
      const displayedXRange = hasUserZoom ? {} : xRange;
      const wellActivityPadId =
        zoomFleetId != null
          ? (() => {
              const fleetGroupForBands =
                fleetGroups.find((group) => group.fleet === zoomFleetId) ??
                null;
              const historicalFrom = fromDateByFleet[zoomFleetId] ?? null;
              const historicalTo = toDateByFleet[zoomFleetId] ?? null;

              return (
                getPadForFleetDateRange(
                  fleetGroupForBands,
                  historicalFrom,
                  historicalTo,
                )?.id ??
                fleetGroupForBands?.latestPad?.id ??
                null
              );
            })()
          : null;

      const normalizeFlaggedReviewSeries = (
        sourceSeries: Highcharts.SeriesOptionsType[],
      ): Highcharts.SeriesOptionsType[] =>
        sourceSeries.map((seriesOption: any) => {
          const seriesName = String(seriesOption?.name || "");
          const isGearSeries = /(^|\s|-)gear(\s|$)/i.test(seriesName);

          return {
            ...seriesOption,
            type: seriesOption?.type === "scatter" ? "scatter" : "line",
            boostThreshold: Number.MAX_SAFE_INTEGER,
            turboThreshold: 0,
            connectNulls: false,
            animation: false,
            fillColor: "transparent",
            fillOpacity: 0,
            step:
              isGearSeries && seriesOption?.type !== "scatter"
                ? "left"
                : seriesOption?.step,
          } as Highcharts.SeriesOptionsType;
        });

      return {
        title: {
          text: titleText,
          align: "center",
          y: 16,
          margin: ALARM_BAND_HEIGHT,
          style: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
            fontSize: "16px",
            fontWeight: "600",
          },
        },
        xAxis: {
          type: "datetime",
          title: {
            text: "Time",
            style: {
              color: isDarkTheme ? "#e5e5e5" : "#333333",
            },
          },
          labels: {
            formatter: function () {
              return Highcharts.dateFormat(
                "%b %e<br/>%I:%M %p",
                Number(this.value),
              );
            },
            style: {
              color: isDarkTheme ? "#e5e5e5" : "#666666",
            },
          },
          min: displayedXRange.min,
          max: displayedXRange.max,
          lineColor: isDarkTheme ? "#4b5563" : "#d1d5db",
          tickColor: isDarkTheme ? "#4b5563" : "#d1d5db",
          // Which well was being pumped over each period.
          plotBands: buildWellActivityPlotBands(
            wellActivityPadId,
            displayedXRange,
            isDarkTheme,
          ),
          events: {
            setExtremes: function (
              this: Highcharts.Axis,
              e: Highcharts.AxisSetExtremesEventObject,
            ) {
              const chart = this.chart;
              if (!chart) return;

              const hasWindow =
                typeof e.min === "number" && typeof e.max === "number";
              const isUserZoomEvent =
                e.trigger === "zoom" || e.trigger === "pan";

              // Let Highcharts own the visible zoom range so its native Reset Zoom
              // button remains available.
              if (zoomFleetId != null && isUserZoomEvent) {
                if (hasWindow) {
                  userZoomRangeByFleetRef.current[zoomFleetId] = {
                    min: e.min as number,
                    max: e.max as number,
                  };
                } else {
                  delete userZoomRangeByFleetRef.current[zoomFleetId];
                  setXAxisRangeByFleet((prev) => ({ ...prev }));
                }
              }

              const canAutoReloadHistoricalZoom =
                zoomFleetId != null &&
                isUserZoomEvent &&
                !streamingFleetIdsRef.current.has(zoomFleetId) &&
                !isFlaggedReviewChart;

              if (canAutoReloadHistoricalZoom) {
                const fleetForZoom = fleetGroups.find(
                  (group) => group.fleet === zoomFleetId,
                );
                const baseFrom = fromDateByFleet[zoomFleetId];
                const baseTo = toDateByFleet[zoomFleetId];
                const channelsForZoom =
                  overrides?.selectedChannels ?? selectedChannels;

                if (!hasWindow) {
                  const pendingTimer =
                    historicalZoomReloadTimerRef.current[zoomFleetId];
                  if (pendingTimer) {
                    clearTimeout(pendingTimer);
                    delete historicalZoomReloadTimerRef.current[zoomFleetId];
                  }

                  if (
                    historicalZoomDetailActiveByFleetRef.current[zoomFleetId] &&
                    fleetForZoom &&
                    baseFrom &&
                    baseTo
                  ) {
                    historicalZoomDetailActiveByFleetRef.current[zoomFleetId] =
                      false;
                    void reloadCombinedHistoricalZoomRange(
                      fleetForZoom,
                      baseFrom,
                      baseTo,
                      channelsForZoom,
                      false,
                    );
                    return;
                  }
                }

                if (hasWindow && fleetForZoom) {
                  const zoomStart = new Date(e.min as number);
                  const zoomEnd = new Date(e.max as number);
                  const zoomResolution = getAutoResolutionConfig(
                    zoomStart,
                    zoomEnd,
                  ).code;
                  const baseResolution = getAutoResolutionConfig(
                    baseFrom ?? zoomStart,
                    baseTo ?? zoomEnd,
                  ).code;

                  const resolutionRank: Record<string, number> = {
                    "1h": 0,
                    "1m": 1,
                    "1s": 2,
                  };

                  if (
                    resolutionRank[zoomResolution] >
                    resolutionRank[baseResolution]
                  ) {
                    const existingTimer =
                      historicalZoomReloadTimerRef.current[zoomFleetId];
                    if (existingTimer) clearTimeout(existingTimer);

                    historicalZoomReloadTimerRef.current[zoomFleetId] =
                      setTimeout(() => {
                        historicalZoomDetailActiveByFleetRef.current[
                          zoomFleetId
                        ] = true;
                        delete historicalZoomReloadTimerRef.current[
                          zoomFleetId
                        ];

                        void reloadCombinedHistoricalZoomRange(
                          fleetForZoom,
                          zoomStart,
                          zoomEnd,
                          channelsForZoom,
                          true,
                        );
                      }, 200);

                    return;
                  }
                }
              }

              const buffers =
                zoomFleetId != null
                  ? fullResByFleetRef.current[zoomFleetId]
                  : undefined;
              if (buffers) {
                const loaded =
                  zoomFleetId != null
                    ? (loadedChartChannelsByFleet[zoomFleetId] ?? [])
                    : [];
                const chans = (
                  overrides?.selectedChannels ?? selectedChannels
                ).filter((c) => loaded.includes(c.value));
                const pumps = overrides?.selectedPumps ?? selectedPumps;
                const byName = new Map<string, [number, number | null][]>();

                for (const pumpName of pumps) {
                  const chMap = buffers.get(pumpName);
                  if (!chMap) continue;

                  for (const ch of chans) {
                    const buf = chMap.get(ch.value);
                    if (!buf || buf.t.length === 0) continue;

                    const windowedBuf = hasWindow
                      ? sliceBuffer(buf, e.min as number, e.max as number)
                      : buf;
                    const renderBuffer = isFlaggedReengagementView
                      ? collapseChannelBufferToOneSecond(windowedBuf)
                      : windowedBuf;

                    byName.set(
                      `${pumpName} - ${ch.label}`,
                      minMaxDownsampleWithGaps(
                        renderBuffer,
                        DEFAULT_MAX_POINTS,
                      ),
                    );
                  }
                }

                chart.series.forEach((series) => {
                  const nextData = byName.get(series.name as string);
                  if (nextData) series.setData(nextData, false);
                });
                chart.redraw();
                return;
              }

              const windowed = hasWindow
                ? data.filter(
                    (row) =>
                      row.time_stamp >= (e.min as number) &&
                      row.time_stamp <= (e.max as number),
                  )
                : data;

              const rebuilt = buildChartSeries(
                windowed,
                overrides?.selectedPumps,
                overrides?.hiddenPumps,
                overrides?.selectedChannels,
              );
              const byName = new Map<string, any>(
                rebuilt.map((series: any) => [series.name, series.data]),
              );

              chart.series.forEach((series) => {
                series.setData(byName.get(series.name) || [], false);
              });
              chart.redraw();
            },
          },
        },
        yAxis: [
          ...configureYAxes(
            overrides?.selectedChannels ?? selectedChannels,
          ).map((axis, axisIndex) => ({
            ...axis,
            plotLines:
              hasFlaggedRpmThreshold && axisIndex === flaggedRpmAxisIndex
                ? [
                    ...(((axis as any).plotLines || []) as any[]),
                    {
                      id: "flagged-rpm-threshold",
                      value: flaggedRpmThreshold,
                      color: "#f59e0b",
                      width: 2,
                      dashStyle: "Dash",
                      zIndex: 7,
                      label: {
                        text: `Min RPM ${flaggedRpmThreshold.toLocaleString()}`,
                        align: "right",
                        x: -8,
                        y: -6,
                        style: {
                          color: "#f59e0b",
                          fontSize: "10px",
                          fontWeight: "700",
                        },
                      },
                    },
                  ]
                : (axis as any).plotLines,
            title: {
              ...axis.title,
              style: {
                ...(axis.title?.style || {}),
                color: isDarkTheme ? "#e5e5e5" : "#333333",
              },
            },
            labels: {
              ...axis.labels,
              style: {
                ...((axis.labels as Highcharts.YAxisLabelsOptions)?.style ||
                  {}),
                color: isDarkTheme ? "#e5e5e5" : "#666666",
              },
            },
          })),
          emptyChartGridAxis(
            isDarkTheme,
            !configureYAxes(
              overrides?.selectedChannels ?? selectedChannels,
            ).some((axis) => axis.visible),
          ),
          {
            min: 0,
            max: 1.15,
            visible: false,
          },
        ],

        series: (() => {
          const sourceSeries = overrides?.prebuiltSeries
            ? overrides?.selectedChannels?.length === 1
              ? overrides.prebuiltSeries.map((seriesOption: any) => ({
                  ...seriesOption,
                  yAxis: 0,
                }))
              : overrides.prebuiltSeries
            : buildChartSeries(
                data,
                overrides?.selectedPumps,
                overrides?.hiddenPumps,
                overrides?.selectedChannels,
              );

          return isFlaggedReviewChart
            ? normalizeFlaggedReviewSeries(sourceSeries)
            : sourceSeries;
        })(),
        credits: { enabled: false },
        chart: {
          zooming: {
            type: "x",
          },
          height: 540,
          spacingTop: 8,
          spacingBottom: 8,
          marginLeft: combinedAxisGutter,
          marginRight: combinedAxisGutter,
          backgroundColor: isDarkTheme ? "#303335" : "#ffffff",
          animation: false,
          events: {
            render: function () {
              const chart = this as Highcharts.Chart;

              renderAlarmMarkers(
                chart,
                overrides?.selectedPumps ?? selectedPumps,
                chartAlarmEvents,
              );

              renderAnnotationMarkers(chart);

              const chartWithReset = chart as Highcharts.Chart & {
                __padTrackResetZoomButton?: Highcharts.SVGElement;
              };

              const hasManualZoom =
                zoomFleetId != null &&
                Boolean(userZoomRangeByFleetRef.current[zoomFleetId]);

              const hasNativeResetButton = Boolean(
                (chart as any).resetZoomButton,
              );

              // Only a manual zoom gets an on-chart way out. A stage focus is
              // undone by the "Reset Graph" button in the chart actions row —
              // showing one here too gave the page two of them.
              const shouldShowCustomReset =
                zoomFleetId != null &&
                hasManualZoom &&
                !hasNativeResetButton &&
                !streamingFleetIdsRef.current.has(zoomFleetId) &&
                !isFlaggedReviewChart;

              if (shouldShowCustomReset && zoomFleetId != null) {
                const buttonX = Math.max(
                  chart.plotLeft,
                  chart.plotLeft + chart.plotWidth - 86,
                );

                const buttonY = Math.max(8, chart.plotTop - 48);

                if (!chartWithReset.__padTrackResetZoomButton) {
                  chartWithReset.__padTrackResetZoomButton = (
                    chart.renderer as any
                  )
                    .button(
                      "Reset zoom",
                      buttonX,
                      buttonY,
                      () => resetCombinedHistoricalZoom(zoomFleetId),
                      {
                        fill: isDarkTheme ? "#3a3d40" : "#f7f7f7",

                        stroke: isDarkTheme ? "#6b7280" : "#cccccc",

                        "stroke-width": 1,

                        r: 2,

                        style: {
                          color: isDarkTheme ? "#f3f4f6" : "#333333",

                          fontSize: "11px",
                        },
                      },
                      {
                        fill: isDarkTheme ? "#4b4f52" : "#e6e6e6",
                      },
                    )
                    .attr({
                      zIndex: 20,
                    })
                    .add();
                } else {
                  // Keep the button correctly positioned after resize/redraw.
                  chartWithReset.__padTrackResetZoomButton.attr({
                    x: buttonX,
                    y: buttonY,
                  });
                }
              } else if (chartWithReset.__padTrackResetZoomButton) {
                chartWithReset.__padTrackResetZoomButton.destroy();

                delete chartWithReset.__padTrackResetZoomButton;
              }
              const fleetGroupForBands =
                zoomFleetId != null
                  ? (fleetGroups.find((group) => group.fleet === zoomFleetId) ??
                    null)
                  : null;
              renderWellActivityLabels(
                chart,
                wellActivityPadId,
                fleetGroupForBands,
              );
            },
          },
        },
        responsive: {
          rules: [
            {
              condition: { maxWidth: 900 },
              chartOptions: {
                chart: {
                  marginLeft: 72,
                  marginRight: 72,
                  height: 520,
                },
                legend: {
                  maxHeight: 88,
                },
              },
            },
          ],
        },
        tooltip: {
          shared: false,
          outside: true,
          useHTML: true,
          xDateFormat: "%Y-%m-%d %H:%M:%S",
          valueDecimals: 2,
          backgroundColor: isDarkTheme ? "#303335" : "#ffffff",
          style: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
          },
          borderColor: isDarkTheme ? "#5C646B" : "#e6e6e6",
        },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          layout: "horizontal",
          alignColumns: false,
          floating: false,
          maxHeight: 96,
          x: 0,
          y: 0,
          itemStyle: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
            fontSize: "10px",
            fontWeight: "400",
            textOverflow: "ellipsis",
          },
          itemHoverStyle: {
            color: isDarkTheme ? "#ffffff" : "#000000",
          },
          itemHiddenStyle: {
            color: isDarkTheme ? "#666666" : "#cccccc",
          },
          navigation: {
            enabled: true,
            activeColor: "#92d13d",
            inactiveColor: "#6b7280",
            style: {
              color: isDarkTheme ? "#e5e5e5" : "#333333",
              fontSize: "10px",
            },
          },
          itemDistance: 14,
          itemMarginTop: 2,
          itemMarginBottom: 2,
          padding: 8,
          backgroundColor: isDarkTheme
            ? "rgba(48, 51, 53, 0.9)"
            : "rgba(255, 255, 255, 0.9)",
          borderRadius: 4,
          borderColor: isDarkTheme ? "#3d4247" : "#e6e6e6",
          borderWidth: 1,
        },
        plotOptions: {
          series: {
            stickyTracking: false,
            animation: false,
            // ~2k-point downsampled series: keep in SVG, not flaky per-series
            // boost (see boostThreshold note in the primary chart builder).
            boostThreshold: isFlaggedReviewChart
              ? Number.MAX_SAFE_INTEGER
              : 10000,
            turboThreshold: 0,
            events: {
              click: function (event: any) {
                if (shouldIgnoreChartClick(event)) return;

                const timestamp = Math.round(event?.point?.x);
                if (!timestamp) return;

                handleCreateAnnotation(timestamp);
              },
            },
            point: {
              events: {
                mouseOver: function () {
                  const chart: any = this.series.chart;

                  chart.customAlarmTooltips?.forEach((tooltip: any) =>
                    tooltip.destroy(),
                  );
                  chart.customAlarmTooltips = [];

                  chart.customAlarmMarkers?.forEach((marker: any) => {
                    marker.show();
                  });
                },
                mouseOut: function () {
                  const chart: any = this.series.chart;

                  setTimeout(() => {
                    chart.customAlarmMarkers?.forEach((marker: any) => {
                      marker.show();
                    });
                  }, 150);
                },
              },
            },
          },
          line: {
            lineWidth: 2,
            marker: {
              enabled: false,
              states: {
                hover: { enabled: true, radius: 4 },
              },
            },
          },
          scatter: {
            marker: {
              enabled: true,
            },
          },
        },
        boost: {
          enabled: !isFlaggedReviewChart,
          useGPUTranslations: !isFlaggedReviewChart,
          // false: preallocation sizes the WebGL buffer from the FIRST series and
          // requires all series to have the same point count. Our min/max-downsampled
          // series have DIFFERENT lengths (flat pumps ~1000 pts, varying ~2000), so
          // true made the longer/varying series silently fail to render — leaving
          // only the overlapping flat line on multi-pump fleets.
          usePreallocated: false,
          // Every series is already min/max-downsampled to ~2k points, so SVG
          // handles a normal multi-pump, multi-channel selection. At 50, picking
          // ~17 pumps x 3 channels flipped the whole chart to WebGL, which drew
          // thin 1px lines and made the chart look nothing like the Blender
          // chart. Boost now only kicks in for very large selections.
          seriesThreshold: 150,
        },
      };
    },
    [
      theme,
      xAxisRange,
      configureYAxes,
      getAxisGutter,
      buildChartSeries,
      renderAlarmMarkers,
      renderAnnotationMarkers,
      alarmEvents,
      alarmEventsByFleet,
      handleCreateAnnotation,
      selectedPumps,
      selectedChannels,
      flaggedChartMinimumRpm,
      resolveAxisNameForChannel,
      axisSettings,
      loadedChartChannelsByFleet,
      isFlaggedReengagementView,
      isDashboardPumpForFleet,
      fleetGroups,
      fromDateByFleet,
      toDateByFleet,
      getAutoResolutionConfig,
      reloadCombinedHistoricalZoomRange,
      resetCombinedHistoricalZoom,
      buildWellActivityPlotBands,
      renderWellActivityLabels,
      getPadForFleetDateRange,
    ],
  );

  const buildChartOptionsForData = useCallback(
    (data: PumpData[], titleText: string): Highcharts.Options => {
      const isDarkTheme = theme === "dark";

      return {
        title: {
          text: titleText,
          style: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
            fontSize: "14px",
            fontWeight: "600",
          },
        },
        xAxis: {
          type: "datetime",
          title: { text: undefined },
          labels: {
            style: {
              color: isDarkTheme ? "#cfd4da" : "#666666",
              fontSize: "10px",
            },
          },
          min: xAxisRange.min,
          max: xAxisRange.max,
          lineColor: isDarkTheme ? "#4b5563" : "#d1d5db",
          tickColor: isDarkTheme ? "#4b5563" : "#d1d5db",
        },
        yAxis: [
          ...configureYAxes().map((axis) => ({
            ...axis,
            title: {
              ...axis.title,
              style: {
                color: isDarkTheme ? "#cfd4da" : "#666666",
                fontSize: "11px",
              },
            },
            labels: {
              ...axis.labels,
              style: {
                color: isDarkTheme ? "#cfd4da" : "#666666",
                fontSize: "10px",
              },
            },
          })),
          emptyChartGridAxis(
            isDarkTheme,
            !configureYAxes().some((axis) => axis.visible),
          ),
          {
            min: 0,
            max: 1.15,
            visible: false,
          },
        ],
        series: buildChartSeries(data),
        credits: { enabled: false },
        chart: {
          zooming: { type: "x" },
          height: 260,
          spacingTop: 35,
          spacingRight: 40,
          marginRight: 260,
          spacingBottom: 8,
          spacingLeft: 8,
          backgroundColor: isDarkTheme ? "#303335" : "#ffffff",
          animation: false,
          events: {
            render: function () {
              const chart = this as Highcharts.Chart;
              renderAlarmMarkers(chart, selectedPumps);
              renderAnnotationMarkers(chart);
            },
          },
        },
        tooltip: {
          shared: false,
          outside: true,
          useHTML: true,
          xDateFormat: "%Y-%m-%d %H:%M:%S",
          backgroundColor: isDarkTheme ? "#303335" : "#ffffff",
          style: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
          },
          borderColor: isDarkTheme ? "#5C646B" : "#e6e6e6",
        },
        legend: {
          enabled: true,
          align: "right",
          verticalAlign: "top",
          layout: "horizontal",
          itemStyle: {
            color: isDarkTheme ? "#e5e5e5" : "#333333",
            fontSize: "10px",
            fontWeight: "400",
          },
          itemMarginBottom: 4,
          padding: 4,
          symbolRadius: 0,
        },
        plotOptions: {
          series: {
            stickyTracking: false,
            animation: false,
            // ~2k-point downsampled series: keep in SVG, not flaky per-series
            // boost (see boostThreshold note in the primary chart builder).
            boostThreshold: 10000,
            turboThreshold: 0,
            events: {
              click: function (event: any) {
                if (shouldIgnoreChartClick(event)) return;

                const timestamp = Math.round(event?.point?.x);
                if (!timestamp) return;

                handleCreateAnnotation(timestamp);
              },
            },
            point: {
              events: {
                mouseOver: function () {
                  const chart: any = this.series.chart;

                  chart.customAlarmTooltips?.forEach((tooltip: any) =>
                    tooltip.destroy(),
                  );
                  chart.customAlarmTooltips = [];

                  chart.customAlarmMarkers?.forEach((marker: any) => {
                    marker.hide();
                  });
                },
                mouseOut: function () {
                  const chart: any = this.series.chart;

                  setTimeout(() => {
                    chart.customAlarmMarkers?.forEach((marker: any) => {
                      marker.show();
                    });
                  }, 150);
                },
              },
            },
          },
          line: {
            lineWidth: 2,
            marker: {
              enabled: false,
              states: {
                hover: { enabled: true, radius: 4 },
              },
            },
          },
          scatter: {
            marker: {
              enabled: true,
            },
          },
        },
        boost: {
          enabled: true,
          useGPUTranslations: true,
          // false: preallocation sizes the WebGL buffer from the FIRST series and
          // requires all series to have the same point count. Our min/max-downsampled
          // series have DIFFERENT lengths (flat pumps ~1000 pts, varying ~2000), so
          // true made the longer/varying series silently fail to render — leaving
          // only the overlapping flat line on multi-pump fleets.
          usePreallocated: false,
          seriesThreshold: 50,
        },
      };
    },
    [
      theme,
      xAxisRange,
      configureYAxes,
      buildChartSeries,
      renderAlarmMarkers,
      renderAnnotationMarkers,
      handleCreateAnnotation,
      selectedPumps,
    ],
  );

  const computeAggregations = (
    data: PumpData[],
    tableChannels: PumpChannel[] = selectedTableChannels,
  ) => {
    const aggregations: any = {};
    if (!Array.isArray(data) || data.length === 0) return aggregations;

    // Precompute the candidate raw keys for each channel ONCE (normalizeChannelKey
    // is regex-heavy, so it must never run per-row). Order matters: exact aliases
    // first, then the channel value itself, mirroring getChannelValue().
    const channelCandidates = tableChannels.map((channel) => {
      const normalized = normalizeChannelKey(channel.value);
      const aliases = CHANNEL_ALIASES[normalized] || [normalized];
      return { value: channel.value, aliases: [...aliases, channel.value] };
    });

    // Group rows by pump without allocating intermediate arrays per lookup.
    const pumpGroups = new Map<string, PumpData[]>();
    for (const item of data) {
      let group = pumpGroups.get(item.pump_name);
      if (!group) {
        group = [];
        pumpGroups.set(item.pump_name, group);
      }
      group.push(item);
    }

    pumpGroups.forEach((items, pumpName) => {
      // Build a normalized-key → actual-key index for this pump's rows ONCE
      // (rows from one query share a schema), so channel resolution is a Map
      // lookup instead of an Object.entries + per-key normalize on every row.
      const actualKeys = new Set<string>();
      for (const item of items) {
        for (const key in item) actualKeys.add(key);
      }
      const normalizedIndex = new Map<string, string>();
      actualKeys.forEach((key) => {
        const norm = normalizeChannelKey(key);
        if (!normalizedIndex.has(norm)) normalizedIndex.set(norm, key);
      });

      // Resolve each channel to a single concrete row key up front.
      const resolved: Array<{ value: string; key: string }> = [];
      for (const candidate of channelCandidates) {
        let resolvedKey: string | null = null;
        for (const alias of candidate.aliases) {
          if (actualKeys.has(alias)) {
            resolvedKey = alias;
            break;
          }
          const hit = normalizedIndex.get(normalizeChannelKey(alias));
          if (hit) {
            resolvedKey = hit;
            break;
          }
        }
        if (resolvedKey)
          resolved.push({ value: candidate.value, key: resolvedKey });
      }

      // Single pass: accumulate min/max/sum/count (+ discharge-gated avg) for
      // every resolved channel at once. No spreads, no repeated scans.
      const acc: Record<
        string,
        {
          min: number;
          max: number;
          sum: number;
          count: number;
          avgSum: number;
          avgCount: number;
        }
      > = {};
      for (const channel of resolved) {
        acc[channel.value] = {
          min: Infinity,
          max: -Infinity,
          sum: 0,
          count: 0,
          avgSum: 0,
          avgCount: 0,
        };
      }

      for (const item of items) {
        const discharge = Number(item.calculated_discharge_rate);
        const inAvgWindow = !isNaN(discharge) && discharge > 0.5;

        for (const channel of resolved) {
          const raw = (item as any)[channel.key];
          if (raw === null || raw === undefined) continue;
          const value = Number(raw);
          if (isNaN(value)) continue;

          const entry = acc[channel.value];
          if (value < entry.min) entry.min = value;
          if (value > entry.max) entry.max = value;
          entry.sum += value;
          entry.count += 1;
          if (inAvgWindow) {
            entry.avgSum += value;
            entry.avgCount += 1;
          }
        }
      }

      const pumpAggregations: any = {};
      for (const channel of resolved) {
        const entry = acc[channel.value];
        if (entry.count > 0) {
          pumpAggregations[channel.value] = {
            min: entry.min,
            max: entry.max,
            sum: entry.sum,
            avg: entry.avgCount > 0 ? entry.avgSum / entry.avgCount : null,
          };
        }
      }

      aggregations[pumpName] = pumpAggregations;
    });

    return aggregations;
  };

  const computeStageAggregations = (
    data: StageChannelData[],
    tableChannels: PumpChannel[],
  ) => {
    const aggregations: Record<
      string,
      { min: number; max: number; sum: number; avg: number }
    > = {};

    tableChannels
      .filter((channel) => isStageChannel(channel.value))
      .forEach((channel) => {
        const values = data
          .map((row) => getChannelValue(row, channel.value))
          .filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              value !== "" &&
              Number.isFinite(Number(value)),
          )
          .map((value) => Number(value));

        if (values.length === 0) return;

        const sum = values.reduce((total, value) => total + value, 0);
        aggregations[channel.value] = {
          min: Math.min(...values),
          max: Math.max(...values),
          sum,
          avg: sum / values.length,
        };
      });

    return aggregations;
  };

  const loadFleetTableChannels = useCallback(
    async (fleetGroup: FleetGroup, channels: PumpChannel[]) => {
      const fleetId = fleetGroup.fleet;
      const start = fromDateByFleet[fleetId];
      const end = toDateByFleet[fleetId];
      const supportedChannels = filterUnsupportedPumpChannels(channels);

      if (!start || !end || supportedChannels.length === 0) return;

      const existingTimer = tableChannelReloadTimerRef.current[fleetId];
      if (existingTimer) clearTimeout(existingTimer);

      tableChannelReloadTimerRef.current[fleetId] = setTimeout(async () => {
        delete tableChannelReloadTimerRef.current[fleetId];

        try {
          const token = getAuthToken();
          if (!token) return;

          const alreadyLoaded = new Set(
            loadedTableChannelsByFleet[fleetId] || [],
          );

          const missingChannels = supportedChannels.filter(
            (channel) => !alreadyLoaded.has(channel.value),
          );

          if (missingChannels.length === 0) {
            setShowTableChannelNoticeByFleet((prev) => ({
              ...prev,
              [fleetId]: false,
            }));
            return;
          }

          setShowTableChannelNoticeByFleet((prev) => ({
            ...prev,
            [fleetId]: true,
          }));

          const pumpChannels = missingChannels.filter(
            (channel) => !isStageChannel(channel.value),
          );
          const stageChannels = missingChannels.filter((channel) =>
            isStageChannel(channel.value),
          );

          const selectedWellIds = getSelectedWellIdsForPad(
            fleetGroup.latestPad.id,
          );
          const wells = padWells[fleetGroup.latestPad.id] || [];
          const showAll =
            showAllWellsByPadRef.current[fleetGroup.latestPad.id] === true ||
            (wells.length > 0 && selectedWellIds.length >= wells.length);
          const wellIdsToSend = showAll ? [] : selectedWellIds;

          if (pumpChannels.length > 0) {
            const aliases = Array.from(
              new Set([
                ...pumpChannels.flatMap(
                  (channel) =>
                    CHANNEL_ALIASES[normalizeChannelKey(channel.value)] || [
                      channel.value,
                    ],
                ),
                "calculated_discharge_rate",
              ]),
            );

            const fetchOneWell = async (wellId?: number) => {
              const params = new URLSearchParams();
              params.append("fleet", String(fleetId));
              params.append("start_time", String(start.getTime()));
              params.append("end_time", String(end.getTime()));
              params.append("resolution", "1m");
              params.append("channels", aliases.join(","));
              if (wellId !== undefined) {
                params.append("well_id", String(wellId));
              }

              const response = await axios.get(
                `${backendUrl}/pumps?${params.toString()}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  timeout: 180000,
                },
              );

              const payload = response.data;
              if (Array.isArray(payload)) return payload as PumpData[];
              if (Array.isArray(payload?.data))
                return payload.data as PumpData[];
              if (Array.isArray(payload?.rows))
                return payload.rows as PumpData[];
              return [] as PumpData[];
            };

            const rows =
              wellIdsToSend.length > 1
                ? (
                    await Promise.all(
                      wellIdsToSend.map((wellId) => fetchOneWell(wellId)),
                    )
                  ).flat()
                : await fetchOneWell(wellIdsToSend[0]);

            const newAggregations = computeAggregations(rows, pumpChannels);

            setAggregatedDataByFleet((prev) => {
              const current = prev[fleetId] || {};
              const merged: Record<string, any> = { ...current };

              Object.entries(newAggregations).forEach(
                ([pumpName, pumpValues]: [string, any]) => {
                  merged[pumpName] = {
                    ...(merged[pumpName] || {}),
                    ...pumpValues,
                  };
                },
              );

              if (selectedFleet?.fleet === fleetId) {
                setAggregatedData(merged);
              }

              return { ...prev, [fleetId]: merged };
            });
          }

          if (stageChannels.length > 0) {
            const params = new URLSearchParams();
            params.append("fleet", String(fleetId));
            params.append("start_time", String(start.getTime()));
            params.append("end_time", String(end.getTime()));
            wellIdsToSend.forEach((wellId) =>
              params.append("well_id", String(wellId)),
            );

            const response = await axios.get(
              `${backendUrl}/treatment/channels?${params.toString()}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 180000,
              },
            );

            const stageRows: StageChannelData[] = Array.isArray(
              response.data?.data,
            )
              ? response.data.data
              : [];
            const stageAggregations = computeStageAggregations(
              stageRows,
              stageChannels,
            );

            setAggregatedDataByFleet((prev) => {
              const current = prev[fleetId] || {};
              const pumpNames =
                Object.keys(current).length > 0
                  ? Object.keys(current)
                  : selectedPumpsByFleet[fleetId] || [];
              const merged: Record<string, any> = { ...current };

              pumpNames.forEach((pumpName) => {
                merged[pumpName] = {
                  ...(merged[pumpName] || {}),
                  ...stageAggregations,
                };
              });

              if (selectedFleet?.fleet === fleetId) {
                setAggregatedData(merged);
              }

              return { ...prev, [fleetId]: merged };
            });
          }

          const loadedValues = Array.from(
            new Set([
              ...(loadedTableChannelsByFleet[fleetId] || []),
              ...missingChannels.map((channel) => channel.value),
            ]),
          );

          setLoadedTableChannelsByFleet((prev) => ({
            ...prev,
            [fleetId]: loadedValues,
          }));
          setShowTableChannelNoticeByFleet((prev) => ({
            ...prev,
            [fleetId]: false,
          }));
        } catch (error) {
          console.error("Error loading newly selected table channels:", error);
          setShowTableChannelNoticeByFleet((prev) => ({
            ...prev,
            [fleetId]: false,
          }));
          toast.error("Failed to load selected table channels");
        }
      }, 250);
    },
    [
      backendUrl,
      fromDateByFleet,
      toDateByFleet,
      loadedTableChannelsByFleet,
      getSelectedWellIdsForPad,
      padWells,
      selectedFleet,
      selectedPumpsByFleet,
    ],
  );

  const getAggregatedChannelValue = (
    rowData: any,
    channel: string,
    type: "avg" | "sum" | "min" | "max",
  ) => {
    const normalized = normalizeChannelKey(channel);
    const aliases = CHANNEL_ALIASES[normalized] || [normalized];

    for (const key of aliases) {
      const value = rowData?.[key]?.[type];

      if (
        value !== null &&
        value !== undefined &&
        !Number.isNaN(Number(value))
      ) {
        return value;
      }
    }

    return null;
  };

  const getAggregatedRowSnapshot = (
    rowData: any,
    type: "avg" | "sum" | "min" | "max",
  ) => {
    const snapshot: Record<string, any> = {};

    if (!rowData) return snapshot;

    Object.keys(rowData).forEach((key) => {
      const value = rowData[key]?.[type];

      if (
        value !== null &&
        value !== undefined &&
        !Number.isNaN(Number(value))
      ) {
        snapshot[key] = value;
      }
    });

    Object.keys(CHANNEL_ALIASES).forEach((channel) => {
      const value = getAggregatedChannelValue(rowData, channel, type);

      if (
        value !== null &&
        value !== undefined &&
        !Number.isNaN(Number(value))
      ) {
        snapshot[channel] = value;
      }
    });

    return snapshot;
  };

  const calculateAggregations = (data: PumpData[]) => {
    setAggregatedData(computeAggregations(data, selectedTableChannels));
  };

  const handleSaveTemplate = async () => {
    if (!templateName) {
      toast.warning("Please enter a template name");
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const templateChannels = selectedChannels.map((ch) => {
        const axisPosition =
          Object.keys(axisSettings).find((axis) =>
            axisSettings[axis].channels.includes(ch.value),
          ) || "left1";

        const axisNumber = parseInt(axisPosition.match(/\d+/)?.[0] || "1");

        return {
          channel: ch.value,
          display_position: axisPosition,
          min_value: axisSettings[axisPosition].min ?? null,
          max_value: axisSettings[axisPosition].max ?? null,
          axis: axisNumber,
        };
      });

      await axios.post(
        `${backendUrl}/widget-templates`,
        {
          template_name: templateName,
          widget_type: "CHART",
          channels: templateChannels,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success("Template saved successfully");
      fetchTemplates();
      setShowTemplateModal(false);
      setTemplateName("");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };
  const buildChartSeriesWithChannels = (
    data: PumpData[],
    pumpsToShow: string[],
    hiddenSet: Set<string>,
    channelsToUse: PumpChannel[],
    axisSettingsToUse: typeof axisSettings,
  ): Highcharts.SeriesOptionsType[] => {
    const isFlaggedReviewChart =
      isFlaggedReengagementView &&
      isDashboardPumpForFleet(selectedFleetRef.current?.fleet);
    const seriesMap = new Map<string, Map<string, [number, number][]>>();

    data.forEach((item) => {
      const pumpName = item.pump_name;

      if (!pumpsToShow.includes(pumpName)) return;

      if (!seriesMap.has(pumpName)) {
        seriesMap.set(pumpName, new Map());
      }

      const pumpSeries = seriesMap.get(pumpName)!;

      channelsToUse.forEach((channel) => {
        const value = getChannelValue(item, channel.value);

        if (
          value !== null &&
          value !== undefined &&
          !Number.isNaN(Number(value))
        ) {
          if (!pumpSeries.has(channel.value)) {
            pumpSeries.set(channel.value, []);
          }

          pumpSeries
            .get(channel.value)!
            .push([
              item.time_stamp,
              toWholeNumberReading(channel.value, Number(value)),
            ]);
        }
      });
    });

    const axisIndexMap: Record<string, number> = {
      left1: 0,
      left2: 1,
      right1: 2,
      right2: 3,
    };

    const series: Highcharts.SeriesOptionsType[] = [];

    const overlappingSeriesDashStyles: Highcharts.DashStyleValue[] = [
      "Solid",
      "ShortDash",
      "ShortDot",
      "ShortDashDot",
      "Dash",
      "Dot",
      "DashDot",
      "LongDash",
      "LongDashDot",
    ];

    seriesMap.forEach((pumpSeries, pumpName) => {
      channelsToUse.forEach((channel, channelIndex) => {
        const points = pumpSeries.get(channel.value);

        if (isPowerEndTemperatureChannelValue(channel.value)) {
          const hasRealTemperatureData = Boolean(
            points?.some(
              ([, value]) =>
                Number.isFinite(Number(value)) && Number(value) !== 0,
            ),
          );

          if (!hasRealTemperatureData) {
            return;
          }
        }

        const axisName = resolveAxisNameForChannel(
          channel.value,
          channelsToUse,
          axisSettingsToUse,
        );

        // Keep every selected channel visible in the legend even when the
        // selected pump/time range has no values for that channel yet.
        // This prevents the UI from saying "2 selected" while showing only
        // one legend item. We do not fabricate telemetry: a no-data channel
        // is represented by an empty series until the backend returns values.
        if (!points || points.length === 0) {
          series.push({
            type: "line",
            name: `${pumpName} - ${channel.label}`,
            data: [],
            showInLegend: true,
            connectNulls: false,
            yAxis: axisIndexMap[axisName] ?? 0,
            stickyTracking: false,
            animation: false,
            boostThreshold: Number.MAX_SAFE_INTEGER,
            turboThreshold: 0,
            fillColor: "transparent",
            fillOpacity: 0,
            marker: { enabled: false },
            dashStyle:
              overlappingSeriesDashStyles[
                channelIndex % overlappingSeriesDashStyles.length
              ],
            lineWidth: 2,
            opacity: 0.95,
            visible: !hiddenSet.has(pumpName),
            zIndex: channelIndex + 1,
          });
          return;
        }

        const sortedData = points.sort((a, b) => a[0] - b[0]);

        // First remove rows that have the exact same timestamp. Because the data
        // is sorted, Map keeps the last value reported for that timestamp.
        const uniqueByTimestamp = Array.from(
          new Map(
            sortedData.map(([timestamp, value]) => [
              timestamp,
              [timestamp, value] as [number, number],
            ]),
          ).values(),
        );

        /*
         * Flagged Re-engagement View Chart is intended to show one-second pump
         * behavior around the event. Some pumps (Fleet 49 / FPQ12015) return
         * multiple sub-second samples. Sending those samples through min/max
         * downsampling creates repeated low/high points inside each pixel bucket,
         * which Highcharts draws as thousands of vertical lines and makes the
         * chart look like a solid filled block.
         *
         * Keep only the latest sample in each second for this view. Normal Fleet
         * charts retain their existing min/max downsampling behavior.
         */
        const flaggedOneSecondData = isFlaggedReviewChart
          ? Array.from(
              new Map(
                uniqueByTimestamp.map(([timestamp, value]) => {
                  const secondTimestamp = Math.floor(timestamp / 1000) * 1000;
                  return [
                    secondTimestamp,
                    [secondTimestamp, value] as [number, number],
                  ];
                }),
              ).values(),
            )
          : uniqueByTimestamp;

        const shouldDownsample =
          !isFlaggedReviewChart &&
          flaggedOneSecondData.length > DEFAULT_MAX_POINTS;

        const downsampledData = shouldDownsample
          ? minMaxDownsampleTuples(flaggedOneSecondData)
          : flaggedOneSecondData;
        // Break the line where the pump stopped reporting instead of drawing
        // straight across the outage. The flagged review chart keeps its
        // existing one-second rendering.
        const renderData = isFlaggedReviewChart
          ? downsampledData
          : insertGapBreaks(downsampledData);

        const validPointCount = renderData.filter(
          (point) => point[1] !== null,
        ).length;

        const isSinglePoint = validPointCount <= 1;

        series.push({
          type: isSinglePoint ? "scatter" : "line",
          name: `${pumpName} - ${channel.label}`,
          data: renderData,
          connectNulls: false,
          yAxis: axisIndexMap[axisName] ?? 0,
          stickyTracking: false,
          animation: false,
          boostThreshold: isFlaggedReviewChart
            ? Number.MAX_SAFE_INTEGER
            : 10000,
          turboThreshold: 0,
          fillColor: "transparent",
          fillOpacity: 0,
          step: channel.value === "gear" && !isSinglePoint ? "left" : undefined,
          marker: isSinglePoint
            ? {
                enabled: true,
                radius: 8,
                symbol: "circle",
                lineWidth: 2,
                lineColor: "#ffffff",
              }
            : {
                enabled: false,
                states: {
                  hover: { enabled: true, radius: 4 },
                },
              },

          dashStyle: isSinglePoint
            ? undefined
            : overlappingSeriesDashStyles[
                channelIndex % overlappingSeriesDashStyles.length
              ],
          lineWidth: isSinglePoint ? 0 : 2,
          opacity: 0.95,
          visible: !hiddenSet.has(pumpName),
          zIndex: isSinglePoint ? 100 : channelIndex + 1,
        });
      });
    });

    return series;
  };

  const getPowerEndTemperatureRole = (
    value: string,
  ): "inlet" | "outlet" | null => {
    const compact = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .replace(/f$/, "");

    // Outlet
    if (
      [
        "powerendoiloutlettemperature",
        "peoiloutlettemperature",
        "peoutlettemp",
      ].includes(compact)
    ) {
      return "outlet";
    }

    // IMPORTANT:
    // Power End Oil Temperature is the old name for INLET temperature.
    if (
      [
        "powerendoiltemperature",
        "powerendoilinlettemperature",
        "peoiltemperature",
        "peoilinlettemperature",
        "petemp",
      ].includes(compact)
    ) {
      return "inlet";
    }

    return null;
  };

  const getCanonicalPowerEndTemperatureChannel = (
    role: "inlet" | "outlet",
  ): PumpChannel => {
    const value =
      role === "inlet"
        ? "powerend_oil_inlet_temperature"
        : "powerend_oil_outlet_temperature";

    const label =
      role === "inlet"
        ? "Power End: Oil Inlet Temperature (°F)"
        : "Power End: Oil Outlet Temperature (°F)";

    return (
      availableChannels.find((channel) => channel.value === value) ||
      findChannelByValue(value) || {
        value,
        label,
      }
    );
  };

  const resolveTemplateChannel = (channelValue: string): PumpChannel => {
    const normalizedValue = String(channelValue || "").trim();

    if (isUnsupportedPowerEndPressureChannel(normalizedValue)) {
      return { value: "", label: "" };
    }

    // Normalize legacy/current Power End temperature names.
    const powerEndTemperatureRole = getPowerEndTemperatureRole(normalizedValue);

    if (powerEndTemperatureRole) {
      return getCanonicalPowerEndTemperatureChannel(powerEndTemperatureRole);
    }

    const normalizedLower = normalizedValue.toLowerCase();

    return (
      availableChannels.find(
        (channel) =>
          channel.value === normalizedValue ||
          channel.value.toLowerCase() === normalizedLower ||
          channel.label.toLowerCase() === normalizedLower ||
          channel.label.toLowerCase().replace(/\s+/g, "_") === normalizedLower,
      ) || {
        value: normalizedValue,
        label: normalizedValue,
      }
    );
  };

  const applyTemplate = (template: Template, fleetGroup?: FleetGroup) => {
    const templateChannels = template.channels || [];

    let resolvedTemplateChannels = templateChannels.map((templateChannel) => ({
      templateChannel,
      channel: resolveTemplateChannel(templateChannel.channel),
    }));

    /*
     * Power End Temperature requirement:
     *
     * Old pumps:
     *   Power End Oil Temperature = Inlet Temperature
     *
     * Pumps with outlet sensor:
     *   Power End Oil Inlet Temperature
     *   Power End Oil Outlet Temperature
     *
     * For CHART templates, whenever any Power End temperature
     * channel is present, show one Inlet + one Outlet channel.
     * Do NOT show Oil Temperature as a third series.
     */
    if (template.widget_type === "CHART") {
      const normalizedTemplateChannels: typeof resolvedTemplateChannels = [];
      let powerEndTemperaturePairAdded = false;

      resolvedTemplateChannels.forEach((entry) => {
        const role = getPowerEndTemperatureRole(entry.channel.value);

        if (!role) {
          normalizedTemplateChannels.push(entry);
          return;
        }

        // Replace the first Power End temperature entry with the
        // canonical Inlet + Outlet pair.
        if (!powerEndTemperaturePairAdded) {
          normalizedTemplateChannels.push(
            {
              templateChannel: entry.templateChannel,
              channel: getCanonicalPowerEndTemperatureChannel("inlet"),
            },
            {
              // Same axis/min/max as inlet so both temperatures
              // are compared on the same scale.
              templateChannel: { ...entry.templateChannel },
              channel: getCanonicalPowerEndTemperatureChannel("outlet"),
            },
          );

          powerEndTemperaturePairAdded = true;
        }

        // Skip additional legacy/inlet/outlet entries.
        // They have already been represented by the pair above.
      });

      resolvedTemplateChannels = normalizedTemplateChannels;
    }

    const channels: PumpChannel[] = resolvedTemplateChannels
      .map(({ channel }) => channel)
      .filter(
        (channel, index, self) =>
          Boolean(channel.value) &&
          self.findIndex((item) => item.value === channel.value) === index,
      );

    if (template.widget_type === "CHART") {
      const newAxisSettings: {
        [axis: string]: {
          channels: string[];
          min: number | undefined;
          max: number | undefined;
        };
      } = {
        left1: { channels: [], min: undefined, max: undefined },
        left2: { channels: [], min: undefined, max: undefined },
        right1: { channels: [], min: undefined, max: undefined },
        right2: { channels: [], min: undefined, max: undefined },
      };

      resolvedTemplateChannels.forEach(
        ({ templateChannel, channel }, index) => {
          if (!channel.value) return;

          const templateAxis =
            templateChannel.display_position as keyof typeof newAxisSettings;
          const fallbackAxis =
            index === 0
              ? "left1"
              : index === 1
                ? "left2"
                : index === 2
                  ? "right1"
                  : "right2";

          const axisName = newAxisSettings[templateAxis]
            ? templateAxis
            : fallbackAxis;

          // Store the resolved channel VALUE in the axis configuration. Older
          // templates may contain a display label/alias instead of the canonical
          // value; using the raw saved string makes the channel appear selected
          // while its series is assigned to the wrong/default axis.
          if (!newAxisSettings[axisName].channels.includes(channel.value)) {
            newAxisSettings[axisName].channels.push(channel.value);
          }

          newAxisSettings[axisName].min =
            templateChannel.min_value !== null
              ? templateChannel.min_value
              : undefined;

          newAxisSettings[axisName].max =
            templateChannel.max_value !== null
              ? templateChannel.max_value
              : undefined;
        },
      );

      setAxisSettings(newAxisSettings);

      if (fleetGroup) {
        const fleetId = fleetGroup.fleet;
        const frozenChannels = channels.map((channel) => ({ ...channel }));

        selectedChannelsByFleetRef.current = {
          ...selectedChannelsByFleetRef.current,
          [fleetId]: frozenChannels,
        };
        setSelectedChannelsByFleet((previous) => ({
          ...previous,
          [fleetId]: frozenChannels,
        }));

        if (selectedFleetRef.current?.fleet === fleetId) {
          setSelectedChannels(frozenChannels);
          selectedChannelsRef.current = frozenChannels;
        }

        if (frozenChannels.length === 0) {
          setChartSeriesByFleet((previous) => ({
            ...previous,
            [fleetId]: [],
          }));
          loadedChartChannelsByFleetRef.current = {
            ...loadedChartChannelsByFleetRef.current,
            [fleetId]: [],
          };
          setLoadedChartChannelsByFleet((previous) => ({
            ...previous,
            [fleetId]: [],
          }));
          return;
        }

        if (streamingFleetIdsRef.current.has(fleetId)) {
          void refreshStreamingChartChannelsForFleet(
            fleetGroup,
            frozenChannels,
          );
        } else {
          scheduleHistoricalChartChannelReload(fleetGroup);
        }

        return;
      }

      const frozenAssetChannels = channels.map((channel) => ({ ...channel }));
      setSelectedAssetChannels(frozenAssetChannels);
      selectedAssetChannelsRef.current = frozenAssetChannels;
      setSelectedChannels(frozenAssetChannels);
      selectedChannelsRef.current = frozenAssetChannels;

      if (frozenAssetChannels.length === 0) {
        setChartSeries([]);
        loadedAssetChannelSignatureRef.current = "";
        return;
      }

      if (activeTab === "asset") {
        scheduleAssetChartChannelReload();
      }
    } else if (template.widget_type === "TABLE") {
      const frozenTableChannels = channels.map((channel) => ({ ...channel }));

      if (fleetGroup) {
        const fleetId = fleetGroup.fleet;

        selectedTableChannelsByFleetRef.current = {
          ...selectedTableChannelsByFleetRef.current,
          [fleetId]: frozenTableChannels,
        };

        setSelectedTableChannelsByFleet((previous) => ({
          ...previous,
          [fleetId]: frozenTableChannels,
        }));

        if (streamingFleetIdsRef.current.has(fleetId)) {
          void refreshStreamingChannelsForFleet(fleetId, frozenTableChannels);
        } else {
          void loadFleetTableChannels(fleetGroup, frozenTableChannels);
        }

        return;
      }

      setSelectedTableChannels(frozenTableChannels);

      if (
        activeTab === "asset" &&
        selectedAssetPumps.length > 0 &&
        fromDate &&
        toDate
      ) {
        void loadAssetData(fromDate, toDate, frozenTableChannels, "table");
      }
    }
  };

  const exportToCSV = () => {
    if (pumpData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const headers = [
      "Pump Name",
      "Timestamp",
      ...selectedTableChannels.map((ch) => ch.label),
    ];
    const rows = pumpData.map((item) => [
      item.pump_name,
      new Date(item.time_stamp).toISOString(),
      ...selectedTableChannels.map((ch) => item[ch.value] || ""),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fleet_${selectedFleet?.fleet}_data_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success("Data exported successfully");
  };

  const filterOldDataPoints = (
    data: PumpData[],
    windowEnd: number,
  ): PumpData[] => {
    const windowStart = windowEnd - STREAMING_WINDOW_MS;
    return data.filter((item) => item.time_stamp >= windowStart);
  };
  const updateLatestValues = (fleetId: number, newData: PumpData[]) => {
    setLatestValuesByFleet((prev) => {
      // Preserve values received in previous SSE messages for this fleet.
      const existingFleetValues = prev[fleetId] || new Map<string, any>();
      const nextFleetValues = new Map<string, any>(existingFleetValues);

      [...newData]
        .sort((a, b) => Number(a.time_stamp || 0) - Number(b.time_stamp || 0))
        .forEach((item) => {
          const pumpName = item.pump_name;
          if (!pumpName) return;

          const previousPump = nextFleetValues.get(pumpName) || {};
          const mergedPump = { ...previousPump };

          Object.entries(item).forEach(([field, value]) => {
            // Preserve previous valid readings when the new sparse row contains null.
            if (value !== null && value !== undefined) {
              mergedPump[field] = value;
            }
          });

          mergedPump.pump_name = pumpName;
          mergedPump.time_stamp = Math.max(
            Number(previousPump.time_stamp || 0),
            Number(item.time_stamp || 0),
          );

          nextFleetValues.set(pumpName, mergedPump);
        });

      // Keep the legacy single-fleet state synchronized.
      if (selectedFleet?.fleet === fleetId) {
        setLatestValues(new Map(nextFleetValues));
      }

      return {
        ...prev,
        [fleetId]: nextFleetValues,
      };
    });
  };

  const applyStreamingPayload = (responseData: any, fleetId: number) => {
    const fleetData = responseData[fleetId] ?? responseData[String(fleetId)];

    // Window anchor = newest timestamp in this payload. The latest-snapshot
    // endpoint (-multi) exposes it as `pump_timestamp`; the lossless incremental
    // endpoint exposes it as `max_time_stamp`. Accept either.
    const anchorTimestamp =
      fleetData?.pump_timestamp ?? fleetData?.max_time_stamp;

    if (!fleetData || anchorTimestamp == null) {
      console.info(
        `Fleet ${fleetId}: no new snapshot for the active well filter; keeping the existing chart.`,
      );
      return;
    }

    let pumpTimestamp = Number(anchorTimestamp);

    if (typeof anchorTimestamp === "string") {
      pumpTimestamp = new Date(anchorTimestamp).getTime();
    } else if (pumpTimestamp < 1000000000000) {
      pumpTimestamp *= 1000;
    }

    const newPumpData: PumpData[] = (fleetData.pumps || []).map(
      (pump: any, index: number) => {
        let rowTimestamp = Number(
          pump.time_stamp ?? pump.timestamp ?? pumpTimestamp,
        );
        if (!Number.isFinite(rowTimestamp)) rowTimestamp = pumpTimestamp;
        if (rowTimestamp < 1000000000000) rowTimestamp *= 1000;

        return {
          ...pump,
          id: pump.id ?? pumpTimestamp + index,
          time_stamp: rowTimestamp,
          pump_name: pump.pump_name,
          engine_fuel_pressure:
            pump.engine_fuel_pressure ??
            pump.engine_fuel_pressure_psi ??
            pump.engine_fuel_press ??
            pump.fuel_pressure ??
            pump.fuel_press ??
            pump.fuel_pressure_psi ??
            pump.eng_fuel_pressure ??
            null,
          calculated_discharge_rate:
            pump.calculated_discharge_rate ??
            pump.discharge_rate ??
            pump.calculated_rate ??
            null,
          discharge_vfd_amps:
            pump.discharge_vfd_amps ??
            pump.motor_current ??
            pump.vfd_amps ??
            pump.vfd_current ??
            null,
          discharge_vfd_dc_link_volts:
            pump.discharge_vfd_dc_link_volts ??
            pump.dc_link_volts ??
            pump.vfd_dc_link_volts ??
            pump.dc_link_voltage ??
            null,
          discharge_motor_winding_a1_temp:
            pump.discharge_motor_winding_a1_temp ??
            pump.winding_a1_temp ??
            pump.winding_a_temp ??
            null,
          discharge_motor_winding_a2_temp:
            pump.discharge_motor_winding_a2_temp ??
            pump.winding_a2_temp ??
            pump.winding_a_temp ??
            null,
          discharge_motor_winding_b1_temp:
            pump.discharge_motor_winding_b1_temp ??
            pump.winding_b1_temp ??
            pump.winding_b_temp ??
            null,
          discharge_motor_winding_b2_temp:
            pump.discharge_motor_winding_b2_temp ??
            pump.winding_b2_temp ??
            pump.winding_b_temp ??
            null,
          discharge_motor_winding_c1_temp:
            pump.discharge_motor_winding_c1_temp ??
            pump.winding_c1_temp ??
            pump.winding_c_temp ??
            null,
          discharge_motor_winding_c2_temp:
            pump.discharge_motor_winding_c2_temp ??
            pump.winding_c2_temp ??
            pump.winding_c_temp ??
            null,
          discharge_motor_de_bearing_temp:
            pump.discharge_motor_de_bearing_temp ??
            pump.de_bearing_temp ??
            pump.drive_end_bearing_temp ??
            null,
          discharge_motor_nde_bearing_temp:
            pump.discharge_motor_nde_bearing_temp ??
            pump.nde_bearing_temp ??
            pump.non_drive_end_bearing_temp ??
            null,
        } as PumpData;
      },
    );

    setPumpDataByFleet((prev) => {
      const existingRows = prev[fleetId] || [];
      // Anchored rolling window: pinned to the selected start, capped at 3h.
      const windowStart = streamingWindowStart(fleetId, pumpTimestamp);

      const rowsByKey = new Map<string, PumpData>();
      const latestTimestampByPump = new Map<string, number>();

      /*
       * Preserve the historical /pumps response exactly.
       *
       * Do not allow a latest-snapshot response to overwrite an existing
       * historical point. The Load Data chart and the initial Real-Time chart
       * must therefore use the same historical values.
       */
      existingRows.forEach((row) => {
        const pumpName = String(row.pump_name || "").trim();
        const timestamp = Number(row.time_stamp);

        if (
          !pumpName ||
          !Number.isFinite(timestamp) ||
          timestamp < windowStart ||
          timestamp > pumpTimestamp
        ) {
          return;
        }

        rowsByKey.set(`${pumpName}-${timestamp}`, row);

        const currentLatest = latestTimestampByPump.get(pumpName);

        if (currentLatest === undefined || timestamp > currentLatest) {
          latestTimestampByPump.set(pumpName, timestamp);
        }
      });

      newPumpData.forEach((incomingRow) => {
        const pumpName = String(incomingRow.pump_name || "").trim();
        const timestamp = Number(incomingRow.time_stamp);

        if (
          !pumpName ||
          !Number.isFinite(timestamp) ||
          timestamp < windowStart ||
          timestamp > pumpTimestamp
        ) {
          return;
        }

        const key = `${pumpName}-${timestamp}`;
        const latestPumpTimestamp = latestTimestampByPump.get(pumpName);

        if (rowsByKey.has(key)) {
          return;
        }

        if (
          latestPumpTimestamp !== undefined &&
          timestamp <= latestPumpTimestamp
        ) {
          return;
        }

        rowsByKey.set(key, incomingRow);
        latestTimestampByPump.set(pumpName, timestamp);
      });

      const mergedRows = Array.from(rowsByKey.values()).sort(
        (a, b) => Number(a.time_stamp) - Number(b.time_stamp),
      );

      const streamingPumps = Array.from(
        new Set(mergedRows.map((item) => item.pump_name).filter(Boolean)),
      ).sort();

      const nextRange = {
        min: windowStart,
        max: pumpTimestamp,
      };
      const nextFromDate = new Date(nextRange.min);
      const nextToDate = new Date(nextRange.max);

      const now = Date.now();
      const lastUpdate = lastStreamingUiUpdateRef.current[fleetId] || 0;
      const shouldUpdateVisibleUi = now - lastUpdate >= 3000;
      if (shouldUpdateVisibleUi) {
        lastStreamingUiUpdateRef.current[fleetId] = now;
      }

      setXAxisRangeByFleet((rangePrev) => ({
        ...rangePrev,
        [fleetId]: nextRange,
      }));

      setFromDateByFleet((previous) => ({
        ...previous,
        [fleetId]: nextFromDate,
      }));
      setToDateByFleet((previous) => ({
        ...previous,
        [fleetId]: nextToDate,
      }));

      if (selectedFleet?.fleet === fleetId) {
        setFromDate(nextFromDate);
        setToDate(nextToDate);
      }

      setAvailablePumpsByFleet((pumpPrev) => ({
        ...pumpPrev,
        [fleetId]: streamingPumps,
      }));
      setSelectedPumpsByFleet((pumpPrev) => ({
        ...pumpPrev,
        [fleetId]:
          pumpPrev[fleetId]?.length > 0
            ? pumpPrev[fleetId].filter((p) => streamingPumps.includes(p))
            : streamingPumps,
      }));

      if (shouldUpdateVisibleUi) {
        const tableChannelsForFleet =
          selectedTableChannelsByFleetRef.current[fleetId] || defaultChannels;
        const nextAggregatedData = computeAggregations(
          mergedRows,
          tableChannelsForFleet,
        );

        setAggregatedDataByFleet((aggPrev) => ({
          ...aggPrev,
          [fleetId]: nextAggregatedData,
        }));

        updateLatestValues(fleetId, newPumpData);

        // Rebuild the fleet's RENDERED chart series. To guarantee the live chart
        // looks IDENTICAL to a static Load Data (same min/max downsample, same
        // line style), render through the SAME path as static: keep the live
        // points in the full-resolution buffers and hand them to
        // buildDownsampledSeries — instead of a separate row-based builder.
        // Build a series for EVERY streaming pump (deterministic set — never
        // depends on the timing of selectedPumps); selection/hide control
        // visibility only.
        const fleetChartChannels =
          selectedChannelsByFleetRef.current[fleetId] ||
          selectedChannelsRef.current;
        const fleetSelectedPumps = selectedPumpsByFleet[fleetId];
        const selectedPumpSet =
          fleetSelectedPumps && fleetSelectedPumps.length > 0
            ? new Set(fleetSelectedPumps)
            : null; // null → all visible
        const fleetHiddenPumps =
          hiddenPumpsByFleet[fleetId] || new Set<string>();
        const effectiveHidden = new Set<string>(fleetHiddenPumps);
        if (selectedPumpSet) {
          streamingPumps.forEach((pumpName) => {
            if (!selectedPumpSet.has(pumpName)) effectiveHidden.add(pumpName);
          });
        }

        // Append the new live points to the retained full-res buffers (trimmed to
        // the rolling window), so history stays full-resolution and the render is
        // the exact static path — no double-downsample, no config divergence.
        const nextBuffers = appendSamplesToBuffers(
          fullResByFleetRef.current[fleetId],
          newPumpData,
          fleetChartChannels.map((channel) => channel.value),
          getChannelValue,
          windowStart,
          pumpTimestamp,
        );
        fullResByFleetRef.current = {
          ...fullResByFleetRef.current,
          [fleetId]: nextBuffers,
        };

        const liveSeries = downsampledToHighcharts(
          buildDownsampledSeries(
            nextBuffers,
            streamingPumps,
            effectiveHidden,
            fleetChartChannels,
            axisSettings,
          ),
        );

        setChartSeriesByFleet((prev) => ({ ...prev, [fleetId]: liveSeries }));

        if (selectedFleet?.fleet === fleetId) {
          const activePumps = selectedPumpSet
            ? streamingPumps.filter((pumpName) => selectedPumpSet.has(pumpName))
            : streamingPumps;

          setPumpData(mergedRows);
          setAvailablePumps(streamingPumps);
          setSelectedPumps(activePumps);
          selectedPumpsRef.current = activePumps;
          setXAxisRange(nextRange);
          setAggregatedData(nextAggregatedData);
          setChartSeries(liveSeries);
        }
      }

      return {
        ...prev,
        [fleetId]: mergedRows,
      };
    });
  };

  const getStreamingWellFilter = (
    fleetId: number,
    wellIdsOverride?: number[],
    showAllOverride?: boolean,
  ) => {
    const fleetGroup = fleetGroups.find((group) => group.fleet === fleetId);
    const padId = fleetGroup?.latestPad?.id;
    const wells = padId ? padWells[padId] || [] : [];

    const selectedIds =
      wellIdsOverride !== undefined
        ? [...wellIdsOverride]
        : (activeStreamingWellIdsByFleetRef.current[fleetId] ??
          getSelectedWellIdsForPad(padId));

    const showAll =
      showAllOverride !== undefined
        ? showAllOverride
        : (activeStreamingShowAllByFleetRef.current[fleetId] ??
          (padId ? showAllWellsByPadRef.current[padId] === true : false));

    const normalizedIds = Array.from(new Set(selectedIds)).sort(
      (a, b) => a - b,
    );

    const isAll =
      showAll || (wells.length > 0 && normalizedIds.length >= wells.length);

    return {
      wellIds: isAll ? [] : normalizedIds,
      showAll: isAll,
    };
  };

  // Rolling-window start for a streaming fleet: pinned to the user's selected
  // start (anchor), but never more than STREAMING_WINDOW_MS (3h) before the live
  // edge. So the window grows from the chosen start until it hits 3h, then rolls.
  const streamingWindowStart = (fleetId: number, liveEdgeMs: number) => {
    const cap = liveEdgeMs - STREAMING_WINDOW_MS;
    const anchor = streamingAnchorStartByFleetRef.current[fleetId];
    return anchor != null ? Math.max(anchor, cap) : cap;
  };

  const buildLatestStreamingUrl = (
    fleetId: number,
    wellIdsOverride?: number[],
    showAllOverride?: boolean,
  ) => {
    const params = new URLSearchParams({ fleets: String(fleetId) });
    const filter = getStreamingWellFilter(
      fleetId,
      wellIdsOverride,
      showAllOverride,
    );

    filter.wellIds.forEach((wellId) =>
      params.append("well_id", String(wellId)),
    );

    return `${backendUrl}/latest-treatment-pump-data-multi?${params.toString()}`;
  };

  // One-shot latest snapshot (-multi). Used for stream validation, the first
  // snapshot on start, and well-switch / channel-refresh — NOT for steady live
  // updates (those use the lossless incremental poll). Records the newest
  // pump_timestamp so startStreamingPoll can seed the incremental cursor.
  const fetchLatestStreamingData = async (
    fleetId: number,
    wellIdsOverride?: number[],
    showAllOverride?: boolean,
    expectedVersion?: number,
  ) => {
    if (!fleetId) return;

    try {
      const token = getAuthToken();
      const response = await axios.get(
        buildLatestStreamingUrl(fleetId, wellIdsOverride, showAllOverride),
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        },
      );

      if (
        expectedVersion !== undefined &&
        streamingWellSwitchVersionRef.current[fleetId] !== expectedVersion
      ) {
        return;
      }

      if (response.data?.message === "Token expired") {
        stopStreaming(fleetId, "Session expired. Please login again.");
        return;
      }

      const fleetData =
        response.data?.[fleetId] ?? response.data?.[String(fleetId)];
      lastAppliedSnapshotTsRef.current[fleetId] =
        Number(fleetData?.pump_timestamp) || 0;

      applyStreamingPayload(response.data, fleetId);
    } catch (error) {
      console.error("Error fetching streaming data:", error);
    }
  };

  const buildIncrementalStreamingUrl = (
    fleetId: number,
    wellIdsOverride?: number[],
    showAllOverride?: boolean,
    since?: number,
  ) => {
    const params = new URLSearchParams({ fleets: String(fleetId) });
    // Omit `since` on the very first call (no cursor yet) — the backend seeds the
    // last ~15s so the chart still gets a live tail immediately.
    if (since != null && since > 0) {
      params.append("since", String(since));
    }
    const filter = getStreamingWellFilter(
      fleetId,
      wellIdsOverride,
      showAllOverride,
    );
    filter.wellIds.forEach((wellId) =>
      params.append("well_id", String(wellId)),
    );
    return `${backendUrl}/treatment-pump-data-incremental?${params.toString()}`;
  };

  // Steady live update: poll the lossless incremental endpoint for EVERY row
  // since our cursor (one row per pump per second), not just the latest point.
  // This is what makes streaming lossless even when a poll takes >1s.
  const fetchIncrementalStreamingData = async (
    fleetId: number,
    wellIdsOverride?: number[],
    showAllOverride?: boolean,
    expectedVersion?: number,
  ) => {
    if (!fleetId) return;

    try {
      const token = getAuthToken();
      const since = streamingCursorByFleetRef.current[fleetId];
      const response = await axios.get(
        buildIncrementalStreamingUrl(
          fleetId,
          wellIdsOverride,
          showAllOverride,
          since,
        ),
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        },
      );

      if (
        expectedVersion !== undefined &&
        streamingWellSwitchVersionRef.current[fleetId] !== expectedVersion
      ) {
        return;
      }

      if (response.data?.message === "Token expired") {
        stopStreaming(fleetId, "Session expired. Please login again.");
        return;
      }

      // If streaming was stopped while this poll was in flight, discard the
      // response. Otherwise this late tick would call applyStreamingPayload after
      // stopStreaming cleared the window anchor — recomputing the window as
      // [liveEdge - 3h, liveEdge] and snapping the just-frozen chart back to a
      // full 3h range instead of leaving it where the user stopped.
      if (!streamingFleetIdsRef.current.has(fleetId)) {
        return;
      }

      const fleetData =
        response.data?.[fleetId] ?? response.data?.[String(fleetId)];
      const rows = Array.isArray(fleetData?.pumps) ? fleetData.pumps : [];
      const maxTs =
        fleetData?.max_time_stamp != null
          ? Number(fleetData.max_time_stamp)
          : null;

      // No new rows since the cursor: keep the cursor and do nothing.
      if (rows.length === 0 || maxTs == null || !Number.isFinite(maxTs)) {
        return;
      }

      // Possible-gap signal: a full ~60s batch means we may have missed data
      // older than the backend cap (backgrounded tab / stalled poll). We still
      // apply what we got (lossless up to the cap); log so a gap is diagnosable.
      if (since && maxTs - since >= STREAMING_INCREMENTAL_MAX_MS) {
        console.warn(
          `Fleet ${fleetId}: incremental batch hit the ~60s cap (possible gap before ${new Date(
            maxTs - STREAMING_INCREMENTAL_MAX_MS,
          ).toISOString()}).`,
        );
      }

      // applyStreamingPayload merges the row list into the rolling window,
      // deduping by (pump_name, time_stamp) and appending only newer samples.
      applyStreamingPayload(response.data, fleetId);

      // Advance the cursor so the next poll only fetches strictly newer rows.
      streamingCursorByFleetRef.current[fleetId] = maxTs;
    } catch (error) {
      console.error("Error fetching incremental streaming data:", error);
    }
  };

  // Start (or restart) the live transport with a frozen well selection.
  //
  // A ~1s poll of the lossless incremental endpoint, with an in-flight guard (no
  // overlapping requests): each tick asks for every row since our cursor (one per
  // pump per second), so no live sample is dropped even if a poll takes >1s.
  // (Measured on Shula: ~0.3% loss vs ~70% for the old latest-only snapshot poll.)
  // SSE was removed earlier — it held a backend worker per session and went
  // silent, freezing the chart with no fallback.
  const startStreamingPoll = (
    fleetId: number,
    wellIdsOverride?: number[],
    showAllOverride?: boolean,
    expectedVersion?: number,
  ) => {
    if (streamingIntervalsRef.current[fleetId]) {
      clearInterval(streamingIntervalsRef.current[fleetId]);
      delete streamingIntervalsRef.current[fleetId];
    }

    const filter = getStreamingWellFilter(
      fleetId,
      wellIdsOverride,
      showAllOverride,
    );

    activeStreamingWellIdsByFleetRef.current[fleetId] = [...filter.wellIds];
    activeStreamingShowAllByFleetRef.current[fleetId] = filter.showAll;

    // Seed the incremental cursor from the newest sample already applied (from
    // the 3h seed + first snapshot), so the first poll fetches only genuinely
    // new rows. 0 → omit `since` and let the backend seed the last ~15s.
    streamingCursorByFleetRef.current[fleetId] =
      lastAppliedSnapshotTsRef.current[fleetId] || 0;

    streamingPollInFlightRef.current[fleetId] = false;
    streamingIntervalsRef.current[fleetId] = setInterval(() => {
      if (streamingPollInFlightRef.current[fleetId]) return;

      if (
        expectedVersion !== undefined &&
        streamingWellSwitchVersionRef.current[fleetId] !== expectedVersion
      ) {
        return;
      }

      streamingPollInFlightRef.current[fleetId] = true;

      fetchIncrementalStreamingData(
        fleetId,
        filter.wellIds,
        filter.showAll,
        expectedVersion,
      ).finally(() => {
        streamingPollInFlightRef.current[fleetId] = false;
      });
    }, STREAMING_POLL_INTERVAL_MS);
  };

  const stopStreaming = (fleetId?: number, message?: string) => {
    const fleetIds =
      fleetId !== undefined
        ? [fleetId]
        : Array.from(streamingFleetIdsRef.current);

    fleetIds.forEach((id) => {
      if (streamingIntervalsRef.current[id]) {
        clearInterval(streamingIntervalsRef.current[id]);
        delete streamingIntervalsRef.current[id];
      }

      // No need to freeze anything on stop: fullResByFleetRef is kept live and
      // full-resolution throughout streaming (see applyStreamingPayload), so when
      // the buffer-rebuild effect re-runs on stop it already renders the exact
      // streamed window. The chart stays put with the streamed progress.
      delete streamingPollInFlightRef.current[id];
      delete lastAppliedSnapshotTsRef.current[id];
      delete streamingCursorByFleetRef.current[id];
      delete streamingAnchorStartByFleetRef.current[id];

      streamingFleetIdsRef.current.delete(id);
      delete activeStreamingWellIdsByFleetRef.current[id];
      delete activeStreamingShowAllByFleetRef.current[id];
      delete streamingWellSignatureByFleetRef.current[id];
      delete streamingWellSwitchVersionRef.current[id];
    });

    setStreamingFleetIds(new Set(streamingFleetIdsRef.current));

    if (fleetId !== undefined) {
      setLatestValuesByFleet((prev) => {
        const next = { ...prev };
        delete next[fleetId];
        return next;
      });
    } else {
      setLatestValuesByFleet({});
    }

    lastAlarmFetchRef.current = 0;
    lastAnnotationFetchRef.current = 0;

    if (message) {
      toast.warning(message);
    }
  };
  const startStreamingForFleet = async (fleetGroup: FleetGroup) => {
    if (!fleetGroup) {
      toast.warning("Please select a fleet");
      return;
    }

    const fleetId = fleetGroup.fleet;

    if (validatingFleetIdsRef.current.has(fleetId)) {
      return;
    }

    validatingFleetIdsRef.current.add(fleetId);
    setValidatingFleetIds(new Set(validatingFleetIdsRef.current));

    // Non-blocking startup: the streaming toggle shows a "Validating…" state
    // (driven by validatingFleetIds) so the page stays interactive during the
    // 10–60s validate + 3h seed. No blocking modal.

    try {
      const token = getAuthToken();

      const validateUrl = buildLatestStreamingUrl(fleetId, [], true);

      let response;
      for (let attempt = 0; ; attempt++) {
        try {
          response = await axios.get(validateUrl, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: STREAMING_VALIDATE_TIMEOUT_MS,
          });
          break;
        } catch (validateError) {
          const timedOut =
            axios.isAxiosError(validateError) &&
            validateError.code === "ECONNABORTED";

          if (!timedOut || attempt >= STREAMING_VALIDATE_RETRIES) {
            throw validateError;
          }
        }
      }

      const fleetData = response.data[fleetId];

      // Normalize a raw timestamp (ISO string, epoch seconds, or epoch ms) to ms.
      const normalizeLiveEdge = (raw: any): number => {
        if (raw == null) return NaN;
        if (typeof raw === "string") return new Date(raw).getTime();
        let ts = Number(raw);
        if (ts < 1000000000000) ts *= 1000;
        return ts;
      };

      // Liveness comes from the backend's server-computed `live` flag, which is
      // based on whether the fleet's newest pump timestamp is ADVANCING (not on
      // how close it is to wall-clock). This correctly marks fleets that stream
      // minutes behind real time (e.g. fleet 38 ~14min) as live, even though the
      // snapshot's 5-min window leaves their pump_timestamp null. Fall back to a
      // present pump_timestamp only if the flag is ever absent (older backend).
      const fleetIsLive =
        fleetData?.live === true || Boolean(fleetData?.pump_timestamp);

      if (!fleetData || !fleetIsLive) {
        toast.warning(
          `Fleet ${fleetId}: no live data right now, please try again shortly.`,
        );
        return;
      }

      // Initial window edge. Lagging-but-live fleets have a null pump_timestamp
      // (dropped by the snapshot's 5-min window), so fall back to the response's
      // server_time; the first incremental poll re-anchors the window to the
      // real latest sample.
      const pumpTimestamp = normalizeLiveEdge(
        fleetData.pump_timestamp ?? fleetData.server_time ?? Date.now(),
      );

      if (!Number.isFinite(pumpTimestamp)) {
        toast.warning(
          `Fleet ${fleetId}: no live data right now, please try again shortly.`,
        );
        return;
      }

      const streamEndDate = new Date(pumpTimestamp);

      // Anchor the live window at the user's SELECTED start date instead of a
      // hardcoded 3h. The end is always the live edge (pumpTimestamp). The start
      // is clamped so the window never spans more than STREAMING_WINDOW_MS (3h)
      // before the live edge, and always leaves at least a small window.
      const selectedStartDate =
        fromDateByFleet[fleetId] ??
        (selectedFleet?.fleet === fleetId ? fromDate : null);
      const selectedStartMs =
        selectedStartDate instanceof Date &&
        !Number.isNaN(selectedStartDate.getTime())
          ? selectedStartDate.getTime()
          : pumpTimestamp - STREAMING_WINDOW_MS;
      const anchorStartMs = Math.min(
        Math.max(selectedStartMs, pumpTimestamp - STREAMING_WINDOW_MS),
        pumpTimestamp - 1000,
      );
      streamingAnchorStartByFleetRef.current[fleetId] = anchorStartMs;

      const streamStartDate = new Date(anchorStartMs);

      setFromDateByFleet((previous) => ({
        ...previous,
        [fleetId]: streamStartDate,
      }));
      setToDateByFleet((previous) => ({
        ...previous,
        [fleetId]: streamEndDate,
      }));

      if (selectedFleet?.fleet === fleetId) {
        setFromDate(streamStartDate);
        setToDate(streamEndDate);
      }

      const defaultChannel = await getDefaultChartChannelForFleet(fleetId);

      setSelectedChannelsByFleet((prev) => ({
        ...prev,
        [fleetId]: prev[fleetId]?.length > 0 ? prev[fleetId] : [defaultChannel],
      }));

      const existingFleetChannels = selectedChannelsByFleetRef.current[fleetId];

      const channelsForFleet =
        existingFleetChannels?.length > 0
          ? existingFleetChannels
          : [defaultChannel];

      selectedChannelsByFleetRef.current = {
        ...selectedChannelsByFleetRef.current,
        [fleetId]: channelsForFleet,
      };

      const initialPadId = fleetGroup.latestPad.id;
      const initialSelectedWellIds = Array.from(
        new Set(getSelectedWellIdsForPad(initialPadId)),
      ).sort((a, b) => a - b);
      const initialShowAll =
        showAllWellsByPadRef.current[initialPadId] === true;
      const initialFilter = getStreamingWellFilter(
        fleetId,
        initialSelectedWellIds,
        initialShowAll,
      );

      activeStreamingWellIdsByFleetRef.current[fleetId] = [
        ...initialFilter.wellIds,
      ];
      activeStreamingShowAllByFleetRef.current[fleetId] = initialFilter.showAll;
      streamingWellSignatureByFleetRef.current[fleetId] = `${
        initialShowAll ? "all" : "selected"
      }:${initialSelectedWellIds.join(",")}`;

      streamingFleetIdsRef.current.add(fleetId);
      setStreamingFleetIds(new Set(streamingFleetIdsRef.current));

      // Seed the streaming rolling window from the columnar buffers that the
      // static load stored (the chart keeps data as typed buffers, not rows).
      // Without this the live stream would begin from an empty history and the
      // data the user just loaded would disappear the moment streaming starts.
      // applyStreamingPayload preserves any rows already in pumpDataByFleet that
      // fall inside the window, so this seed carries forward as live samples arrive.
      const seedWindowStart = anchorStartMs;

      const seedRowsFromBuffers = () => {
        const seedBuffers = fullResByFleetRef.current[fleetId];
        if (!seedBuffers || seedBuffers.size === 0) return [];
        return buffersToSeedRows(
          seedBuffers,
          seedWindowStart,
          pumpTimestamp,
          fleetId,
        );
      };

      const buffersCoverSelectedChannels = () => {
        const seedBuffers = fullResByFleetRef.current[fleetId];
        if (!seedBuffers || seedBuffers.size === 0) return false;
        const needed = channelsForFleet.map((c) => c.value);
        return needed.every((channelValue) =>
          Array.from(seedBuffers.values()).some((channelMap) => {
            const buf = channelMap.get(channelValue);
            return !!buf && buf.t.length > 0;
          }),
        );
      };

      await loadData(
        streamStartDate,
        streamEndDate,
        fleetGroup,
        undefined,
        false,
        channelsForFleet,
        "combined",
        true,
        true,
        undefined,
        initialSelectedWellIds,
        initialShowAll,
      );

      const seededRows = seedRowsFromBuffers();
      if (seededRows.length > 0 && buffersCoverSelectedChannels()) {
        setPumpDataByFleet((prev) => ({
          ...prev,
          [fleetId]: seededRows as PumpData[],
        }));
      }

      await fetchLatestStreamingData(
        fleetId,
        initialFilter.wellIds,
        initialFilter.showAll,
      );

      startStreamingPoll(fleetId);

      toast.success(`Fleet ${fleetId} real-time streaming started`);
    } catch (error) {
      console.error(`Error starting Fleet ${fleetId} streaming:`, error);

      streamingFleetIdsRef.current.delete(fleetId);
      setStreamingFleetIds(new Set(streamingFleetIdsRef.current));

      toast.error(
        axios.isAxiosError(error) && error.code === "ECONNABORTED"
          ? `Fleet ${fleetId} stream validation timed out.`
          : `Failed to start Fleet ${fleetId} streaming`,
      );
    } finally {
      validatingFleetIdsRef.current.delete(fleetId);
      setValidatingFleetIds(new Set(validatingFleetIdsRef.current));
    }
  };

  const refreshStreamingChartChannelsForFleet = async (
    fleetGroup: FleetGroup,
    channels: PumpChannel[],
  ) => {
    const fleetId = fleetGroup.fleet;
    if (!streamingFleetIdsRef.current.has(fleetId) || channels.length === 0) {
      return;
    }

    const refreshVersion =
      (streamingChannelRefreshVersionRef.current[fleetId] || 0) + 1;
    streamingChannelRefreshVersionRef.current[fleetId] = refreshVersion;

    const frozenChannels = channels.map((channel) => ({ ...channel }));
    const activeWellFilter = getStreamingWellFilter(fleetId);

    selectedChannelsByFleetRef.current = {
      ...selectedChannelsByFleetRef.current,
      [fleetId]: frozenChannels,
    };

    setLoadedChartChannelsByFleet((prev) => ({
      ...prev,
      [fleetId]: frozenChannels.map((channel) => channel.value),
    }));

    const endMs = xAxisRangeByFleet[fleetId]?.max || Date.now();
    const end = new Date(endMs);
    const start = new Date(streamingWindowStart(fleetId, endMs));

    try {
      await loadData(
        start,
        end,
        fleetGroup,
        undefined,
        false,
        frozenChannels,
        "combined",
        true,
        true,
        undefined,
        activeWellFilter.wellIds,
        activeWellFilter.showAll,
      );

      if (
        streamingChannelRefreshVersionRef.current[fleetId] !== refreshVersion ||
        !streamingFleetIdsRef.current.has(fleetId)
      ) {
        return;
      }

      const refreshedBuffers = fullResByFleetRef.current[fleetId];
      if (refreshedBuffers && refreshedBuffers.size > 0) {
        const seededRows = buffersToSeedRows(
          refreshedBuffers,
          start.getTime(),
          end.getTime(),
          fleetId,
        ) as PumpData[];

        if (seededRows.length > 0) {
          setPumpDataByFleet((prev) => ({
            ...prev,
            [fleetId]: seededRows,
          }));
        }
      }

      if (
        streamingChannelRefreshVersionRef.current[fleetId] !== refreshVersion ||
        !streamingFleetIdsRef.current.has(fleetId)
      ) {
        return;
      }

      await fetchLatestStreamingData(
        fleetId,
        activeWellFilter.wellIds,
        activeWellFilter.showAll,
      );

      if (
        streamingChannelRefreshVersionRef.current[fleetId] !== refreshVersion ||
        !streamingFleetIdsRef.current.has(fleetId)
      ) {
        return;
      }

      startStreamingPoll(
        fleetId,
        activeWellFilter.wellIds,
        activeWellFilter.showAll,
      );
    } catch (error) {
      console.error(
        `Failed to refresh Fleet ${fleetId} streaming chart channels:`,
        error,
      );
      toast.error("Failed to load the selected real-time channel");
    }
  };

  const refreshStreamingChannelsForFleet = async (
    fleetId: number,
    channels: PumpChannel[],
  ) => {
    if (!streamingFleetIdsRef.current.has(fleetId)) return;

    setLoadedTableChannelsByFleet((prev) => ({
      ...prev,
      [fleetId]: channels.map((channel) => channel.value),
    }));
    setShowTableChannelNoticeByFleet((prev) => ({
      ...prev,
      [fleetId]: false,
    }));

    await fetchLatestStreamingData(fleetId);

    // Restart the 1s poll so the newly selected channels keep updating.
    startStreamingPoll(fleetId);
  };

  const handleStreamingToggle = async (fleetGroup: FleetGroup) => {
    const fleetId = fleetGroup.fleet;

    if (
      validatingFleetIdsRef.current.has(fleetId) ||
      streamingTransitionFleetIds.has(fleetId)
    ) {
      return;
    }

    if (streamingFleetIdsRef.current.has(fleetId)) {
      setStreamingTransitionFleetIds((previous) =>
        new Set(previous).add(fleetId),
      );

      try {
        stopStreaming(fleetId);
        delete userZoomRangeByFleetRef.current[fleetId];

        const savedRange = historicalRangeBeforeStreamingRef.current[fleetId];
        const fallbackTo =
          toDateByFleet[fleetId] instanceof Date
            ? (toDateByFleet[fleetId] as Date)
            : new Date();
        const fallbackFrom =
          fromDateByFleet[fleetId] instanceof Date
            ? (fromDateByFleet[fleetId] as Date)
            : new Date(fallbackTo.getTime() - 6 * 60 * 60 * 1000);

        const historicalFrom = savedRange?.from || fallbackFrom;
        const historicalTo = savedRange?.to || fallbackTo;
        const channelsForHistorical =
          selectedChannelsByFleetRef.current[fleetId]?.length > 0
            ? selectedChannelsByFleetRef.current[fleetId]
            : [defaultBoostChannel];

        setSelectedFleet(fleetGroup);
        setFromDateByFleet((previous) => ({
          ...previous,
          [fleetId]: historicalFrom,
        }));
        setToDateByFleet((previous) => ({
          ...previous,
          [fleetId]: historicalTo,
        }));

        if (selectedFleet?.fleet === fleetId) {
          setFromDate(historicalFrom);
          setToDate(historicalTo);
        }

        fleetAutoLoadSignatureRef.current = "";

        toast.info("Real-time streaming stopped. Restoring historical data...");

        const restored = await loadData(
          historicalFrom,
          historicalTo,
          fleetGroup,
          isDashboardPumpForFleet(fleetId)
            ? dashboardPumpParam || undefined
            : undefined,
          false,
          channelsForHistorical,
          undefined,
          false,
          false,
          undefined,
          undefined,
          undefined,
          false,
          "Historical mode active. Date and channel changes refresh automatically.",
        );

        if (restored !== false) {
          delete historicalRangeBeforeStreamingRef.current[fleetId];
        }
      } finally {
        setStreamingTransitionFleetIds((previous) => {
          const next = new Set(previous);
          next.delete(fleetId);
          return next;
        });
      }

      return;
    }

    if (streamingFleetIdsRef.current.size >= 2) {
      toast.warning(
        "A maximum of two fleets can stream in real time simultaneously.",
      );
      return;
    }

    const currentFrom = fromDateByFleet[fleetId];
    const currentTo = toDateByFleet[fleetId];

    if (currentFrom instanceof Date && currentTo instanceof Date) {
      historicalRangeBeforeStreamingRef.current[fleetId] = {
        from: new Date(currentFrom.getTime()),
        to: new Date(currentTo.getTime()),
      };
    }

    setSelectedFleet(fleetGroup);
    await startStreamingForFleet(fleetGroup);
  };

  useEffect(() => {
    fleetGroups.forEach((fleetGroup) => {
      const fleetId = fleetGroup.fleet;
      if (!streamingFleetIdsRef.current.has(fleetId)) return;

      const padId = fleetGroup.latestPad.id;
      const selectedIds = Array.from(
        new Set(getSelectedWellIdsForPad(padId)),
      ).sort((a, b) => a - b);
      const showAll = showAllWellsByPadRef.current[padId] === true;
      const signature = `${showAll ? "all" : "selected"}:${selectedIds.join(
        ",",
      )}`;

      if (streamingWellSignatureByFleetRef.current[fleetId] === signature) {
        return;
      }

      streamingWellSignatureByFleetRef.current[fleetId] = signature;

      const nextVersion =
        (streamingWellSwitchVersionRef.current[fleetId] || 0) + 1;
      streamingWellSwitchVersionRef.current[fleetId] = nextVersion;

      const existingTimer = streamingWellSwitchTimerRef.current[fleetId];
      if (existingTimer) clearTimeout(existingTimer);

      const previousFilter = {
        wellIds: [...(activeStreamingWellIdsByFleetRef.current[fleetId] || [])],
        showAll: activeStreamingShowAllByFleetRef.current[fleetId] === true,
      };

      streamingWellSwitchTimerRef.current[fleetId] = setTimeout(async () => {
        if (!streamingFleetIdsRef.current.has(fleetId)) {
          finishWellSelectionLoading(padId);
          return;
        }
        if (streamingWellSwitchVersionRef.current[fleetId] !== nextVersion) {
          return;
        }

        try {
          if (streamingIntervalsRef.current[fleetId]) {
            clearInterval(streamingIntervalsRef.current[fleetId]);
            delete streamingIntervalsRef.current[fleetId];
          }

          streamingPollInFlightRef.current[fleetId] = false;

          const currentRangeEnd = Number(
            xAxisRangeByFleet[fleetId]?.max ??
              toDateByFleet[fleetId]?.getTime() ??
              0,
          );

          const latestBufferedTimestamp = (() => {
            const rows = pumpDataByFleet[fleetId] || [];
            let latest = 0;

            rows.forEach((row) => {
              const timestamp = Number(row?.time_stamp);
              if (Number.isFinite(timestamp) && timestamp > latest) {
                latest = timestamp;
              }
            });

            const buffers = fullResByFleetRef.current[fleetId];
            buffers?.forEach((channelMap) => {
              channelMap.forEach((buffer) => {
                if (!buffer?.t?.length) return;
                const timestamp = Number(buffer.t[buffer.t.length - 1]);
                if (Number.isFinite(timestamp) && timestamp > latest) {
                  latest = timestamp;
                }
              });
            });

            return latest;
          })();

          const endMs =
            (Number.isFinite(currentRangeEnd) && currentRangeEnd > 0
              ? currentRangeEnd
              : 0) ||
            latestBufferedTimestamp ||
            Date.now();

          const end = new Date(endMs);
          const start = new Date(streamingWindowStart(fleetId, endMs));
          const channels =
            selectedChannelsByFleetRef.current[fleetId]?.length > 0
              ? selectedChannelsByFleetRef.current[fleetId]
              : [await getDefaultChartChannelForFleet(fleetId)];

          if (streamingWellSwitchVersionRef.current[fleetId] !== nextVersion) {
            return;
          }

          let selectedWellHasData = Boolean(
            await loadData(
              start,
              end,
              fleetGroup,
              undefined,
              false,
              channels,
              "combined",
              true,
              true,
              nextVersion,
              selectedIds,
              showAll,
            ),
          );

          if (
            !streamingFleetIdsRef.current.has(fleetId) ||
            streamingWellSwitchVersionRef.current[fleetId] !== nextVersion
          ) {
            return;
          }

          if (!selectedWellHasData) {
            await new Promise((resolve) => window.setTimeout(resolve, 650));

            selectedWellHasData = Boolean(
              await loadData(
                start,
                end,
                fleetGroup,
                undefined,
                false,
                channels,
                "combined",
                true,
                true,
                nextVersion,
                selectedIds,
                showAll,
              ),
            );
          }

          if (
            !streamingFleetIdsRef.current.has(fleetId) ||
            streamingWellSwitchVersionRef.current[fleetId] !== nextVersion
          ) {
            return;
          }

          if (!selectedWellHasData) {
            const filter = getStreamingWellFilter(
              fleetId,
              selectedIds,
              showAll,
            );

            activeStreamingWellIdsByFleetRef.current[fleetId] = [
              ...filter.wellIds,
            ];
            activeStreamingShowAllByFleetRef.current[fleetId] = filter.showAll;
            streamingWellSignatureByFleetRef.current[fleetId] = `${
              filter.showAll ? "all" : "selected"
            }:${filter.wellIds.join(",")}`;

            setPumpDataByFleet((previous) => ({
              ...previous,
              [fleetId]: [],
            }));
            setChartSeriesByFleet((previous) => ({
              ...previous,
              [fleetId]: [],
            }));
            setAvailablePumpsByFleet((previous) => ({
              ...previous,
              [fleetId]: [],
            }));
            setSelectedPumpsByFleet((previous) => ({
              ...previous,
              [fleetId]: [],
            }));
            setHiddenPumpsByFleet((previous) => ({
              ...previous,
              [fleetId]: new Set<string>(),
            }));
            setAggregatedDataByFleet((previous) => ({
              ...previous,
              [fleetId]: {},
            }));
            setLatestValuesByFleet((previous) => ({
              ...previous,
              [fleetId]: new Map(),
            }));

            const nextBuffers = { ...fullResByFleetRef.current };
            delete nextBuffers[fleetId];
            fullResByFleetRef.current = nextBuffers;

            if (selectedFleet?.fleet === fleetId) {
              setPumpData([]);
              setChartSeries([]);
              setAvailablePumps([]);
              setSelectedPumps([]);
              setHiddenPumps(new Set<string>());
              setAggregatedData({});
              setLatestValues(new Map());
              setAlarmEvents([]);
              setAlarmEventsByFleet((previous) => ({
                ...previous,
                [fleetId]: [],
              }));
              setSelectedAlarmEvents([]);
              setChartAnnotations([]);
            }

            toast.info("No data available for the selected time range.");

            startStreamingPoll(
              fleetId,
              filter.wellIds,
              filter.showAll,
              nextVersion,
            );
            return;
          }

          const filter = getStreamingWellFilter(fleetId, selectedIds, showAll);
          activeStreamingWellIdsByFleetRef.current[fleetId] = [
            ...filter.wellIds,
          ];
          activeStreamingShowAllByFleetRef.current[fleetId] = filter.showAll;

          await fetchLatestStreamingData(
            fleetId,
            filter.wellIds,
            filter.showAll,
            nextVersion,
          );

          if (
            !streamingFleetIdsRef.current.has(fleetId) ||
            streamingWellSwitchVersionRef.current[fleetId] !== nextVersion
          ) {
            return;
          }

          startStreamingPoll(
            fleetId,
            filter.wellIds,
            filter.showAll,
            nextVersion,
          );
        } finally {
          if (streamingWellSwitchVersionRef.current[fleetId] === nextVersion) {
            finishWellSelectionLoading(padId);
          }
        }
      }, 250);
    });
  }, [
    selectedWellIdsByPad,
    showAllWellsByPad,
    fleetGroups,
    getSelectedWellIdsForPad,
    getDefaultChartChannelForFleet,
    finishWellSelectionLoading,
    padWells,
    xAxisRangeByFleet,
    toDateByFleet,
    pumpDataByFleet,
  ]);

  useEffect(() => {
    return () => {
      Object.values(streamingIntervalsRef.current).forEach(clearInterval);
      Object.values(streamingWellSwitchTimerRef.current).forEach(clearTimeout);
    };
  }, []);

  const filteredAssetTableEntries = useMemo(() => {
    const selectedPumpSet = new Set(selectedAssetTablePumps);

    return Object.entries(aggregatedData).filter(([pumpName]) =>
      selectedPumpSet.has(pumpName),
    );
  }, [aggregatedData, selectedAssetTablePumps]);

  const hasInvalidDateRange =
    !!fromDate && !!toDate && toDate.getTime() < fromDate.getTime();

  // Match Fleet-tab behavior: once an Asset request is in progress, freeze the
  // date range until all active Asset loading (including background 1-second
  // refinement) has finished. This prevents a date edit from appearing to take
  // effect while the previous request still owns the visible result.
  const isAssetDateBusy =
    isLoadDataLoading || isAssetTableLoading || assetHighResolutionLoading;

  const isAssetLoadDisabled =
    loading ||
    !fromDate ||
    !toDate ||
    selectedAssetPumps.length === 0 ||
    hasInvalidDateRange;

  // From/To were edited after the last load and Load Data hasn't been pressed.
  const isAssetDateRangePending =
    !!assetLoadedRange &&
    !!fromDate &&
    !!toDate &&
    (fromDate.getTime() !== assetLoadedRange.start ||
      toDate.getTime() !== assetLoadedRange.end);

  const datePickerCommonProps = {
    showTimeSelect: true,
    timeIntervals: 30,
    timeFormat: "h:mm aa",
    timeCaption: "Time",
    dateFormat: "MMM d, yyyy h:mm aa",
    popperPlacement: "bottom-start" as const,
    popperClassName: "fleetDatePickerPopper",
    calendarClassName: "fleetDatePickerCalendar",
    portalId: "root",
    todayButton: undefined,
  };

  const getAxisRangeError = (axisName: string) => {
    const axisMin = axisSettings[axisName].min;
    const axisMax = axisSettings[axisName].max;
    const isAutoScale = axisAutoScale[axisName];

    if (isAutoScale) {
      return "";
    }

    const hasMin = axisMin !== undefined && axisMin !== null;
    const hasMax = axisMax !== undefined && axisMax !== null;

    if (hasMin && !hasMax) {
      return "Please enter Max value";
    }

    if (!hasMin && hasMax) {
      return "Please enter Min value";
    }

    if (hasMin && hasMax && axisMin > axisMax) {
      return "Min cannot be greater than Max";
    }

    return "";
  };

  const hasAxisValidationErrors = Object.keys(axisSettings).some((axis) => {
    return getAxisRangeError(axis) !== "";
  });

  const getAxisTitle = (axis: string) => {
    const match = axis.match(/(left|right)(\d+)/i);
    if (!match) {
      return axis;
    }

    const [, side, index] = match;
    return `${side.charAt(0).toUpperCase()}${side.slice(1)} ${index} Axis`;
  };

  const getAxisSelectableChannels = (axis: string) => {
    return availableChannels
      .filter((channel) =>
        selectedChannels.some((selected) => selected.value === channel.value),
      )
      .filter(
        (channel) =>
          !Object.keys(axisSettings).some(
            (otherAxis) =>
              otherAxis !== axis &&
              axisSettings[otherAxis].channels.includes(channel.value),
          ),
      );
  };

  const handleAxisChannelsChange = (
    axis: string,
    selected: readonly PumpChannel[] | null,
  ) => {
    const selectedValues = (selected ?? []).map((item) => item.value);

    setAxisSettings((prev) => ({
      ...prev,
      [axis]: {
        ...prev[axis],
        channels: selectedValues,
      },
    }));
  };

  const handleAxisAutoScaleChange = (axis: string, checked: boolean) => {
    setAxisAutoScale((prev) => ({
      ...prev,
      [axis]: checked,
    }));

    if (checked) {
      setAxisSettings((prev) => ({
        ...prev,
        [axis]: {
          ...prev[axis],
          min: undefined,
          max: undefined,
        },
      }));
    }
  };

  const handleAxisRangeChange = (
    axis: string,
    field: "min" | "max",
    value: string,
  ) => {
    setAxisSettings((prev) => ({
      ...prev,
      [axis]: {
        ...prev[axis],
        [field]: value === "" ? undefined : Number(value),
      },
    }));
  };
  const openAnnotationInChart = async (annotation: ChartAnnotation) => {
    const fleetNumber = Number(annotation.fleet);

    if (!fleetNumber || !annotation.timestamp) {
      toast.warning("Annotation does not have fleet or timestamp");
      return;
    }

    const fleetGroup = fleetGroups.find((group) => group.fleet === fleetNumber);

    if (!fleetGroup) {
      toast.warning(`Fleet ${fleetNumber} not found in fleet list`);
      return;
    }

    const annotationTime = Number(annotation.timestamp);
    const from = new Date(annotationTime - 60 * 60 * 1000);
    const to = new Date(annotationTime + 60 * 60 * 1000);

    setActiveTab("fleet");
    setExpandedFleets((prev) => new Set(prev).add(fleetNumber));
    setSelectedFleet(fleetGroup);

    setFromDateByFleet((prev) => ({
      ...prev,
      [fleetNumber]: from,
    }));

    setToDateByFleet((prev) => ({
      ...prev,
      [fleetNumber]: to,
    }));

    const historicalPad = getPadForFleetDateRange(fleetGroup, from, to);
    if (historicalPad?.id) {
      await fetchPadWells(historicalPad.id);
    }
    await loadData(from, to, fleetGroup);

    setTimeout(() => {
      document
        .getElementById(`fleet-${fleetNumber}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  };
  const hasUnloadedChannels = (
    loadedValues: string[] | undefined,
    selectedValues: string[],
  ): boolean => {
    const loaded = new Set(loadedValues ?? []);
    return selectedValues.some((value) => !loaded.has(value));
  };

  return (
    <section
      className={`${styles.section} ${theme === "dark" ? "dark-theme" : "light-theme"}`}
      style={{
        height: "100dvh",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        className={styles.profile}
        style={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Header
          title="Historical Data"
          setShowSidebar={setShowSidebar}
          showSidebar={showSidebar}
        />

        <div
          className={styles.container}
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
          }}
        >
          <div
            className={`${styles.sidebarStyle} app-sidebar-rail`}
            style={{
              width: showSidebar ? "224px" : "56px",
              transition: "width 0.3s",
            }}
          >
            <Sidebar />
          </div>

          <div
            className={styles.mainContentStyle}
            ref={mainContentRef}
            data-modal-scroll-lock="fleet-main-content"
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {" "}
            <div className={styles.pageHeaderStack}>
              <div className={styles.pageHeaderToggleRow}>
                <div className={styles.toggleWrapper}>
                  <div className={styles.toggleContainer}>
                    <button
                      onClick={() => {
                        setActiveTab("fleet");
                        setPumpData([]);
                        setAggregatedData({});
                        setChartSeries([]);
                        setXAxisRange({});
                      }}
                      className={`${styles.toggleButton} ${
                        activeTab === "fleet"
                          ? styles.toggleButtonActive
                          : styles.toggleButtonInactive
                      }`}
                    >
                      Fleet
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab("asset");
                        setExpandedFleets(new Set());
                        setSelectedFleet(null);
                        setPumpData([]);
                        setAggregatedData({});
                        setChartSeries([]);
                        setXAxisRange({});
                        // Asset date/list initialization is handled by the
                        // activeTab effect so browser refresh and button navigation
                        // use the same initialization path.
                        setFromDate(null);
                        setToDate(null);
                      }}
                      className={`${styles.toggleButton} ${
                        activeTab === "asset"
                          ? styles.toggleButtonActive
                          : styles.toggleButtonInactive
                      }`}
                    >
                      Pump Asset
                    </button>
                    {
                      <button
                        onClick={() => {
                          setActiveTab("blender");
                          setExpandedFleets(new Set());
                          setSelectedFleet(null);
                          setPumpData([]);
                          setAggregatedData({});
                          setChartSeries([]);
                          setXAxisRange({});
                        }}
                        className={`${styles.toggleButton} ${
                          activeTab === "blender"
                            ? styles.toggleButtonActive
                            : styles.toggleButtonInactive
                        }`}
                      >
                        Blender Asset
                      </button>
                    }
                    <button
                      onClick={() => {
                        setActiveTab("comments");
                      }}
                      className={`${styles.toggleButton} ${
                        activeTab === "comments"
                          ? styles.toggleButtonActive
                          : styles.toggleButtonInactive
                      }`}
                    >
                      Comments
                    </button>
                  </div>
                </div>
              </div>
              <p className={styles.pageHeaderDescription}>
                {activeTab === "fleet"
                  ? "Select a fleet to load and visualize historical pump data across a date range."
                  : activeTab === "asset"
                    ? "Search and track individual pump assets, view their history and fleet assignments."
                    : activeTab === "blender"
                      ? "Select a diesel or electric blender and browse the approved PadTrack channels by subsystem."
                      : "Search chart comments and annotations across fleets, pumps, users, and dates."}
              </p>
              {activeTab === "fleet" && (
                <div className={styles.pageHeaderSearchRow}>
                  <div className={styles.fleetSearchWrap}>
                    <input
                      type="text"
                      className={`${styles.fleetSearchInput} ${
                        fleetSearch.trim()
                          ? styles.fleetSearchInputWithActions
                          : ""
                      }`}
                      placeholder="Fleet #, pad, status, stages, wells…"
                      title="Search by fleet number, pad name, status, stage count, or well count"
                      value={fleetSearch}
                      onChange={(e) => setFleetSearch(e.target.value)}
                      aria-label="Search fleets by number, pad name, status, stage count, or well count"
                    />

                    {fleetSearch.trim() && (
                      <div className={styles.fleetSearchActions}>
                        <span className={styles.fleetSearchCount}>
                          {filteredFleetGroups.length}/{fleetGroups.length}
                        </span>

                        <button
                          type="button"
                          className={styles.fleetSearchClear}
                          onClick={() => setFleetSearch("")}
                          aria-label="Clear fleet search"
                          title="Clear search"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {activeTab === "comments" && (
              <div className={styles.commentsPage}>
                <div className={styles.commentsHeader}>
                  <div>
                    <h2>Comments / Annotations</h2>
                    <p>
                      Search the latest 100 chart comments across fleets, pumps,
                      emails, and dates.
                    </p>
                  </div>
                </div>

                <div className={styles.commentsFilters}>
                  <input
                    type="text"
                    placeholder="Search comment text"
                    value={annotationSearchText}
                    onChange={(e) => setAnnotationSearchText(e.target.value)}
                    aria-label="Search comment text"
                  />

                  <select
                    className={styles.commentsFleetSelect}
                    value={annotationSearchFleet}
                    onChange={(e) => setAnnotationSearchFleet(e.target.value)}
                    aria-label="Fleet"
                  >
                    <option value="">All Fleets</option>
                    {fleetGroups.map((group) => (
                      <option key={group.fleet} value={String(group.fleet)}>
                        Fleet {group.fleet}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    placeholder="Pump"
                    value={annotationSearchPump}
                    onChange={(e) => setAnnotationSearchPump(e.target.value)}
                    aria-label="Pump"
                  />

                  <input
                    type="email"
                    placeholder="Created by email"
                    value={annotationSearchUser}
                    onChange={(e) => setAnnotationSearchUser(e.target.value)}
                    aria-label="Created by email"
                  />

                  <DatePicker
                    selected={annotationSearchFromDate}
                    onChange={(date) => setAnnotationSearchFromDate(date)}
                    showTimeSelect
                    timeIntervals={30}
                    timeFormat="h:mm aa"
                    timeCaption="Time"
                    dateFormat="MMM d, yyyy h:mm aa"
                    placeholderText="From date"
                    className={styles.commentsDateInput}
                    portalId="root"
                    popperPlacement="bottom-start"
                    popperClassName="fleetDatePickerPopper"
                    calendarClassName={
                      theme === "dark"
                        ? "fleetDatePickerCalendar fleetDatePickerCalendarDark"
                        : "fleetDatePickerCalendar fleetDatePickerCalendarLight"
                    }
                  />

                  <DatePicker
                    selected={annotationSearchToDate}
                    onChange={(date) => setAnnotationSearchToDate(date)}
                    showTimeSelect
                    timeIntervals={30}
                    timeFormat="h:mm aa"
                    timeCaption="Time"
                    dateFormat="MMM d, yyyy h:mm aa"
                    placeholderText="To date"
                    className={styles.commentsDateInput}
                    portalId="root"
                    popperPlacement="bottom-start"
                    popperClassName="fleetDatePickerPopper"
                    calendarClassName={
                      theme === "dark"
                        ? "fleetDatePickerCalendar fleetDatePickerCalendarDark"
                        : "fleetDatePickerCalendar fleetDatePickerCalendarLight"
                    }
                  />

                  <div className={styles.commentsFilterActions}>
                    <button
                      type="button"
                      className={styles.commentsSearchBtn}
                      onClick={searchChartAnnotations}
                      disabled={annotationSearchLoading}
                    >
                      {annotationSearchLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      type="button"
                      className={styles.commentsClearBtn}
                      onClick={() => {
                        setAnnotationSearchText("");
                        setAnnotationSearchFleet("");
                        setAnnotationSearchPump("");
                        setAnnotationSearchUser("");
                        setAnnotationSearchFromDate(null);
                        setAnnotationSearchToDate(null);
                        setAnnotationSearchResults([]);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className={styles.commentsTableWrap}>
                  <table className={styles.commentsTable}>
                    <thead>
                      <tr>
                        <th>Annotation Time</th>
                        <th>Fleet</th>
                        <th>Pump</th>
                        <th>Comment</th>
                        <th>Created By</th>
                        <th>Created Date</th>
                        <th
                          style={{
                            width: "260px",
                            minWidth: "260px",
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {annotationSearchLoading ? (
                        <tr>
                          <td colSpan={7} className={styles.commentsEmptyCell}>
                            Searching...
                          </td>
                        </tr>
                      ) : annotationSearchResults.length === 0 ? (
                        <tr>
                          <td colSpan={7} className={styles.commentsEmptyCell}>
                            No comments found
                          </td>
                        </tr>
                      ) : (
                        annotationSearchResults.map((item) => (
                          <tr key={item.id}>
                            <td
                              title={new Date(item.timestamp).toLocaleString()}
                            >
                              {new Date(item.timestamp).toLocaleString()}
                            </td>
                            <td>{item.fleet ?? "-"}</td>
                            <td title={item.pump || "-"}>{item.pump || "-"}</td>
                            <td
                              className={styles.commentsCommentCell}
                              title={item.comment}
                            >
                              {item.comment}
                            </td>
                            <td title={item.createdByEmail || "-"}>
                              {item.createdByEmail || "-"}
                            </td>
                            <td
                              title={
                                item.createdAt
                                  ? new Date(item.createdAt).toLocaleString()
                                  : "-"
                              }
                            >
                              {item.createdAt
                                ? new Date(item.createdAt).toLocaleString()
                                : "-"}
                            </td>
                            <td className={styles.commentsActionCell}>
                              <div className={styles.commentsActionMenuWrap}>
                                <button
                                  type="button"
                                  className={styles.commentsKebabBtn}
                                  aria-label="Comment actions"
                                  onClick={() =>
                                    setOpenAnnotationActionMenuId((current) =>
                                      current === item.id ? null : item.id,
                                    )
                                  }
                                >
                                  ⋮
                                </button>

                                {openAnnotationActionMenuId === item.id && (
                                  <div className={styles.commentsActionMenu}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenAnnotationActionMenuId(null);
                                        openAnnotationInChart(item);
                                      }}
                                    >
                                      Open Chart
                                    </button>
                                    {isManager && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenAnnotationActionMenuId(null);
                                          fetchAnnotationAudit(item);
                                        }}
                                      >
                                        History
                                      </button>
                                    )}

                                    {(item.canEdit ||
                                      canManageAnnotation(item)) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenAnnotationActionMenuId(null);
                                          handleEditAnnotation(item);
                                        }}
                                      >
                                        Edit
                                      </button>
                                    )}

                                    {(item.canDelete ||
                                      canManageAnnotation(item)) && (
                                      <button
                                        type="button"
                                        className={
                                          styles.commentsActionMenuDelete
                                        }
                                        onClick={() => {
                                          setOpenAnnotationActionMenuId(null);
                                          handleDeleteAnnotation(item);
                                        }}
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {activeTab === "fleet" && (
              <div className={styles.fleetListSection}>
                {loading && fleetGroups.length === 0 ? (
                  <p>Loading fleets...</p>
                ) : filteredFleetGroups.length === 0 ? (
                  <p className={styles.fleetSearchEmpty}>
                    {fleetGroups.length === 0
                      ? "No fleets to show."
                      : "No fleets match your search."}
                  </p>
                ) : (
                  <div className={styles.fleetCard}>
                    {fleetRows.map((row) => {
                      if (row.kind === "header") {
                        return (
                          <div
                            key={row.key}
                            className={`${styles.fleetGroupHeader} ${
                              row.variant === "active"
                                ? styles.fleetGroupHeaderActive
                                : row.variant === "lost_comms"
                                  ? styles.fleetGroupHeaderLostComms
                                  : row.variant === "expired"
                                    ? styles.fleetGroupHeaderExpired
                                    : styles.fleetGroupHeaderComplete
                            }`}
                          >
                            <span>{row.label}</span>
                            <span className={styles.fleetGroupHeaderCount}>
                              {row.count}
                            </span>
                          </div>
                        );
                      }

                      const group = row.group;
                      const fleetKey = group.fleet;
                      const fleetFromDate = fromDateByFleet[fleetKey] ?? null;
                      const fleetToDate = toDateByFleet[fleetKey] ?? null;
                      const pad =
                        getPadForFleetDateRange(
                          group,
                          fleetFromDate,
                          fleetToDate,
                        ) || group.latestPad;
                      const isFlaggedEventFleet =
                        isDashboardPumpForFleet(fleetKey);
                      const currentPadId = pad?.id ?? null;
                      const currentSelectedWellIds =
                        getSelectedWellIdsForPad(currentPadId);
                      const currentChartView = currentPadId
                        ? chartViewByPad[currentPadId] || "stacked"
                        : "stacked";
                      const isComparingMultipleWells =
                        currentSelectedWellIds.length > 1;
                      const currentChartMode = currentPadId
                        ? chartModeByPad[currentPadId] || "combined"
                        : "combined";
                      const currentCombinedData =
                        pumpDataByFleet[fleetKey] ?? [];
                      // Combined mode renders these prebuilt (columnar) series when
                      // present; otherwise it falls back to transforming raw rows.
                      const fleetPrebuiltSeries = chartSeriesByFleet[fleetKey];
                      const hasFleetChartData =
                        (fleetPrebuiltSeries?.length ?? 0) > 0 ||
                        currentCombinedData.length > 0;
                      const currentChartTitle = isFlaggedEventFleet
                        ? `Pump Data - ${dashboardPumpParam}`
                        : currentSelectedWellIds.length === 1
                          ? `Pump Data - ${getWellNameById(currentPadId, currentSelectedWellIds[0])}`
                          : `Pump Data - Fleet ${group.fleet}`;
                      const fleetSelectedPumps =
                        selectedPumpsByFleet[fleetKey] ?? [];
                      const hasFleetChannelSelection =
                        Object.prototype.hasOwnProperty.call(
                          selectedChannelsByFleet,
                          fleetKey,
                        );
                      const fleetSelectedChannels = hasFleetChannelSelection
                        ? selectedChannelsByFleet[fleetKey]
                        : [];
                      const fleetHiddenPumps =
                        hiddenPumpsByFleet[fleetKey] ?? new Set<string>();
                      const fleetAvailablePumps =
                        availablePumpsByFleet[fleetKey] ?? [];
                      const fleetXAxisRange = xAxisRangeByFleet[fleetKey] ?? {};
                      const isAlarmReviewNoTelemetry = Boolean(
                        alarmReviewNoTelemetryContext &&
                        alarmReviewNoTelemetryContext.fleetId === fleetKey &&
                        fleetFromDate?.getTime() ===
                          alarmReviewNoTelemetryContext.startTime &&
                        fleetToDate?.getTime() ===
                          alarmReviewNoTelemetryContext.endTime &&
                        (selectedPumpsByFleet[fleetKey] || []).includes(
                          alarmReviewNoTelemetryContext.pumpName,
                        ) &&
                        getChannelSelectionSignature(fleetSelectedChannels) ===
                          getChannelSelectionSignature(
                            alarmReviewNoTelemetryContext.channels,
                          ),
                      );
                      const isCurrentFleetHistoricalLoading =
                        loadingFleetIds.has(fleetKey) &&
                        !streamingFleetIds.has(fleetKey);
                      const isCurrentFleetDateRefreshing =
                        dateRefreshPendingFleetIds.has(fleetKey) &&
                        !streamingFleetIds.has(fleetKey) &&
                        Boolean(fleetFromDate) &&
                        Boolean(fleetToDate) &&
                        fleetToDate!.getTime() > fleetFromDate!.getTime();
                      const isCurrentFleetDateBusy =
                        isCurrentFleetHistoricalLoading ||
                        isCurrentFleetDateRefreshing;
                      const isCurrentFleetDateControlsDisabled =
                        isCurrentFleetDateBusy ||
                        streamingFleetIds.has(group.fleet);
                      const fleetAggregatedData =
                        aggregatedDataByFleet[fleetKey] ?? {};
                      const fleetTableChannels =
                        selectedTableChannelsByFleet[fleetKey] ||
                        defaultChannels;
                      const fleetTableTemplate =
                        selectedTableTemplateByFleet[fleetKey] || null;
                      const isCurrentFleetStreaming = streamingFleetIds.has(
                        group.fleet,
                      );
                      const isCurrentFleetTransitioning =
                        streamingTransitionFleetIds.has(group.fleet);
                      const isCurrentFleetValidating = validatingFleetIds.has(
                        group.fleet,
                      );
                      // What the date pickers show: the unsaved draft when the
                      // user is editing, otherwise the loaded range.
                      const fleetDraftDates = draftDatesByFleet[fleetKey];
                      const fleetPickerFromDate =
                        fleetDraftDates?.from ?? fleetFromDate;
                      const fleetPickerToDate =
                        fleetDraftDates?.to ?? fleetToDate;
                      const isFleetDateRangePending =
                        fleetPickerFromDate?.getTime() !==
                          fleetFromDate?.getTime() ||
                        fleetPickerToDate?.getTime() !==
                          fleetToDate?.getTime();
                      const fleetHasInvalidDateRange =
                        !!fleetPickerFromDate &&
                        !!fleetPickerToDate &&
                        fleetPickerToDate.getTime() <=
                          fleetPickerFromDate.getTime();
                      const combinedUniqueTimePoints = new Set(
                        currentCombinedData.map((d) => d.time_stamp),
                      ).size;
                      const hasCombinedSinglePointOnly = fleetPrebuiltSeries
                        ? fleetPrebuiltSeries.every(
                            (s: any) => (s.data?.length ?? 0) <= 1,
                          )
                        : currentCombinedData.length <= 1 ||
                          combinedUniqueTimePoints === 1;
                      const fleetGroupedChannelOptions =
                        getGroupedChannelOptionsForFleet(
                          fleetKey,
                          currentCombinedData,
                        );
                      const fleetChannelOptions = flattenChannelGroups(
                        fleetGroupedChannelOptions,
                      );
                      const listStatus = getFleetListStatus(group);
                      const pillClass =
                        listStatus === "active"
                          ? styles.statusPillActive
                          : listStatus === "lost_comms"
                            ? styles.statusPillLostComms
                            : listStatus === "expired"
                              ? styles.statusPillExpired
                              : styles.statusPillComplete;
                      const statusLabel = fleetListStatusLabel(listStatus);
                      const startLabel =
                        pad?.start_ts != null
                          ? formatFleetStartDate(pad.start_ts)
                          : "—";
                      const isExpanded = expandedFleets.has(group.fleet);
                      return (
                        <div key={group.fleet} className={styles.accordionItem}>
                          <button
                            type="button"
                            className={styles.accordionHeader}
                            onClick={() => handleFleetSelect(group)}
                            aria-expanded={isExpanded}
                          >
                            <span
                              className={styles.accordionChevron}
                              aria-hidden
                            >
                              {isExpanded ? "▼" : "▶"}
                            </span>
                            <span
                              className={styles.accordionTitle}
                              title={`Fleet ${group.fleet} – ${pad?.pad_name ?? ""}`}
                            >
                              Fleet {group.fleet} – {pad?.pad_name ?? ""}
                            </span>
                            <span className={styles.accordionMeta}>
                              <span
                                className={`${styles.statusPill} ${pillClass}`}
                              >
                                {statusLabel}
                              </span>
                              <span className={styles.accordionMetaStat}>
                                {pad?.stage_count ?? 0} stages
                              </span>
                              <span className={styles.accordionMetaStat}>
                                {padWells[pad.id]
                                  ? `${padWells[pad.id].length} ${padWells[pad.id].length === 1 ? "well" : "wells"}`
                                  : `${pad?.well_count ?? 0} ${(pad?.well_count ?? 0) === 1 ? "well" : "wells"}`}
                              </span>
                              <span className={styles.fleetStartDate}>
                                {startLabel}
                              </span>
                            </span>
                          </button>

                          {isExpanded && (
                            <div
                              id={`fleet-${group.fleet}`}
                              className={styles.accordionContent}
                            >
                              <div className={styles.fleetControlsBox}>
                                {padWells[pad.id]?.length > 0 && (
                                  <div className={styles.wellSection}>
                                    <div className={styles.wellSectionHeader}>
                                      <div className={styles.wellTitleWrap}>
                                        <span
                                          className={styles.wellSectionLabel}
                                        >
                                          Wells
                                        </span>
                                        <span className={styles.wellCountPill}>
                                          (
                                          {
                                            getSelectedWellIdsForPad(pad.id)
                                              .length
                                          }{" "}
                                          active)
                                        </span>
                                        <span className={styles.wellInfoIcon}>
                                          ⓘ
                                        </span>
                                      </div>

                                      {padWells[pad.id].length > 1 && (
                                        <label
                                          className={styles.wellToggleLabel}
                                        >
                                          <input
                                            type="checkbox"
                                            disabled={
                                              loadingWellSelectionByPad[
                                                pad.id
                                              ] === true
                                            }
                                            checked={
                                              !isFlaggedEventFleet &&
                                              (showAllWellsByPad[pad.id] ||
                                                false)
                                            }
                                            onChange={(e) =>
                                              handleShowAllWellsChange(
                                                pad.id,
                                                e.target.checked,
                                              )
                                            }
                                          />
                                          <span>Show all wells together</span>
                                        </label>
                                      )}
                                    </div>

                                    <div className={styles.wellSelectorRow}>
                                      <div className={styles.wellSegmentGroup}>
                                        {padWells[pad.id].map((well) => {
                                          const isSelected = (
                                            selectedWellIdsByPad[pad.id] || []
                                          ).includes(well.id);

                                          return (
                                            <button
                                              key={well.id}
                                              type="button"
                                              disabled={
                                                loadingWellSelectionByPad[
                                                  pad.id
                                                ] === true
                                              }
                                              aria-busy={
                                                loadingWellSelectionByPad[
                                                  pad.id
                                                ] === true
                                              }
                                              className={`${styles.wellSegmentBtn} ${
                                                isSelected
                                                  ? styles.wellSegmentBtnActive
                                                  : ""
                                              }`}
                                              style={{
                                                opacity:
                                                  loadingWellSelectionByPad[
                                                    pad.id
                                                  ]
                                                    ? 0.62
                                                    : 1,
                                                cursor:
                                                  loadingWellSelectionByPad[
                                                    pad.id
                                                  ]
                                                    ? "not-allowed"
                                                    : "pointer",
                                              }}
                                              onClick={() =>
                                                toggleWellSelection(
                                                  pad.id,
                                                  well.id,
                                                )
                                              }
                                            >
                                              <span
                                                className={
                                                  styles.wellSegmentText
                                                }
                                                title={well.well_name}
                                              >
                                                {well.well_name}
                                              </span>
                                              <span
                                                className={`${styles.wellSegmentIcon} ${
                                                  isSelected
                                                    ? styles.wellSegmentIconSelected
                                                    : ""
                                                }`}
                                                aria-hidden="true"
                                              >
                                                {isSelected ? "✓" : ""}
                                              </span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div className={styles.fleetControlsInner}>
                                  <div className={styles.fleetDateRow}>
                                    <div className={styles.fleetControlField}>
                                      <label
                                        className={styles.fleetControlLabel}
                                      >
                                        From:
                                      </label>
                                      <div
                                        className={styles.fleetDateInputWrap}
                                      >
                                        <DatePicker
                                          ref={(picker) => {
                                            fleetFromDatePickerRefs.current[
                                              fleetKey
                                            ] = picker;
                                          }}
                                          selected={fleetPickerFromDate}
                                          onChange={(date) => {
                                            if (!date) return;

                                            // Draft only; Load Data applies it.
                                            setDraftDatesByFleet((prev) => ({
                                              ...prev,
                                              [fleetKey]: {
                                                ...prev[fleetKey],
                                                from: date,
                                              },
                                            }));
                                          }}
                                          disabled={
                                            isCurrentFleetDateControlsDisabled
                                          }
                                          aria-busy={isCurrentFleetDateBusy}
                                          aria-label="From date and time"
                                          className={`${styles.fleetDateTextInput} ${
                                            isCurrentFleetDateBusy
                                              ? styles.fleetDateLoadingInput
                                              : ""
                                          }`}
                                          {...datePickerCommonProps}
                                        />
                                      </div>
                                    </div>

                                    <div className={styles.fleetControlField}>
                                      <label
                                        className={styles.fleetControlLabel}
                                      >
                                        To:
                                      </label>
                                      <div
                                        className={styles.fleetDateInputWrap}
                                      >
                                        <DatePicker
                                          ref={(picker) => {
                                            fleetToDatePickerRefs.current[
                                              fleetKey
                                            ] = picker;
                                          }}
                                          selected={fleetPickerToDate}
                                          onChange={(date) => {
                                            if (!date) return;

                                            // Draft only; Load Data applies it.
                                            setDraftDatesByFleet((prev) => ({
                                              ...prev,
                                              [fleetKey]: {
                                                ...prev[fleetKey],
                                                to: date,
                                              },
                                            }));
                                          }}
                                          disabled={
                                            isCurrentFleetDateControlsDisabled
                                          }
                                          aria-busy={isCurrentFleetDateBusy}
                                          aria-label="To date and time"
                                          className={`${styles.fleetDateTextInput} ${
                                            fleetHasInvalidDateRange
                                              ? styles.fleetDateErrorInput
                                              : ""
                                          } ${
                                            isCurrentFleetDateBusy
                                              ? styles.fleetDateLoadingInput
                                              : ""
                                          }`}
                                          {...datePickerCommonProps}
                                        />
                                      </div>
                                    </div>

                                    <div className={styles.fleetLoadColumn}>
                                      {/* Same height as the From/To labels so
                                          the button lines up with the inputs;
                                          the pending-dates hint sits here,
                                          directly above the button. */}
                                      <div
                                        className={styles.fleetLoadHintSlot}
                                      >
                                        {!fleetHasInvalidDateRange &&
                                          isFleetDateRangePending && (
                                            <span
                                              role="status"
                                              className={styles.assetLoadHint}
                                              style={{ left: 0, right: "auto" }}
                                            >
                                              Dates changed. Press Load Data to
                                              update.
                                            </span>
                                          )}
                                      </div>
                                      <button
                                        type="button"
                                        className={`${styles.btnPrimary} ${styles.assetLoadButton}`}
                                        onClick={() =>
                                          handleFleetLoadData(group)
                                        }
                                        disabled={
                                          isCurrentFleetDateControlsDisabled ||
                                          fleetHasInvalidDateRange ||
                                          !fleetPickerFromDate ||
                                          !fleetPickerToDate
                                        }
                                        title="Load the chart and table for the selected From/To range."
                                      >
                                        {isCurrentFleetDateBusy
                                          ? "Loading…"
                                          : "Load Data"}
                                      </button>
                                    </div>
                                  </div>

                                  <div className={styles.fleetHelperRow}>
                                    {fleetHasInvalidDateRange ? (
                                      <div className={styles.fleetDateErrorBox}>
                                        <span
                                          className={styles.fleetDateErrorIcon}
                                        >
                                          ⚠
                                        </span>
                                        <span>
                                          End date must be after start date.
                                        </span>
                                      </div>
                                    ) : isCurrentFleetDateBusy ? (
                                      <div
                                        className={
                                          styles.fleetDateLoadingStatus
                                        }
                                      >
                                        <span
                                          className={
                                            styles.fleetDateLoadingSpinner
                                          }
                                          aria-hidden="true"
                                        />
                                        <span>
                                          Loading selected date range... Date
                                          controls are temporarily disabled.
                                        </span>
                                      </div>
                                    ) : (
                                      <div className={styles.fleetAutoNote}>
                                        <span
                                          className={styles.fleetAutoNoteIcon}
                                        >
                                          ⓘ
                                        </span>
                                        <span
                                          className={styles.fleetAutoNoteText}
                                        >
                                          Showing data at{" "}
                                          {
                                            getAutoResolutionConfig(
                                              fleetFromDate,
                                              fleetToDate,
                                            ).label
                                          }{" "}
                                          resolution based on selected date
                                          range.
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  <div className={styles.fleetActionRow}>
                                    <button
                                      className={styles.btnSecondary}
                                      onClick={() => {
                                        setTemplateModalContext({
                                          view: "fleet",
                                          fleetId: fleetKey,
                                        });
                                        setShowTemplateModal(true);
                                      }}
                                    >
                                      <span className={styles.exportBtnContent}>
                                        <FiSettings size={16} />
                                        <span>Manage Templates</span>
                                      </span>
                                    </button>
                                    <div
                                      className={styles.fleetStreamingInline}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.65rem",
                                      }}
                                    >
                                      <span
                                        className={styles.fleetControlLabel}
                                        style={{ whiteSpace: "nowrap" }}
                                      >
                                        Real-Time Streaming
                                      </span>

                                      <button
                                        type="button"
                                        className={`${styles.fleetStreamingSwitch} ${
                                          isCurrentFleetStreaming
                                            ? styles.fleetStreamingSwitchOn
                                            : ""
                                        }`}
                                        onClick={
                                          isCurrentFleetValidating ||
                                          isCurrentFleetTransitioning
                                            ? undefined
                                            : () => handleStreamingToggle(group)
                                        }
                                        disabled={
                                          isCurrentFleetValidating ||
                                          isCurrentFleetTransitioning
                                        }
                                        aria-pressed={isCurrentFleetStreaming}
                                        aria-label={
                                          isCurrentFleetStreaming
                                            ? "Turn off real-time streaming"
                                            : "Turn on real-time streaming"
                                        }
                                        title={
                                          isCurrentFleetStreaming
                                            ? "Turn off real-time streaming and return to historical data."
                                            : "Turn on real-time streaming."
                                        }
                                      >
                                        <span
                                          className={`${styles.fleetStreamingSwitchThumb} ${
                                            isCurrentFleetStreaming
                                              ? styles.fleetStreamingSwitchThumbOn
                                              : ""
                                          }`}
                                        />
                                      </button>

                                      {(isCurrentFleetTransitioning ||
                                        isCurrentFleetValidating ||
                                        isCurrentFleetStreaming) && (
                                        <span
                                          role="status"
                                          aria-live="polite"
                                          style={{
                                            color: isCurrentFleetStreaming
                                              ? "#92d13d"
                                              : "#f59e0b",
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {isCurrentFleetTransitioning
                                            ? "Restoring historical data..."
                                            : isCurrentFleetValidating
                                              ? "Starting live..."
                                              : "Live"}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {isFlaggedEventFleet && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    gap: "12px",
                                    margin: "0 0 12px 0",
                                    padding: "10px 14px",
                                    border:
                                      "1px solid rgba(255, 255, 255, 0.14)",
                                    borderRadius: "8px",
                                    background: "rgba(255, 255, 255, 0.04)",
                                  }}
                                >
                                  {dashboardAlarmRpm != null &&
                                    Number.isFinite(dashboardAlarmRpm) && (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "baseline",
                                          gap: "6px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "0.78rem",
                                            opacity: 0.78,
                                          }}
                                        >
                                          RPM at alarm:
                                        </span>
                                        <strong style={{ fontSize: "0.95rem" }}>
                                          {Number(
                                            dashboardAlarmRpm,
                                          ).toLocaleString()}{" "}
                                          RPM
                                        </strong>
                                      </div>
                                    )}

                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "7px",
                                      fontSize: "0.78rem",
                                    }}
                                  >
                                    <span style={{ opacity: 0.78 }}>
                                      Minimum RPM:
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="50"
                                      value={flaggedChartMinimumRpm}
                                      onChange={(event) =>
                                        setFlaggedChartMinimumRpm(
                                          event.target.value,
                                        )
                                      }
                                      placeholder="Optional"
                                      aria-label="Minimum RPM at alarm threshold for flagged re-engagement review"
                                      style={{
                                        width: "100px",
                                        padding: "6px 8px",
                                        borderRadius: "6px",
                                        border:
                                          "1px solid rgba(255, 255, 255, 0.2)",
                                        background: "rgba(0, 0, 0, 0.12)",
                                        color: "inherit",
                                      }}
                                    />
                                  </label>

                                  {flaggedChartMinimumRpm.trim() ? (
                                    (() => {
                                      const threshold = Number(
                                        flaggedChartMinimumRpm,
                                      );
                                      const thresholdIsValid =
                                        Number.isFinite(threshold) &&
                                        threshold >= 0;
                                      const comparisonRpm =
                                        dashboardAlarmRpm != null &&
                                        Number.isFinite(dashboardAlarmRpm)
                                          ? dashboardAlarmRpm
                                          : null;
                                      const meetsThreshold =
                                        thresholdIsValid &&
                                        comparisonRpm != null &&
                                        comparisonRpm >= threshold;

                                      const badgeColors = !thresholdIsValid
                                        ? {
                                            background:
                                              "rgba(245, 158, 11, 0.18)",
                                            color: "#f59e0b",
                                          }
                                        : comparisonRpm == null
                                          ? {
                                              background:
                                                "rgba(148, 163, 184, 0.16)",
                                              color: "#94a3b8",
                                            }
                                          : meetsThreshold
                                            ? {
                                                background:
                                                  "rgba(34, 197, 94, 0.18)",
                                                color: "#22c55e",
                                              }
                                            : {
                                                background:
                                                  "rgba(239, 68, 68, 0.18)",
                                                color: "#ef4444",
                                              };

                                      return (
                                        <span
                                          style={{
                                            padding: "5px 10px",
                                            borderRadius: "999px",
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            background: badgeColors.background,
                                            color: badgeColors.color,
                                            border: `1px solid ${badgeColors.color}`,
                                          }}
                                        >
                                          {!thresholdIsValid
                                            ? "Enter a valid RPM"
                                            : comparisonRpm == null
                                              ? "Alarm RPM unavailable"
                                              : meetsThreshold
                                                ? "Meets RPM threshold"
                                                : "Below RPM threshold"}
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span
                                      style={{
                                        fontSize: "0.75rem",
                                        opacity: 0.68,
                                      }}
                                    >
                                      Filter comparison uses engine RPM at the
                                      alarm timestamp.
                                    </span>
                                  )}

                                  <span
                                    style={{
                                      marginLeft: "auto",
                                      fontSize: "0.72rem",
                                      opacity: 0.62,
                                    }}
                                  >
                                    Threshold filters alarm markers only; chart
                                    telemetry remains unchanged.
                                  </span>
                                </div>
                              )}

                              <div className={styles.compareBlock}>
                                <div className={styles.compareHeader}>
                                  <div className={styles.compareHeaderTitle}>
                                    {isFlaggedEventFleet
                                      ? `Chart - ${dashboardPumpParam}`
                                      : "Charts"}
                                    {!isFlaggedEventFleet &&
                                      isComparingMultipleWells && (
                                        <span
                                          className={styles.compareHeaderMuted}
                                        >
                                          (Comparing Wells)
                                        </span>
                                      )}
                                    <span className={styles.compareHeaderInfo}>
                                      ⓘ
                                    </span>
                                  </div>

                                  <div className={styles.compareToolbar}>
                                    <span
                                      className={styles.compareToolbarLabel}
                                    >
                                      Alarm Severity:
                                    </span>
                                    <select
                                      className={styles.compareViewSelect}
                                      value={alarmSeverityFilter}
                                      onChange={(e) =>
                                        setAlarmSeverityFilter(
                                          e.target.value as
                                            | "critical"
                                            | "warning",
                                        )
                                      }
                                    >
                                      <option value="critical">Critical</option>
                                      <option value="warning">Warning</option>
                                    </select>

                                    <span
                                      className={styles.compareToolbarLabel}
                                    >
                                      Mode:
                                    </span>
                                    <select
                                      className={styles.compareViewSelect}
                                      value={currentChartMode}
                                      onChange={async (e) => {
                                        if (!currentPadId) return;

                                        const nextMode = e.target.value as
                                          | "combined"
                                          | "compare";

                                        setIsModeSwitching(true);

                                        setChartModeByPad((prev) => ({
                                          ...prev,
                                          [currentPadId]: nextMode,
                                        }));

                                        setPumpData([]);
                                        setChartSeries([]);

                                        await loadData(
                                          fleetFromDate || undefined,
                                          fleetToDate || undefined,
                                          group,
                                          undefined,
                                          false,
                                          undefined,
                                          nextMode,
                                        );

                                        setIsModeSwitching(false);
                                      }}
                                    >
                                      <option value="combined">Combined</option>
                                      <option value="compare">
                                        Compare Wells
                                      </option>
                                    </select>

                                    {currentChartMode === "compare" &&
                                      isComparingMultipleWells && (
                                        <>
                                          <span
                                            className={
                                              styles.compareToolbarLabel
                                            }
                                          >
                                            View:
                                          </span>
                                          <select
                                            className={styles.compareViewSelect}
                                            value={currentChartView}
                                            onChange={(e) => {
                                              if (!currentPadId) return;
                                              setChartViewByPad((prev) => ({
                                                ...prev,
                                                [currentPadId]: e.target
                                                  .value as "stacked",
                                              }));
                                            }}
                                          >
                                            <option value="stacked">
                                              Stacked
                                            </option>
                                          </select>
                                        </>
                                      )}
                                  </div>
                                </div>

                                <style>{`
                                  @keyframes fleetWellLoadingSpin {
                                    to { transform: rotate(360deg); }
                                  }
                                `}</style>

                                <div
                                  className={styles.chartContainer}
                                  style={{ position: "relative" }}
                                  aria-busy={
                                    isCurrentFleetDateRefreshing ||
                                    isCurrentFleetHistoricalLoading
                                  }
                                >
                                  {(isCurrentFleetDateRefreshing ||
                                    isCurrentFleetHistoricalLoading) &&
                                    hasFleetChartData &&
                                    !hasUnloadedChannels(
                                      loadedChartChannelsByFleet[fleetKey],
                                      fleetSelectedChannels.map(
                                        (channel) => channel.value,
                                      ),
                                    ) &&
                                    !loadingWellSelectionByPad[pad.id] && (
                                      <div
                                        role="status"
                                        aria-live="polite"
                                        style={{
                                          position: "absolute",
                                          top: "12px",
                                          right: "12px",
                                          zIndex: 45,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                          padding: "8px 12px",
                                          borderRadius: "6px",
                                          border:
                                            "1px solid var(--color-border, #5b6168)",
                                          background:
                                            "var(--color-surface, #262b2f)",
                                          color: "var(--color-text, #ffffff)",
                                          boxShadow:
                                            "0 4px 14px rgba(0, 0, 0, 0.25)",
                                          fontSize: "0.78rem",
                                          fontWeight: 600,
                                          pointerEvents: "none",
                                        }}
                                      >
                                        <span
                                          aria-hidden="true"
                                          style={{
                                            width: "14px",
                                            height: "14px",
                                            flex: "0 0 auto",
                                            border:
                                              "2px solid var(--color-border, #5b6168)",
                                            borderTopColor: "#8cc540",
                                            borderRadius: "50%",
                                            animation:
                                              "fleetWellLoadingSpin 0.8s linear infinite",
                                          }}
                                        />
                                        <span>
                                          Updating chart for selected date
                                          range…
                                        </span>
                                      </div>
                                    )}

                                  {loadingWellSelectionByPad[pad.id] && (
                                    <div
                                      role="status"
                                      aria-live="polite"
                                      aria-busy="true"
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 50,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "rgba(35, 39, 42, 0.66)",
                                        backdropFilter: "blur(1px)",
                                        pointerEvents: "all",
                                      }}
                                    >
                                      <div
                                        style={{
                                          minWidth: 280,
                                          maxWidth: "80%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: 12,
                                          padding: "14px 20px",
                                          borderRadius: 9,
                                          border: "1px solid #5b6168",
                                          background: "#262b2f",
                                          color: "#ffffff",
                                          boxShadow:
                                            "0 10px 30px rgba(0,0,0,0.35)",
                                          fontSize: 14,
                                          fontWeight: 700,
                                          textAlign: "center",
                                        }}
                                      >
                                        <span
                                          aria-hidden="true"
                                          style={{
                                            width: 20,
                                            height: 20,
                                            flex: "0 0 auto",
                                            border: "3px solid #5b6168",
                                            borderTopColor: "#8cc540",
                                            borderRadius: "50%",
                                            animation:
                                              "fleetWellLoadingSpin 0.8s linear infinite",
                                          }}
                                        />
                                        <span>
                                          {loadingWellLabelByPad[pad.id] ||
                                            "Loading selected well data…"}
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {isModeSwitching ? (
                                    <div
                                      style={{
                                        height: "540px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "18px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Switching mode...
                                    </div>
                                  ) : currentChartMode === "combined" ? (
                                    hasFleetChartData ? (
                                      <>
                                        <HighchartsReact
                                          key={`combined-${group.fleet}-${currentChartMode}-${fleetSelectedPumps.join("|")}`}
                                          highcharts={Highcharts}
                                          immutable={false}
                                          options={buildCombinedChartOptions(
                                            currentCombinedData,
                                            currentChartTitle,
                                            {
                                              selectedPumps: fleetSelectedPumps,
                                              hiddenPumps: fleetHiddenPumps,
                                              selectedChannels:
                                                fleetSelectedChannels,
                                              xAxisRange: fleetXAxisRange,
                                              prebuiltSeries:
                                                fleetPrebuiltSeries,
                                              fleetId: group.fleet,
                                            },
                                          )}
                                          ref={
                                            selectedFleet?.fleet === group.fleet
                                              ? chartRef
                                              : undefined
                                          }
                                          callback={bindEditableYAxisLabels}
                                        />

                                        {hasCombinedSinglePointOnly && (
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "var(--color-text-muted)",
                                              marginTop: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Limited data available for this time
                                            range.
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div
                                        style={{
                                          textAlign: "center",
                                          padding: "2rem 1rem 1.5rem",
                                          color: "var(--color-text-muted)",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "1rem",
                                            fontWeight: 600,
                                            marginBottom: "0.4rem",
                                            color: "var(--color-text)",
                                          }}
                                        >
                                          Pump Data - Fleet {group.fleet}
                                        </div>
                                        <div className={styles.noDataText}>
                                          {isCurrentFleetHistoricalLoading
                                            ? `Loading historical ${
                                                fleetSelectedChannels.length ===
                                                1
                                                  ? fleetSelectedChannels[0]
                                                      .label
                                                  : "selected channel"
                                              } data...`
                                            : isAlarmReviewNoTelemetry
                                              ? "No telemetry is available around this alarm timestamp."
                                              : "No data available for the selected range."}
                                        </div>
                                      </div>
                                    )
                                  ) : !isComparingMultipleWells ? (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        padding: "2rem 1rem 1.5rem",
                                        color: "var(--color-text-muted)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "1rem",
                                          fontWeight: 600,
                                          marginBottom: "0.4rem",
                                          color: "var(--color-text)",
                                        }}
                                      >
                                        Compare Wells
                                      </div>
                                      <div className={styles.noDataText}>
                                        Select at least 2 wells to compare.
                                      </div>
                                    </div>
                                  ) : currentSelectedWellIds.length > 0 ? (
                                    <div
                                      className={`${styles.compareChartsGrid} ${
                                        currentSelectedWellIds.length === 1
                                          ? styles.compareChartsSingle
                                          : currentChartView === "stacked"
                                            ? styles.compareChartsStacked
                                            : ""
                                      }`}
                                    >
                                      {currentSelectedWellIds.map((wellId) => {
                                        const wellData =
                                          currentCombinedData.filter(
                                            (item: any) =>
                                              Number(item.well_id) ===
                                              Number(wellId),
                                          );

                                        const wellName = getWellNameById(
                                          currentPadId,
                                          wellId,
                                        );
                                        const wellSeries = buildChartSeries(
                                          wellData,
                                          fleetSelectedPumps,
                                          fleetHiddenPumps,
                                          fleetSelectedChannels,
                                        );
                                        const uniqueTimePoints = new Set(
                                          wellData.map((d) => d.time_stamp),
                                        ).size;

                                        const hasSinglePointOnly =
                                          wellData.length <= 1 ||
                                          uniqueTimePoints === 1;

                                        return (
                                          <div
                                            key={wellId}
                                            className={styles.compareChartCard}
                                          >
                                            <div
                                              className={
                                                styles.compareChartHeader
                                              }
                                            >
                                              {wellName}
                                            </div>

                                            {wellSeries.length > 0 ? (
                                              <>
                                                <HighchartsReact
                                                  key={`compare-${group.fleet}-${wellId}-${currentChartMode}-${fleetSelectedPumps.join("|")}`}
                                                  highcharts={Highcharts}
                                                  immutable={true}
                                                  options={buildCombinedChartOptions(
                                                    wellData,
                                                    `Pump Data - ${wellName}`,
                                                    {
                                                      selectedPumps:
                                                        fleetSelectedPumps,
                                                      hiddenPumps:
                                                        fleetHiddenPumps,
                                                      selectedChannels:
                                                        fleetSelectedChannels,
                                                      xAxisRange:
                                                        fleetXAxisRange,
                                                      fleetId: group.fleet,
                                                    },
                                                  )}
                                                  callback={
                                                    bindEditableYAxisLabels
                                                  }
                                                />

                                                {hasSinglePointOnly && (
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      color:
                                                        "var(--color-text-muted)",
                                                      marginTop: "8px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Limited data available for
                                                    this time range.
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <div
                                                className={
                                                  styles.noDataTableBlock
                                                }
                                              >
                                                No data available for {wellName}{" "}
                                                in the selected date range.
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        padding: "2rem 1rem 1.5rem",
                                        color: "var(--color-text-muted)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "1rem",
                                          fontWeight: 600,
                                          marginBottom: "0.4rem",
                                          color: "var(--color-text)",
                                        }}
                                      >
                                        Pump Data - Fleet {group.fleet}
                                      </div>
                                      <div className={styles.noDataText}>
                                        No data available for the selected
                                        range.
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className={styles.chartContainer}>
                                {!hasFleetChartData && !loading && (
                                  <div
                                    style={{
                                      marginBottom: "1rem",
                                      textAlign: "center",
                                      fontSize: "0.8125rem",
                                      color: "var(--color-text-muted)",
                                    }}
                                  ></div>
                                )}

                                <div
                                  className={styles.chartControlsGrid}
                                  style={{ marginBottom: "1rem" }}
                                >
                                  <div className={styles.controlField}>
                                    <label className={styles.chartControlLabel}>
                                      Chart Template:
                                    </label>
                                    <CustomSingleSelect
                                      options={templates
                                        .filter(
                                          (t) => t.widget_type === "CHART",
                                        )
                                        .map((t) => ({
                                          value: String(t.id),
                                          label: t.template_name,
                                          template: t,
                                        }))}
                                      value={
                                        selectedChartTemplateByFleet[
                                          fleetKey
                                        ] || null
                                      }
                                      onChange={(option: any) => {
                                        setSelectedChartTemplateByFleet(
                                          (previous) => ({
                                            ...previous,
                                            [fleetKey]: option,
                                          }),
                                        );

                                        if (option) {
                                          applyTemplate(option.template, group);
                                        }
                                      }}
                                      placeholder="Select Template"
                                      isClearable
                                    />
                                  </div>

                                  <div
                                    ref={(element) => {
                                      fleetChannelContainerRefsRef.current[
                                        fleetKey
                                      ] = element;
                                    }}
                                    className={styles.controlField}
                                    onBlurCapture={(event) =>
                                      handleHistoricalChannelDropdownBlur(
                                        event,
                                        group,
                                      )
                                    }
                                  >
                                    <label className={styles.chartControlLabel}>
                                      Channels:
                                      {hasFleetChartData &&
                                        !isCurrentFleetStreaming &&
                                        hasUnloadedChannels(
                                          loadedChartChannelsByFleet[fleetKey],
                                          fleetSelectedChannels.map(
                                            (c) => c.value,
                                          ),
                                        ) && (
                                          <span
                                            style={{
                                              marginLeft: "0.5rem",
                                              fontSize: "0.75rem",
                                              fontWeight: 400,
                                              color:
                                                "var(--color-warning, #d9822b)",
                                            }}
                                          >
                                            {isCurrentFleetHistoricalLoading &&
                                            (
                                              loadedChartChannelsByFleet[
                                                fleetKey
                                              ] || []
                                            ).length === 0
                                              ? `Loading historical ${
                                                  fleetSelectedChannels.length ===
                                                  1
                                                    ? fleetSelectedChannels[0]
                                                        .label
                                                    : "channel"
                                                } data…`
                                              : "Loading selected channels…"}
                                          </span>
                                        )}
                                    </label>
                                    <CustomMultiSelect
                                      options={fleetGroupedChannelOptions}
                                      selected={fleetSelectedChannels}
                                      onChange={(sel) => {
                                        const selectedValues =
                                          sel as PumpChannel[];

                                        const nextChannels =
                                          normalizePowerEndTemperatureSelection(
                                            selectedValues,
                                          );

                                        // Manual channel edits mean the current
                                        // Fleet no longer exactly represents the
                                        // previously applied template.
                                        setSelectedChartTemplateByFleet(
                                          (previous) => ({
                                            ...previous,
                                            [fleetKey]: null,
                                          }),
                                        );

                                        setSelectedChannelsByFleet((prev) => ({
                                          ...prev,
                                          [fleetKey]: nextChannels,
                                        }));

                                        selectedChannelsByFleetRef.current = {
                                          ...selectedChannelsByFleetRef.current,
                                          [fleetKey]: nextChannels,
                                        };

                                        if (selectedFleet?.fleet === fleetKey) {
                                          setSelectedChannels(nextChannels);
                                          selectedChannelsRef.current =
                                            nextChannels;
                                        }

                                        if (nextChannels.length === 0) {
                                          setChartSeriesByFleet((previous) => ({
                                            ...previous,
                                            [fleetKey]: [],
                                          }));

                                          loadedChartChannelsByFleetRef.current =
                                            {
                                              ...loadedChartChannelsByFleetRef.current,
                                              [fleetKey]: [],
                                            };

                                          setLoadedChartChannelsByFleet(
                                            (previous) => ({
                                              ...previous,
                                              [fleetKey]: [],
                                            }),
                                          );

                                          if (
                                            selectedFleet?.fleet === fleetKey
                                          ) {
                                            setChartSeries([]);
                                          }

                                          return;
                                        }

                                        if (isCurrentFleetStreaming) {
                                          void refreshStreamingChartChannelsForFleet(
                                            group,
                                            nextChannels,
                                          );
                                        } else {
                                          scheduleHistoricalChartChannelReload(
                                            group,
                                          );
                                        }
                                      }}
                                      allOptions={fleetChannelOptions}
                                      selectAllLabel="✓ Select All"
                                      placeholder="Search channels..."
                                    />
                                  </div>

                                  {fleetAvailablePumps.length > 0 && (
                                    <div className={styles.controlFieldFull}>
                                      <label
                                        className={styles.chartControlLabel}
                                      >
                                        Pumps:
                                      </label>
                                      <CustomMultiSelect
                                        options={fleetAvailablePumps.map(
                                          (pump) => ({
                                            value: pump,
                                            label: pump,
                                          }),
                                        )}
                                        selected={fleetSelectedPumps.map(
                                          (pump) => ({
                                            value: pump,
                                            label: pump,
                                          }),
                                        )}
                                        onChange={(sel) => {
                                          const next = sel.map((s) => s.value);
                                          setSelectedPumpsByFleet((prev) => ({
                                            ...prev,
                                            [fleetKey]: next,
                                          }));
                                          if (
                                            selectedFleet?.fleet === fleetKey
                                          ) {
                                            setSelectedPumps(next);
                                          }
                                        }}
                                        selectAllLabel="✓ Select All"
                                        placeholder="Search pumps..."
                                      />
                                    </div>
                                  )}

                                  <div
                                    className={styles.chartActionsRow}
                                    style={{
                                      gridColumn: "1 / -1",
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      alignItems: "flex-end",
                                      gap: "0.75rem",
                                      marginTop: "0.25rem",
                                    }}
                                  >
                                    {hasFleetChartData && (
                                      <button
                                        type="button"
                                        className={styles.btnSecondary}
                                        onClick={() => {
                                          if (!fleetFromDate || !fleetToDate)
                                            return;

                                          openChartPopout(
                                            group.fleet,
                                            fleetFromDate.getTime(),
                                            fleetToDate.getTime(),
                                            fleetSelectedChannels,
                                            fleetSelectedPumps,
                                          );
                                        }}
                                      >
                                        <span
                                          className={styles.exportBtnContent}
                                        >
                                          <FiExternalLink size={16} />
                                          <span>Open Graph</span>
                                        </span>
                                      </button>
                                    )}
                                    {hasFleetChartData &&
                                      stageSelectionByFleet[group.fleet] && (
                                        <button
                                          type="button"
                                          className={styles.btnSecondary}
                                          title={`Return to full range (leaving ${
                                            stageSelectionByFleet[group.fleet]!
                                              .label || "selected stage"
                                          })`}
                                          onClick={() =>
                                            resetStageSelection(group.fleet)
                                          }
                                        >
                                          <span
                                            className={styles.exportBtnContent}
                                          >
                                            <FiRotateCcw size={16} />
                                            <span>Reset Graph</span>
                                          </span>
                                        </button>
                                      )}
                                    {hasFleetChartData && (
                                      <button
                                        type="button"
                                        className={styles.btnSecondary}
                                        onClick={() =>
                                          setShowPumpDetailsModal(true)
                                        }
                                      >
                                        <span
                                          className={styles.exportBtnContent}
                                        >
                                          <FiInfo size={16} />
                                          <span>Pump Details</span>
                                        </span>
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      className={styles.btnSecondary}
                                      onClick={() => setShowAxisModal(true)}
                                    >
                                      <span className={styles.exportBtnContent}>
                                        <FiSettings size={16} />
                                        <span>Configure Axes</span>
                                      </span>
                                    </button>

                                    <button
                                      className={styles.btnSecondary}
                                      onClick={() => setShowExportModal(true)}
                                      disabled={isStreaming}
                                      style={{
                                        opacity: isStreaming ? 0.5 : 1,
                                        cursor: isStreaming
                                          ? "not-allowed"
                                          : "pointer",
                                      }}
                                    >
                                      <span className={styles.exportBtnContent}>
                                        <FiDownload size={16} />
                                        <span>Export Data</span>
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div
                                className={styles.tableContainer}
                                style={{
                                  borderRadius: "8px",
                                  padding: "1rem",
                                  marginBottom: "1rem",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "1rem",
                                    marginBottom: "0.75rem",
                                  }}
                                >
                                  <div>
                                    <h3 style={{ margin: 0 }}>
                                      Flagged Re-engagement Events
                                    </h3>
                                    <div
                                      style={{
                                        fontSize: "0.8rem",
                                        opacity: 0.75,
                                        marginTop: "0.25rem",
                                        marginBottom: "1rem",
                                        lineHeight: "1.4",
                                      }}
                                    >
                                      Critical shutdowns followed by Gear &gt; 0
                                      and Discharge Pressure &gt; 3,000 PSI
                                      within 30 minutes.
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className={styles.btnSecondary}
                                    disabled={
                                      reengagementEventsLoadingByFleet[fleetKey]
                                    }
                                    onClick={() =>
                                      void fetchPumpReengagementEvents(
                                        fleetKey,
                                        fleetFromDate,
                                        fleetToDate,
                                      )
                                    }
                                  >
                                    {reengagementEventsLoadingByFleet[fleetKey]
                                      ? "Checking..."
                                      : "Refresh"}
                                  </button>
                                </div>

                                <div
                                  className={styles.reengagementTableWrapper}
                                >
                                  <table className={styles.dataTable}>
                                    <thead>
                                      <tr>
                                        <th>Pump</th>
                                        <th>Alarm</th>
                                        <th>Alarm Time</th>
                                        <th>Neutral Time</th>
                                        <th>Re-engagement Time</th>
                                        <th>Time to Re-engage</th>
                                        <th>Gear</th>
                                        <th>Discharge Pressure</th>
                                        <th>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reengagementEventsLoadingByFleet[
                                        fleetKey
                                      ] ? (
                                        <tr>
                                          <td
                                            colSpan={9}
                                            className={
                                              styles.reengagementEmptyCell
                                            }
                                          >
                                            Checking one-second pump data...
                                          </td>
                                        </tr>
                                      ) : (
                                          reengagementEventsByFleet[fleetKey] ||
                                          []
                                        ).length === 0 ? (
                                        <tr>
                                          <td
                                            colSpan={9}
                                            className={
                                              styles.reengagementEmptyCell
                                            }
                                          >
                                            No flagged re-engagement events
                                            found for this time range.
                                          </td>
                                        </tr>
                                      ) : (
                                        reengagementEventsByFleet[fleetKey].map(
                                          (event) => (
                                            <tr
                                              key={`${event.pump}-${event.alarm_time_ms}-${event.reengagement_time_ms}`}
                                            >
                                              <td>{event.pump}</td>
                                              <td
                                                className={
                                                  styles.reengagementAlarmCell
                                                }
                                              >
                                                {event.alarm ===
                                                "transmissionOilPressureLowCriticalAlarm"
                                                  ? "Transmission Oil Pressure Low Critical"
                                                  : event.alarm}
                                              </td>
                                              <td>
                                                {new Date(
                                                  event.alarm_time_ms,
                                                ).toLocaleString()}
                                              </td>
                                              <td>
                                                {new Date(
                                                  event.neutral_time_ms,
                                                ).toLocaleString()}
                                              </td>
                                              <td>
                                                {new Date(
                                                  event.reengagement_time_ms,
                                                ).toLocaleString()}
                                              </td>
                                              <td>
                                                {formatReengagementDelay(
                                                  event.time_to_reengage_seconds,
                                                )}
                                              </td>
                                              <td>{event.gear}</td>
                                              <td>
                                                {Number(
                                                  event.discharge_pressure,
                                                ).toLocaleString(undefined, {
                                                  maximumFractionDigits: 0,
                                                })}{" "}
                                                PSI
                                              </td>
                                              <td>
                                                <button
                                                  type="button"
                                                  className={`${styles.btnSecondary} ${styles.reengagementViewButton}`}
                                                  onClick={() =>
                                                    void handleViewReengagementChart(
                                                      event,
                                                      group,
                                                    )
                                                  }
                                                >
                                                  View Chart
                                                </button>
                                              </td>
                                            </tr>
                                          ),
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              <div
                                className={styles.tableContainer}
                                style={{ borderRadius: "8px", padding: "1rem" }}
                              >
                                <div style={{ marginBottom: "1rem" }}>
                                  <div
                                    className={styles.chartControlsGrid}
                                    style={{ marginBottom: "1rem" }}
                                  >
                                    <div className={styles.controlField}>
                                      <label
                                        className={styles.chartControlLabel}
                                      >
                                        Table Template:
                                      </label>
                                      <CustomSingleSelect
                                        options={templates
                                          .filter(
                                            (t) => t.widget_type === "TABLE",
                                          )
                                          .map((t) => ({
                                            value: String(t.id),
                                            label: t.template_name,
                                            template: t,
                                          }))}
                                        value={fleetTableTemplate}
                                        onChange={(option: any) => {
                                          setSelectedTableTemplateByFleet(
                                            (prev) => ({
                                              ...prev,
                                              [fleetKey]: option,
                                            }),
                                          );

                                          if (option) {
                                            const channels =
                                              filterUnsupportedPumpChannels(
                                                option.template.channels.map(
                                                  (ch: TemplateChannel) => ({
                                                    value: ch.channel,
                                                    label:
                                                      availableChannels.find(
                                                        (c) =>
                                                          c.value ===
                                                          ch.channel,
                                                      )?.label || ch.channel,
                                                  }),
                                                ),
                                              );

                                            selectedTableChannelsByFleetRef.current =
                                              {
                                                ...selectedTableChannelsByFleetRef.current,
                                                [fleetKey]: channels,
                                              };

                                            setSelectedTableChannelsByFleet(
                                              (prev) => ({
                                                ...prev,
                                                [fleetKey]: channels,
                                              }),
                                            );

                                            if (!isCurrentFleetStreaming) {
                                              void loadFleetTableChannels(
                                                group,
                                                channels,
                                              );
                                            }

                                            if (isCurrentFleetStreaming) {
                                              setLoadedTableChannelsByFleet(
                                                (prev) => ({
                                                  ...prev,
                                                  [fleetKey]: channels.map(
                                                    (c) => c.value,
                                                  ),
                                                }),
                                              );
                                            }
                                          }
                                        }}
                                        placeholder="Select Template"
                                        isClearable
                                      />
                                    </div>

                                    <div className={styles.controlField}>
                                      <label
                                        className={styles.chartControlLabel}
                                      >
                                        Table Channels:
                                        {!isCurrentFleetStreaming &&
                                          showTableChannelNoticeByFleet[
                                            fleetKey
                                          ] && (
                                            <span
                                              style={{
                                                marginLeft: "0.5rem",
                                                fontSize: "0.75rem",
                                                fontWeight: 400,
                                                color:
                                                  "var(--color-warning, #d9822b)",
                                              }}
                                            >
                                              Loading selected table channels…
                                            </span>
                                          )}
                                      </label>

                                      <CustomMultiSelect
                                        options={fleetGroupedChannelOptions}
                                        selected={fleetTableChannels}
                                        onChange={(sel) => {
                                          const channels = sel as PumpChannel[];

                                          setSelectedTableTemplateByFleet(
                                            (previous) => ({
                                              ...previous,
                                              [fleetKey]: null,
                                            }),
                                          );

                                          // Update both state and ref immediately so the reload
                                          // uses the newly selected channels instead of stale state.
                                          selectedTableChannelsByFleetRef.current =
                                            {
                                              ...selectedTableChannelsByFleetRef.current,
                                              [fleetKey]: channels,
                                            };

                                          setSelectedTableChannelsByFleet(
                                            (prev) => ({
                                              ...prev,
                                              [fleetKey]: channels,
                                            }),
                                          );

                                          if (isCurrentFleetStreaming) {
                                            void refreshStreamingChannelsForFleet(
                                              fleetKey,
                                              channels,
                                            );
                                            return;
                                          }

                                          // Load only newly selected table channels
                                          // at 1-minute resolution and merge them into
                                          // the existing aggregates. Existing values stay
                                          // visible while the new columns are loading.
                                          void loadFleetTableChannels(
                                            group,
                                            channels,
                                          );
                                        }}
                                        allOptions={fleetChannelOptions}
                                        selectAllLabel="✓ Select All"
                                        placeholder="Search channels."
                                      />
                                    </div>

                                    <div
                                      className={styles.chartActionsRow}
                                      style={{ alignItems: "flex-end" }}
                                    >
                                      <button
                                        className={styles.btnSecondary}
                                        onClick={() => setShowExportModal(true)}
                                        disabled={isCurrentFleetStreaming}
                                        style={{
                                          opacity: isStreaming ? 0.5 : 1,
                                          cursor: isStreaming
                                            ? "not-allowed"
                                            : "pointer",
                                        }}
                                      >
                                        <span
                                          className={styles.exportBtnContent}
                                        >
                                          <FiDownload size={16} />
                                          <span>Export Data</span>
                                        </span>
                                      </button>
                                    </div>
                                  </div>

                                  {!isStreaming && (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "1rem",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <label
                                        style={{
                                          color: "var(--color-text)",
                                          fontWeight: 500,
                                        }}
                                      >
                                        Aggregation:
                                      </label>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        {(
                                          ["avg", "sum", "min", "max"] as const
                                        ).map((type) => (
                                          <button
                                            key={type}
                                            onClick={() =>
                                              setAggregationType(type)
                                            }
                                            style={{
                                              padding: "0.5rem 1rem",
                                              border: `2px solid ${aggregationType === type ? "var(--color-green)" : "var(--color-border)"}`,
                                              borderRadius: "6px",
                                              backgroundColor:
                                                aggregationType === type
                                                  ? "var(--color-green)"
                                                  : "transparent",
                                              color:
                                                aggregationType === type
                                                  ? "white"
                                                  : "var(--color-text)",
                                              cursor: "pointer",
                                              fontWeight:
                                                aggregationType === type
                                                  ? 600
                                                  : 500,
                                              fontSize: "0.875rem",
                                              transition: "all 0.2s ease",
                                              textTransform: "uppercase",
                                            }}
                                            onMouseEnter={(e) => {
                                              if (aggregationType !== type) {
                                                e.currentTarget.style.borderColor =
                                                  "var(--color-green)";
                                                e.currentTarget.style.backgroundColor =
                                                  theme === "dark"
                                                    ? "var(--color-background-dark)"
                                                    : "#e8f5e9";
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (aggregationType !== type) {
                                                e.currentTarget.style.borderColor =
                                                  "var(--color-border)";
                                                e.currentTarget.style.backgroundColor =
                                                  "transparent";
                                              }
                                            }}
                                          >
                                            {type}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {tableLoadingByFleet[fleetKey] &&
                                  Object.keys(fleetAggregatedData).length >
                                    0 && (
                                    <div
                                      style={{
                                        padding: "0.4rem 0",
                                        fontSize: "0.85rem",
                                        color: "var(--color-text-muted, #888)",
                                        textAlign: "center",
                                      }}
                                    >
                                      Updating table data…
                                    </div>
                                  )}

                                <div className={styles.historicalTableScroll}>
                                  <table className={styles.table}>
                                    <thead>
                                      <tr>
                                        <th className={styles.tableHeader}>
                                          Pump
                                        </th>
                                        {fleetTableChannels.map((ch) => (
                                          <th
                                            key={ch.value}
                                            className={styles.tableHeader}
                                          >
                                            {ch.label}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {streamingFleetIds.has(group.fleet) ? (
                                        Array.from(
                                          (
                                            latestValuesByFleet[group.fleet] ||
                                            new Map()
                                          ).entries(),
                                        )
                                          .filter(
                                            ([pumpName]) =>
                                              fleetSelectedPumps.length === 0 ||
                                              fleetSelectedPumps.includes(
                                                pumpName,
                                              ),
                                          )
                                          .map(([pumpName, data]) => (
                                            <tr
                                              key={pumpName}
                                              className={styles.tableRow}
                                            >
                                              <td className={styles.tableCell}>
                                                {pumpName}
                                              </td>
                                              {fleetTableChannels.map((ch) => (
                                                <td
                                                  key={ch.value}
                                                  className={styles.tableCell}
                                                  style={getThresholdCellStyle(
                                                    getThresholdSeverity(
                                                      getChannelValue(
                                                        data,
                                                        ch.value,
                                                      ),
                                                      ch.value,
                                                      data,
                                                    ),
                                                  )}
                                                >
                                                  {(() => {
                                                    const value =
                                                      getChannelValue(
                                                        data,
                                                        ch.value,
                                                      );

                                                    if (
                                                      value === null ||
                                                      value === undefined ||
                                                      Number.isNaN(
                                                        Number(value),
                                                      )
                                                    ) {
                                                      return "N/A";
                                                    }

                                                    return Number(
                                                      value,
                                                    ).toFixed(2);
                                                  })()}
                                                </td>
                                              ))}
                                            </tr>
                                          ))
                                      ) : Object.keys(fleetAggregatedData)
                                          .length === 0 ? (
                                        <tr>
                                          <td
                                            colSpan={
                                              fleetTableChannels.length + 1
                                            }
                                            className={styles.noDataTable}
                                          >
                                            {tableLoadingByFleet[fleetKey]
                                              ? "Loading table data…"
                                              : "No data available."}
                                          </td>
                                        </tr>
                                      ) : (
                                        Object.entries(fleetAggregatedData).map(
                                          ([pumpName, data]: [string, any]) => (
                                            <tr
                                              key={pumpName}
                                              className={styles.tableRow}
                                            >
                                              <td className={styles.tableCell}>
                                                {pumpName}
                                              </td>
                                              {fleetTableChannels.map((ch) => (
                                                <td
                                                  key={ch.value}
                                                  className={styles.tableCell}
                                                  style={getThresholdCellStyle(
                                                    getThresholdSeverity(
                                                      getAggregatedChannelValue(
                                                        data,
                                                        ch.value,
                                                        aggregationType,
                                                      ),
                                                      ch.value,
                                                      getAggregatedRowSnapshot(
                                                        data,
                                                        aggregationType,
                                                      ),
                                                    ),
                                                  )}
                                                >
                                                  {(() => {
                                                    const value =
                                                      getAggregatedChannelValue(
                                                        data,
                                                        ch.value,
                                                        aggregationType,
                                                      );

                                                    if (
                                                      value === null ||
                                                      value === undefined ||
                                                      Number.isNaN(
                                                        Number(value),
                                                      )
                                                    ) {
                                                      return "N/A";
                                                    }

                                                    return Number(
                                                      value,
                                                    ).toFixed(2);
                                                  })()}
                                                </td>
                                              ))}
                                            </tr>
                                          ),
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {activeTab === "blender" && (
              <div className={styles.blenderPreviewPage}>
                <div className={styles.fleetControlsBox}>
                  {/* Column sizes live in .blenderTopGrid (Fleet.module.css) so
                      the row shrinks with the sidebar open and its responsive
                      breakpoints apply. */}
                  <div className={styles.blenderTopGrid}>
                    <div className={styles.assetPumpField}>
                      <label className={styles.assetFieldLabel}>Blender:</label>
                      <CustomSingleSelect
                        options={blenderAssetOptions}
                        value={
                          blenderAssetOptions.find(
                            (option) => option.value === selectedBlenderId,
                          ) || null
                        }
                        onChange={(option: any) => {
                          const nextId = String(option?.value || "").trim();
                          if (!nextId) return;

                          const nextAsset = blenderAssets.find(
                            (asset) => asset.id === nextId,
                          );
                          if (nextAsset) selectBlender(nextAsset);
                        }}
                        placeholder={
                          blenderListStatus === "error"
                            ? "Failed to load blenders"
                            : blenderListStatus === "loaded" &&
                                blenderAssets.length === 0
                              ? "No blenders available"
                              : blenderListStatus === "loading"
                                ? "Loading blenders..."
                                : "Search Blender..."
                        }
                        isClearable={false}
                      />

                      {recentBlenderIds.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "6px",
                            marginTop: "6px",
                          }}
                        >
                          <span style={{ fontSize: "12px", opacity: 0.7 }}>
                            Recent:
                          </span>

                          {recentBlenderIds.map((blenderId) => {
                            const recentAsset = blenderAssets.find(
                              (asset) => asset.id === blenderId,
                            );
                            if (!recentAsset) return null;

                            return (
                              <button
                                key={blenderId}
                                type="button"
                                className={styles.btnSecondary}
                                disabled={blenderId === selectedBlenderId}
                                onClick={() => selectBlender(recentAsset)}
                                style={{
                                  padding: "3px 8px",
                                  fontSize: "12px",
                                  minWidth: "auto",
                                }}
                              >
                                {blenderId}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {selectedBlender.id && (
                        <div className={styles.blenderAssetMeta}>
                          <span
                            className={`${styles.blenderTypePill} ${
                              selectedBlender.type === "electric"
                                ? styles.blenderTypeElectric
                                : styles.blenderTypeDiesel
                            }`}
                          >
                            {selectedBlender.type === "electric"
                              ? "Electric"
                              : "Diesel"}
                          </span>
                          <span>
                            {selectedBlender.fleet != null
                              ? `Fleet ${selectedBlender.fleet}`
                              : "No fleet data yet"}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className={styles.assetDateField}>
                      <label className={styles.assetFieldLabel}>From:</label>
                      <div className={styles.fleetDateInputWrap}>
                        <DatePicker
                          selected={blenderPickerFromDate}
                          onChange={(date) =>
                            // Draft only; Load Data applies it.
                            date &&
                            setBlenderDraftDates((previous) => ({
                              ...previous,
                              from: date,
                            }))
                          }
                          aria-label="Blender from date and time"
                          disabled={isBlenderStreaming}
                          className={styles.fleetDateTextInput}
                          {...datePickerCommonProps}
                        />
                      </div>
                    </div>

                    <div className={styles.assetDateField}>
                      <label className={styles.assetFieldLabel}>To:</label>
                      <div className={styles.fleetDateInputWrap}>
                        <DatePicker
                          selected={blenderPickerToDate}
                          onChange={(date) =>
                            // Draft only; Load Data applies it.
                            date &&
                            setBlenderDraftDates((previous) => ({
                              ...previous,
                              to: date,
                            }))
                          }
                          aria-label="Blender to date and time"
                          disabled={isBlenderStreaming}
                          className={`${styles.fleetDateTextInput} ${
                            blenderHasInvalidDateRange
                              ? styles.fleetDateErrorInput
                              : ""
                          }`}
                          {...datePickerCommonProps}
                        />
                      </div>
                    </div>

                    <div className={styles.assetLoadColumn}>
                      {/* Label-height slot so the button lines up with the
                          inputs; the hint sits directly above the button. */}
                      <div className={styles.assetLoadHintSlot}>
                        {!isBlenderStreaming &&
                          (blenderHasInvalidDateRange ? (
                            <span
                              role="alert"
                              className={styles.assetLoadHint}
                              style={{ color: "#ef4444" }}
                            >
                              End date must be after start date.
                            </span>
                          ) : (
                            isBlenderDateRangePending && (
                              <span
                                role="status"
                                className={styles.assetLoadHint}
                              >
                                Dates changed. Press Load Data to update.
                              </span>
                            )
                          ))}
                      </div>
                      <button
                        type="button"
                        className={`${styles.btnPrimary} ${styles.assetLoadButton}`}
                        onClick={handleBlenderLoadData}
                        disabled={
                          isBlenderStreaming ||
                          blenderHasInvalidDateRange ||
                          !selectedBlenderId
                        }
                        title={
                          isBlenderStreaming
                            ? "Turn off real-time streaming to load a From/To range."
                            : "Load the chart and table for the selected From/To range."
                        }
                      >
                        {blenderDataStatus === "loading" && !isBlenderStreaming
                          ? "Loading…"
                          : "Load Data"}
                      </button>
                    </div>

                    <div className={styles.blenderQuickRangeField}>
                      <label className={styles.assetFieldLabel}>
                        Quick Range:
                      </label>
                      <div className={styles.blenderQuickRangeButtons}>
                        <button
                          type="button"
                          className={styles.btnSecondary}
                          disabled={isBlenderStreaming}
                          onClick={() => {
                            // A quick range is applied and loaded straight
                            // away, replacing any unsaved date edits.
                            const nextEnd = new Date();
                            setBlenderDraftDates(null);
                            setBlenderToDate(nextEnd);
                            setBlenderFromDate(
                              new Date(nextEnd.getTime() - 6 * 60 * 60 * 1000),
                            );
                          }}
                        >
                          Last 6 Hours
                        </button>

                        <button
                          type="button"
                          className={styles.btnSecondary}
                          disabled={isBlenderStreaming || !selectedBlenderId}
                          onClick={copyBlenderShareLink}
                          title="Copy a link to the current Blender historical view"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.blenderWorkspace}>
                  <aside className={styles.blenderChannelPane}>
                    <div className={styles.blenderChannelPaneHeader}>
                      <div>
                        <h3>Channels</h3>
                        <span>
                          {selectedBlenderChannels.length} of{" "}
                          {blenderChannelCount} selected •{" "}
                          {selectedBlender.type}
                        </span>
                      </div>
                    </div>

                    <div className={styles.blenderChannelSearchWrap}>
                      <input
                        type="text"
                        value={blenderChannelSearch}
                        onChange={(event) =>
                          setBlenderChannelSearch(event.target.value)
                        }
                        placeholder="Search channels..."
                        aria-label="Search Blender channels"
                        className={styles.blenderChannelSearch}
                      />
                      {blenderChannelSearch && (
                        <button
                          type="button"
                          className={styles.blenderSearchClear}
                          onClick={() => setBlenderChannelSearch("")}
                          aria-label="Clear Blender channel search"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid var(--color-border)",
                        boxSizing: "border-box",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                          marginBottom:
                            blenderChannelFavorites.length > 0 ? "8px" : "0",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          Channel Favorites
                        </span>

                        <button
                          type="button"
                          className={styles.btnSecondary}
                          disabled={
                            selectedBlenderChannels.length === 0 ||
                            blenderChannelsStatus !== "loaded"
                          }
                          onClick={saveBlenderChannelFavorite}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            minWidth: "auto",
                          }}
                          title="Save the currently selected Blender channels"
                        >
                          + Save Current
                        </button>
                      </div>

                      {blenderChannelFavorites.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {blenderChannelFavorites.map((favorite) => (
                            <div
                              key={favorite.id}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                border: "1px solid #5b636b",
                                borderRadius: "6px",
                                overflow: "hidden",
                                background:
                                  theme === "dark" ? "#3b4146" : "#f3f4f6",
                              }}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  applyBlenderChannelFavorite(favorite)
                                }
                                disabled={
                                  !selectedBlenderId ||
                                  blenderChannelsStatus !== "loaded"
                                }
                                title={`${favorite.channels.length} saved channels`}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "inherit",
                                  cursor: "pointer",
                                  padding: "6px 8px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                }}
                              >
                                {favorite.name}
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  deleteBlenderChannelFavorite(favorite)
                                }
                                aria-label={`Delete ${favorite.name}`}
                                title={`Delete ${favorite.name}`}
                                style={{
                                  border: "none",
                                  borderLeft: "1px solid #5b636b",
                                  background: "transparent",
                                  color: "#ef4444",
                                  cursor: "pointer",
                                  padding: "6px 7px",
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className={styles.blenderChannelScroller}>
                      {blenderChannelsStatus === "loading" ? (
                        <div className={styles.blenderNoChannels}>
                          Loading channels...
                        </div>
                      ) : blenderChannelsStatus === "error" ? (
                        <div className={styles.blenderNoChannels}>
                          Failed to load channels for {selectedBlender.id}.
                        </div>
                      ) : blenderChannelsStatus === "not_found" ? (
                        <div className={styles.blenderNoChannels}>
                          Blender {selectedBlender.id} was not found.
                        </div>
                      ) : blenderChannelGroups.length === 0 ? (
                        <div className={styles.blenderNoChannels}>
                          {selectedBlender.id
                            ? `${selectedBlender.id} has not reported any channels yet.`
                            : "Select a blender to view its channels."}
                        </div>
                      ) : visibleBlenderChannelGroups.length === 0 ? (
                        <div className={styles.blenderNoChannels}>
                          No channels match “{blenderChannelSearch}”.
                        </div>
                      ) : (
                        visibleBlenderChannelGroups.map((group) => {
                          const isExpanded =
                            !!blenderChannelSearch ||
                            expandedBlenderGroups.has(group.group);
                          const selectedInGroup = group.channels.filter(
                            (channel) =>
                              selectedBlenderChannels.includes(channel.key),
                          ).length;

                          return (
                            <div
                              key={group.group}
                              className={styles.blenderChannelGroup}
                            >
                              <button
                                type="button"
                                className={styles.blenderGroupHeader}
                                onClick={() => {
                                  if (blenderChannelSearch) return;
                                  setExpandedBlenderGroups((previous) => {
                                    const next = new Set(previous);
                                    if (next.has(group.group))
                                      next.delete(group.group);
                                    else next.add(group.group);
                                    return next;
                                  });
                                }}
                              >
                                <span>{isExpanded ? "▾" : "▸"}</span>
                                <strong>{group.group}</strong>
                                <span className={styles.blenderGroupCount}>
                                  {selectedInGroup}/{group.channels.length}
                                </span>
                              </button>

                              {isExpanded && (
                                <div className={styles.blenderGroupChannels}>
                                  {group.channels.map((channel) => {
                                    const checked =
                                      selectedBlenderChannels.includes(
                                        channel.key,
                                      );
                                    return (
                                      <label
                                        key={channel.key}
                                        className={styles.blenderChannelOption}
                                        title={channel.key}
                                      >
                                        <input
                                          type="checkbox"
                                          value={channel.key}
                                          checked={checked}
                                          onChange={() =>
                                            setSelectedBlenderChannels(
                                              (previous) =>
                                                checked
                                                  ? previous.filter(
                                                      (item) =>
                                                        item !== channel.key,
                                                    )
                                                  : [...previous, channel.key],
                                            )
                                          }
                                        />
                                        <span>{channel.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className={styles.blenderPaneFooter}>
                      <span>
                        {selectedBlenderChannels.length} channels selected
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedBlenderChannels([])}
                        disabled={selectedBlenderChannels.length === 0}
                      >
                        Clear All
                      </button>
                    </div>
                  </aside>

                  <div className={styles.blenderDataArea}>
                    <div className={styles.chartContainer}>
                      <div className={styles.blenderChartToolbar}>
                        <div>
                          <span className={styles.blenderToolbarLabel}>
                            Selected asset
                          </span>
                          <strong>{selectedBlender.id}</strong>
                        </div>
                        <div className={styles.blenderToolbarActions}>
                          <div className={styles.blenderToolbarStatus}>
                            <span className={styles.blenderPreviewDot} />
                            {selectedBlender.type} Blender
                          </div>
                          {isBlenderStreaming && blenderLiveStatus && (
                            <span
                              className={`${styles.blenderLiveBadge} ${
                                blenderLiveStatus.live
                                  ? styles.blenderLiveBadgeOn
                                  : ""
                              }`}
                              title={
                                blenderLiveStatus.connectionError
                                  ? "Can't reach the server. Retrying..."
                                  : blenderLiveStatus.lastAdvanceAgeMs !== null
                                    ? `Last new reading ${Math.round(
                                        blenderLiveStatus.lastAdvanceAgeMs /
                                          1000,
                                      )} s ago`
                                    : undefined
                              }
                            >
                              {blenderLiveStatus.connectionError
                                ? "Reconnecting"
                                : blenderLiveStatus.live
                                  ? "Live"
                                  : "Offline"}
                            </span>
                          )}
                          <div className={styles.blenderStreamingToggle}>
                            <span className={styles.fleetControlLabel}>
                              Real-Time Streaming
                            </span>
                            <button
                              type="button"
                              className={`${styles.fleetStreamingSwitch} ${
                                isBlenderStreaming
                                  ? styles.fleetStreamingSwitchOn
                                  : ""
                              }`}
                              onClick={() =>
                                setIsBlenderStreaming((previous) => !previous)
                              }
                              disabled={
                                !selectedBlenderId ||
                                selectedBlenderChannels.length === 0
                              }
                              aria-pressed={isBlenderStreaming}
                              aria-label={
                                isBlenderStreaming
                                  ? "Turn off real-time streaming"
                                  : "Turn on real-time streaming"
                              }
                              title={
                                isBlenderStreaming
                                  ? "Turn off real-time streaming and return to the From/To range."
                                  : "Stream this blender's data every second. The chart keeps a rolling window the length of the From/To range."
                              }
                            >
                              <span
                                className={`${styles.fleetStreamingSwitchThumb} ${
                                  isBlenderStreaming
                                    ? styles.fleetStreamingSwitchThumbOn
                                    : ""
                                }`}
                              />
                            </button>
                          </div>
                          {isBlenderStreaming && (
                            <select
                              className={styles.compareViewSelect}
                              value={
                                blenderLiveViewMs === null
                                  ? "full"
                                  : String(blenderLiveViewMs)
                              }
                              onChange={(event) => {
                                setBlenderLiveViewMs(
                                  event.target.value === "full"
                                    ? null
                                    : Number(event.target.value),
                                );
                                // Picking a window means "follow live again".
                                setBlenderChartView(null);
                              }}
                              aria-label="Live chart window"
                              title="How much of the live data the chart shows. It follows the newest reading."
                            >
                              {BLENDER_LIVE_VIEW_OPTIONS.map((option) => (
                                <option
                                  key={option.label}
                                  value={
                                    option.ms === null
                                      ? "full"
                                      : String(option.ms)
                                  }
                                >
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          )}
                          {blenderChartView && (
                            <button
                              type="button"
                              className={styles.btnSecondary}
                              onClick={() => setBlenderChartView(null)}
                            >
                              {isBlenderStreaming
                                ? "Follow live"
                                : "Reset zoom"}
                            </button>
                          )}
                          {blenderChartOptions && (
                            <button
                              type="button"
                              className={styles.btnSecondary}
                              onClick={openBlenderChartPopout}
                            >
                              <span className={styles.exportBtnContent}>
                                <FiExternalLink size={16} />
                                <span>Open Graph</span>
                              </span>
                            </button>
                          )}
                          {blenderData && blenderData.rowCount > 0 && (
                            <button
                              type="button"
                              className={styles.btnSecondary}
                              disabled={isBlenderStreaming}
                              onClick={exportBlenderCsv}
                              title={
                                isBlenderStreaming
                                  ? "Turn off real-time streaming to export data"
                                  : "Export Blender data to CSV"
                              }
                              style={{
                                opacity: isBlenderStreaming ? 0.5 : 1,
                                cursor: isBlenderStreaming
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              <span className={styles.exportBtnContent}>
                                <FiDownload size={16} />
                                <span>Export CSV</span>
                              </span>
                            </button>
                          )}
                        </div>
                      </div>

                      {selectedBlenderChannels.length === 0 ? (
                        <div className={styles.blenderEmptyChart}>
                          <strong>No channels selected</strong>
                          <span>
                            Select channels from the left pane to view the
                            Blender chart.
                          </span>
                        </div>
                      ) : blenderDataStatus === "error" ? (
                        <div className={styles.blenderEmptyChart}>
                          <strong>Could not load blender data</strong>
                          <span>{blenderDataError}</span>
                        </div>
                      ) : blenderChartOptions ? (
                        <HighchartsReact
                          highcharts={Highcharts}
                          // Update the existing chart in place (one-to-one
                          // series match, no animation) so the per-second live
                          // ticks don't rebuild the whole chart.
                          immutable={false}
                          updateArgs={BLENDER_CHART_UPDATE_ARGS}
                          options={blenderChartOptions}
                        />
                      ) : blenderDataStatus === "loaded" ? (
                        <div className={styles.blenderEmptyChart}>
                          <strong>No data in this time range</strong>
                          <span>
                            {selectedBlender.id} did not report the selected
                            channels between the From and To times.
                          </span>
                        </div>
                      ) : (
                        <div className={styles.blenderEmptyChart}>
                          <strong>Loading blender data...</strong>
                        </div>
                      )}
                    </div>

                    <div className={styles.tableContainer}>
                      <div className={styles.blenderTableHeader}>
                        <div>
                          <h3>Blender Data</h3>
                          <span>
                            {blenderData && blenderData.rowCount > 0
                              ? `${blenderData.resolution} resolution • ${blenderData.rowCount.toLocaleString("en-US")} rows${
                                  blenderData.rowCount > BLENDER_TABLE_MAX_ROWS
                                    ? ` (showing latest ${BLENDER_TABLE_MAX_ROWS})`
                                    : ""
                                }`
                              : "Selected PadTrack channels, newest first"}
                          </span>
                        </div>
                        {blenderDataStatus === "loading" && (
                          <span className={styles.blenderApiPending}>
                            Loading...
                          </span>
                        )}
                      </div>
                      <div className={styles.historicalTableScroll}>
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              <th className={styles.tableHeader}>Timestamp</th>
                              {(
                                blenderData?.channels ?? selectedBlenderChannels
                              ).map((key) => {
                                const channel = blenderChannelByKey.get(key);
                                return (
                                  <th
                                    key={key}
                                    className={styles.tableHeader}
                                    title={key}
                                  >
                                    {channel?.label ?? key}
                                    {channel?.unit ? ` (${channel.unit})` : ""}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {blenderData && blenderTableRows.length > 0 ? (
                              blenderTableRows.map((rowIndex) => (
                                <tr
                                  key={blenderData.t[rowIndex]}
                                  className={styles.tableRow}
                                >
                                  <td className={styles.tableCell}>
                                    {formatBlenderTimestamp(
                                      blenderData.t[rowIndex],
                                    )}
                                  </td>
                                  {blenderData.channels.map((key) => (
                                    <td key={key} className={styles.tableCell}>
                                      {formatBlenderValue(
                                        blenderData.values[key]?.[rowIndex] ??
                                          null,
                                        blenderChannelByKey.get(key),
                                        blenderData.resolution,
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  className={styles.reengagementEmptyCell}
                                  colSpan={
                                    (blenderData?.channels.length ??
                                      selectedBlenderChannels.length) + 1
                                  }
                                >
                                  {selectedBlenderChannels.length === 0
                                    ? "Select one or more Blender channels to view the table."
                                    : blenderDataStatus === "error"
                                      ? blenderDataError
                                      : blenderDataStatus === "loaded"
                                        ? "No data in this time range."
                                        : "Loading blender data..."}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === "asset" && (
              <div className={styles.accordionContent}>
                <div className={styles.fleetControlsBox}>
                  <div className={styles.assetTopGrid}>
                    <div className={styles.assetPumpField}>
                      <label className={styles.assetFieldLabel}>Pumps:</label>
                      <div className={styles.assetPumpSelectWrap}>
                        <CustomMultiSelect
                          options={pumpListOptions}
                          selected={selectedAssetPumps}
                          onChange={handleAssetPumpSelectionChange}
                          placeholder="Search pumps..."
                        />
                      </div>
                    </div>

                    <div className={styles.assetDateField}>
                      <label className={styles.assetFieldLabel}>From:</label>
                      <div className={styles.fleetDateInputWrap}>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => date && setFromDate(date)}
                          disabled={isAssetDateBusy}
                          aria-busy={isAssetDateBusy}
                          aria-label="Asset from date and time"
                          className={`${styles.fleetDateTextInput} ${
                            isAssetDateBusy ? styles.fleetDateLoadingInput : ""
                          }`}
                          {...datePickerCommonProps}
                        />
                      </div>
                    </div>

                    <div className={styles.assetToColumn}>
                      <div className={styles.assetDateField}>
                        <label className={styles.assetFieldLabel}>To:</label>
                        <div className={styles.fleetDateInputWrap}>
                          <DatePicker
                            selected={toDate}
                            onChange={(date) => date && setToDate(date)}
                            disabled={isAssetDateBusy}
                            aria-busy={isAssetDateBusy}
                            aria-label="Asset to date and time"
                            className={`${styles.fleetDateTextInput} ${
                              hasInvalidDateRange
                                ? styles.fleetDateErrorInput
                                : ""
                            } ${
                              isAssetDateBusy
                                ? styles.fleetDateLoadingInput
                                : ""
                            }`}
                            {...datePickerCommonProps}
                          />
                        </div>
                      </div>

                      {hasInvalidDateRange && (
                        <div className={styles.assetErrorBox}>
                          <span className={styles.assetErrorIcon}>⚠</span>
                          <span>End date must be after start date.</span>
                        </div>
                      )}
                    </div>

                    <div className={styles.assetLoadColumn}>
                      {/* Same height as the field labels, so the button lines
                          up with the inputs; the pending-dates hint sits here,
                          directly above the button. */}
                      <div className={styles.assetLoadHintSlot}>
                        {!hasInvalidDateRange && isAssetDateRangePending && (
                          <span role="status" className={styles.assetLoadHint}>
                            Dates changed. Press Load Data to update.
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className={`${styles.btnPrimary} ${styles.assetLoadButton}`}
                        onClick={handleAssetLoadData}
                        disabled={isAssetLoadDisabled || isAssetDateBusy}
                        title={
                          selectedAssetPumps.length === 0
                            ? "Select one or more pumps first."
                            : "Load the chart and table for the selected From/To range."
                        }
                      >
                        {isLoadDataLoading ? "Loading…" : "Load Data"}
                      </button>
                    </div>
                  </div>
                  <div className={styles.assetDivider}></div>
                  <div className={styles.assetButtonRow}>
                    {pumpData.length > 0 && (
                      <button
                        type="button"
                        className={`${styles.btnSecondary} ${styles.assetActionButton}`}
                        onClick={() => setShowPumpDetailsModal(true)}
                      >
                        <FiInfo size={16} />
                        <span>Pump Details</span>
                      </button>
                    )}

                    <button
                      type="button"
                      className={`${styles.btnSecondary} ${styles.assetActionButton}`}
                      onClick={() => {
                        setTemplateModalContext({ view: "asset" });
                        setShowTemplateModal(true);
                      }}
                    >
                      <FiSettings size={16} />
                      <span>Manage Templates</span>
                    </button>
                  </div>
                </div>

                <div className={styles.chartContainer}>
                  <div
                    className={styles.chartControlsGrid}
                    style={{ marginBottom: "1rem" }}
                  >
                    <div className={styles.controlField}>
                      <label className={styles.chartControlLabel}>
                        Chart Template:
                      </label>
                      <CustomSingleSelect
                        options={templates
                          .filter((t) => t.widget_type === "CHART")
                          .map((t) => ({
                            value: String(t.id),
                            label: t.template_name,
                            template: t,
                          }))}
                        value={selectedTemplate}
                        onChange={(option: any) => {
                          setSelectedTemplate(option);
                          if (option) applyTemplate(option.template);
                        }}
                        placeholder="Select Template"
                        isClearable
                      />
                    </div>

                    <div
                      ref={assetChannelContainerRef}
                      className={styles.controlField}
                      onBlurCapture={handleAssetChannelDropdownBlur}
                    >
                      <label className={styles.chartControlLabel}>
                        Channels:
                        {isLoadDataLoading && (
                          <span
                            role="status"
                            aria-live="polite"
                            style={{
                              marginLeft: "0.5rem",
                              fontSize: "0.75rem",
                              fontWeight: 400,
                              color: "var(--color-warning, #d9822b)",
                            }}
                          >
                            Loading selected channels…
                          </span>
                        )}
                      </label>
                      <CustomMultiSelect
                        options={
                          selectedFleet
                            ? getGroupedChannelOptionsForFleet(
                                selectedFleet.fleet,
                                pumpDataByFleet[selectedFleet.fleet] ||
                                  pumpData,
                              )
                            : baseGroupedChannels
                        }
                        selected={selectedAssetChannels}
                        onChange={(sel) =>
                          handleChartChannelChange(sel as PumpChannel[])
                        }
                        allOptions={availableChannels}
                        selectAllLabel="✓ Select All"
                        placeholder="Search channels..."
                      />
                    </div>

                    <div
                      className={styles.chartActionsRow}
                      style={{ alignItems: "flex-end" }}
                    >
                      <div
                        className={styles.controlField}
                        style={{ width: "140px", margin: 0 }}
                      >
                        <label className={styles.chartControlLabel}>
                          Alarm Severity:
                        </label>
                        <select
                          className={styles.compareViewSelect}
                          value={alarmSeverityFilter}
                          onChange={(e) =>
                            setAlarmSeverityFilter(
                              e.target.value as "critical" | "warning",
                            )
                          }
                        >
                          <option value="critical">Critical</option>
                          <option value="warning">Warning</option>
                        </select>
                      </div>

                      {chartSeries.length > 0 && fromDate && toDate && (
                        <button
                          type="button"
                          className={styles.btnSecondary}
                          onClick={() =>
                            openAssetChartPopout(
                              fromDate.getTime(),
                              toDate.getTime(),
                              selectedAssetChannels,
                            )
                          }
                        >
                          <span className={styles.exportBtnContent}>
                            <FiExternalLink size={16} />
                            <span>Open Graph</span>
                          </span>
                        </button>
                      )}

                      <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={() => setShowAxisModal(true)}
                      >
                        <span className={styles.exportBtnContent}>
                          <FiSettings size={16} />
                          <span>Configure Axes</span>
                        </span>
                      </button>
                      <button
                        className={styles.btnSecondary}
                        onClick={() => setShowExportModal(true)}
                      >
                        <span className={styles.exportBtnContent}>
                          <FiDownload size={16} />
                          <span>Export Data</span>
                        </span>
                      </button>
                    </div>
                  </div>

                  {assetZoomLoading && (
                    <div
                      role="status"
                      aria-live="polite"
                      style={{
                        textAlign: "center",
                        margin: "0.25rem 0 0.5rem",
                        color: "var(--color-text-muted)",
                        fontSize: "0.82rem",
                      }}
                    >
                      <span style={{ marginRight: "0.35rem" }}>⏳</span>
                      Loading higher-resolution detail for the zoomed range…
                    </div>
                  )}

                  {chartSeries.length > 0 && assetDetailStatusText && (
                    <div
                      role="status"
                      aria-live="polite"
                      style={{
                        textAlign: "center",
                        margin: "0.25rem 0 0.5rem",
                        color: "var(--color-text-muted)",
                        fontSize: "0.82rem",
                      }}
                    >
                      {assetHighResolutionLoading && (
                        <span style={{ marginRight: "0.35rem" }}>⏳</span>
                      )}
                      {assetDetailStatusText}
                    </div>
                  )}

                  {chartSeries.length > 0 ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      immutable={true}
                      options={assetChartOptions}
                      ref={chartRef}
                      callback={bindEditableYAxisLabels}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "2rem 1rem 1.5rem",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 600,
                          marginBottom: "0.4rem",
                          color: "var(--color-text)",
                        }}
                      >
                        Pump Data - Asset View
                      </div>
                      <div
                        className={styles.noDataText}
                        role="status"
                        aria-live="polite"
                      >
                        {isLoadDataLoading
                          ? assetLoadStatusText
                          : assetLoadErrorText ||
                            "No data available for the selected range."}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={styles.tableContainer}
                  style={{ borderRadius: "8px", padding: "1rem" }}
                >
                  <div style={{ marginBottom: "1rem" }}>
                    <div
                      className={styles.chartControlsGrid}
                      style={{ marginBottom: "1rem" }}
                    >
                      <div className={styles.controlField}>
                        <label className={styles.chartControlLabel}>
                          Table Template:
                        </label>
                        <CustomSingleSelect
                          options={templates
                            .filter((t) => t.widget_type === "TABLE")
                            .map((t) => ({
                              value: String(t.id),
                              label: t.template_name,
                              template: t,
                            }))}
                          value={selectedTableTemplate}
                          onChange={(option: any) => {
                            setSelectedTableTemplate(option);

                            if (!option) {
                              return;
                            }

                            const channels: PumpChannel[] =
                              filterUnsupportedPumpChannels(
                                option.template.channels.map(
                                  (ch: TemplateChannel) => ({
                                    value: ch.channel,
                                    label:
                                      availableChannels.find(
                                        (channel) =>
                                          channel.value === ch.channel,
                                      )?.label || ch.channel,
                                  }),
                                ),
                              );

                            setSelectedTableChannels(channels);

                            // Asset table channels are independent from Fleet.
                            // Reload using the final table selection so the API
                            // returns those columns before aggregation.
                            if (
                              selectedAssetPumps.length > 0 &&
                              fromDate &&
                              toDate
                            ) {
                              void loadAssetData(
                                fromDate,
                                toDate,
                                channels,
                                "table",
                              );
                            }
                          }}
                          placeholder="Select Template"
                          isClearable
                        />
                      </div>

                      <div className={styles.controlField}>
                        <label className={styles.chartControlLabel}>
                          Table Channels:
                          {isAssetTableLoading && (
                            <span
                              style={{
                                marginLeft: "0.5rem",
                                color: "#f59e0b",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                              }}
                            >
                              Loading selected table channels...
                            </span>
                          )}
                        </label>
                        <CustomMultiSelect
                          options={availableChannels}
                          selected={selectedTableChannels}
                          onChange={(sel) => {
                            const channels = sel as PumpChannel[];

                            setSelectedTableTemplate(null);
                            setSelectedTableChannels(channels);

                            if (
                              selectedAssetPumps.length > 0 &&
                              fromDate &&
                              toDate
                            ) {
                              void loadAssetData(
                                fromDate,
                                toDate,
                                channels,
                                "table",
                              );
                            }
                          }}
                          selectAllLabel="✓ Select All"
                          placeholder="Search channels."
                        />
                      </div>

                      <div className={styles.controlField}>
                        <label className={styles.chartControlLabel}>
                          Filter Pumps:
                        </label>

                        <CustomMultiSelect
                          options={availablePumps.map((pump) => ({
                            value: pump,
                            label: pump,
                          }))}
                          selected={selectedAssetTablePumps.map((pump) => ({
                            value: pump,
                            label: pump,
                          }))}
                          onChange={(sel) => {
                            setSelectedAssetTablePumps(
                              sel.map((option) => option.value),
                            );
                          }}
                          selectAllLabel="✓ Select All"
                          placeholder={
                            isAssetTableLoading
                              ? "Loading pumps..."
                              : availablePumps.length === 0
                                ? "No pumps with data"
                                : "Search pumps..."
                          }
                          isDisabled={
                            isAssetTableLoading || availablePumps.length === 0
                          }
                        />
                      </div>

                      <div
                        className={styles.chartActionsRow}
                        style={{ alignItems: "flex-end" }}
                      >
                        <button
                          className={styles.btnSecondary}
                          onClick={() => setShowExportModal(true)}
                          disabled={isStreaming}
                          style={{
                            opacity: isStreaming ? 0.5 : 1,
                            cursor: isStreaming ? "not-allowed" : "pointer",
                          }}
                        >
                          <span className={styles.exportBtnContent}>
                            <FiDownload size={16} />
                            <span>Export Data</span>
                          </span>
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "1rem",
                        alignItems: "center",
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <label
                        style={{ color: "var(--color-text)", fontWeight: 500 }}
                      >
                        Aggregation:
                      </label>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        {(["avg", "sum", "min", "max"] as const).map((type) => (
                          <button
                            key={type}
                            onClick={() => setAggregationType(type)}
                            style={{
                              padding: "0.5rem 1rem",
                              border: `2px solid ${
                                aggregationType === type
                                  ? "var(--color-green)"
                                  : "var(--color-border)"
                              }`,
                              borderRadius: "6px",
                              backgroundColor:
                                aggregationType === type
                                  ? "var(--color-green)"
                                  : "transparent",
                              color:
                                aggregationType === type
                                  ? "white"
                                  : "var(--color-text)",
                              cursor: "pointer",
                              fontWeight: aggregationType === type ? 600 : 500,
                              fontSize: "0.875rem",
                              transition: "all 0.2s ease",
                              textTransform: "uppercase",
                            }}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className={styles.historicalTableScroll}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={styles.tableHeader}>Pump</th>
                          {selectedTableChannels.map((ch) => (
                            <th key={ch.value} className={styles.tableHeader}>
                              {ch.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAssetTableEntries.length > 0 ? (
                          filteredAssetTableEntries.map(
                            ([pumpName, data]: [string, any]) => (
                              <tr key={pumpName} className={styles.tableRow}>
                                <td className={styles.tableCell}>{pumpName}</td>
                                {selectedTableChannels.map((ch) => (
                                  <td
                                    key={ch.value}
                                    className={styles.tableCell}
                                    style={getThresholdCellStyle(
                                      getThresholdSeverity(
                                        getAggregatedChannelValue(
                                          data,
                                          ch.value,
                                          aggregationType,
                                        ),
                                        ch.value,
                                        getAggregatedRowSnapshot(
                                          data,
                                          aggregationType,
                                        ),
                                      ),
                                    )}
                                  >
                                    {(() => {
                                      const value = getAggregatedChannelValue(
                                        data,
                                        ch.value,
                                        aggregationType,
                                      );

                                      if (
                                        value === null ||
                                        value === undefined ||
                                        Number.isNaN(Number(value))
                                      ) {
                                        return isAssetTableLoading
                                          ? "Loading..."
                                          : "N/A";
                                      }

                                      return Number(value).toFixed(2);
                                    })()}
                                  </td>
                                ))}
                              </tr>
                            ),
                          )
                        ) : (
                          <tr>
                            <td
                              colSpan={selectedTableChannels.length + 1}
                              className={styles.noDataTable}
                            >
                              {isAssetTableLoading
                                ? assetLoadStatusText
                                : assetLoadErrorText
                                  ? assetLoadErrorText
                                  : Object.keys(aggregatedData).length > 0
                                    ? "No pumps selected for table."
                                    : "No data available."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ManageTemplatesModal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        theme={theme}
        templates={templates}
        onTemplatesChange={fetchTemplates}
        selectedChannels={
          templateModalContext.view === "fleet"
            ? selectedChannelsByFleetRef.current[
                templateModalContext.fleetId
              ] ||
              selectedChannelsByFleet[templateModalContext.fleetId] ||
              []
            : selectedAssetChannels
        }
        selectedTableChannels={
          templateModalContext.view === "fleet"
            ? selectedTableChannelsByFleetRef.current[
                templateModalContext.fleetId
              ] ||
              selectedTableChannelsByFleet[templateModalContext.fleetId] ||
              defaultChannels
            : selectedTableChannels
        }
        axisSettings={axisSettings}
        availableChannels={availableChannels}
      />

      {showAlarmModal && (
        <div className={styles.alarmModalOverlay}>
          <div className={styles.alarmModal}>
            <div className={styles.alarmModalHeader}>
              <h3>Alarm Events</h3>

              <button type="button" onClick={() => setShowAlarmModal(false)}>
                ×
              </button>
            </div>

            <div className={styles.alarmTableWrapper}>
              <table className={styles.alarmTable}>
                <thead>
                  <tr>
                    <th>Pump</th>
                    <th>Severity</th>
                    <th>Alarm</th>
                    <th>Alarm Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {Array.from(
                    new Map(
                      selectedAlarmEvents.map((alarm: any) => [
                        `${String(alarm?.pump || "")}::${String(alarm?.alarm || "")}::${String(
                          alarm?.alarm_time ??
                            alarm?.time_stamp ??
                            alarm?.timestamp ??
                            "",
                        )}`,
                        alarm,
                      ]),
                    ).values(),
                  )
                    .filter(
                      (alarm: any) =>
                        normalizeAlarmSeverity(alarm) === alarmSeverityFilter,
                    )
                    .map((alarm, index) => {
                      const severity = normalizeAlarmSeverity(alarm);
                      const severityMeta = getAlarmSeverityMeta(severity);

                      return (
                        <tr
                          key={`${alarm.pump}-${alarm.alarm}-${index}`}
                          className={styles[`alarmRow${severityMeta.label}`]}
                        >
                          <td>{alarm.pump}</td>
                          <td>
                            <span className={styles.alarmSeverityBadge}>
                              <span
                                className={styles.alarmSeverityIcon}
                                style={{ color: severityMeta.color }}
                              >
                                {severityMeta.icon}
                              </span>
                              {severityMeta.label}
                            </span>
                          </td>
                          <td>{alarm.alarm}</td>

                          <td>
                            {getAlarmDate(alarm)?.toLocaleString() || "-"}
                          </td>

                          <td>
                            <div className={styles.alarmActions}>
                              <button
                                type="button"
                                className={styles.btnSecondary}
                                onClick={() => handleReviewAlarmData(alarm)}
                              >
                                Review Data
                              </button>

                              <button
                                type="button"
                                className={styles.btnSecondary}
                                onClick={() => handleExportAlarmData(alarm)}
                              >
                                Export 1s
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showAnnotationModal && selectedAnnotation && (
        <div className={styles.alarmModalOverlay}>
          <div className={styles.alarmModal}>
            <div className={styles.alarmModalHeader}>
              <h3>Chart Comment</h3>

              <button
                type="button"
                onClick={() => {
                  setShowAnnotationModal(false);
                  setSelectedAnnotation(null);
                  setSelectedAnnotationGroup([]);
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "1rem" }}>
              <p>
                <strong>Time:</strong>{" "}
                {new Date(selectedAnnotation.timestamp).toLocaleString()}
              </p>

              {selectedAnnotation.pump && (
                <p>
                  <strong>Pump:</strong> {selectedAnnotation.pump}
                </p>
              )}

              <p>
                <strong>Comments:</strong>
              </p>

              {selectedAnnotationGroup.map((item, index) => (
                <div
                  key={item.id}
                  style={{
                    background: theme === "dark" ? "#3d4247" : "#f8fafc",
                    border: "1px solid var(--color-border)",
                    borderRadius: "8px",
                    padding: "0.75rem",
                    marginBottom: "0.75rem",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <strong>Comment {index + 1}:</strong>

                  <div style={{ marginTop: "0.35rem" }}>{item.comment}</div>

                  {canManageAnnotation(item) && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px",
                        marginTop: "10px",
                      }}
                    >
                      <button
                        onClick={() => handleEditAnnotation(item)}
                        style={{
                          background: "#8CC540",
                          border: "none",
                          color: "#fff",
                          padding: "6px 14px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      >
                        ✏ Edit
                      </button>

                      <button
                        onClick={() => handleDeleteAnnotation(item)}
                        style={{
                          background: "#dc3545",
                          border: "none",
                          color: "#fff",
                          padding: "6px 14px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showAnnotationAuditModal && selectedAuditAnnotation && (
        <div className={styles.alarmModalOverlay}>
          <div className={styles.alarmModal}>
            <div className={styles.alarmModalHeader}>
              <h3>Comment Audit History</h3>

              <button
                type="button"
                onClick={() => {
                  setShowAnnotationAuditModal(false);
                  setSelectedAuditAnnotation(null);
                  setAnnotationAuditRows([]);
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "1rem" }}>
              <p>
                <strong>Comment:</strong> {selectedAuditAnnotation.comment}
              </p>
              <p>
                <strong>Annotation Time:</strong>{" "}
                {new Date(selectedAuditAnnotation.timestamp).toLocaleString()}
              </p>

              <div className={styles.alarmTableWrapper}>
                <table className={styles.alarmTable}>
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Performed By</th>
                      <th>Performed At</th>
                      <th>Old Comment</th>
                      <th>New Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {annotationAuditLoading ? (
                      <tr>
                        <td colSpan={5}>Loading history...</td>
                      </tr>
                    ) : annotationAuditRows.length === 0 ? (
                      <tr>
                        <td colSpan={5}>No audit history found.</td>
                      </tr>
                    ) : (
                      annotationAuditRows.map((row, index) => {
                        const actionType = getAuditValue(
                          row,
                          "actionType",
                          "action_type",
                        );
                        const performedByEmail = getAuditValue(
                          row,
                          "performedByEmail",
                          "performed_by_email",
                        );
                        const performedAt = getAuditValue(
                          row,
                          "performedAt",
                          "performed_at",
                        );
                        const oldComment = getAuditValue(
                          row,
                          "oldComment",
                          "old_comment",
                        );
                        const newComment = getAuditValue(
                          row,
                          "newComment",
                          "new_comment",
                        );

                        return (
                          <tr key={`${selectedAuditAnnotation.id}-${index}`}>
                            <td>{actionType || "-"}</td>
                            <td>{performedByEmail || "-"}</td>
                            <td>
                              {performedAt
                                ? new Date(Number(performedAt)).toLocaleString()
                                : "-"}
                            </td>
                            <td style={{ whiteSpace: "pre-wrap" }}>
                              {oldComment || "-"}
                            </td>
                            <td style={{ whiteSpace: "pre-wrap" }}>
                              {newComment || "-"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportDataModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        theme={theme}
        selectedChannels={selectedChannels}
        availableChannels={availableChannels}
        fromDate={fromDate}
        toDate={toDate}
        activeTab={activeTab}
        fleetNumber={selectedFleet?.fleet}
        pumpNames={
          activeTab === "asset"
            ? selectedAssetPumps.map((p) => p.value)
            : undefined
        }
        backendUrl={backendUrl}
      />

      {showAxisModal && (
        <>
          <div
            className={styles.axisModalOverlay}
            onClick={() => setShowAxisModal(false)}
          />

          <div
            className={styles.axisModalContainer}
            role="dialog"
            aria-modal="true"
            aria-labelledby="configure-axes-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.axisModalHeader}>
              <h3 id="configure-axes-title" className={styles.axisModalTitle}>
                Configure Axes
              </h3>

              <button
                type="button"
                className={styles.axisModalCloseButton}
                onClick={() => setShowAxisModal(false)}
                aria-label="Close Configure Axes modal"
              >
                ×
              </button>
            </div>

            <div className={styles.axisModalBody}>
              {["left1", "right1", "left2", "right2"].map((axis) => {
                const axisRangeError = getAxisRangeError(axis);
                const isAutoScale = axisAutoScale[axis];
                const selectedAxisChannels = axisSettings[axis].channels
                  .map((channelValue) => findChannelByValue(channelValue))
                  .filter(Boolean) as PumpChannel[];

                return (
                  <div key={axis} className={styles.axisSection}>
                    <h4 className={styles.axisSectionTitle}>
                      {getAxisTitle(axis)}
                    </h4>

                    <div className={styles.axisChannelsRow}>
                      <label className={styles.axisFieldLabel}>Channels</label>
                      <CustomMultiSelect
                        options={getAxisSelectableChannels(axis)}
                        selected={selectedAxisChannels}
                        onChange={(sel) =>
                          handleAxisChannelsChange(axis, sel as PumpChannel[])
                        }
                        placeholder="Search channels..."
                      />
                    </div>

                    <div className={styles.axisAutoScaleRow}>
                      <span className={styles.axisAutoScaleLabel}>
                        Auto Scale
                      </span>

                      <label className={styles.axisSwitch}>
                        <input
                          type="checkbox"
                          checked={isAutoScale}
                          onChange={(e) =>
                            handleAxisAutoScaleChange(axis, e.target.checked)
                          }
                        />
                        <span className={styles.axisSlider} />
                      </label>

                      <span className={styles.axisAutoScaleState}>
                        {isAutoScale ? "ON" : "OFF"}
                      </span>
                    </div>

                    <div className={styles.axisMinMaxRow}>
                      <div className={styles.axisMinMaxField}>
                        <label className={styles.axisFieldLabel}>Min</label>
                        <input
                          type="number"
                          value={axisSettings[axis].min ?? ""}
                          onChange={(e) =>
                            handleAxisRangeChange(axis, "min", e.target.value)
                          }
                          placeholder="Auto"
                          disabled={isAutoScale}
                          className={`${styles.axisInput} ${axisRangeError ? styles.fleetControlInputError : ""}`}
                        />
                      </div>

                      <div className={styles.axisMinMaxField}>
                        <label className={styles.axisFieldLabel}>Max</label>
                        <input
                          type="number"
                          value={axisSettings[axis].max ?? ""}
                          onChange={(e) =>
                            handleAxisRangeChange(axis, "max", e.target.value)
                          }
                          placeholder="Auto"
                          disabled={isAutoScale}
                          className={`${styles.axisInput} ${axisRangeError ? styles.fleetControlInputError : ""}`}
                        />
                      </div>
                    </div>

                    {axisRangeError && (
                      <div className={styles.axisModalErrorBox}>
                        <span className={styles.axisModalErrorIcon}>⚠</span>
                        <span className={styles.axisModalErrorText}>
                          {axisRangeError}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className={styles.axisModalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setShowAxisModal(false)}
              >
                Close
              </button>

              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => setShowAxisModal(false)}
                disabled={hasAxisValidationErrors}
                style={{
                  opacity: hasAxisValidationErrors ? 0.55 : 1,
                  cursor: hasAxisValidationErrors ? "not-allowed" : "pointer",
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {axisValueEdit && (
        <>
          <div
            className={styles.axisValueModalOverlay}
            onClick={closeAxisValueEditor}
          />
          <div
            className={styles.axisValueModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="axis-value-modal-title"
          >
            <div className={styles.axisValueModalHeader}>
              <div>
                <h3
                  id="axis-value-modal-title"
                  className={styles.axisValueModalTitle}
                >
                  Edit Axis Scale
                </h3>
                <p className={styles.axisValueModalSubtitle}>
                  {axisValueEdit.axisTitle}
                </p>
              </div>
              <button
                type="button"
                className={styles.axisValueModalClose}
                onClick={closeAxisValueEditor}
                aria-label="Close axis scale modal"
              >
                ×
              </button>
            </div>

            <div className={styles.axisValueModalBody}>
              <div className={styles.axisValueInfoGrid}>
                <div className={styles.axisValueInfoCard}>
                  <span className={styles.axisValueInfoLabel}>
                    Clicked value
                  </span>
                  <strong>
                    {Number(axisValueEdit.clickedValue.toFixed(2))}
                  </strong>
                </div>
                <div className={styles.axisValueInfoCard}>
                  <span className={styles.axisValueInfoLabel}>
                    Current range
                  </span>
                  <strong>
                    {Number(axisValueEdit.currentMin.toFixed(2))} –{" "}
                    {Number(axisValueEdit.currentMax.toFixed(2))}
                  </strong>
                </div>
              </div>

              <label className={styles.axisValueInputLabel}>
                New value
                <input
                  type="number"
                  value={axisValueInput}
                  onChange={(event) => {
                    setAxisValueInput(event.target.value);
                    setAxisValueEdit((prev) =>
                      prev ? { ...prev, error: undefined } : prev,
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") applyAxisValueEdit();
                    if (event.key === "Escape") closeAxisValueEditor();
                  }}
                  className={`${styles.axisValueInput} ${
                    axisValueEdit.error ? styles.axisErrorInput : ""
                  }`}
                  autoFocus
                />
              </label>

              {axisValueEdit.error && (
                <div className={styles.axisModalErrorBox}>
                  <span className={styles.axisModalErrorIcon}>⚠</span>
                  <span className={styles.axisModalErrorText}>
                    {axisValueEdit.error}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.axisValueModalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={closeAxisValueEditor}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={resetAxisValueToAuto}
              >
                Reset Auto
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={applyAxisValueEdit}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      <PumpDetailsModal
        show={showPumpDetailsModal}
        onHide={() => setShowPumpDetailsModal(false)}
        theme={theme}
        pumpNames={availablePumps}
        initialFromDate={fromDate}
        initialToDate={toDate}
      />

      {showBackToTop && (
        <button
          type="button"
          className={styles.backToTopButton}
          onClick={handleBackToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <span className={styles.backToTopIcon} aria-hidden="true">
            ↑
          </span>
        </button>
      )}
    </section>
  );
};
export default Fleet;
